/**
 * API Client for Fias Platform
 */

import type { VaultFileKind } from './vault-file-kinds';
import type {
  AgentAppGrantEntry,
  AgentAppSummary,
  AgentKeyAppGrant,
  AgentKeyGrantCapabilities,
} from './agent-app-grants';
import { API_BASE, API_ORIGIN, isCodespacesHost } from './api-base';
import { traceApiRequest, traceApiResponse, traceApiError } from './telemetry';
import { sessionCredentials } from './embed-document';
import { maintenanceState } from './maintenance';
import { attachCsrfHeader } from './csrf';
import { refreshAuthSession, notifySessionExpired } from './auth-refresh';
import { retryWhileSessionRefreshing } from './session-refreshing';
import {
  CHAT_FILE_TYPE_POLICY,
  resolveFileType,
  type ChatSearchMatchMode,
} from '@fias/platform-resources';
import type { PluginPermission } from '@fias/arche-sdk';
import type { CompanyPortalBrand } from './auth-api';
import { withoutPackageFiles } from './builder-files';

// `API_BASE` / `API_ORIGIN` live in a leaf module (`api-base.ts`) and are
// re-exported here so the ~20 existing `from '@/lib/api'` importers are
// unchanged. The split exists because `telemetry.ts` needs the base URL and
// cannot import this file (this file imports telemetry's tracers) — see the
// header comment on api-base.ts for the bug that cycle caused.
export { API_BASE, API_ORIGIN };

/**
 * Append a status filter to URLSearchParams, supporting multi-select: a single
 * value or an array become repeated `status=` params (`?status=a&status=b`),
 * which the support admin routes parse back into an array.
 */
function appendStatusParams(params: URLSearchParams, status: string | string[] | undefined): void {
  if (status == null) return;
  const list = Array.isArray(status) ? status : [status];
  for (const s of list) {
    if (s) params.append('status', s);
  }
}

// Same Codespaces override for WebSockets. The WS service is on port 4001
// inside the Codespace, but the laptop browser can't reach it directly.
// Connect to the page's own origin on path `/stream` and let Vite's proxy
// (with ws:true) upgrade and forward to ws://localhost:4001/stream.
// Falls back to VITE_WEBSOCKET_ENDPOINT on WSL/native dev (where the env
// var points directly at ws://localhost:4001/stream).
export const WEBSOCKET_ENDPOINT = (() => {
  const envValue = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
  if (!isCodespacesHost) return envValue;
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${window.location.host}/stream`;
})();

/** GET/PUT /contributor/arches/:archeId/content-billing — mirrors PluginContentBillingSummary. */
export interface ContentPackBillingResponse {
  summary: {
    month: string;
    storageCredits: number;
    egressCredits: number;
    egressBytes: { read: number; url: number; other: number };
    provisionalEgressBytes: { read: number; url: number; other: number };
    chargedDays: number;
    failedDays: number;
    heldDays: number;
    alertThresholdBytes: number;
    hardCapBytes: number | null;
    throttledToday: boolean;
    publishingSuspendedAt: string | null;
  };
  rates: {
    storageCreditsPerGbMonth: number;
    egressCreditsPerGb: number;
    billedWhen: string;
    finalizeAfterHours: number;
    retentionDays: number;
  };
  canEdit: boolean;
}

export class ApiError extends Error {
  /**
   * For INSUFFICIENT_CREDITS errors, true when the user's total balance
   * across every bucket cannot cover the request — i.e. they need to top
   * up before they can do anything else. The global request interceptor
   * uses this to auto-navigate to the credits store.
   */
  public readonly allBucketsExhausted?: boolean;
  public readonly isCoreSystem?: boolean;
  /**
   * For document-sign `ENVELOPE_TERMINAL` (410) errors, the envelope's
   * terminal status (`completed` / `voided` / `declined` / `expired`). Lets
   * the SigningPage offer a download for a completed envelope rather than a
   * flat "no longer active" message.
   */
  public readonly envelopeStatus?: string;
  /** The calendar warned-reset 409 payload (challenge + counts). */
  public readonly confirmation?: SeriesRescheduleConfirmation;
  /**
   * Per-field validation issues, when the server returns them (e.g. the
   * `CONFIG_SCHEMA_INVALID` envelope from structured config writes). Each entry
   * is a dotted field `path` and its `message`, so a form can point at the
   * offending field instead of showing a single generic "invalid" line.
   */
  public readonly issues?: ApiErrorIssue[];
  /**
   * The parsed error body, for the few endpoints whose refusal carries data
   * the caller acts on — e.g. the Save dialog's 409 `NAME_EXISTS`, which lists
   * the files a save would collide with. Read it through a typed accessor at
   * the call site; never trust its shape blindly.
   */
  public readonly body?: unknown;

  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    extras?: {
      allBucketsExhausted?: boolean;
      isCoreSystem?: boolean;
      envelopeStatus?: string;
      issues?: ApiErrorIssue[];
      confirmation?: SeriesRescheduleConfirmation;
      body?: unknown;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    if (extras) {
      this.allBucketsExhausted = extras.allBucketsExhausted;
      this.isCoreSystem = extras.isCoreSystem;
      this.envelopeStatus = extras.envelopeStatus;
      this.confirmation = extras.confirmation;
      this.issues = extras.issues;
      this.body = extras.body;
    }
  }
}

/** A single per-field validation issue surfaced by the server. */
export interface ApiErrorIssue {
  path: string;
  message: string;
}

/** One failed invocation, as recorded by the invocation gateway's audit trail. */
export interface InvocationFailureDetail {
  invocationId: string;
  requestId: string;
  entityId: string;
  /**
   * `interrupted` is server-substituted for a row left `pending` past the
   * provider ceiling (a crash mid-request), so it IS reachable on the wire.
   * `cancelled` is a legal value of the audit enum but has no writer today.
   */
  outcome: 'failure' | 'cancelled' | 'interrupted';
  /** Gateway error classification (`timeout`, `provider_server`, …). */
  errorCode: string | null;
  startedAt: string;
  latencyMs: number | null;
}

/**
 * Failure counts for one (entity, error code) pair on a published arche, over
 * the whole requested window. Carries no identity and no time dimension — see
 * the server-side module for why removing the timestamp IS the privacy
 * mechanism.
 */
export interface InvocationFailureBucket {
  entityId: string;
  errorCode: string | null;
  failureCount: number;
}

/** Extracted fields from a response body that the API client raises as an `ApiError`. */
export interface ExtractedApiError {
  message: string;
  code?: string;
  allBucketsExhausted?: boolean;
  isCoreSystem?: boolean;
  envelopeStatus?: string;
  issues?: ApiErrorIssue[];
  confirmation?: SeriesRescheduleConfirmation;
}

/** The calendar warned-reset 409 payload: authoritative counts + the signed
 *  state-bound challenge presented on the confirmed retry. */
export interface SeriesRescheduleConfirmation {
  challenge: string;
  changedCount: number;
  deletedCount: number;
}

/**
 * Single source of truth for turning a non-OK response body into a
 * user-readable error. Covers every envelope the platform produces:
 *
 *   - Lambda / Fastify route handlers: `{ error: { code, message } }`.
 *   - Legacy APIs: `{ error: "string" }`.
 *   - Fastify default errors: `{ statusCode, error: "Bad Request", message: "real text" }`
 *     — `message` is the real explanation; `error` is just the status name. Prefer `message`.
 *   - API Gateway / Cognito / WAF: `{ message: "..." }`.
 *   - Non-JSON text bodies (nginx 502 HTML, ALB plain text, Cognito unauthorized, …):
 *     surfaced verbatim when short, replaced with a generic message for HTML.
 *
 * The default fallback names the HTTP status so even an unrecognized
 * envelope tells the user what went wrong.
 */
export function extractErrorFromResponse(
  data: unknown,
  responseText: string,
  status: number,
): ExtractedApiError {
  type ErrorEnvelope = {
    error?:
      | string
      | {
          code?: string;
          message?: string;
          allBucketsExhausted?: boolean;
          isCoreSystem?: boolean;
          envelopeStatus?: string;
          issues?: unknown;
        };
    message?: string;
    confirmation?: SeriesRescheduleConfirmation;
  };
  const errorData = data as ErrorEnvelope | null;

  let message = `Request failed (status ${status})`;
  let code: string | undefined;
  let allBucketsExhausted: boolean | undefined;
  let isCoreSystem: boolean | undefined;
  let envelopeStatus: string | undefined;
  let issues: ApiErrorIssue[] | undefined;
  let confirmation: SeriesRescheduleConfirmation | undefined;

  if (errorData) {
    if (typeof errorData.error === 'object' && errorData.error?.message) {
      message = errorData.error.message;
      code = errorData.error.code;
      allBucketsExhausted = errorData.error.allBucketsExhausted;
      isCoreSystem = errorData.error.isCoreSystem;
      envelopeStatus = errorData.error.envelopeStatus;
      issues = parseApiErrorIssues(errorData.error.issues);
      if (
        errorData.confirmation &&
        typeof errorData.confirmation.challenge === 'string' &&
        typeof errorData.confirmation.changedCount === 'number' &&
        typeof errorData.confirmation.deletedCount === 'number'
      ) {
        confirmation = errorData.confirmation;
      }
    } else if (typeof errorData.error === 'string' && errorData.error.length > 0) {
      // Fastify default errors put the human-readable text in `message` and
      // the status name in `error` — surface `message` when present so users
      // see "Body must be a valid JSON" instead of "Bad Request".
      message = errorData.message ?? errorData.error;
    } else if (errorData.message) {
      message = errorData.message;
    }
  } else if (responseText.trim().length > 0 && responseText.length < 500) {
    // Surface short non-JSON bodies verbatim, but skip HTML pages — a 400-byte
    // chunk of nginx HTML in a toast is ugly and unhelpful.
    message = /^\s*<[!a-z]/i.test(responseText)
      ? `Server error (status ${status})`
      : responseText.trim();
  }

  return { message, code, allBucketsExhausted, isCoreSystem, envelopeStatus, issues, confirmation };
}

/**
 * Normalize the server's `issues` array (dotted `path` + `message` per field)
 * into a typed list, tolerating a malformed/partial shape by dropping entries
 * that aren't `{ path, message }`. Returns undefined when there's nothing usable
 * so callers can fall back to the single top-level message.
 */
function parseApiErrorIssues(raw: unknown): ApiErrorIssue[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const parsed = raw.flatMap((entry) => {
    if (entry && typeof entry === 'object') {
      const { path, message } = entry as { path?: unknown; message?: unknown };
      if (typeof message === 'string' && message.length > 0) {
        return [{ path: typeof path === 'string' ? path : '', message }];
      }
    }
    return [];
  });
  return parsed.length > 0 ? parsed : undefined;
}

/**
 * Reads a `Response` body once, returning both the raw text (always) and
 * a parsed JSON form (when the body looks like JSON). Returning both
 * lets the caller surface non-JSON bodies verbatim while still using
 * `data` to read structured error envelopes.
 */
async function readResponseBody(response: Response): Promise<{
  data: unknown;
  responseText: string;
}> {
  const responseText = await response.text();
  let data: unknown = null;
  if (responseText.length > 0) {
    try {
      data = JSON.parse(responseText);
    } catch {
      // Leave data null; the caller's error path will fall back to responseText.
    }
  }
  return { data, responseText };
}

// ---------------------------------------------------------------------------
// Vault consent — internal wire shape for /vault/consents responses.
// Matches `VaultConsentWithStatus` from @fias/db-types after Fastify
// JSON serialization (Date → ISO string). Not exported; the client
// translates to the calendar-specific shapes defined further below.
// Placed up here (not next to the calendar types) so the ApiClient
// methods can reference CALENDAR_DATA_CLASS and the converter without
// a forward-reference to a block-scoped const further down the file.
// ---------------------------------------------------------------------------

interface VaultConsentWire {
  consentId: string;
  granterHolderId: string;
  granteeKind: 'holder';
  granteeHolderId: string | null;
  dataClass: string;
  scopeJson: Record<string, unknown>;
  grantedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  grantedByPrincipalId: string;
  status: 'active' | 'expired' | 'revoked';
}

/**
 * The Calendar Vault Entity ID. Hardcoded here because @fias/entities
 * isn't a client dep; the value is substrate-immutable (the entity_id
 * the vault-calendar-consent.ts validator registers against and the
 * routes scope on).
 */
const CALENDAR_DATA_CLASS = 'ent_intg_vault_calendar_00000000';

function vaultConsentToCalendarShare(c: VaultConsentWire, calendarId: string): CalendarShareDetail {
  const calendarIds = c.scopeJson?.calendar_ids;
  return {
    consentId: c.consentId,
    calendarId,
    granteeHolderId: c.granteeHolderId ?? '',
    visibility: (c.scopeJson?.visibility as CalendarShareVisibility) ?? 'free-busy',
    dateRange: c.scopeJson?.date_range as CalendarShareDateRange | undefined,
    createdAt: c.grantedAt,
    expiresAt: c.expiresAt,
    // Omitted/null calendar_ids = all-calendars grant. Substrate rejects
    // empty arrays, so a defined non-array would already be a substrate
    // violation; treat anything non-array as global to be safe.
    isGlobal: !Array.isArray(calendarIds),
  };
}

class ApiClient {
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: {
      signal?: AbortSignal;
      isRetry?: boolean;
      /** Let the request outlive the page (a save flushed as the tab closes).
       *  The browser caps a keepalive body at 64 KB — only for small bodies. */
      keepalive?: boolean;
    },
  ): Promise<T> {
    const url = `${API_BASE}${path}`;
    const traceId = traceApiRequest(method, url, body);

    const headers: Record<string, string> = {};

    // Only set Content-Type if there's a body to send
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    // Cookie-based auth: cookies are auto-attached via credentials:'include'.
    // For unsafe verbs we also echo the CSRF cookie value as a header so
    // the backend's double-submit check passes (see lib/csrf.ts).
    attachCsrfHeader(method, headers);

    // A Company portal's session is refreshed server-side; a request that
    // lands mid-refresh gets 503 SESSION_REFRESHING (still signed in). Wait
    // out the Retry-After once and replay (ADR-024). The replay is a fresh
    // request, so a 401 on it still gets its refresh-and-retry below.
    let response: Response;
    try {
      response = await retryWhileSessionRefreshing(
        () =>
          fetch(url, {
            method,
            headers,
            credentials: sessionCredentials(),
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal: options?.signal,
            ...(options?.keepalive ? { keepalive: true } : {}),
          }),
        { maxRetries: 1, signal: options?.signal },
      );
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }

    // Any successful response from the API means maintenance is no longer
    // blocking us — clear the global flag so the SPA stops showing the
    // maintenance page on the next render.
    if (response.ok && maintenanceState.getState().isActive) {
      maintenanceState.clearActive();
    }

    // Handle 204 No Content responses (e.g., DELETE operations)
    if (response.status === 204) {
      traceApiResponse(traceId, response.status, null);
      return undefined as T;
    }

    const { data, responseText } = await readResponseBody(response);
    traceApiResponse(traceId, response.status, data ?? responseText);

    // Transparent recovery from an expired session cookie (401) or a stale CSRF
    // token (403 CSRF_FAILED). The CSRF cookie can lapse or rotate while a session
    // sits idle, and a stale state-changing request then 403s instead of 401ing —
    // historically these failed silently (writes broke while reads, which carry no
    // CSRF, kept self-healing on 401). One refresh re-mints BOTH the session and
    // CSRF cookies; replay the request afterward. Skipped for /auth/* (their own
    // flow) and for the retry attempt itself (single retry, no loops).
    const isCsrfFailure =
      response.status === 403 &&
      (data as { error?: { code?: string } } | null)?.error?.code === 'CSRF_FAILED';
    if (
      (response.status === 401 || isCsrfFailure) &&
      !options?.isRetry &&
      !path.startsWith('/auth/') &&
      !(options?.signal?.aborted ?? false)
    ) {
      const refreshed = await refreshAuthSession();
      if (refreshed) {
        return this.request<T>(method, path, body, { ...options, isRetry: true });
      }
      // Refresh-token rejected: the user is genuinely signed out — prompt re-login.
      // The throw below still fires so the caller's existing error handling runs —
      // the modal is purely additive UX.
      notifySessionExpired();
    }

    if (!response.ok) {
      this.throwApiErrorFromBody(data, responseText, response);
    }

    // OK status but body wasn't valid JSON — preserve the long-standing
    // contract that JSON endpoints throw a parse error rather than
    // silently returning null. (The 204 path above already covers
    // intentionally-empty success bodies.)
    if (responseText.length > 0 && data === null) {
      const requestSize = body ? JSON.stringify(body).length : 0;
      traceApiError(traceId, `Failed to parse JSON response (status: ${response.status})`, {
        status: response.status,
        responseText,
        responseSize: responseText.length,
        requestSize,
      });
      throw new Error(`Failed to parse response (status: ${response.status})`);
    }

    return data as T;
  }

  /**
   * Builds an `ApiError` from a non-OK response body, fires the
   * platform-wide side effects (maintenance-mode banner, credit-exhaustion
   * navigation event), and throws. `request` delegates here so every
   * endpoint surfaces errors the same way.
   */
  private throwApiErrorFromBody(data: unknown, responseText: string, response: Response): never {
    const extracted = extractErrorFromResponse(data, responseText, response.status);

    // Detect maintenance mode — set global state so the UI can react.
    if (response.status === 503 && extracted.code === 'MAINTENANCE_MODE') {
      maintenanceState.setActive(extracted.message);
    }

    // Dispatch a custom event so a top-level React component can navigate
    // via react-router (preserving SPA state) instead of doing a hard
    // browser redirect from this non-component context. The listener
    // (CreditExhaustionListener mounted under the router) is the single
    // point that decides whether to navigate. We only fire on the most
    // decisive case — total exhaustion — so partial-balance scenarios
    // don't trigger an unwanted navigation.
    if (
      response.status === 402 &&
      extracted.code === 'INSUFFICIENT_CREDITS' &&
      extracted.allBucketsExhausted === true &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new CustomEvent('fias:credits-exhausted'));
    }

    // Guest Trial Mode: a guest hit a full-account-only surface
    // (requireFullAccount → 403 GUEST_ACCOUNT_RESTRICTED). Only guests ever
    // receive this code. The listener routes them to signup (they can't buy
    // credits or take the gated action without a real account).
    if (
      response.status === 403 &&
      extracted.code === 'GUEST_ACCOUNT_RESTRICTED' &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new CustomEvent('fias:guest-restricted'));
    }

    throw new ApiError(extracted.message, response.status, extracted.code, {
      allBucketsExhausted: extracted.allBucketsExhausted,
      isCoreSystem: extracted.isCoreSystem,
      envelopeStatus: extracted.envelopeStatus,
      issues: extracted.issues,
      confirmation: extracted.confirmation,
      body: data ?? undefined,
    });
  }

  /**
   * Public transport for arche-local API modules. Thin wrapper around the
   * private `request` method. Lets per-arche API files
   * (e.g. `client/src/arche/arches/<arche>/api/index.ts`) use the shared
   * fetch/CSRF/auth/retry/telemetry pipeline without growing the central
   * `ApiClient` class with arche-specific surface area. See
   * docs/audits/social-media-modularity-audit.md §10 PW-B.
   */
  public async httpRequest<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    return this.request<T>(method, path, body, options);
  }

  /**
   * Generic GET request with optional query parameters
   */
  async get<T>(path: string, options?: { params?: Record<string, string> }): Promise<{ data: T }> {
    let url = path;
    if (options?.params) {
      const params = new URLSearchParams(options.params);
      url = `${path}?${params.toString()}`;
    }
    const data = await this.request<T>('GET', url);
    return { data };
  }

  /**
   * Generic POST request. Cookie auth + CSRF handled by `request()`.
   */
  async post<T>(path: string, body?: Record<string, unknown>): Promise<{ data: T }> {
    const data = await this.request<T>('POST', path, body);
    return { data };
  }

  // Arche API
  async listArches(options?: { categoryId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ arches: unknown[]; total: number }>(
      'GET',
      `/arches${query ? `?${query}` : ''}`,
    );
  }

  /**
   * `channel: 'test'` asks for the Forge test-channel view: the owner and
   * active collaborators get a draft's reduced record (ADR-023 D4).
   */
  async getArche(id: string, options?: { channel?: 'test' }) {
    const query = options?.channel ? `?channel=${options.channel}` : '';
    return this.request<{ arche: unknown }>('GET', `/arches/${id}${query}`);
  }

  /**
   * Gets lightweight arche metadata for route protection
   * Used to determine if an arche is public/fullscreen before loading full content
   */
  async getArcheMetadata(id: string, options?: { channel?: 'test' }) {
    const query = options?.channel ? `?channel=${options.channel}` : '';
    return this.request<ArcheMetadata>('GET', `/arches/${id}/metadata${query}`);
  }

  // Arche File Operations API

  /**
   * Write or manipulate a file in an arche's sandboxed folder
   */
  async archeFileWrite(
    archeId: string,
    payload: {
      filename: string;
      content: string;
      format: string;
      mode: string;
      separator?: string;
      section?: string;
      find?: string;
      requestId?: string;
    },
  ) {
    return this.request<{
      success: boolean;
      fileId: string;
      filename: string;
    }>('POST', `/arches/${archeId}/files/write`, payload);
  }

  /**
   * Read a file from an arche's sandboxed folder
   */
  async archeFileRead(archeId: string, options: { filename?: string; fileId?: string }) {
    const params = new URLSearchParams();
    if (options.filename) params.set('filename', options.filename);
    if (options.fileId) params.set('fileId', options.fileId);
    const query = params.toString();
    return this.request<{
      content: string;
      filename: string;
      fileId: string;
    }>('GET', `/arches/${archeId}/files/read${query ? `?${query}` : ''}`);
  }

  /**
   * Get a presigned download URL for a file in an arche's sandboxed folder
   */
  async archeFileDownloadUrl(archeId: string, options: { filename?: string; fileId?: string }) {
    const params = new URLSearchParams();
    if (options.filename) params.set('filename', options.filename);
    if (options.fileId) params.set('fileId', options.fileId);
    const query = params.toString();
    return this.request<{
      downloadUrl: string;
      filename: string;
    }>('GET', `/arches/${archeId}/files/download-url${query ? `?${query}` : ''}`);
  }

  /**
   * List files in an arche's sandboxed folder
   */
  async archeFileList(archeId: string) {
    return this.request<{
      files: Array<{ fileId: string; filename: string; format: string; createdAt: string }>;
    }>('GET', `/arches/${archeId}/files`);
  }

  // Entity API
  async listEntities(options?: { categoryId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ entities: unknown[]; total: number }>(
      'GET',
      `/entities${query ? `?${query}` : ''}`,
    );
  }

  async getEntity(id: string) {
    return this.request<{ entity: unknown }>('GET', `/entities/${id}`);
  }

  async invokeEntity(entityId: string, input: Record<string, unknown>) {
    return this.request<{
      output: unknown;
      usage?: {
        tokens?: { input: number; output: number };
        cost?: number;
      };
    }>('POST', `/entities/${entityId}/invoke`, { input });
  }

  // Category API
  async listCategories() {
    return this.request<{ categories: unknown[] }>('GET', '/categories');
  }

  async getCategory(id: string) {
    return this.request<{ category: unknown }>('GET', `/categories/${id}`);
  }

  // Workflow API
  async startArche(archeId: string, input?: Record<string, unknown>) {
    return this.request<{
      requestId: string;
      archId: string;
      status: 'pending' | 'running';
    }>('POST', `/arches/${archeId}/start`, { input });
  }

  async getRequestStatus(requestId: string) {
    return this.request<{
      requestId: string;
      archId: string;
      userId: string;
      status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timed_out';
      input: Record<string, unknown> | null;
      output: Record<string, unknown> | null;
      startedAt: string;
      completedAt: string | null;
      totalCostUsd: number | null;
      errorMessage: string | null;
      nodes: Array<{
        nodeId: string;
        entityId: string | null;
        entityName: string | null;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
        input: Record<string, unknown> | null;
        output: Record<string, unknown> | null;
        startedAt: string | null;
        completedAt: string | null;
        costUsd: number | null;
        latencyMs: number | null;
        errorMessage: string | null;
      }>;
    }>('GET', `/requests/${requestId}`);
  }

  async listRequests(options?: {
    archId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archId) params.set('archId', options.archId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      requests: Array<{
        requestId: string;
        archId: string;
        status: string;
        startedAt: string;
        completedAt: string | null;
      }>;
      total: number;
    }>('GET', `/requests${query ? `?${query}` : ''}`);
  }

  // ==========================================================================
  // Favorites API
  // ==========================================================================

  async getFavorites() {
    return this.request<{ favorites: UserFavoriteWithArche[] }>('GET', '/arches/favorites');
  }

  async addFavorite(archId: string) {
    return this.request<{ favorite: unknown }>('POST', '/arches/favorites', { archId });
  }

  async removeFavorite(archId: string) {
    return this.request<void>('DELETE', `/arches/favorites/${archId}`);
  }

  async checkFavorite(archId: string) {
    return this.request<{ isFavorite: boolean }>('GET', `/arches/favorites/check/${archId}`);
  }

  async reorderFavorites(
    placements: { archId: string; position: number }[],
    layout: WorktopLayout,
  ) {
    return this.request<{ success: boolean }>('PUT', '/arches/favorites/reorder', {
      placements,
      layout,
    });
  }

  /**
   * Resolve display metadata (name + icon + one-line description) for a set of
   * arche ids. Backs the worktop "Add Featured" chooser. Returns only published
   * arches; unknown/unpublished ids are omitted from the response.
   */
  async getArcheDisplayMeta(archeIds: string[]) {
    const ids = encodeURIComponent(archeIds.join(','));
    return this.request<{ arches: ArcheDisplayMeta[] }>(
      'GET',
      `/arches/favorites/metadata?ids=${ids}`,
    );
  }

  /**
   * Plugin apps the signed-in user may open a Vault file in, with the file
   * kinds each opens (admin-approved ∩ declared; ADR-030). Internal arches
   * are not listed — they register in code (`ARCHE_HANDOFF_INTAKE`).
   */
  async getFileIntakeDestinations() {
    return this.request<{ destinations: FileIntakeDestination[] }>('GET', '/arches/file-intakes');
  }

  // ==========================================================================
  // Marketplace API
  // ==========================================================================

  async searchMarketplace(options?: {
    q?: string;
    categoryId?: string;
    archeType?: ArcheType;
    tags?: string[];
    sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
    limit?: number;
    offset?: number;
    enableSemanticSearch?: boolean;
    signal?: AbortSignal;
  }) {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.archeType) params.set('archeType', options.archeType);
    if (options?.tags?.length) params.set('tags', options.tags.join(','));
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.enableSemanticSearch === false) params.set('enableSemanticSearch', 'false');
    const query = params.toString();
    return this.request<MarketplaceSearchResult>(
      'GET',
      `/marketplace/search${query ? `?${query}` : ''}`,
      undefined,
      { signal: options?.signal },
    );
  }

  async getFeaturedArches(limit?: number) {
    const query = limit ? `?limit=${limit}` : '';
    return this.request<{ arches: MarketplaceArche[] }>('GET', `/marketplace/featured${query}`);
  }

  async getMarketplaceCategories() {
    return this.request<{ categories: MarketplaceCategory[] }>('GET', '/marketplace/categories');
  }

  async getMarketplaceCategoryArches(
    slug: string,
    options?: {
      sortBy?: 'fitness' | 'popular' | 'newest';
      limit?: number;
      offset?: number;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      category: MarketplaceCategory;
      arches: MarketplaceArche[];
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>('GET', `/marketplace/categories/${slug}${query ? `?${query}` : ''}`);
  }

  async getMarketplaceArche(id: string) {
    return this.request<MarketplaceArcheDetail>('GET', `/marketplace/arches/${id}`);
  }

  // ==========================================================================
  // Marketplace Entity API
  // ==========================================================================

  async searchMarketplaceEntities(options?: {
    q?: string;
    categoryId?: string;
    type?: EntityType;
    sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }) {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.type) params.set('type', options.type);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<MarketplaceEntitySearchResult>(
      'GET',
      `/marketplace/entities/search${query ? `?${query}` : ''}`,
      undefined,
      { signal: options?.signal },
    );
  }

  async getFeaturedEntities(limit?: number) {
    const query = limit ? `?limit=${limit}` : '';
    return this.request<{ entities: MarketplaceEntity[] }>(
      'GET',
      `/marketplace/entities/featured${query}`,
    );
  }

  async getMarketplaceEntity(id: string) {
    return this.request<MarketplaceEntityDetail>('GET', `/marketplace/entities/${id}`);
  }

  async getMarketplaceCategoriesWithEntityCounts() {
    return this.request<{ categories: MarketplaceCategoryWithEntityCount[] }>(
      'GET',
      '/marketplace/entity-categories',
    );
  }

  // ==========================================================================
  // Contributor API
  // ==========================================================================

  async getContributorMetrics() {
    return this.request<ContributorMetricsResult>('GET', '/contributor/entities/metrics');
  }

  async getContributorArcheDashboard() {
    return this.request<ContributorArcheDashboardResult>('GET', '/contributor/arches/dashboard');
  }

  // ==========================================================================
  // Credits API
  // ==========================================================================

  async getCreditBalance() {
    return this.request<CreditBalance>('GET', '/credits');
  }

  async getCreditSpendingSummary() {
    return this.request<{ categories: SpendingCategorySummary[] }>('GET', '/credits/summary');
  }

  async getCreditSpendByArche() {
    return this.request<{ arches: SpendByArche[] }>('GET', '/credits/by-arche');
  }

  async getCreditTransactions(options?: {
    type?: 'grant' | 'usage' | 'refund' | 'adjustment';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<CreditTransactionsResult>(
      'GET',
      `/credits/transactions${query ? `?${query}` : ''}`,
    );
  }

  async getCreditTransactionGroups(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    params.set('view', 'grouped');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    return this.request<CreditTransactionGroupsResult>(
      'GET',
      `/credits/transactions?${params.toString()}`,
    );
  }

  async getRequestCreditsUsed(requestId: string) {
    return this.request<{ requestId: string; creditsUsed: number }>(
      'GET',
      `/credits/request/${requestId}`,
    );
  }

  async grantCredits(targetUserId: string, amount: number, description?: string) {
    return this.request<CreditGrantResult>('POST', '/admin/credits/grant', {
      userId: targetUserId,
      amount,
      description,
    });
  }

  // ==========================================================================
  // Payments API
  // ==========================================================================

  async getPaymentConfig() {
    return this.request<PaymentConfig>('GET', '/payments/config');
  }

  async createCheckoutSession(amountCents: number) {
    return this.request<CheckoutSessionResult>('POST', '/payments/checkout-session', {
      amountCents,
    });
  }

  async createCryptoCheckout(amountCents: number) {
    return this.request<CryptoCheckoutResult>('POST', '/payments/crypto/checkout', {
      amountCents,
    });
  }

  // ==========================================================================
  // Subscriptions API
  // ==========================================================================

  async getSubscriptionTiers() {
    return this.request<SubscriptionTiersResult>('GET', '/payments/subscriptions/tiers');
  }

  async getCurrentSubscription() {
    return this.request<SubscriptionInfo>('GET', '/payments/subscriptions/current');
  }

  async subscribeToTier(tier: 'standard' | 'pro') {
    return this.request<{ sessionUrl: string }>('POST', '/payments/subscriptions/subscribe', {
      tier,
    });
  }

  async cancelSubscription() {
    return this.request<{
      status: string;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('POST', '/payments/subscriptions/cancel');
  }

  async reactivateSubscription() {
    return this.request<{
      status: string;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('POST', '/payments/subscriptions/reactivate');
  }

  async getSubscriptionPortalUrl() {
    return this.request<{ url: string }>('GET', '/payments/subscriptions/portal');
  }

  async changeSubscriptionTier(tier: 'standard' | 'pro') {
    return this.request<SubscriptionInfo>('POST', '/payments/subscriptions/change-tier', { tier });
  }

  async syncSubscription() {
    return this.request<SubscriptionInfo>('POST', '/payments/subscriptions/sync');
  }

  // ==========================================================================
  // Admin Payments API
  // ==========================================================================

  // Company email approvals (ADR-023) — which Companies Fias lets send, from which domains
  async adminSearchCompanies(q: string) {
    return this.request<{ companies: AdminCompanySearchResultDto[] }>(
      'GET',
      `/admin/companies?q=${encodeURIComponent(q)}`,
    );
  }

  /** Companies holding one Fias-granted entitlement (ADR-023). */
  async adminListCompanyGrants(entitlement: CompanyEntitlementKey) {
    const qs = new URLSearchParams({ entitlement, limit: '200' });
    return this.request<{ grants: CompanyEntitlementGrantDto[] }>(
      'GET',
      `/admin/company-entitlements?${qs}`,
    );
  }

  /**
   * Grant an entitlement, or (the server upserts) replace an existing grant.
   * `approvedDomains` is required for `company_email` and refused otherwise.
   */
  async adminGrantCompanyEntitlement(input: {
    companyId: string;
    entitlement: CompanyEntitlementKey;
    reason: string;
    approvedDomains?: string[];
    /** `company_web` only (ADR-024 rev 4). */
    domainPurchases?: boolean;
    maxDomains?: number;
  }) {
    return this.request<{ grant: CompanyEntitlementGrantDto }>(
      'POST',
      '/admin/company-entitlements',
      input,
    );
  }

  async adminRevokeCompanyEntitlement(
    companyId: string,
    entitlement: CompanyEntitlementKey,
    reason: string,
  ) {
    const qs = new URLSearchParams({ companyId, entitlement, reason });
    return this.request<{ revoked: boolean }>('DELETE', `/admin/company-entitlements?${qs}`);
  }

  // Company portals oversight (ADR-024)
  async adminListCompanyWebHosts(page: { limit?: number; offset?: number } = {}) {
    const query = new URLSearchParams();
    if (page.limit !== undefined) query.set('limit', String(page.limit));
    if (page.offset !== undefined) query.set('offset', String(page.offset));
    const qs = query.toString();
    return this.request<{ hosts: AdminCompanyWebHostDto[] }>(
      'GET',
      `/admin/company-web/hosts${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetCompanyWebHost(hostId: string) {
    return this.request<{ host: AdminCompanyWebHostDto; holds: AdminCompanyWebHoldDto[] }>(
      'GET',
      `/admin/company-web/hosts/${encodeURIComponent(hostId)}`,
    );
  }

  async adminPlaceCompanyWebHold(
    hostId: string,
    kind: 'admin' | 'dns_drift' | 'ct_alert',
    note: string,
  ) {
    return this.request<{ hold: AdminCompanyWebHoldDto }>(
      'POST',
      `/admin/company-web/hosts/${encodeURIComponent(hostId)}/holds`,
      { kind, note },
    );
  }

  async adminReleaseCompanyWebHold(holdId: string) {
    return this.request<{ hold: AdminCompanyWebHoldDto }>(
      'POST',
      `/admin/company-web/holds/${encodeURIComponent(holdId)}/release`,
    );
  }

  async adminGetCompanyWebKillSwitch() {
    return this.request<{ killSwitch: CompanyWebKillSwitchDto }>(
      'GET',
      '/admin/company-web/kill-switch',
    );
  }

  async adminSetCompanyWebKillSwitch(paused: boolean, reason: string) {
    return this.request<{ killSwitch: CompanyWebKillSwitchDto }>(
      'PUT',
      '/admin/company-web/kill-switch',
      { paused, reason },
    );
  }

  // Company email oversight (ADR-023)
  async adminListCompanySendingDomains() {
    return this.request<{ domains: AdminCompanySendingDomainDto[] }>(
      'GET',
      '/admin/company-email/domains',
    );
  }

  async adminPauseCompanySendingDomain(domainId: string, reason: string) {
    return this.request<{ domain: CompanySendingDomainDto | null }>(
      'POST',
      `/admin/company-email/domains/${encodeURIComponent(domainId)}/pause`,
      { reason },
    );
  }

  async adminResumeCompanySendingDomain(domainId: string, reason: string) {
    return this.request<{ domain: CompanySendingDomainDto | null }>(
      'POST',
      `/admin/company-email/domains/${encodeURIComponent(domainId)}/resume`,
      { reason },
    );
  }

  async adminGetCompanyEmailKillSwitch() {
    return this.request<{ killSwitch: CompanyEmailKillSwitchDto }>(
      'GET',
      '/admin/company-email/kill-switch',
    );
  }

  async adminSetCompanyEmailKillSwitch(paused: boolean, reason: string) {
    return this.request<{ killSwitch: CompanyEmailKillSwitchDto }>(
      'PUT',
      '/admin/company-email/kill-switch',
      { paused, reason },
    );
  }

  /**
   * Arche governance switches (ADR-026), Dev Console → Arche Management.
   * Creation-only: they gate NEW restrictions / NEW Company transfers; the
   * server reads a missing or unreadable row as OFF.
   */
  async adminGetArcheGovernanceSettings() {
    return this.request<ArcheGovernanceSettingsDto>(
      'GET',
      '/admin/arche-management/governance-settings',
    );
  }

  async adminSetArcheGovernanceSetting(key: ArcheGovernanceSettingKey, enabled: boolean) {
    return this.request<{ key: ArcheGovernanceSettingKey; enabled: boolean }>(
      'PUT',
      '/admin/arche-management/governance-settings',
      { key, enabled },
    );
  }

  // Fias Forge admin (dev-console Forge page) — `/admin/forge`, admin-only.
  async adminForgeGetSettings() {
    return this.request<{ enabled: boolean }>('GET', '/admin/forge/settings');
  }

  async adminForgeSetEnabled(enabled: boolean) {
    return this.request<{ enabled: boolean }>('PUT', '/admin/forge/enabled', { enabled });
  }

  async adminForgeGetMetering() {
    return this.request<{ enabled: boolean }>('GET', '/admin/forge/metering');
  }

  async adminForgeSetMetering(enabled: boolean) {
    return this.request<{ enabled: boolean }>('PUT', '/admin/forge/metering', { enabled });
  }

  async adminForgeGetMetrics() {
    return this.request<{ metrics: AdminForgeMetrics }>('GET', '/admin/forge/metrics');
  }

  async adminForgeListForges(params: AdminForgeListParams = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.userId) qs.set('userId', params.userId);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const suffix = qs.toString();
    return this.request<AdminForgeListResult>(
      'GET',
      `/admin/forge/forges${suffix ? `?${suffix}` : ''}`,
    );
  }

  async adminForgeGetForge(forgeId: string) {
    return this.request<AdminForgeDetailResult>('GET', `/admin/forge/forges/${forgeId}`);
  }

  /** FORCE stop: kills the task and finalizes the row; unsnapshotted work is lost. */
  async adminForgeStop(forgeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', `/admin/forge/forges/${forgeId}/stop`);
  }

  async adminForgeArchive(forgeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', `/admin/forge/forges/${forgeId}/archive`);
  }

  async adminForgeListHolds() {
    return this.request<{ holds: AdminForgeUserHold[] }>('GET', '/admin/forge/holds');
  }

  async adminForgeCreateHold(userId: string, reason: string) {
    return this.request<{ hold: AdminForgeUserHold }>('POST', '/admin/forge/holds', {
      userId,
      reason,
    });
  }

  async adminForgeReleaseHold(userId: string) {
    return this.request<{ released: boolean; userId: string }>(
      'DELETE',
      `/admin/forge/holds/${encodeURIComponent(userId)}`,
    );
  }

  // Maintenance Mode
  async adminGetMaintenanceStatus() {
    return this.request<MaintenanceStatus>('GET', '/admin/maintenance');
  }

  async adminSetMaintenanceMode(params: { enabled: boolean; message?: string }) {
    return this.request<MaintenanceStatus>('POST', '/admin/maintenance', params);
  }

  // Guest Trial Mode kill switch (Dev Console → Platform Usage → Guest trials).
  // Only the on/off switch is writable; the ad-click requirement is read-only.
  async adminGetGuestMode() {
    return this.request<AdminGuestModeState>('GET', '/admin/guest-mode');
  }

  async adminSetGuestModeEnabled(enabled: boolean) {
    return this.request<AdminGuestModeState>('PUT', '/admin/guest-mode', { enabled });
  }

  async adminGetPaymentsDashboard(period?: string) {
    return this.request<AdminPaymentsDashboard>(
      'GET',
      `/admin/payments/dashboard${period ? `?period=${period}` : ''}`,
    );
  }

  async adminGetPaymentsCosts(period?: string) {
    return this.request<AdminPaymentsCosts>(
      'GET',
      `/admin/payments/costs${period ? `?period=${period}` : ''}`,
    );
  }

  async adminProcessRefund(paymentId: string, reason?: string) {
    return this.request<{ success: boolean; paymentId: string }>('POST', '/admin/payments/refund', {
      paymentId,
      reason,
    });
  }

  async adminGrantCredits(userId: string, amount: number, description?: string) {
    return this.request<{ success: boolean; newBalance: number; transactionId: string }>(
      'POST',
      '/admin/payments/grant',
      { userId, amount, description },
    );
  }

  async adminGetUserPaymentDetail(userId: string) {
    return this.request<AdminUserPaymentDetail>('GET', `/admin/payments/users/${userId}`);
  }

  async adminGetCreditActivity(params: {
    kind: 'grants' | 'refunds';
    limit?: number;
    offset?: number;
    days?: number;
  }) {
    const q = new URLSearchParams({ kind: params.kind });
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    if (params.days != null) q.set('days', String(params.days));
    return this.request<AdminCreditActivity>('GET', `/admin/credits/activity?${q.toString()}`);
  }

  // ── Roles management (dev-console Roles pages) ──
  //
  // READS ONLY. Grants and revocations carry the acting admin's password and
  // TOTP code, and must go through the untraced `stepUpFetch` so those
  // credentials never enter this client's apiTraces telemetry buffer.
  async adminGetRoles() {
    return this.request<AdminRolesCatalogue>('GET', '/admin/roles');
  }

  async adminGetRoleMembers(role: string, cursor?: string) {
    const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.request<AdminRoleMembersPage>(
      'GET',
      `/admin/roles/${encodeURIComponent(role)}/members${q}`,
    );
  }

  // ── Providers cost/safety control center (dev-console Providers page) ──
  async adminGetProviders() {
    return this.request<AdminProvidersOverview>('GET', '/admin/providers');
  }

  async adminUpdateProviderConfig(provider: string, body: AdminProviderConfigInput) {
    return this.request<{ config: AdminProviderConfigInput & { provider: string } }>(
      'PUT',
      `/admin/providers/${encodeURIComponent(provider)}/config`,
      body,
    );
  }

  async adminGetProviderDetail(provider: string) {
    return this.request<AdminProviderDetail>(
      'GET',
      `/admin/providers/${encodeURIComponent(provider)}`,
    );
  }

  async adminCreateProviderSnapshot(provider: string, body: AdminProviderSnapshotInput) {
    return this.request<{ snapshot: AdminProviderBalanceSnapshot }>(
      'POST',
      `/admin/providers/${encodeURIComponent(provider)}/snapshot`,
      body,
    );
  }

  async adminRefreshProviderBalance(provider: string) {
    return this.request<{
      supported: boolean;
      result: AdminProviderBalanceResult | null;
      snapshot: AdminProviderBalanceSnapshot | null;
    }>('POST', `/admin/providers/${encodeURIComponent(provider)}/refresh-balance`);
  }

  async adminGetAwsCosts(period: 'mtd' | '30d' = 'mtd', refresh = false) {
    const qs = new URLSearchParams({ period });
    if (refresh) qs.set('refresh', 'true');
    return this.request<{ report: AdminAwsCostReport }>(
      'GET',
      `/admin/providers/aws-costs?${qs.toString()}`,
    );
  }

  async adminGetProviderSpendHistory(from: string, to: string) {
    const qs = new URLSearchParams({ from, to });
    return this.request<AdminProviderSpendHistory>(
      'GET',
      `/admin/providers/spend-history?${qs.toString()}`,
    );
  }

  async adminGetProviderHealth(window: AdminProviderHealthWindow = '24h') {
    const qs = new URLSearchParams({ window });
    return this.request<AdminProviderHealth>('GET', `/admin/providers/health?${qs.toString()}`);
  }

  async adminGetProviderIssueCount() {
    return this.request<{ count: number; generatedAt: string }>(
      'GET',
      '/admin/providers/issue-count',
    );
  }

  async adminGetProviderModelHealth(provider: string, window: AdminProviderHealthWindow = '24h') {
    const qs = new URLSearchParams({ window });
    return this.request<AdminProviderModelHealth>(
      'GET',
      `/admin/providers/${encodeURIComponent(provider)}/models?${qs.toString()}`,
    );
  }

  async adminGetProviderStatus(provider: string) {
    return this.request<AdminProviderStatus>(
      'GET',
      `/admin/providers/${encodeURIComponent(provider)}/status`,
    );
  }

  // ── Platform Usage Analytics (Layer 2 admin dashboard) ──────────────
  async adminGetAnalyticsOverview(period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsOverview>('GET', `/admin/analytics/overview?${qs}`);
  }

  async adminGetAnalyticsEngagement(period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsEngagement>('GET', `/admin/analytics/engagement?${qs}`);
  }

  async adminGetAnalyticsHealth(period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsHealth>('GET', `/admin/analytics/health?${qs}`);
  }

  async adminGetAnalyticsUser(userId: string, period: AnalyticsPeriod, nocache = false) {
    const qs = new URLSearchParams({ period });
    if (nocache) qs.set('nocache', '1');
    return this.request<AdminAnalyticsUserDetail>(
      'GET',
      `/admin/analytics/users/${encodeURIComponent(userId)}?${qs}`,
    );
  }

  async adminSearchPayments(params: AdminPaymentSearchParams) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.userId) qs.set('userId', params.userId);
    if (params.startDate) qs.set('startDate', params.startDate);
    if (params.endDate) qs.set('endDate', params.endDate);
    if (params.minAmountCents) qs.set('minAmountCents', String(params.minAmountCents));
    if (params.maxAmountCents) qs.set('maxAmountCents', String(params.maxAmountCents));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.request<AdminPaymentSearchResult>(
      'GET',
      `/admin/payments/search${query ? `?${query}` : ''}`,
    );
  }

  async adminListSubscriptions(params: AdminSubscriptionListParams) {
    const qs = new URLSearchParams();
    if (params.tier) qs.set('tier', params.tier);
    if (params.status) qs.set('status', params.status);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.request<AdminSubscriptionListResult>(
      'GET',
      `/admin/payments/subscriptions${query ? `?${query}` : ''}`,
    );
  }

  async adminChangeSubscriptionTier(userId: string, newTier: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/change-tier`,
      { newTier },
    );
  }

  async adminCancelSubscription(userId: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/cancel`,
    );
  }

  async adminReactivateSubscription(userId: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/reactivate`,
    );
  }

  async adminReconcileSubscription(userId: string) {
    return this.request<{ success: boolean; subscription: AdminSubscriptionRecord }>(
      'POST',
      `/admin/payments/subscriptions/${userId}/reconcile`,
    );
  }

  async adminListStripeEvents(params: AdminStripeEventListParams) {
    const qs = new URLSearchParams();
    if (params.eventType) qs.set('eventType', params.eventType);
    if (params.startDate) qs.set('startDate', params.startDate);
    if (params.endDate) qs.set('endDate', params.endDate);
    if (params.processedOk !== undefined) qs.set('processedOk', String(params.processedOk));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.request<AdminStripeEventListResult>(
      'GET',
      `/admin/payments/stripe-events${query ? `?${query}` : ''}`,
    );
  }

  async adminGetStripeEvent(eventId: string) {
    return this.request<AdminStripeEventDetail>('GET', `/admin/payments/stripe-events/${eventId}`);
  }

  async adminDeductCredits(userId: string, amount: number, bucket: string, reason: string) {
    return this.request<{ success: boolean; newBalance: number; transactionId: string }>(
      'POST',
      '/admin/payments/credits/deduct',
      { userId, amount, bucket, reason },
    );
  }

  async getPaymentHistory(options?: { limit?: number; offset?: number; status?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.status) params.set('status', options.status);
    const query = params.toString();
    return this.request<PaymentHistoryResult>(
      'GET',
      `/payments/history${query ? `?${query}` : ''}`,
    );
  }

  // ==========================================================================
  // Models API
  // ==========================================================================

  async listModels(options?: {
    provider?: ModelProvider;
    tier?: 'fast' | 'balanced' | 'powerful';
  }) {
    const params = new URLSearchParams();
    if (options?.provider) params.set('provider', options.provider);
    if (options?.tier) params.set('tier', options.tier);
    const query = params.toString();
    return this.request<ModelListResponse>('GET', `/models${query ? `?${query}` : ''}`);
  }

  async getModel(id: string) {
    return this.request<ModelEntity>('GET', `/models/${id}`);
  }

  async listTextCapabilities() {
    return this.request<{ capabilities: TextCapability[] }>('GET', '/models/text-capabilities');
  }

  // ==========================================================================
  // Model Definitions API (Entity-driven parameter schema)
  // ==========================================================================

  /**
   * Lists model definitions with their parameter schemas.
   * Used by the frontend to render data-driven parameter controls.
   *
   * @param options.provider - Filter by provider (openai, anthropic, google)
   * @param options.tier - Filter by tier (fast, balanced, powerful)
   * @param options.modelType - Filter by model type (text, image)
   */
  async listModelDefinitions(options?: {
    provider?: ModelProvider;
    tier?: 'fast' | 'balanced' | 'powerful';
    modelType?: 'text' | 'image';
  }) {
    const params = new URLSearchParams();
    if (options?.provider) params.set('provider', options.provider);
    if (options?.tier) params.set('tier', options.tier);
    if (options?.modelType) params.set('modelType', options.modelType);
    const query = params.toString();
    return this.request<ModelDefinitionListResponse>(
      'GET',
      `/models/definitions${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Gets a model definition by ID, including full parameter schema.
   */
  async getModelDefinition(id: string) {
    return this.request<ModelDefinition>('GET', `/models/definitions/${id}`);
  }

  /**
   * Gets a model definition by provider and model name.
   * Useful when you have the model name but not the entity ID.
   */
  async getModelDefinitionByLookup(provider: ModelProvider, modelName: string) {
    const params = new URLSearchParams();
    params.set('provider', provider);
    params.set('modelName', modelName);
    return this.request<ModelDefinition>('GET', `/models/definitions/lookup?${params.toString()}`);
  }

  // ==========================================================================
  // Conversations API
  // ==========================================================================

  async listConversations(options?: {
    archeId?: string;
    status?: 'active' | 'archived';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<ConversationListResult>('GET', `/conversations${query ? `?${query}` : ''}`);
  }

  async getConversation(conversationId: string) {
    return this.request<ConversationWithMessages>('GET', `/conversations/${conversationId}`);
  }

  async createConversation(archeId: string, title?: string) {
    return this.request<ConversationResponse>('POST', '/conversations', { archeId, title });
  }

  async addMessages(conversationId: string, messages: ConversationMessageInput[]) {
    return this.request<{ messages: ConversationMessageResponse[] }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { messages },
    );
  }

  async addMessage(conversationId: string, message: ConversationMessageInput) {
    return this.request<{ messages: ConversationMessageResponse[] }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { message },
    );
  }

  async updateConversation(
    conversationId: string,
    updates: { title?: string; status?: 'active' | 'archived' },
  ) {
    return this.request<ConversationResponse>('PATCH', `/conversations/${conversationId}`, updates);
  }

  async deleteConversation(conversationId: string) {
    return this.request<void>('DELETE', `/conversations/${conversationId}`);
  }

  async deleteAllConversations() {
    return this.request<DeleteAllConversationsResult>('DELETE', '/conversations/all');
  }

  async exportConversation(conversationId: string, format: 'json' | 'markdown' = 'markdown') {
    // Export returns plain text (markdown or JSON string), not a JSON object
    // So we need to handle it separately from the standard request method
    const url = `${API_BASE}/conversations/${conversationId}/export?format=${format}`;

    const response = await fetch(url, { method: 'GET', credentials: sessionCredentials() });

    if (!response.ok) {
      throw new Error(`Export failed with status ${response.status}`);
    }

    return response.text();
  }

  async shareConversation(conversationId: string) {
    return this.request<ShareConversationResponse>(
      'POST',
      `/conversations/${conversationId}/share`,
      {},
    );
  }

  async getSharedConversation(shareId: string) {
    return this.request<SharedConversationResponse>('GET', `/shared/${shareId}`);
  }

  // ==========================================================================
  // Arche Session API
  // ==========================================================================

  async getArcheSession(archeId: string) {
    return this.request<ArcheSessionResponse>('GET', `/arches/${archeId}/session`);
  }

  async updateArcheSession(archeId: string, updates: ArcheSessionUpdateInput) {
    return this.request<ArcheSessionResponse>('PUT', `/arches/${archeId}/session`, updates);
  }

  async deleteArcheSession(archeId: string) {
    return this.request<void>('DELETE', `/arches/${archeId}/session`);
  }

  // ==========================================================================
  // Fias AI API
  // ==========================================================================

  /**
   * Assistant agent-loop turn (global Fias AI orchestrator). Send exactly
   * one of `message` (fresh turn / question answer) or
   * `confirmationResponse` (settle a pending confirmation). History is
   * server-loaded — never sent from the client.
   */
  async assistantTurn(payload: {
    conversationId?: string;
    sessionId?: string;
    message?: string;
    routingPreference?: 'auto' | 'speed' | 'balanced' | 'performance' | 'cost';
    /** Surface that started the turn (scopes core-system search gating). */
    origin?: 'make_a_request' | 'assistant';
    /** Per-turn tool toggles (MaR's "Arche Search" switch in AI mode). */
    toolPreferences?: { archeSearch?: boolean };
    /** Temporary (incognito) chat: no memory reads, no memory writes. */
    temporaryChat?: boolean;
    /** Workspace (org holder) the user is acting in — server-validated, session-pinned. */
    activeHolderId?: string;
    /** ADMIN-ONLY verbose debug stream (silently ignored otherwise). */
    verbose?: boolean;
    archeContext?: {
      archeId: string;
      /** What the arche IS (`ArcheAICapabilities.archeDescription`) — the model is told. */
      description?: string;
      state: Record<string, unknown>;
      actions: Array<{
        id: string;
        description: string;
        parameterSchema: Record<string, unknown>;
        requiresConfirmation?: boolean;
        category?: string;
      }>;
    };
    confirmationResponse?: {
      interactionId: string;
      approved: boolean;
      feedback?: string;
      /** User edits to editable params (key → new value); re-parsed server-side. */
      edits?: Record<string, string>;
    };
  }) {
    return this.request<{
      requestId: string;
      sessionId: string;
      conversationId: string;
      /** Classified turn intent (navigate/act/research/chat); fresh turns only. */
      intent?: 'navigate' | 'act' | 'research' | 'chat';
      selectedModel: {
        provider: string;
        modelName: string;
        displayName: string;
        tier: string;
      };
    }>('POST', '/arches/fias-ai/assistant/turn', payload);
  }

  /**
   * Report the outcome of an assistant UI action applied in the open
   * arche. The server records it, atomically claims the paused turn, and
   * resumes it — the response carries the new requestId to subscribe to.
   */

  async assistantActionResult(payload: {
    sessionId: string;
    toolCallId: string;
    ok: boolean;
    result?: Record<string, unknown>;
    /** The arche's own failure text (applyAction threw). */
    error?: string;
    /** Refusal to dispatch, as a fact — the server words what the model hears. */
    code?: 'ARCHE_NOT_OPEN' | 'ARCHE_MISMATCH';
    /** ARCHE_MISMATCH: the arche that was open instead. */
    openArcheId?: string;
    archeContext?: {
      archeId: string;
      /** What the arche IS (`ArcheAICapabilities.archeDescription`) — the model is told. */
      description?: string;
      state: Record<string, unknown>;
      actions: Array<{
        id: string;
        description: string;
        parameterSchema: Record<string, unknown>;
        requiresConfirmation?: boolean;
        category?: string;
      }>;
    };
  }) {
    return this.request<{
      accepted: boolean;
      resumed?: boolean;
      requestId?: string;
      sessionId?: string;
      conversationId?: string;
    }>('POST', '/arches/fias-ai/assistant/action-result', payload);
  }

  /**
   * Scheduled actions (the /v1/schedules surface; AI tasks use archeId
   * arche_make_a_request). `status` may be a comma-separated set — the Fias AI
   * pane fetches live {active,paused} and history {completed,failed,cancelled}
   * separately.
   */
  async listSchedules(options?: {
    archeId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const query = params.toString();
    return this.request<{
      actions: Array<{
        id: string;
        name: string;
        description: string;
        status: string;
        scheduleType: 'one_shot' | 'recurring';
        cronExpression: string | null;
        nextFireAt: string | null;
        lastFiredAt: string | null;
        fireCount: number;
        maxFires: number | null;
        lastError: string | null;
        authorizedActions: Array<{ name: string; label: string }>;
        /** Terminal-transition time; drives the per-row "finished since seen" alert. */
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/schedules${query ? `?${query}` : ''}`);
  }

  /** Per-task run history (narrowed DTO — no raw result payloads). */
  async listScheduleExecutions(id: string, limit = 20) {
    return this.request<{
      executions: Array<{
        id: string;
        firedAt: string;
        completedAt: string | null;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
        error: string | null;
        durationMs: number | null;
      }>;
    }>('GET', `/schedules/${id}/executions?limit=${limit}`);
  }

  /** Count of finished (completed/failed) scheduled tasks for an arche, optionally since a timestamp. */
  async getCompletedScheduleCount(options: { archeId: string; since?: string }) {
    const params = new URLSearchParams({ archeId: options.archeId });
    if (options.since) params.set('since', options.since);
    return this.request<{ count: number }>(
      'GET',
      `/schedules/completed-count?${params.toString()}`,
    );
  }

  async cancelSchedule(id: string) {
    return this.request<void>('DELETE', `/schedules/${id}`);
  }

  async pauseSchedule(id: string) {
    return this.request<{ id: string; status: string }>('POST', `/schedules/${id}/pause`);
  }

  async resumeSchedule(id: string) {
    return this.request<{ id: string; status: string }>('POST', `/schedules/${id}/resume`);
  }

  /** Cooperative cancel for an in-flight assistant turn (Stop button). */
  async assistantStop(sessionId: string) {
    return this.request<{ stopping: boolean }>('POST', '/arches/fias-ai/assistant/stop', {
      sessionId,
    });
  }

  // --- Assistant memory (user-visible, user-controlled) -------------------

  async getAssistantMemory() {
    return this.request<{
      enabled: boolean;
      memories: Array<{
        memoryId: string;
        kind: 'instruction' | 'rule' | 'learned';
        title: string | null;
        content: string;
        source: 'user' | 'assistant';
        enabled: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
    }>('GET', '/arches/fias-ai/assistant/memory');
  }

  async createAssistantMemory(payload: {
    kind: 'instruction' | 'rule' | 'learned';
    content: string;
    title?: string;
  }) {
    return this.request<{ memory: { memoryId: string } }>(
      'POST',
      '/arches/fias-ai/assistant/memory',
      payload,
    );
  }

  async updateAssistantMemory(
    memoryId: string,
    payload: { content?: string; title?: string | null; enabled?: boolean },
  ) {
    return this.request<{ memory: { memoryId: string } }>(
      'PATCH',
      `/arches/fias-ai/assistant/memory/${memoryId}`,
      payload,
    );
  }

  async deleteAssistantMemory(memoryId: string) {
    return this.request<{ deleted: boolean }>(
      'DELETE',
      `/arches/fias-ai/assistant/memory/${memoryId}`,
    );
  }

  async deleteAllAssistantMemories() {
    return this.request<{ deleted: number }>('DELETE', '/arches/fias-ai/assistant/memory');
  }

  async setAssistantMemoryEnabled(enabled: boolean) {
    return this.request<{ enabled: boolean }>('PUT', '/arches/fias-ai/assistant/memory/settings', {
      enabled,
    });
  }

  async getAssistantSession(sessionId: string) {
    return this.request<{
      sessionId: string;
      conversationId: string;
      status: string;
      pendingInteraction: {
        interactionId: string;
        kind: 'confirmation' | 'question' | 'ui_action';
        toolName: string;
        preview?: {
          type: 'generic';
          title: string;
          fields: Array<{ label: string; value: string }>;
          actingAs: string;
        };
        question?: string;
        choices?: string[];
        actionId?: string;
        params?: Record<string, unknown>;
        /** ui_action reconcile fields (see GET /session handler). */
        toolCallId?: string;
        archeId?: string;
      } | null;
      tainted: boolean;
    }>('GET', `/arches/fias-ai/assistant/session/${sessionId}`);
  }

  async listFiasAIConversations(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      conversations: Array<{
        conversationId: string;
        title: string | null;
        messageCount: number;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>('GET', `/arches/fias-ai/conversations${query ? `?${query}` : ''}`);
  }

  async getFiasAIConversation(id: string) {
    return this.request<{
      conversationId: string;
      title: string | null;
      messages: Array<{
        messageId: string;
        role: 'user' | 'assistant';
        content: string;
        provider: string | null;
        modelName: string | null;
        createdAt: string;
      }>;
    }>('GET', `/arches/fias-ai/conversations/${id}`);
  }

  async deleteFiasAIConversation(id: string) {
    return this.request<void>('DELETE', `/arches/fias-ai/conversations/${id}`);
  }

  /**
   * Submit a problem report about the Fias AI assistant. conversationId is
   * optional (a temporary chat has none); when supplied the server verifies
   * ownership and folds in the durable transcript.
   */
  async submitAssistantProblemReport(
    conversationId: string | null,
    report: {
      description: string;
      conversationHistory?: unknown[];
      modelInfo?: unknown;
      telemetryState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      '/arches/fias-ai/assistant/report-problem',
      { ...report, ...(conversationId ? { conversationId } : {}) },
    );
  }

  // ==========================================================================
  // Contact Book API
  // ==========================================================================

  async listContacts(options?: {
    categoryId?: string;
    priorityId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: 'active' | 'archived' | 'deleted';
    sortBy?: 'name' | 'gatheredAt' | 'company' | 'createdAt';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.priorityId) params.set('priorityId', options.priorityId);
    if (options?.search) params.set('search', options.search);
    if (options?.dateFrom) params.set('dateFrom', options.dateFrom);
    if (options?.dateTo) params.set('dateTo', options.dateTo);
    if (options?.status) params.set('status', options.status);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortDir) params.set('sortDir', options.sortDir);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ contacts: ContactDetail[]; total: number }>(
      'GET',
      `/arches/contact-book/contacts${query ? `?${query}` : ''}`,
    );
  }

  async getContact(id: string) {
    return this.request<ContactDetail>('GET', `/arches/contact-book/contacts/${id}`);
  }

  async createContact(input: ContactInput) {
    return this.request<ContactDetail>('POST', '/arches/contact-book/contacts', input);
  }

  async updateContact(id: string, input: Partial<ContactInput>) {
    return this.request<ContactDetail>('PUT', `/arches/contact-book/contacts/${id}`, input);
  }

  async deleteContact(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/contacts/${id}`);
  }

  async archiveContact(id: string, archived: boolean) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/archive`, {
      archived,
    });
  }

  async toggleContactFavorite(id: string) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/favorite`);
  }

  async restoreContact(id: string) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/restore`);
  }

  // ── Personal ↔ Business contact bridge ────────────────────────────────────

  async listContactMatchCandidates(id: string) {
    return this.request<{
      candidates: Array<{
        businessContactId: string;
        reason: 'email' | 'phone';
        firstName: string;
        lastName: string | null;
        companyId: string | null;
        primaryEmail: string | null;
        primaryPhone: string | null;
      }>;
    }>('GET', `/arches/contact-book/contacts/${id}/match-candidates`);
  }

  async linkContactToBusinessContact(id: string, businessContactId: string) {
    return this.request<{ contact: ContactDetail }>(
      'POST',
      `/arches/contact-book/contacts/${id}/link`,
      { businessContactId },
    );
  }

  async unlinkContactFromBusinessContact(id: string) {
    return this.request<{ contact: ContactDetail }>(
      'DELETE',
      `/arches/contact-book/contacts/${id}/link`,
    );
  }

  // ==========================================================================
  // Vault Notes API (Phase 3 — first Vault Entity consumer)
  // ==========================================================================
  //
  // Every endpoint below dispatches through invokeVaultEntity() on the
  // backend; the substrate enforces verb allowlist + Principal-can-act-
  // for-Holder + audit. The client only needs to know the URL shape.

  async listVaultNotes(options?: { includeDeleted?: boolean; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.includeDeleted) params.set('includeDeleted', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request<{ notes: VaultNoteDto[] }>(
      'GET',
      `/arches/notes${query ? `?${query}` : ''}`,
    );
  }

  async getVaultNote(noteId: string) {
    return this.request<{ note: VaultNoteDto }>('GET', `/arches/notes/${noteId}`);
  }

  async createVaultNote(input: { title: string; bodyMarkdown?: string }) {
    return this.request<{ note: VaultNoteDto }>('POST', '/arches/notes', input);
  }

  async updateVaultNote(noteId: string, input: { title?: string; bodyMarkdown?: string }) {
    return this.request<{ note: VaultNoteDto }>('PATCH', `/arches/notes/${noteId}`, input);
  }

  async softDeleteVaultNote(noteId: string) {
    return this.request<{ note: VaultNoteDto }>('DELETE', `/arches/notes/${noteId}`);
  }

  async restoreVaultNote(noteId: string) {
    return this.request<{ note: VaultNoteDto }>('POST', `/arches/notes/${noteId}/restore`);
  }

  // ==========================================================================
  // Fias 360 (Phase 10)
  // ==========================================================================
  // SMB CRM arche. All methods take `holderId` — the user's selected
  // Org Holder. The backend's invokeVaultEntity wrapper enforces the
  // trust check; the client just relays.

  async listFias360Customers(
    holderId: string,
    options?: { companyId?: string; includeArchived?: boolean; limit?: number },
  ) {
    const params = new URLSearchParams({ holder: holderId });
    if (options?.companyId) params.set('companyId', options.companyId);
    if (options?.includeArchived) params.set('includeArchived', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    return this.request<{ customers: Fias360CustomerDto[] }>(
      'GET',
      `/arches/fias360/customers?${params.toString()}`,
    );
  }

  async getFias360Customer(holderId: string, customerId: string) {
    return this.request<{ customer: Fias360CustomerDto }>(
      'GET',
      `/arches/fias360/customers/${customerId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async createFias360Customer(
    holderId: string,
    input: {
      firstName: string;
      lastName?: string;
      primaryEmail?: string;
      primaryPhone?: string;
      primaryPhoneLabel?: string | null;
      title?: string;
      notes?: string;
      companyId?: string;
      ownerPrincipalId?: string;
      lifecycleStage?: string | null;
      tags?: string[];
      altEmails?: Fias360AltContactValueDto[];
      altPhones?: Fias360AltContactValueDto[];
    },
  ) {
    return this.request<{ customer: Fias360CustomerDto }>(
      'POST',
      `/arches/fias360/customers?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async updateFias360Customer(
    holderId: string,
    customerId: string,
    input: Partial<{
      firstName: string;
      lastName: string | null;
      primaryEmail: string | null;
      primaryPhone: string | null;
      primaryPhoneLabel: string | null;
      title: string | null;
      notes: string | null;
      companyId: string | null;
      lifecycleStage: string | null;
      tags: string[];
      altEmails: Fias360AltContactValueDto[];
      altPhones: Fias360AltContactValueDto[];
    }>,
  ) {
    return this.request<{ customer: Fias360CustomerDto }>(
      'PATCH',
      `/arches/fias360/customers/${customerId}?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async archiveFias360Customer(holderId: string, customerId: string) {
    return this.request<{ customer: Fias360CustomerDto }>(
      'DELETE',
      `/arches/fias360/customers/${customerId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async restoreFias360Customer(holderId: string, customerId: string) {
    return this.request<{ customer: Fias360CustomerDto }>(
      'POST',
      `/arches/fias360/customers/${customerId}/restore?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async setOwnerFias360Customer(
    holderId: string,
    customerId: string,
    ownerPrincipalId: string | null,
  ) {
    return this.request<{ customer: Fias360CustomerDto }>(
      'PUT',
      `/arches/fias360/customers/${customerId}/owner?holder=${encodeURIComponent(holderId)}`,
      { ownerPrincipalId },
    );
  }

  async listFias360Companies(
    holderId: string,
    options?: { includeArchived?: boolean; limit?: number },
  ) {
    const params = new URLSearchParams({ holder: holderId });
    if (options?.includeArchived) params.set('includeArchived', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    return this.request<{ companies: Fias360CompanyDto[] }>(
      'GET',
      `/arches/fias360/companies?${params.toString()}`,
    );
  }

  async getFias360Company(holderId: string, companyId: string) {
    return this.request<{ company: Fias360CompanyDto }>(
      'GET',
      `/arches/fias360/companies/${companyId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async createFias360Company(
    holderId: string,
    input: {
      name: string;
      domain?: string;
      industry?: string;
      website?: string;
      description?: string;
      ownerPrincipalId?: string;
      tags?: string[];
    },
  ) {
    return this.request<{ company: Fias360CompanyDto }>(
      'POST',
      `/arches/fias360/companies?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async updateFias360Company(
    holderId: string,
    companyId: string,
    input: Partial<{
      name: string;
      domain: string | null;
      industry: string | null;
      website: string | null;
      description: string | null;
      tags: string[];
    }>,
  ) {
    return this.request<{ company: Fias360CompanyDto }>(
      'PATCH',
      `/arches/fias360/companies/${companyId}?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async archiveFias360Company(holderId: string, companyId: string) {
    return this.request<{ company: Fias360CompanyDto }>(
      'DELETE',
      `/arches/fias360/companies/${companyId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async restoreFias360Company(holderId: string, companyId: string) {
    return this.request<{ company: Fias360CompanyDto }>(
      'POST',
      `/arches/fias360/companies/${companyId}/restore?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async setOwnerFias360Company(
    holderId: string,
    companyId: string,
    ownerPrincipalId: string | null,
  ) {
    return this.request<{ company: Fias360CompanyDto }>(
      'PUT',
      `/arches/fias360/companies/${companyId}/owner?holder=${encodeURIComponent(holderId)}`,
      { ownerPrincipalId },
    );
  }

  async importFias360Customers(
    holderId: string,
    rows: Array<{
      firstName: string;
      lastName?: string;
      primaryEmail?: string;
      primaryPhone?: string;
      title?: string;
      notes?: string;
      lifecycleStage?: string;
      tags?: string[];
      altEmails?: Fias360AltContactValueDto[];
      altPhones?: Fias360AltContactValueDto[];
      companyId?: string;
      companyName?: string;
      companyExternalKey?: string;
    }>,
  ) {
    return this.request<Fias360ImportResult>(
      'POST',
      `/arches/fias360/import/customers?holder=${encodeURIComponent(holderId)}`,
      { rows },
    );
  }

  async importFias360Companies(
    holderId: string,
    rows: Array<{
      name: string;
      domain?: string;
      industry?: string;
      website?: string;
      description?: string;
      tags?: string[];
      externalKey?: string;
    }>,
  ) {
    return this.request<Fias360ImportResult>(
      'POST',
      `/arches/fias360/import/companies?holder=${encodeURIComponent(holderId)}`,
      { rows },
    );
  }

  // Segments (Phase 10d-2)
  async listFias360Segments(
    holderId: string,
    options?: { targetKind?: 'customer' | 'company'; includeArchived?: boolean; limit?: number },
  ) {
    const params = new URLSearchParams({ holder: holderId });
    if (options?.targetKind) params.set('targetKind', options.targetKind);
    if (options?.includeArchived) params.set('includeArchived', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    return this.request<{ segments: Fias360SegmentDto[] }>(
      'GET',
      `/arches/fias360/segments?${params.toString()}`,
    );
  }

  async getFias360Segment(holderId: string, segmentId: string) {
    return this.request<{ segment: Fias360SegmentDto }>(
      'GET',
      `/arches/fias360/segments/${segmentId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async createFias360Segment(
    holderId: string,
    input: {
      name: string;
      targetKind: 'customer' | 'company';
      filter?: Fias360SegmentFilterDto;
      ownerPrincipalId?: string;
    },
  ) {
    return this.request<{ segment: Fias360SegmentDto }>(
      'POST',
      `/arches/fias360/segments?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async updateFias360Segment(
    holderId: string,
    segmentId: string,
    input: { name?: string; filter?: Fias360SegmentFilterDto },
  ) {
    return this.request<{ segment: Fias360SegmentDto }>(
      'PATCH',
      `/arches/fias360/segments/${segmentId}?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async archiveFias360Segment(holderId: string, segmentId: string) {
    return this.request<{ segment: Fias360SegmentDto }>(
      'DELETE',
      `/arches/fias360/segments/${segmentId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async restoreFias360Segment(holderId: string, segmentId: string) {
    return this.request<{ segment: Fias360SegmentDto }>(
      'POST',
      `/arches/fias360/segments/${segmentId}/restore?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async applyFias360Segment(holderId: string, segmentId: string, limit?: number) {
    const params = new URLSearchParams({ holder: holderId });
    if (limit) params.set('limit', String(limit));
    return this.request<Fias360ApplySegmentResultDto>(
      'GET',
      `/arches/fias360/segments/${segmentId}/apply?${params.toString()}`,
    );
  }

  // Reports (Phase 10d-3)
  async reportFias360CustomersByLifecycleStage(holderId: string) {
    return this.request<{ rows: Array<{ lifecycleStage: string | null; count: number }> }>(
      'GET',
      `/arches/fias360/reports/customers-by-lifecycle-stage?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async reportFias360ActivityVolumeByActor(holderId: string, days?: number) {
    const params = new URLSearchParams({ holder: holderId });
    if (days) params.set('days', String(days));
    return this.request<{
      rows: Array<{ principalId: string; count: number }>;
      days: number;
    }>('GET', `/arches/fias360/reports/activity-volume-by-actor?${params.toString()}`);
  }

  async reportFias360UncontactedCustomers(holderId: string, days?: number, limit?: number) {
    const params = new URLSearchParams({ holder: holderId });
    if (days) params.set('days', String(days));
    if (limit) params.set('limit', String(limit));
    return this.request<{
      rows: Array<{
        contactId: string;
        firstName: string;
        lastName: string | null;
        primaryEmail: string | null;
        lifecycleStage: string | null;
        ownerPrincipalId: string | null;
        lastContactedAt: string | null;
      }>;
      days: number;
    }>('GET', `/arches/fias360/reports/uncontacted-customers?${params.toString()}`);
  }

  async reportFias360NewCustomersOverTime(holderId: string, days?: number) {
    const params = new URLSearchParams({ holder: holderId });
    if (days) params.set('days', String(days));
    return this.request<{
      rows: Array<{ bucket: string; count: number }>;
      days: number;
    }>('GET', `/arches/fias360/reports/new-customers-over-time?${params.toString()}`);
  }

  async reportFias360RenewalsByStatus(holderId: string) {
    return this.request<{
      rows: Array<{ status: 'open' | 'at_risk' | 'renewed' | 'churned'; count: number }>;
    }>('GET', `/arches/fias360/reports/renewals-by-status?holder=${encodeURIComponent(holderId)}`);
  }

  // Custom fields (Phase 10e-1 — arche-private, outside Vault substrate)
  async listFias360CustomFieldDefs(
    holderId: string,
    options?: { targetKind?: 'customer' | 'company'; includeArchived?: boolean },
  ) {
    const params = new URLSearchParams({ holder: holderId });
    if (options?.targetKind) params.set('targetKind', options.targetKind);
    if (options?.includeArchived) params.set('includeArchived', 'true');
    return this.request<{ defs: Fias360CustomFieldDefDto[] }>(
      'GET',
      `/arches/fias360/custom-fields/defs?${params.toString()}`,
    );
  }

  async createFias360CustomFieldDef(
    holderId: string,
    input: {
      targetKind: 'customer' | 'company';
      name: string;
      valueType: 'text' | 'number' | 'date' | 'select';
      options?: string[];
      sortOrder?: number;
    },
  ) {
    return this.request<{ def: Fias360CustomFieldDefDto }>(
      'POST',
      `/arches/fias360/custom-fields/defs?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async updateFias360CustomFieldDef(
    holderId: string,
    defId: string,
    input: { name?: string; options?: string[]; sortOrder?: number },
  ) {
    return this.request<{ def: Fias360CustomFieldDefDto }>(
      'PATCH',
      `/arches/fias360/custom-fields/defs/${defId}?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async archiveFias360CustomFieldDef(holderId: string, defId: string) {
    return this.request<{ def: Fias360CustomFieldDefDto }>(
      'DELETE',
      `/arches/fias360/custom-fields/defs/${defId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async restoreFias360CustomFieldDef(holderId: string, defId: string) {
    return this.request<{ def: Fias360CustomFieldDefDto }>(
      'POST',
      `/arches/fias360/custom-fields/defs/${defId}/restore?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async listFias360CustomFieldValuesForCustomer(holderId: string, customerId: string) {
    return this.request<{ values: Fias360CustomFieldValueWithDefDto[] }>(
      'GET',
      `/arches/fias360/custom-fields/customers/${customerId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async upsertFias360CustomFieldValueForCustomer(
    holderId: string,
    customerId: string,
    input: {
      defId: string;
      valueText?: string | null;
      valueNumber?: number | null;
      valueDate?: string | null;
    },
  ) {
    return this.request<{ value: Fias360CustomFieldValueDto | null }>(
      'PUT',
      `/arches/fias360/custom-fields/customers/${customerId}?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async listFias360CustomFieldValuesForCompany(holderId: string, companyId: string) {
    return this.request<{ values: Fias360CustomFieldValueWithDefDto[] }>(
      'GET',
      `/arches/fias360/custom-fields/companies/${companyId}?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async upsertFias360CustomFieldValueForCompany(
    holderId: string,
    companyId: string,
    input: {
      defId: string;
      valueText?: string | null;
      valueNumber?: number | null;
      valueDate?: string | null;
    },
  ) {
    return this.request<{ value: Fias360CustomFieldValueDto | null }>(
      'PUT',
      `/arches/fias360/custom-fields/companies/${companyId}?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  // Workspace preferences (Phase 10e follow-up — vocabulary perspective per Holder)
  async getFias360WorkspacePreferences(holderId: string) {
    return this.request<{ preferences: Fias360WorkspacePreferencesDto }>(
      'GET',
      `/arches/fias360/workspace-preferences?holder=${encodeURIComponent(holderId)}`,
    );
  }

  async upsertFias360WorkspacePreferences(holderId: string, input: { vocabularyPresetId: string }) {
    return this.request<{ preferences: Fias360WorkspacePreferencesDto }>(
      'PUT',
      `/arches/fias360/workspace-preferences?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async listFias360Timeline(
    holderId: string,
    options: {
      regardingType?: 'vault_company' | 'vault_contact';
      regardingId?: string;
      kind?: 'note' | 'call' | 'email' | 'meeting';
      before?: string;
      includeDeleted?: boolean;
      limit?: number;
    },
  ) {
    const params = new URLSearchParams({ holder: holderId });
    for (const [k, v] of Object.entries(options)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
    return this.request<{ activities: Fias360ActivityDto[] }>(
      'GET',
      `/arches/fias360/timeline?${params.toString()}`,
    );
  }

  async createFias360Activity(
    holderId: string,
    input: {
      kind: 'note' | 'call' | 'email' | 'meeting';
      regardingType: 'vault_company' | 'vault_contact';
      regardingId: string;
      subject: string;
      bodyMarkdown?: string;
      typeData?: Record<string, unknown>;
      occurredAt?: string;
    },
  ) {
    return this.request<{ activity: Fias360ActivityDto }>(
      'POST',
      `/arches/fias360/activities?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  // --- Tasks (vault_tasks substrate) ---

  async listFias360Tasks(
    holderId: string,
    options?: {
      regardingType?: Fias360TaskRegardingType;
      regardingId?: string;
      status?: Fias360TaskStatus;
      assigneePrincipalId?: string;
      dueBefore?: string;
      includeDeleted?: boolean;
      limit?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.regardingType) qs.set('regardingType', options.regardingType);
    if (options?.regardingId) qs.set('regardingId', options.regardingId);
    if (options?.status) qs.set('status', options.status);
    if (options?.assigneePrincipalId) qs.set('assigneePrincipalId', options.assigneePrincipalId);
    if (options?.dueBefore) qs.set('dueBefore', options.dueBefore);
    if (options?.includeDeleted) qs.set('includeDeleted', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ tasks: Fias360TaskDto[] }>('GET', `/arches/fias360/tasks?${qs}`);
  }

  async getFias360Task(holderId: string, taskId: string) {
    return this.request<{ task: Fias360TaskDto }>(
      'GET',
      `/arches/fias360/tasks/${taskId}?holder=${holderId}`,
    );
  }

  async createFias360Task(holderId: string, input: Fias360CreateTaskInput) {
    return this.request<{ task: Fias360TaskDto }>(
      'POST',
      `/arches/fias360/tasks?holder=${holderId}`,
      input,
    );
  }

  async updateFias360Task(holderId: string, taskId: string, input: Fias360UpdateTaskInput) {
    return this.request<{ task: Fias360TaskDto }>(
      'PATCH',
      `/arches/fias360/tasks/${taskId}?holder=${holderId}`,
      input,
    );
  }

  async completeFias360Task(holderId: string, taskId: string) {
    return this.request<{ task: Fias360TaskDto }>(
      'POST',
      `/arches/fias360/tasks/${taskId}/complete?holder=${holderId}`,
    );
  }

  async reopenFias360Task(holderId: string, taskId: string) {
    return this.request<{ task: Fias360TaskDto }>(
      'POST',
      `/arches/fias360/tasks/${taskId}/reopen?holder=${holderId}`,
    );
  }

  async cancelFias360Task(holderId: string, taskId: string) {
    return this.request<{ task: Fias360TaskDto }>(
      'POST',
      `/arches/fias360/tasks/${taskId}/cancel?holder=${holderId}`,
    );
  }

  async deleteFias360Task(holderId: string, taskId: string) {
    return this.request<{ task: Fias360TaskDto }>(
      'DELETE',
      `/arches/fias360/tasks/${taskId}?holder=${holderId}`,
    );
  }

  async restoreFias360Task(holderId: string, taskId: string) {
    return this.request<{ task: Fias360TaskDto }>(
      'POST',
      `/arches/fias360/tasks/${taskId}/restore?holder=${holderId}`,
    );
  }

  async listFias360HealthSnapshots(
    holderId: string,
    subjectKind: 'vault_company' | 'vault_contact',
    subjectId: string,
    limit?: number,
  ) {
    const params = new URLSearchParams({
      holder: holderId,
      subjectKind,
      subjectId,
    });
    if (limit) params.set('limit', limit.toString());
    return this.request<{ snapshots: Fias360HealthSnapshotDto[] }>(
      'GET',
      `/arches/fias360/health/snapshots?${params.toString()}`,
    );
  }

  async getLatestFias360HealthSnapshot(
    holderId: string,
    subjectKind: 'vault_company' | 'vault_contact',
    subjectId: string,
  ) {
    const params = new URLSearchParams({ holder: holderId, subjectKind, subjectId });
    return this.request<{ snapshot: Fias360HealthSnapshotDto | null }>(
      'GET',
      `/arches/fias360/health/latest?${params.toString()}`,
    );
  }

  async recordFias360HealthSnapshot(
    holderId: string,
    input: {
      subjectKind: 'vault_company' | 'vault_contact';
      subjectId: string;
      score: number;
      breakdown?: Record<string, unknown>;
      computedAt?: string;
    },
  ) {
    return this.request<{ snapshot: Fias360HealthSnapshotDto }>(
      'POST',
      `/arches/fias360/health/snapshots?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async listFias360Renewals(
    holderId: string,
    options?: {
      companyId?: string;
      status?: 'open' | 'at_risk' | 'renewed' | 'churned';
      before?: string;
      onOrAfter?: string;
      includeDeleted?: boolean;
      limit?: number;
    },
  ) {
    const params = new URLSearchParams({ holder: holderId });
    if (options) {
      for (const [k, v] of Object.entries(options)) {
        if (v !== undefined && v !== null) params.set(k, String(v));
      }
    }
    return this.request<{ renewals: Fias360RenewalDto[] }>(
      'GET',
      `/arches/fias360/renewals?${params.toString()}`,
    );
  }

  async createFias360Renewal(
    holderId: string,
    input: {
      companyId: string;
      renewalDate: string;
      status?: 'open' | 'at_risk' | 'renewed' | 'churned';
      valueCents?: number;
      termMonths?: number;
      notes?: string;
      ownerPrincipalId?: string;
    },
  ) {
    return this.request<{ renewal: Fias360RenewalDto }>(
      'POST',
      `/arches/fias360/renewals?holder=${encodeURIComponent(holderId)}`,
      input,
    );
  }

  async markFias360RenewalStatus(
    holderId: string,
    renewalId: string,
    status: 'open' | 'at_risk' | 'renewed' | 'churned',
  ) {
    return this.request<{ renewal: Fias360RenewalDto }>(
      'PUT',
      `/arches/fias360/renewals/${renewalId}/status?holder=${encodeURIComponent(holderId)}`,
      { status },
    );
  }

  // ==========================================================================
  // Vault Identity — backs the platform-shared HolderSwitcher
  // ==========================================================================

  async listMyVaultMemberships() {
    return this.request<{
      principalId: string;
      /** Phase 12d — the caller's Personal Holder so a unified
       * picker can offer "Personal" alongside org workspaces. */
      personalHolder: VaultPersonalHolderDto | null;
      memberships: VaultMembershipDto[];
      /** Guild shared data spaces the caller can reach (may be absent
       * from older server builds during a deploy window). */
      guildSpaces?: VaultGuildSpaceDto[];
    }>('GET', '/vault/me/memberships');
  }

  /**
   * External agent (MCP) keys pointed at one workspace — owner/admin only.
   * Governance view: which non-human callers reach this workspace's shared
   * data, and whose they are. Metadata only; never the secret.
   */
  async listWorkspaceAgentKeys(holderId: string) {
    return this.request<{
      agentKeys: Array<{
        keyId: string;
        name: string;
        canWrite: boolean;
        ownerDisplayName: string | null;
        ownerUsername: string | null;
        createdAt: string;
        lastUsedAt: string | null;
      }>;
    }>('GET', `/vault/holders/${encodeURIComponent(holderId)}/agent-keys`);
  }

  /** Enable a guild's shared Vault data space (guild owner only;
   * idempotent — re-enabling returns the existing space). */
  async enableGuildDataSpace(guildId: string) {
    return this.request<{ guildId: string; holderId: string; created: boolean }>(
      'POST',
      `/arches/guilds/${encodeURIComponent(guildId)}/data-space`,
    );
  }

  /** Turn a guild's plugin data sharing on or off (guild owner only).
   *  Distinct from the Vault data space above, and reversible. */
  async setGuildPluginSharing(guildId: string, enabled: boolean) {
    return this.request<{ guildId: string; enabled: boolean; changed: boolean }>(
      'PUT',
      `/arches/guilds/${encodeURIComponent(guildId)}/plugin-sharing`,
      { enabled },
    );
  }

  // ── Companies + workspace arche approvals (ADR-022) ──────────────────────

  async listMyCompanies() {
    return this.request<{ companies: Array<{ company: CompanyDto; role: CompanyMemberRole }> }>(
      'GET',
      '/companies/mine',
    );
  }

  async createCompany(name: string) {
    return this.request<{ company: CompanyDto }>('POST', '/companies', { name });
  }

  async getCompany(companyId: string) {
    return this.request<CompanySettingsDto>('GET', `/companies/${encodeURIComponent(companyId)}`);
  }

  /**
   * The Company's brand (ADR-023) and whether it may have one
   * (`company_branding`, granted by Fias).
   */
  async getCompanyBranding(companyId: string) {
    return this.request<{
      branding: DocumentSignPublicBranding | null;
      brandingEnabled: boolean;
    }>('GET', `/companies/${encodeURIComponent(companyId)}/branding`);
  }

  async putCompanyBranding(companyId: string, input: Partial<DocumentSignPublicBranding>) {
    return this.request<{ branding: DocumentSignPublicBranding }>(
      'PUT',
      `/companies/${encodeURIComponent(companyId)}/branding`,
      input,
    );
  }

  async deleteCompanyBranding(companyId: string) {
    return this.request<{ removed: boolean }>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/branding`,
    );
  }

  // Company portals (ADR-024) — Company → Web
  async getCompanyWeb(companyId: string) {
    return this.request<CompanyWebDto>('GET', `/companies/${encodeURIComponent(companyId)}/web`);
  }

  // ── Company → Web → Domains (ADR-024 rev 4) ───────────────────────────────

  private companyDomainsPath(companyId: string, rest = '') {
    return `/companies/${encodeURIComponent(companyId)}/web/domains${rest}`;
  }

  async searchCompanyWebDomain(companyId: string, name: string) {
    return this.request<CompanyWebDomainQuoteDto>(
      'GET',
      this.companyDomainsPath(companyId, `/search?name=${encodeURIComponent(name)}`),
    );
  }

  /**
   * The charge is the server's own quote at the moment of purchase, charged to
   * the Company's payer. `expectedCredits` is the price the owner was shown: a
   * ceiling — a higher fresh quote is refused (409
   * COMPANY_WEB_DOMAIN_PRICE_CHANGED, with the new price) and nothing is charged.
   */
  async purchaseCompanyWebDomain(
    companyId: string,
    input: {
      zoneName: string;
      registrantContact: CompanyDomainRegistrantContactDto;
      expectedCredits: number;
    },
  ) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'POST',
      this.companyDomainsPath(companyId, '/purchase'),
      input,
    );
  }

  async startCompanyWebDomainTransfer(companyId: string, zoneName: string) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'POST',
      this.companyDomainsPath(companyId, '/transfer'),
      { zoneName },
    );
  }

  /** What submitting this transfer would charge now (the registrar's transfer price + markup). */
  async quoteCompanyWebDomainTransfer(companyId: string, domainId: string) {
    return this.request<{ priceCredits: number; renewalPriceCredits: number }>(
      'GET',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/transfer/quote`),
    );
  }

  /** `expectedCredits` caps the charge, as for a purchase. */
  async submitCompanyWebDomainTransfer(
    companyId: string,
    domainId: string,
    input: {
      authCode: string;
      registrantContact: CompanyDomainRegistrantContactDto;
      expectedCredits: number;
    },
  ) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'POST',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/transfer/submit`),
      input,
    );
  }

  async delegateCompanyWebDomain(companyId: string, zoneName: string) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'POST',
      this.companyDomainsPath(companyId, '/delegate'),
      { zoneName },
    );
  }

  /** A whole approved domain whose name servers the Company points at Fias, at its own registrar. */
  async pointCompanyWebDomainNameservers(companyId: string, zoneName: string) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'POST',
      this.companyDomainsPath(companyId, '/nameservers'),
      { zoneName },
    );
  }

  /**
   * Verify: what the parent zone's authoritative servers show for the NS
   * records the Company was asked to publish, right now. Advisory — the
   * domain's status still moves on Fias's own schedule.
   */
  async checkCompanyWebDomainDelegation(companyId: string, domainId: string) {
    return this.request<{ domain: CompanyWebDomainDto; check: CompanyWebDelegationCheckDto }>(
      'POST',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/check`),
    );
  }

  async setCompanyWebDomainAutoRenew(companyId: string, domainId: string, autoRenew: boolean) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'PATCH',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}`),
      { autoRenew },
    );
  }

  async confirmCompanyWebDomainRenewalQuote(companyId: string, domainId: string) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'POST',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/renewal/confirm`),
    );
  }

  async removeCompanyWebDomain(companyId: string, domainId: string) {
    return this.request<{ domain: CompanyWebDomainDto }>(
      'DELETE',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}`),
    );
  }

  /** Unlocks the domain and returns its auth code — shown once, never stored by Fias. */
  async transferOutCompanyWebDomain(companyId: string, domainId: string) {
    return this.request<{ domain: CompanyWebDomainDto; authCode: string }>(
      'POST',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/transfer-out`),
    );
  }

  async listCompanyWebDnsRecords(companyId: string, domainId: string) {
    return this.request<{ domain: CompanyWebDomainDto; records: CompanyWebDnsRecordDto[] }>(
      'GET',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/records`),
    );
  }

  async putCompanyWebDnsRecord(
    companyId: string,
    domainId: string,
    input: { name: string; type: CompanyWebDnsRecordType; ttl: number; values: string[] },
  ) {
    return this.request<{ record: CompanyWebDnsRecordDto }>(
      'PUT',
      this.companyDomainsPath(companyId, `/${encodeURIComponent(domainId)}/records`),
      input,
    );
  }

  async deleteCompanyWebDnsRecord(companyId: string, domainId: string, recordId: string) {
    return this.request<void>(
      'DELETE',
      this.companyDomainsPath(
        companyId,
        `/${encodeURIComponent(domainId)}/records/${encodeURIComponent(recordId)}`,
      ),
    );
  }

  async claimCompanyWebHost(
    companyId: string,
    input: {
      /** The Fias-managed domain the portal lives in (ADR-024 rev 4). */
      domainId: string;
      hostname: string;
      displayLabel: string;
      accessPolicy?: 'members_only' | 'open';
      landingArcheId?: string | null;
    },
  ) {
    return this.request<{ host: CompanyWebHostDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/web/hosts`,
      input,
    );
  }

  async updateCompanyWebHost(
    companyId: string,
    hostId: string,
    input: {
      displayLabel?: string;
      accessPolicy?: 'members_only' | 'open';
      landingArcheId?: string | null;
    },
  ) {
    return this.request<{ host: CompanyWebHostDto }>(
      'PATCH',
      `/companies/${encodeURIComponent(companyId)}/web/hosts/${encodeURIComponent(hostId)}`,
      input,
    );
  }

  async removeCompanyWebHost(companyId: string, hostId: string) {
    return this.request<{ host: CompanyWebHostDto }>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/web/hosts/${encodeURIComponent(hostId)}`,
    );
  }

  async listCompanyWebPortalUsers(
    companyId: string,
    hostId: string,
    page: { limit?: number; offset?: number } = {},
  ) {
    const query = new URLSearchParams();
    if (page.limit !== undefined) query.set('limit', String(page.limit));
    if (page.offset !== undefined) query.set('offset', String(page.offset));
    const qs = query.toString();
    return this.request<{ users: CompanyWebPortalUserDto[] }>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/web/hosts/${encodeURIComponent(hostId)}/users${qs ? `?${qs}` : ''}`,
    );
  }

  async setCompanyWebPortalUserBlocked(
    companyId: string,
    hostId: string,
    userId: string,
    blocked: boolean,
  ) {
    return this.request<{ blocked: boolean }>(
      'PUT',
      `/companies/${encodeURIComponent(companyId)}/web/hosts/${encodeURIComponent(hostId)}/users/${encodeURIComponent(userId)}/blocked`,
      { blocked },
    );
  }

  /** Company email (ADR-023): whether Fias enabled it, the approved domains, and live state. */
  async getCompanyEmail(companyId: string) {
    return this.request<CompanyEmailDto>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/email`,
    );
  }

  async addCompanySendingDomain(companyId: string, domain: string) {
    return this.request<{ domain: CompanySendingDomainDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/domains`,
      { domain },
    );
  }

  /** Verify: SES's verdict (recorded) plus what the DNS shows for each record. */
  async checkCompanySendingDomain(companyId: string, domainId: string) {
    return this.request<{
      domain: CompanySendingDomainDto | null;
      recordChecks: CompanyDnsRecordCheckDto[];
    }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/domains/${encodeURIComponent(domainId)}/check`,
    );
  }

  async retryCompanySendingDomain(companyId: string, domainId: string) {
    return this.request<{ domain: CompanySendingDomainDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/domains/${encodeURIComponent(domainId)}/retry`,
    );
  }

  async removeCompanySendingDomain(companyId: string, domainId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/email/domains/${encodeURIComponent(domainId)}`,
    );
  }

  /** Fias publishes the domain's records into the Company's managed zone. */
  async publishCompanySendingDomainDns(companyId: string, domainId: string) {
    return this.request<{
      domain: CompanySendingDomainDto;
      conflicts: Array<{ name: string; type: CompanyDnsRecordDto['type']; reason: string }>;
    }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/domains/${encodeURIComponent(domainId)}/publish-dns`,
    );
  }

  async addCompanySenderIdentity(
    companyId: string,
    body: { domainId: string; localPart: string; displayName: string; replyTo?: string | null },
  ) {
    return this.request<{ identity: CompanySenderIdentityDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/identities`,
      body,
    );
  }

  /** Mail the signed-in admin a test from one of the Company's addresses. */
  async sendCompanyEmailTest(companyId: string, identityId: string, requestKey: string) {
    return this.request<{
      status: 'accepted' | 'failed' | 'unknown';
      to: string;
      duplicate: boolean;
    }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/identities/${encodeURIComponent(identityId)}/test`,
      { requestKey },
    );
  }

  async removeCompanySenderIdentity(companyId: string, identityId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/email/identities/${encodeURIComponent(identityId)}`,
    );
  }

  async authorizeCompanySender(
    companyId: string,
    body: {
      archeId: string;
      identityId: string;
      recipientScope?: 'workspace' | 'company' | 'any' | null;
      showMuteLine?: boolean;
    },
  ) {
    return this.request<{ authorization: CompanySenderAuthorizationDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/email/authorizations`,
      body,
    );
  }

  async revokeCompanySender(companyId: string, authorizationId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/email/authorizations/${encodeURIComponent(authorizationId)}`,
    );
  }

  async updateCompany(
    companyId: string,
    body: { name?: string; billingUserId?: string; trustFiasArches?: boolean },
  ) {
    return this.request<{ company: CompanyDto }>(
      'PATCH',
      `/companies/${encodeURIComponent(companyId)}`,
      body,
    );
  }

  /** Invites a person to a seat; nothing is granted until they accept from their own session. */
  async addCompanyMember(companyId: string, body: { username: string; role: CompanyMemberRole }) {
    return this.request<{ invitation: CompanyMemberInvitationDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/members`,
      body,
    );
  }

  async cancelCompanyMemberInvitation(companyId: string, invitationId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/member-invitations/${encodeURIComponent(invitationId)}`,
    );
  }

  /** The caller's own open Company seat invitations (the invitee's side). */
  async listMyCompanySeatInvitations() {
    return this.request<{ invitations: CompanySeatInvitationForMeDto[] }>(
      'GET',
      '/companies/invitations/mine',
    );
  }

  /** Takes the seat. MFA is required, as for every Company route. */
  async acceptCompanySeatInvitation(invitationId: string) {
    return this.request<{ membership: CompanyMembershipDto }>(
      'POST',
      `/companies/invitations/${encodeURIComponent(invitationId)}/accept`,
    );
  }

  async declineCompanySeatInvitation(invitationId: string) {
    return this.request<void>(
      'POST',
      `/companies/invitations/${encodeURIComponent(invitationId)}/decline`,
    );
  }

  async updateCompanyMemberRole(companyId: string, membershipId: string, role: CompanyMemberRole) {
    return this.request<{ membership: CompanyMembershipDto }>(
      'PATCH',
      `/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(membershipId)}`,
      { role },
    );
  }

  async removeCompanyMember(companyId: string, membershipId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(membershipId)}`,
    );
  }

  async proposeCompanyLink(companyId: string, holderId: string) {
    return this.request<{ invitation: CompanyLinkInvitationDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/links`,
      { holderId },
    );
  }

  async cancelCompanyLink(companyId: string, invitationId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/links/${encodeURIComponent(invitationId)}`,
    );
  }

  /** Read-only billing roll-up per linked workspace for one ISO month (default: current). */
  async getCompanyBilling(companyId: string, month?: string) {
    return this.request<CompanyBillingDto>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/billing${month ? `?month=${encodeURIComponent(month)}` : ''}`,
    );
  }

  /** Owner-only. Unlinks every workspace (billing back to each owner) and archives the Company. */
  async deleteCompany(companyId: string) {
    return this.request<{
      unlinkedWorkspaces: number;
      proposalsCancelled: number;
      invitationsCancelled: number;
      approvalsRevoked: number;
      membersRevoked: number;
    }>('DELETE', `/companies/${encodeURIComponent(companyId)}`);
  }

  /** Owner-only, immediate: billing returns to whoever paid before the link. */
  async unlinkCompanyWorkspace(companyId: string, holderId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/workspaces/${encodeURIComponent(holderId)}`,
    );
  }

  /** One page of the Company's decisions, newest first, with the total for paging. */
  async listCompanyApprovals(companyId: string, page?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (page?.limit !== undefined) params.set('limit', String(page.limit));
    if (page?.offset !== undefined) params.set('offset', String(page.offset));
    const qs = params.toString();
    return this.request<{ approvals: ArcheApprovalDto[]; total: number }>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/approvals${qs ? `?${qs}` : ''}`,
    );
  }

  /** Unscoped catalog lookups for the Create-Company wizard (no Company exists yet). */
  async searchArcheCatalog(q: string) {
    return this.request<ArcheSearchDto>(
      'GET',
      `/companies/arche-catalog/search?q=${encodeURIComponent(q)}`,
    );
  }

  async getArcheCatalogDisclosure(archeId: string) {
    return this.request<{ disclosure: ArcheApprovalDisclosureDto }>(
      'GET',
      `/companies/arche-catalog/disclosure/${encodeURIComponent(archeId)}`,
    );
  }

  async searchCompanyApprovalArches(companyId: string, q: string) {
    return this.request<ArcheSearchDto>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/approvals/search?q=${encodeURIComponent(q)}`,
    );
  }

  async getCompanyApprovalDisclosure(companyId: string, archeId: string) {
    return this.request<{ disclosure: ArcheApprovalDisclosureDto }>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/approvals/disclosure/${encodeURIComponent(archeId)}`,
    );
  }

  async decideCompanyApproval(
    companyId: string,
    body: { archeId: string; decision: 'approve' | 'deny'; note?: string },
  ) {
    return this.request<{ approval: ArcheApprovalDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/approvals`,
      body,
    );
  }

  async revokeCompanyApproval(companyId: string, approvalId: string) {
    return this.request<void>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/approvals/${encodeURIComponent(approvalId)}`,
    );
  }

  // ── Company-owned arches + arche audience (ADR-026) ───────────────────────

  /** Company → Owned arches: the arches a Company owns, and its open/recent proposals. */
  async listCompanyArches(companyId: string) {
    return this.request<CompanyArchesDto>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/arches`,
    );
  }

  /** What a Company owner is shown before accepting an arche — resolved server-side. */
  async getCompanyArcheTransferDisclosure(companyId: string, transferId: string) {
    return this.request<CompanyArcheTransferDisclosureDto>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/arche-transfers/${encodeURIComponent(transferId)}`,
    );
  }

  async decideCompanyArcheTransfer(
    companyId: string,
    transferId: string,
    body: {
      decision: 'accept' | 'decline';
      expected?: CompanyArcheTransferExpectedTerms;
    },
  ) {
    return this.request<{ transfer: CompanyArcheTransferDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/arche-transfers/${encodeURIComponent(transferId)}/decision`,
      body,
    );
  }

  async releaseCompanyArche(
    companyId: string,
    archeId: string,
    body: { username: string; reason?: string },
  ) {
    return this.request<{ transfer: CompanyArcheTransferDto }>(
      'POST',
      `/companies/${encodeURIComponent(companyId)}/arches/${encodeURIComponent(archeId)}/release`,
      body,
    );
  }

  async cancelCompanyArcheRelease(companyId: string, transferId: string) {
    return this.request<{ transfer: CompanyArcheTransferDto }>(
      'DELETE',
      `/companies/${encodeURIComponent(companyId)}/arche-transfers/${encodeURIComponent(transferId)}`,
    );
  }

  /**
   * Proposals that concern me: offers I sent to a Company, releases offered to
   * me — and, as a Company owner, offers waiting for one of my Companies.
   */
  async listMyArcheTransfers() {
    return this.request<MyArcheTransfersDto>('GET', '/arches/company-transfers/mine');
  }

  /**
   * Revoke a collaborator from a plugin arche. For a company-owned arche the
   * Company's owners and admins may (session + MFA, decided server-side).
   */
  async revokeArcheCollaborator(archeId: string, userId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/${encodeURIComponent(archeId)}/collaborators/${encodeURIComponent(userId)}`,
    );
  }

  async listArcheTransferCompanies() {
    return this.request<{
      companies: Array<{ companyId: string; name: string }>;
      transferEnabled: boolean;
    }>('GET', '/arches/company-transfers/eligible-companies');
  }

  async proposeArcheTransferToCompany(
    archeId: string,
    body: { companyId: string; reason?: string },
  ) {
    return this.request<{ transfer: CompanyArcheTransferDto }>(
      'POST',
      `/arches/${encodeURIComponent(archeId)}/company-transfer`,
      body,
    );
  }

  async cancelArcheTransfer(transferId: string) {
    return this.request<{ transfer: CompanyArcheTransferDto }>(
      'DELETE',
      `/arches/company-transfers/${encodeURIComponent(transferId)}`,
    );
  }

  async decideArcheRelease(transferId: string, decision: 'accept' | 'decline') {
    return this.request<{ transfer: CompanyArcheTransferDto }>(
      'POST',
      `/arches/company-transfers/${encodeURIComponent(transferId)}/decision`,
      { decision },
    );
  }

  async getArcheOwnership(archeId: string) {
    return this.request<ArcheOwnershipDto>(
      'GET',
      `/arches/${encodeURIComponent(archeId)}/ownership`,
    );
  }

  async getArcheAudience(archeId: string) {
    return this.request<ArcheAudienceDto>('GET', `/arches/${encodeURIComponent(archeId)}/audience`);
  }

  async setArcheAudience(
    archeId: string,
    audience: ArcheAudienceKind,
    options?: { grantOwningCompany?: boolean },
  ) {
    return this.request<{
      archeId: string;
      audience: ArcheAudienceKind;
      siteSnapshotRemovalPending: boolean;
      grantedOwningCompany: boolean;
    }>('PUT', `/arches/${encodeURIComponent(archeId)}/audience`, {
      audience,
      ...(options?.grantOwningCompany ? { grantOwningCompany: true } : {}),
    });
  }

  async finishArcheRestriction(archeId: string) {
    return this.request<{ archeId: string; siteSnapshotRemovalPending: boolean }>(
      'POST',
      `/arches/${encodeURIComponent(archeId)}/audience/finish-restriction`,
    );
  }

  async grantArcheAudience(
    archeId: string,
    body:
      | { granteeType: 'user'; username: string }
      | { granteeType: 'workspace' | 'company' | 'guild'; granteeId: string },
  ) {
    return this.request<{ grant: ArcheAudienceGrantDto }>(
      'POST',
      `/arches/${encodeURIComponent(archeId)}/audience/grantees`,
      body,
    );
  }

  async revokeArcheAudienceGrant(archeId: string, grantId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/${encodeURIComponent(archeId)}/audience/grantees/${encodeURIComponent(grantId)}`,
    );
  }

  /** Restricted arches shared with me — keyset paginated. */
  async listArchesSharedWithMe(cursor?: ArchesSharedWithMeCursor | null) {
    const params = new URLSearchParams();
    if (cursor) {
      params.set('cursorCreatedAt', cursor.createdAt);
      params.set('cursorArchId', cursor.archId);
    }
    const qs = params.toString();
    return this.request<ArchesSharedWithMeDto>(
      'GET',
      `/arches/shared-with-me${qs ? `?${qs}` : ''}`,
    );
  }

  /**
   * Per-workspace counts only — who read what stays on each workspace's own
   * page. 404 unless the Company approved that arche.
   */
  async getCompanyApprovalActivity(companyId: string, archeId: string) {
    return this.request<{ activity: CompanyArcheActivityDto[] }>(
      'GET',
      `/companies/${encodeURIComponent(companyId)}/approvals/${encodeURIComponent(archeId)}/activity`,
    );
  }

  /** Open Company link proposals on workspaces the caller OWNS (the alert feed). */
  async listMyCompanyLinkProposals() {
    return this.request<{ proposals: CompanyLinkProposalForMeDto[] }>(
      'GET',
      '/vault/me/company-link-proposals',
    );
  }

  /**
   * Workspaces the caller belongs to that joined a Company recently — every
   * member is told, not only the owner who accepted.
   */
  async listMyCompanyLinkNotices() {
    return this.request<{ notices: CompanyLinkNoticeDto[] }>(
      'GET',
      '/vault/me/company-link-notices',
    );
  }

  /** The workspace side: its Company link and every open proposal (one per Company). */
  async getWorkspaceCompany(holderId: string) {
    return this.request<WorkspaceCompanyDto>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/company`,
    );
  }

  /**
   * `expectedOwnerPrincipalIds` are the Company owners the accept screen
   * NAMED; required with `addOwnersAsAdmins`. If the owners changed since,
   * the server refuses with 409 COMPANY_OWNERS_CHANGED.
   */
  async acceptCompanyLink(
    invitationId: string,
    body: { addOwnersAsAdmins: boolean; expectedOwnerPrincipalIds?: string[] },
  ) {
    return this.request<{
      company: { companyId: string; name: string };
      /** Principal ids seated as admins by this accept. */
      ownersSeated: string[];
      /** Principal ids that were already on the roster (left as they were). */
      ownersAlreadyMembers: string[];
    }>('POST', `/vault/company-links/${encodeURIComponent(invitationId)}/accept`, body);
  }

  async declineCompanyLink(invitationId: string) {
    return this.request<void>(
      'POST',
      `/vault/company-links/${encodeURIComponent(invitationId)}/decline`,
    );
  }

  async listWorkspaceApprovals(holderId: string) {
    return this.request<{
      myRole: string;
      approvals: WorkspaceArcheApprovalDto[];
      /** True when the workspace's Company has "Trust Fias arches" on. */
      fiasTrusted: boolean;
      fiasTrustedArches: Array<{ archeId: string; name: string }>;
    }>('GET', `/vault/holders/${encodeURIComponent(holderId)}/approvals`);
  }

  async searchWorkspaceApprovalArches(holderId: string, q: string) {
    return this.request<ArcheSearchDto>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/approvals/search?q=${encodeURIComponent(q)}`,
    );
  }

  async getWorkspaceApprovalDisclosure(holderId: string, archeId: string) {
    return this.request<{ disclosure: ArcheApprovalDisclosureDto }>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/approvals/disclosure/${encodeURIComponent(archeId)}`,
    );
  }

  async decideWorkspaceApproval(
    holderId: string,
    body: { archeId: string; decision: 'approve' | 'deny'; note?: string },
  ) {
    return this.request<{ approval: ArcheApprovalDto }>(
      'POST',
      `/vault/holders/${encodeURIComponent(holderId)}/approvals`,
      body,
    );
  }

  async revokeWorkspaceApproval(holderId: string, approvalId: string) {
    return this.request<void>(
      'DELETE',
      `/vault/holders/${encodeURIComponent(holderId)}/approvals/${encodeURIComponent(approvalId)}`,
    );
  }

  async getWorkspaceApprovalActivity(holderId: string, archeId: string) {
    return this.request<{ activity: ArcheReadActivityDto[] }>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/approvals/${encodeURIComponent(archeId)}/activity`,
    );
  }

  async listWorkspaceAccessDenials(holderId: string) {
    return this.request<{ denials: HolderAccessDenialDto[] }>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/access-denials`,
    );
  }

  async listMyVaultInvitations() {
    return this.request<{ invitations: VaultInvitationForMeDto[] }>('GET', '/vault/me/invitations');
  }

  async acceptVaultInvitation(invitationId: string) {
    return this.request<{ membership: VaultMembershipDto; invitation: VaultInvitationDto }>(
      'POST',
      `/vault/invitations/${invitationId}/accept`,
    );
  }

  async declineVaultInvitationFromMe(invitationId: string) {
    return this.request<{ invitation: VaultInvitationDto }>(
      'POST',
      `/vault/invitations/${invitationId}/decline`,
    );
  }

  async listVaultHolderMembers(holderId: string) {
    return this.request<{ members: VaultHolderMemberDto[] }>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/members`,
    );
  }

  async listVaultHolderInvitations(holderId: string) {
    return this.request<{ invitations: VaultInvitationDto[] }>(
      'GET',
      `/vault/holders/${encodeURIComponent(holderId)}/invitations`,
    );
  }

  async inviteVaultMember(holderId: string, input: VaultInviteInput) {
    return this.request<{ invitation: VaultInvitationDto }>(
      'POST',
      `/vault/holders/${encodeURIComponent(holderId)}/invitations`,
      input,
    );
  }

  async cancelVaultInvitation(invitationId: string) {
    return this.request<void>('DELETE', `/vault/invitations/${encodeURIComponent(invitationId)}`);
  }

  /**
   * Platform-level user directory search by username prefix (active
   * users only, caller excluded, ≥2 characters server-side). Backs the
   * shared UserSearchCombobox; arche-agnostic — chat's DM search has
   * its own endpoint.
   */
  async searchUsers(query: string) {
    return this.request<{ users: UserSearchResultDto[] }>(
      'GET',
      `/users/search?username=${encodeURIComponent(query)}`,
    );
  }

  async updateVaultMembershipRole(
    membershipId: string,
    role: 'owner' | 'admin' | 'member' | 'viewer',
  ) {
    return this.request<{ membership: VaultMembershipDto }>(
      'PATCH',
      `/vault/memberships/${encodeURIComponent(membershipId)}`,
      { role },
    );
  }

  async revokeVaultMembership(membershipId: string) {
    return this.request<{ changed: boolean }>(
      'DELETE',
      `/vault/memberships/${encodeURIComponent(membershipId)}`,
    );
  }

  async createVaultOrgHolder(displayName: string) {
    return this.request<{
      holder: VaultHolderDto;
      ownerMembership: VaultMembershipDto;
    }>('POST', '/vault/holders/org', { displayName });
  }

  /** Rename an org workspace. Owner-only; personal workspaces are not
   * renameable. Returns the persisted display name. */
  async renameVaultOrgHolder(holderId: string, displayName: string) {
    return this.request<{
      holderId: string;
      displayName: string;
    }>('PATCH', `/vault/holders/${encodeURIComponent(holderId)}`, { displayName });
  }

  /** Soft-delete an org workspace. Owner-only. Server returns the
   * scheduled-purge timestamp so the UI can communicate the grace
   * window. Hard delete happens later via the purger. */
  async deleteVaultOrgHolder(holderId: string) {
    return this.request<{
      holderId: string;
      archivedAt: string | null;
      scheduledPurgeAt: string;
      graceDays: number;
    }>('DELETE', `/vault/holders/${encodeURIComponent(holderId)}`);
  }

  /** Undo a soft-delete during the grace window. Owner-only. */
  async undeleteVaultOrgHolder(holderId: string) {
    return this.request<{
      holderId: string;
      displayName: string;
    }>('POST', `/vault/holders/${encodeURIComponent(holderId)}/undelete`);
  }

  // Hard-delete a single contact: not exposed in v1. Use the trash flow
  // (deleteContact → emptyTrash). The Vault Personal Contacts entity has
  // no per-contact `permanent_delete` verb on purpose — `empty_trash` is
  // the only hard-delete surface.
  async permanentlyDeleteContact(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/contacts/${id}/permanent`);
  }

  async emptyTrash() {
    return this.request<{ deletedCount: number }>('DELETE', '/arches/contact-book/trash');
  }

  async getTrashCount() {
    return this.request<{ count: number }>('GET', '/arches/contact-book/trash/count');
  }

  async listContactCategories() {
    return this.request<{ categories: ContactCategory[] }>(
      'GET',
      '/arches/contact-book/categories',
    );
  }

  async createContactCategory(input: ContactCategoryInput) {
    return this.request<ContactCategory>('POST', '/arches/contact-book/categories', input);
  }

  async updateContactCategory(id: string, input: Partial<ContactCategoryInput>) {
    return this.request<ContactCategory>('PUT', `/arches/contact-book/categories/${id}`, input);
  }

  async deleteContactCategory(id: string) {
    return this.request<{ uncategorizedCount: number }>(
      'DELETE',
      `/arches/contact-book/categories/${id}`,
    );
  }

  // Bulk reorder of categories: not exposed by the Vault Personal Contacts
  // entity in v1. Reorder one at a time via updateContactCategory(id, {
  // displayOrder }) instead. Method kept so the existing UI compiles; the
  // PUT will 404 at runtime.
  async reorderContactCategories(categoryIds: string[]) {
    return this.request<{ categories: ContactCategory[] }>(
      'PUT',
      '/arches/contact-book/categories/reorder',
      { categoryIds },
    );
  }

  async listContactPriorities() {
    return this.request<{ priorities: ContactPriority[] }>(
      'GET',
      '/arches/contact-book/priorities',
    );
  }

  async createContactPriority(input: ContactPriorityInput) {
    return this.request<ContactPriority>('POST', '/arches/contact-book/priorities', input);
  }

  async updateContactPriority(id: string, input: Partial<ContactPriorityInput>) {
    return this.request<ContactPriority>('PUT', `/arches/contact-book/priorities/${id}`, input);
  }

  async deleteContactPriority(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/priorities/${id}`);
  }

  async listContactAttachments(contactId: string) {
    return this.request<{ attachments: ContactAttachment[] }>(
      'GET',
      `/arches/contact-book/contacts/${contactId}/attachments`,
    );
  }

  async addContactAttachment(contactId: string, fileId: string, label?: string) {
    return this.request<ContactAttachment>(
      'POST',
      `/arches/contact-book/contacts/${contactId}/attachments`,
      { fileId, label },
    );
  }

  async removeContactAttachment(contactId: string, attachmentId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/contact-book/contacts/${contactId}/attachments/${attachmentId}`,
    );
  }

  async bulkDeleteContactBookContacts(contactIds: string[]) {
    return this.request<{ deleted: number }>('DELETE', '/arches/contact-book/contacts/bulk', {
      contactIds,
    });
  }

  // Tags
  async listContactTags() {
    return this.request<{ tags: string[] }>('GET', '/arches/contact-book/tags');
  }

  // Upcoming dates (birthdays & anniversaries)
  async getUpcomingDates(days?: number) {
    const params = days ? `?days=${days}` : '';
    return this.request<{
      upcoming: Array<{
        contact: ContactDetail;
        dateType: 'birthday' | 'anniversary';
        date: string;
        daysAway: number;
      }>;
    }>('GET', `/arches/contact-book/upcoming-dates${params}`);
  }

  // ---------------------------------------------------------------------------
  // Import / Export / OAuth contact sync — DEFERRED follow-up.
  //
  // These methods existed on the legacy contact-book service. The Vault
  // refactor (2026-05-16) deleted the legacy services and disabled the
  // sync routes pending a port of sync-engine.ts to the new operations
  // module. The methods below are kept so the existing import/sync UI
  // compiles, but they will 404 at runtime in v1.
  // See docs/vault/personal-contacts.md §"Deferred follow-ups".
  // ---------------------------------------------------------------------------

  async parseContactImport(file: File): Promise<ParsedImportResult> {
    const content = await file.text();
    return this.request<ParsedImportResult>('POST', '/arches/contact-book/import/parse', {
      fileName: file.name,
      content,
    });
  }

  async confirmContactImport(request: ImportConfirmRequest): Promise<ImportConfirmResult> {
    return this.request<ImportConfirmResult>(
      'POST',
      '/arches/contact-book/import/confirm',
      request,
    );
  }

  // ==========================================================================
  // OAuth Contact Import
  // ==========================================================================

  async getImportConnections(): Promise<{ connections: OAuthConnection[] }> {
    return this.request<{ connections: OAuthConnection[] }>(
      'GET',
      '/arches/contact-book/import/connections',
    );
  }

  async getOAuthAuthorizeUrl(provider: OAuthProvider): Promise<{ authUrl: string; state: string }> {
    return this.request<{ authUrl: string; state: string }>(
      'GET',
      `/arches/contact-book/import/oauth/${provider}/authorize`,
    );
  }

  async fetchProviderContacts(provider: OAuthProvider): Promise<ParsedImportResult> {
    return this.request<ParsedImportResult>(
      'POST',
      `/arches/contact-book/import/oauth/${provider}/fetch`,
    );
  }

  async disconnectProvider(provider: OAuthProvider): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/contact-book/import/oauth/${provider}/disconnect`,
    );
  }

  // Duplicate detection & merge
  async findDuplicates(sensitivity?: DuplicateSensitivity) {
    const params = sensitivity ? `?sensitivity=${sensitivity}` : '';
    return this.request<{ groups: DuplicateGroup[] }>(
      'GET',
      `/arches/contact-book/duplicates${params}`,
    );
  }

  async mergeContactBookContacts(request: MergeRequest) {
    return this.request<ContactDetail>('POST', '/arches/contact-book/contacts/merge', request);
  }

  // Export
  async exportContacts(
    format: 'vcf' | 'csv',
    options?: { categoryId?: string; priorityId?: string; search?: string },
  ): Promise<string> {
    const params = new URLSearchParams({ format });
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.priorityId) params.set('priorityId', options.priorityId);
    if (options?.search) params.set('search', options.search);
    const url = `${API_BASE}/arches/contact-book/export?${params}`;
    const response = await fetch(url, { credentials: sessionCredentials() });
    if (!response.ok) throw new Error('Export failed');
    return response.text();
  }

  // Custom fields
  async listCustomFields() {
    return this.request<{ fields: CustomFieldDefinition[] }>(
      'GET',
      '/arches/contact-book/custom-fields',
    );
  }

  async createCustomField(input: CustomFieldInput) {
    return this.request<CustomFieldDefinition>('POST', '/arches/contact-book/custom-fields', input);
  }

  async updateCustomField(id: string, input: Partial<CustomFieldInput>) {
    return this.request<CustomFieldDefinition>(
      'PUT',
      `/arches/contact-book/custom-fields/${id}`,
      input,
    );
  }

  async deleteCustomField(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/custom-fields/${id}`);
  }

  // Contact sharing
  async shareContact(contactId: string) {
    return this.request<ContactShare>('POST', `/arches/contact-book/contacts/${contactId}/shares`);
  }

  async getSharedContact(token: string) {
    return this.request<SharedContactView>('GET', `/arches/contact-book/shared/${token}`);
  }

  // Contact interactions
  async listInteractions(contactId: string, limit = 50, offset = 0) {
    return this.request<{ interactions: ContactInteraction[]; total: number }>(
      'GET',
      `/arches/contact-book/contacts/${contactId}/interactions?limit=${limit}&offset=${offset}`,
    );
  }

  async createInteraction(contactId: string, input: InteractionInput) {
    return this.request<ContactInteraction>(
      'POST',
      `/arches/contact-book/contacts/${contactId}/interactions`,
      input,
    );
  }

  async updateInteraction(interactionId: string, input: Partial<InteractionInput>) {
    return this.request<ContactInteraction>(
      'PUT',
      `/arches/contact-book/interactions/${interactionId}`,
      input,
    );
  }

  async deleteInteraction(interactionId: string) {
    return this.request<void>('DELETE', `/arches/contact-book/interactions/${interactionId}`);
  }

  // Contact relationships
  async listRelationships(contactId: string) {
    return this.request<{ relationships: ContactRelationship[] }>(
      'GET',
      `/arches/contact-book/contacts/${contactId}/relationships`,
    );
  }

  async createRelationship(contactId: string, input: RelationshipInput) {
    return this.request<ContactRelationship>(
      'POST',
      `/arches/contact-book/contacts/${contactId}/relationships`,
      input,
    );
  }

  async deleteRelationship(relationshipId: string) {
    return this.request<void>('DELETE', `/arches/contact-book/relationships/${relationshipId}`);
  }

  // ==========================================================================
  // Calendar API
  // ==========================================================================

  async listCalendarCollections() {
    return this.request<{ calendars: CalendarCollectionDetail[] }>(
      'GET',
      '/arches/calendar/calendars',
    );
  }

  async createCalendarCollection(input: CalendarCollectionInput) {
    return this.request<CalendarCollectionDetail>('POST', '/arches/calendar/calendars', input);
  }

  async updateCalendarCollection(id: string, input: Partial<CalendarCollectionInput>) {
    return this.request<CalendarCollectionDetail>(
      'PATCH',
      `/arches/calendar/calendars/${id}`,
      input,
    );
  }

  /**
   * Delete a calendar. Refuses with CALENDAR_NOT_EMPTY when it still holds
   * events unless `deleteEvents` is set — the caller must have confirmed the
   * count with the user first.
   */
  async deleteCalendarCollection(id: string, deleteEvents = false) {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/calendars/${id}${deleteEvents ? '?deleteEvents=true' : ''}`,
    );
  }

  async listCalendarEvents(options: {
    rangeStart: string;
    rangeEnd: string;
    calendarId?: string;
    search?: string;
    status?: string;
  }) {
    const params = new URLSearchParams();
    params.set('rangeStart', options.rangeStart);
    params.set('rangeEnd', options.rangeEnd);
    if (options.calendarId) params.set('calendarId', options.calendarId);
    if (options.search) params.set('search', options.search);
    if (options.status) params.set('status', options.status);
    return this.request<{ events: CalendarEventInstance[] }>(
      'GET',
      `/arches/calendar/events?${params.toString()}`,
    );
  }

  async getCalendarEvent(id: string) {
    return this.request<CalendarEventDetail>('GET', `/arches/calendar/events/${id}`);
  }

  async createCalendarEvent(input: CalendarEventCreateInput) {
    return this.request<CalendarEventDetail>('POST', '/arches/calendar/events', input);
  }

  async updateCalendarEvent(
    id: string,
    input: Partial<CalendarEventCreateInput> & {
      scope?: 'this' | 'this_and_future' | 'all';
      instanceDate?: string;
      /** The signed warned-reset challenge from a prior 409's
       *  `ApiError.confirmation` — see SeriesRescheduleConfirmation. */
      confirmDiscardExceptions?: string;
    },
  ) {
    return this.request<CalendarEventDetail>('PATCH', `/arches/calendar/events/${id}`, input);
  }

  async deleteCalendarEvent(
    id: string,
    scope?: 'this' | 'this_and_future' | 'all',
    instanceDate?: string,
  ) {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (instanceDate) params.set('instanceDate', instanceDate);
    const query = params.toString();
    return this.request<void>('DELETE', `/arches/calendar/events/${id}${query ? `?${query}` : ''}`);
  }

  async countDeletedCalendarEvents() {
    return this.request<{ count: number }>('GET', '/arches/calendar/events/deleted/count');
  }

  async listDeletedCalendarEvents() {
    return this.request<{ events: CalendarEventDetail[] }>(
      'GET',
      '/arches/calendar/events/deleted',
    );
  }

  /**
   * Restores a soft-deleted event.
   *
   * There is deliberately no single-occurrence form: the server refuses it
   * (409 OCCURRENCE_RESTORE_UNAVAILABLE) because the restore cannot be synced
   * back to the provider. See ~/notes/calendar-resurrect-occurrence.md.
   */
  async restoreCalendarEvent(eventId: string) {
    return this.request<CalendarEventDetail>('POST', `/arches/calendar/events/${eventId}/restore`);
  }

  async clearCalendarEvents(
    calendarId: string,
    options: { dryRun?: boolean } = {},
  ): Promise<{ deleted: number } | { wouldDelete: number }> {
    const query = options.dryRun ? '?dryRun=true' : '';
    return this.request<{ deleted: number } | { wouldDelete: number }>(
      'DELETE',
      `/arches/calendar/calendars/${calendarId}/events${query}`,
    );
  }

  async parseCalendarImport(file: File): Promise<CalendarParsedImportResult> {
    const content = await file.text();
    return this.request<CalendarParsedImportResult>('POST', '/arches/calendar/import/parse', {
      fileName: file.name,
      content,
    });
  }

  async confirmCalendarImport(
    request: CalendarImportConfirmRequest,
  ): Promise<CalendarImportConfirmResult> {
    return this.request<CalendarImportConfirmResult>(
      'POST',
      '/arches/calendar/import/confirm',
      request,
    );
  }

  async parseNaturalEvent(input: {
    text: string;
    timezone?: string;
    referenceDate?: string;
  }): Promise<ParsedEventFields> {
    return this.request<ParsedEventFields>('POST', '/arches/calendar/events/parse-natural', input);
  }

  async exportCalendar(calendarId: string): Promise<string> {
    const url = `${API_BASE}/arches/calendar/export/${calendarId}`;
    const traceId = traceApiRequest('GET', url);
    let response: Response;
    try {
      response = await fetch(url, { credentials: sessionCredentials() });
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
    if (!response.ok) {
      const text = await response.text();
      const error = new Error(text || `Export failed with status ${response.status}`);
      traceApiError(traceId, error);
      throw error;
    }
    const text = await response.text();
    traceApiResponse(traceId, response.status, { length: text.length });
    return text;
  }

  // ==========================================================================
  // Calendar Event Templates API
  // ==========================================================================

  async listCalendarEventTemplates() {
    return this.request<{ templates: CalendarEventTemplateDetail[] }>(
      'GET',
      '/arches/calendar/templates',
    );
  }

  async createCalendarEventTemplate(input: CalendarEventTemplateInput) {
    return this.request<CalendarEventTemplateDetail>('POST', '/arches/calendar/templates', input);
  }

  async updateCalendarEventTemplate(id: string, input: Partial<CalendarEventTemplateInput>) {
    return this.request<CalendarEventTemplateDetail>(
      'PATCH',
      `/arches/calendar/templates/${id}`,
      input,
    );
  }

  async deleteCalendarEventTemplate(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/templates/${id}`);
  }

  // ==========================================================================
  // Calendar Sharing API — Vault consent backed
  // ==========================================================================
  //
  // Share CRUD goes through /vault/consents/*. The per-calendar dialog
  // hides global (all-calendars) grants — those have `scope.calendar_ids`
  // omitted and will be managed in a future "Manage all shares" page.
  // Per-calendar grants always set `scope.calendar_ids: [calendarId]`,
  // which the substrate validates as non-empty (empty arrays rejected).

  async searchCalendarUsers(username: string) {
    return this.request<{ user: CalendarUserSearchResult | null }>(
      'GET',
      `/arches/calendar/users/search?username=${encodeURIComponent(username)}`,
    );
  }

  /**
   * Batch reverse-resolve Personal Holder IDs to display usernames.
   * Returns the same shape as the server: { users: [{ holderId,
   * username | null }] }. Used by the share dialog to enrich rows
   * that only carry granteeHolderId after a fresh list.
   */
  async resolveCalendarShareHolders(holderIds: string[]) {
    if (holderIds.length === 0)
      return { users: [] as Array<{ holderId: string; username: string | null }> };
    return this.request<{ users: Array<{ holderId: string; username: string | null }> }>(
      'POST',
      '/arches/calendar/users/resolve-holders',
      { holderIds },
    );
  }

  async listCalendarShares(calendarId: string): Promise<{ shares: CalendarShareDetail[] }> {
    const { consents } = await this.request<{ consents: VaultConsentWire[] }>(
      'GET',
      `/vault/consents/granted?dataClass=${encodeURIComponent(CALENDAR_DATA_CLASS)}`,
    );
    // Per-calendar dialog shows grants that apply to this calendar:
    //   - explicit per-calendar grants (calendar_ids includes calendarId)
    //   - global grants (calendar_ids omitted/null — all-calendars)
    // The dialog tags globals with isGlobal so the UI can label them
    // and confirm before revoking. The update path is read-modify-write
    // on the full scope (see updateCalendarShare), so editing
    // visibility on a global grant won't accidentally narrow it.
    const shares = consents
      .filter((c) => {
        const ids = c.scopeJson?.calendar_ids;
        if (!Array.isArray(ids)) return true; // global grant — applies
        return (ids as string[]).includes(calendarId);
      })
      .map((c) => vaultConsentToCalendarShare(c, calendarId));
    return { shares };
  }

  async createCalendarShare(
    calendarId: string,
    input: CalendarShareInput,
  ): Promise<CalendarShareDetail> {
    const scope: Record<string, unknown> = {
      visibility: input.visibility,
      calendar_ids: [calendarId],
    };
    if (input.dateRange) scope.date_range = input.dateRange;
    const { consent } = await this.request<{ consent: VaultConsentWire; reactivated: boolean }>(
      'POST',
      '/vault/consents',
      {
        granteeHolderId: input.granteeHolderId,
        dataClass: CALENDAR_DATA_CLASS,
        scope,
        expiresAt: input.expiresAt,
      },
    );
    const detail = vaultConsentToCalendarShare(consent, calendarId);
    // Echo the just-resolved username so the UI doesn't have to
    // round-trip a reverse lookup for the new row.
    if (input.granteeUsername) detail.granteeUsername = input.granteeUsername;
    return detail;
  }

  async updateCalendarShare(
    calendarId: string,
    consentId: string,
    input: CalendarShareUpdate,
  ): Promise<CalendarShareDetail> {
    // Read-modify-write: PATCH replaces the entire scope object, so
    // preserve any fields the dialog doesn't touch (e.g. an externally
    // set date_range we don't expose in the v1 UI).
    const { consents } = await this.request<{ consents: VaultConsentWire[] }>(
      'GET',
      `/vault/consents/granted?dataClass=${encodeURIComponent(CALENDAR_DATA_CLASS)}`,
    );
    const existing = consents.find((c) => c.consentId === consentId);
    if (!existing) throw new Error(`Calendar share ${consentId} not found`);
    const mergedScope: Record<string, unknown> = { ...existing.scopeJson };
    if (input.visibility !== undefined) mergedScope.visibility = input.visibility;
    if (input.dateRange === null) delete mergedScope.date_range;
    else if (input.dateRange !== undefined) mergedScope.date_range = input.dateRange;

    const body: Record<string, unknown> = { scope: mergedScope };
    if (input.expiresAt !== undefined) body.expiresAt = input.expiresAt;
    const { consent } = await this.request<{ consent: VaultConsentWire }>(
      'PATCH',
      `/vault/consents/${consentId}`,
      body,
    );
    return vaultConsentToCalendarShare(consent, calendarId);
  }

  async deleteCalendarShare(_calendarId: string, consentId: string): Promise<void> {
    await this.request<void>('DELETE', `/vault/consents/${consentId}`);
  }

  async listSharedCalendars(): Promise<{ sharedCalendars: SharedCalendarSummary[] }> {
    const { consents } = await this.request<{ consents: VaultConsentWire[] }>(
      'GET',
      `/vault/consents/received?dataClass=${encodeURIComponent(CALENDAR_DATA_CLASS)}`,
    );
    const sharedCalendars = consents.map((c) => ({
      consentId: c.consentId,
      granterHolderId: c.granterHolderId,
      visibility: (c.scopeJson?.visibility as CalendarShareVisibility) ?? 'free-busy',
      calendarIds: Array.isArray(c.scopeJson?.calendar_ids)
        ? (c.scopeJson?.calendar_ids as string[])
        : null,
      dateRange: c.scopeJson?.date_range as CalendarShareDateRange | undefined,
      expiresAt: c.expiresAt,
    }));
    return { sharedCalendars };
  }

  /**
   * Read events from a granter Holder's calendar (the consent-grantee
   * path). The legacy `/arches/calendar/shared-calendars/...` route was
   * replaced by the cross-Holder URL pattern on the events route:
   * `/arches/calendar/holders/:granterHolderId/events?...`. The wrapper
   * resolves the consent-grantee trust leg and the ops layer applies
   * the consent's scope (visibility / date_range / calendar_ids).
   */
  async listSharedCalendarEvents(granterHolderId: string, rangeStart: string, rangeEnd: string) {
    const params = new URLSearchParams({ rangeStart, rangeEnd });
    return this.request<{ events: CalendarEventInstance[] }>(
      'GET',
      `/arches/calendar/holders/${granterHolderId}/events?${params.toString()}`,
    );
  }

  // findTime is now computed client-side by useFindTime — the legacy
  // POST /arches/calendar/scheduling/find-time endpoint was removed in
  // the Vault refactor. The hook composes listSharedCalendars,
  // listSharedCalendarEvents (cross-Holder), and listCalendarEvents.

  // ==========================================================================
  // Calendar Scheduling Links API
  // ==========================================================================

  async listSchedulingLinks() {
    return this.request<{ schedulingLinks: SchedulingLinkDetail[] }>(
      'GET',
      '/arches/calendar/scheduling-links',
    );
  }

  async createSchedulingLink(input: SchedulingLinkInput) {
    return this.request<SchedulingLinkDetail>('POST', '/arches/calendar/scheduling-links', input);
  }

  async getSchedulingLink(id: string) {
    return this.request<SchedulingLinkDetail>('GET', `/arches/calendar/scheduling-links/${id}`);
  }

  async updateSchedulingLink(id: string, input: Partial<SchedulingLinkInput>) {
    return this.request<SchedulingLinkDetail>(
      'PATCH',
      `/arches/calendar/scheduling-links/${id}`,
      input,
    );
  }

  async deleteSchedulingLink(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/scheduling-links/${id}`);
  }

  async listSchedulingLinkBookings(linkId: string, status?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.request<{ bookings: BookingDetail[] }>(
      'GET',
      `/arches/calendar/scheduling-links/${linkId}/bookings${query ? `?${query}` : ''}`,
    );
  }

  async cancelBookingByHost(linkId: string, bookingId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/scheduling-links/${linkId}/bookings/${bookingId}`,
    );
  }

  // Public scheduling (no auth required)

  async getPublicSchedulingInfo(slug: string, date?: string, timezone?: string) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (timezone) params.set('timezone', timezone);
    const query = params.toString();
    return this.request<PublicSchedulingInfo>(
      'GET',
      `/public/scheduling/${slug}${query ? `?${query}` : ''}`,
    );
  }

  async createPublicBooking(
    slug: string,
    input: {
      bookerName: string;
      bookerEmail: string;
      startAt: string;
      notes?: string;
    },
  ) {
    return this.request<BookingConfirmation>('POST', `/public/scheduling/${slug}/book`, input);
  }

  async cancelPublicBooking(slug: string, bookingId: string, cancellationToken: string) {
    return this.request<{ success: boolean }>('POST', `/public/scheduling/${slug}/cancel`, {
      bookingId,
      cancellationToken,
    });
  }

  // ==========================================================================
  // Calendar Schedulable Tasks API
  // ==========================================================================

  async listSchedulableTasks(status?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.request<{ tasks: SchedulableTaskDetail[] }>(
      'GET',
      `/arches/calendar/schedulable-tasks${query ? `?${query}` : ''}`,
    );
  }

  async createSchedulableTask(input: SchedulableTaskInput) {
    return this.request<SchedulableTaskDetail>('POST', '/arches/calendar/schedulable-tasks', input);
  }

  async updateSchedulableTask(id: string, input: Partial<SchedulableTaskInput>) {
    return this.request<SchedulableTaskDetail>(
      'PATCH',
      `/arches/calendar/schedulable-tasks/${id}`,
      input,
    );
  }

  async deleteSchedulableTask(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/schedulable-tasks/${id}`);
  }

  async suggestSchedule(
    taskId: string,
    opts?: { workingHoursStart?: number; workingHoursEnd?: number; timezone?: string },
  ) {
    return this.request<{ suggestions: AiScheduleSuggestion[] }>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/suggest`,
      opts || {},
    );
  }

  async acceptScheduleSuggestion(taskId: string, suggestionIndex: number) {
    return this.request<SchedulableTaskDetail>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/accept`,
      { suggestionIndex },
    );
  }

  async completeSchedulableTask(taskId: string) {
    return this.request<SchedulableTaskDetail>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/complete`,
    );
  }

  // ==========================================================================
  // Calendar Focus Time API
  // ==========================================================================

  async getFocusTimeSettings() {
    return this.request<{ settings: FocusTimeSettingsDetail | null }>(
      'GET',
      '/arches/calendar/focus-time/settings',
    );
  }

  async updateFocusTimeSettings(input: FocusTimeSettingsInput) {
    return this.request<FocusTimeSettingsDetail>(
      'PUT',
      '/arches/calendar/focus-time/settings',
      input,
    );
  }

  async generateFocusTimeBlocks() {
    return this.request<{ blocksCreated: number }>('POST', '/arches/calendar/focus-time/generate');
  }

  async regenerateFocusTimeBlocks() {
    return this.request<{ blocksCreated: number }>(
      'POST',
      '/arches/calendar/focus-time/regenerate',
    );
  }

  async clearFocusTimeBlocks() {
    return this.request<{ deleted: number }>('DELETE', '/arches/calendar/focus-time/blocks');
  }

  // ==========================================================================
  // Calendar Subscriptions API
  // ==========================================================================

  async subscribeToCalendarFeed(input: { feedUrl: string; name?: string }) {
    return this.request<CalendarSubscriptionDetail>(
      'POST',
      '/arches/calendar/subscriptions',
      input,
    );
  }

  async listCalendarSubscriptions() {
    return this.request<{ subscriptions: CalendarSubscriptionDetail[] }>(
      'GET',
      '/arches/calendar/subscriptions',
    );
  }

  async refreshCalendarSubscription(id: string) {
    return this.request<CalendarSubscriptionDetail>(
      'POST',
      `/arches/calendar/subscriptions/${id}/refresh`,
    );
  }

  /** How many events would `mode=remove` delete when unsubscribing? */
  async calendarSubscriptionDeletePreview(id: string) {
    return this.request<{ removable: number; keptLocal: number }>(
      'GET',
      `/arches/calendar/subscriptions/${id}/delete-preview`,
    );
  }

  /**
   * Unsubscribe from a feed. `mode=remove` also deletes the events the feed
   * imported; anything you wrote into that calendar yourself always stays.
   */
  async unsubscribeCalendar(id: string, mode: 'keep' | 'remove' = 'keep') {
    return this.request<{ deleted: boolean; removed: number }>(
      'DELETE',
      `/arches/calendar/subscriptions/${id}?mode=${mode}`,
    );
  }

  // ==========================================================================
  // Calendar Published Feeds API (outbound webcal — the inverse of subscriptions)
  // ==========================================================================

  async listCalendarFeeds(calendarId: string) {
    return this.request<{ feeds: CalendarFeedDetail[] }>(
      'GET',
      `/arches/calendar/calendars/${calendarId}/feeds`,
    );
  }

  /** Mint a feed. The raw URL comes back ONCE in the response and is never recoverable. */
  async createCalendarFeed(calendarId: string, input: CalendarFeedInput) {
    return this.request<CalendarFeedMintResult>(
      'POST',
      `/arches/calendar/calendars/${calendarId}/feeds`,
      input,
    );
  }

  /** Mint a NEW token for a feed; the old URL dies immediately. Returns the new URL once. */
  async rotateCalendarFeed(feedId: string) {
    return this.request<CalendarFeedMintResult>('POST', `/arches/calendar/feeds/${feedId}/rotate`);
  }

  async updateCalendarFeed(feedId: string, input: CalendarFeedUpdate) {
    return this.request<
      Pick<
        CalendarFeedDetail,
        | 'feedId'
        | 'label'
        | 'visibility'
        | 'windowPastDays'
        | 'windowFutureDays'
        | 'refreshHintMinutes'
      >
    >('PATCH', `/arches/calendar/feeds/${feedId}`, input);
  }

  async deleteCalendarFeed(feedId: string) {
    return this.request<void>('DELETE', `/arches/calendar/feeds/${feedId}`);
  }

  // Calendar Analytics
  async getCalendarAnalytics(start: string, end: string) {
    const params = new URLSearchParams({ start, end });
    return this.request<CalendarAnalyticsResult>(
      'GET',
      `/arches/calendar/analytics?${params.toString()}`,
    );
  }

  // ==========================================================================
  // Lit Legion API
  // ==========================================================================

  async listLitLegionTasks(options?: {
    category?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitLegionTaskStatusListResult>(
      'GET',
      `/arches/lit-legion/tasks${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitLegionTask(taskId: string) {
    return this.request<{ task: LitLegionTaskWithStatus }>(
      'GET',
      `/arches/lit-legion/tasks/${taskId}`,
    );
  }

  async updateLitLegionTaskStatus(
    taskId: string,
    status: string,
    body?: { submission?: LitLegionTaskSubmission; userInput?: string },
  ) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'POST',
      `/arches/lit-legion/tasks/${taskId}/status`,
      {
        status,
        ...(body?.submission !== undefined ? { submission: body.submission } : {}),
        ...(body?.userInput !== undefined ? { userInput: body.userInput } : {}),
      },
    );
  }

  async getLitLegionStats() {
    return this.request<{ stats: LitLegionPlayerStats }>('GET', '/arches/lit-legion/stats');
  }

  async listLitLegionNpcs() {
    return this.request<{ npcs: LitLegionNpc[] }>('GET', '/arches/lit-legion/npcs');
  }

  async assignLitLegionNpc(npcId: string) {
    return this.request<{ taskStatuses: LitLegionTaskStatusResponse[]; count: number }>(
      'POST',
      '/arches/lit-legion/assign-npc',
      { npcId },
    );
  }

  // Lit Legion Admin Methods

  async adminListLitLegionTasks(options?: {
    category?: string;
    taskType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.taskType) params.set('taskType', options.taskType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitLegionAdminTaskListResult>(
      'GET',
      `/arches/lit-legion/admin/tasks${qs ? `?${qs}` : ''}`,
    );
  }

  async createLitLegionTask(input: CreateLitLegionTaskPayload) {
    return this.request<{ task: LitLegionAdminTask }>(
      'POST',
      '/arches/lit-legion/admin/tasks',
      input,
    );
  }

  async adminGetLitLegionTask(taskId: string) {
    return this.request<{
      task: LitLegionAdminTask;
      coinRewards: LitLegionCoinReward[];
      badgeRewards: LitLegionBadgeReward[];
    }>('GET', `/arches/lit-legion/admin/tasks/${taskId}`);
  }

  async adminListAllLitLegionTasks() {
    return this.request<{ tasks: LitLegionAdminTask[]; total: number }>(
      'GET',
      '/arches/lit-legion/admin/tasks/all',
    );
  }

  async adminUpdateLitLegionTask(taskId: string, input: UpdateLitLegionTaskPayload) {
    return this.request<{ task: LitLegionAdminTask }>(
      'PATCH',
      `/arches/lit-legion/admin/tasks/${taskId}`,
      input,
    );
  }

  async adminDeleteLitLegionTask(taskId: string) {
    return this.request<void>('DELETE', `/arches/lit-legion/admin/tasks/${taskId}`);
  }

  async adminListLitLegionNpcs() {
    return this.request<{ npcs: LitLegionNpc[] }>('GET', '/arches/lit-legion/admin/npcs');
  }

  async adminListLitLegionLevels() {
    return this.request<{ levels: LitLegionUserLevel[] }>('GET', '/arches/lit-legion/admin/levels');
  }

  async adminListLitLegionBadges() {
    return this.request<{ badges: LitLegionBadge[] }>('GET', '/arches/lit-legion/admin/badges');
  }

  async adminListLitLegionUserTaskStatuses(
    username: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      username: string;
      userId: string;
      statuses: LitLegionAdminTaskStatusWithTask[];
      total: number;
    }>(
      'GET',
      `/arches/lit-legion/admin/users/by-username/${encodeURIComponent(username)}/statuses${
        qs ? `?${qs}` : ''
      }`,
    );
  }

  async adminUpdateLitLegionTaskStatus(taskStatusId: string, status: string, pinned?: boolean) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'PATCH',
      `/arches/lit-legion/admin/statuses/${taskStatusId}`,
      { status, ...(pinned !== undefined ? { pinned } : {}) },
    );
  }

  /**
   * Hard-delete a task-status row. To refresh availability after a delete,
   * use `adminRecomputeLitLegionAvailability`.
   */
  async adminDeleteLitLegionTaskStatus(taskStatusId: string) {
    return this.request<void>('DELETE', `/arches/lit-legion/admin/statuses/${taskStatusId}`);
  }

  /**
   * Delete EVERY lit-legion task status for a user in one call. Hard delete
   * with no re-evaluation — pair with `adminRecomputeLitLegionAvailability`
   * to hand the player a fresh set of root tasks.
   */
  async adminDeleteAllLitLegionUserTaskStatuses(userId: string) {
    return this.request<{ deletedCount: number }>(
      'DELETE',
      `/arches/lit-legion/admin/users/${encodeURIComponent(userId)}/statuses`,
    );
  }

  async adminAssignLitLegionTaskToUser(userId: string, taskId: string, status?: string) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'POST',
      `/arches/lit-legion/admin/users/${encodeURIComponent(userId)}/assign-task`,
      { taskId, ...(status ? { status } : {}) },
    );
  }

  /**
   * Sweep every lit-legion task and insert an 'available' row for any task
   * that the user is missing AND is currently unblocked (root task or parent
   * is awarded). Existing rows are left alone.
   */
  async adminRecomputeLitLegionAvailability(userId: string) {
    return this.request<{ taskStatuses: LitLegionTaskStatusResponse[]; count: number }>(
      'POST',
      `/arches/lit-legion/admin/users/${encodeURIComponent(userId)}/recompute-availability`,
    );
  }

  // ==========================================================================
  // Legacy /v1/files, /v1/folders, /v1/storage APIs were removed in Phase
  // 12d-3. Vault Documents APIs (listVaultDocuments, initVaultDocumentUpload,
  // finalizeVaultDocumentUpload, getVaultDocumentDownloadUrl, etc.) are the
  // forward path. See the Vault Documents block further down.
  //
  // The two compat shims below smooth the rename for callers that haven't
  // been ported to the Vault method names yet. Both delegate to the Vault
  // methods so there's no separate code path — just a different name.
  // ==========================================================================

  /**
   * @deprecated Use `getVaultDocumentDownloadUrl(documentId)` directly. This
   * shim keeps the old return shape (`{ downloadUrl, expiresAt }`) for
   * call sites that haven't migrated yet.
   */
  async getDownloadUrl(documentId: string): Promise<{ downloadUrl: string; expiresAt: string }> {
    const { url, expiresAt } = await this.getVaultDocumentDownloadUrl(documentId);
    return { downloadUrl: url, expiresAt };
  }

  /**
   * @deprecated Compat shim — maps a Vault document onto the legacy
   * `{ file: UserFileResponse }` shape pre-migration callers expect.
   * New code should call `getVaultDocument` directly.
   */
  async getFile(documentId: string): Promise<{
    file: {
      fileId: string;
      fileName: string;
      mimeType: string | null;
      sizeBytes: number;
      s3Key: string;
    };
  }> {
    const { document } = await this.getVaultDocument(documentId);
    return {
      file: {
        fileId: document.documentId,
        fileName: document.name,
        mimeType: document.mimeType,
        sizeBytes: document.sizeBytes,
        s3Key: document.s3Key,
      },
    };
  }

  /**
   * High-level upload helper. Takes a `File` and the target Personal Holder
   * id (resolve via `listMyVaultMemberships`), runs init → PUT → finalize,
   * and returns the finalized `VaultDocumentDto`.
   */
  async uploadFileToVault(
    file: File,
    holderId: string,
    options?: { tags?: string[]; folderId?: string | null; purpose?: VaultInputPurposeDto },
  ): Promise<VaultDocumentDto> {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    const sha = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    const init = await this.initVaultDocumentUpload({
      holderId,
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      checksumSha256: sha,
      ...(options?.tags ? { tags: options.tags } : {}),
      ...(options?.folderId !== undefined ? { folderId: options.folderId } : {}),
      ...(options?.purpose ? { purpose: options.purpose } : {}),
    });
    const put = await fetch(init.uploadUrl, {
      method: 'PUT',
      headers: init.requiredHeaders,
      body: file,
    });
    if (!put.ok) {
      throw new Error(`Upload failed (HTTP ${put.status})`);
    }
    const finalized = await this.finalizeVaultDocumentUpload(init.document.documentId, {
      checksumSha256: sha,
    });
    return finalized.document;
  }

  // ==========================================================================
  // Chat attachments (channel-scoped store)
  // ==========================================================================

  async chatAttachmentUploadInit(
    channelId: string,
    input: { fileName: string; mimeType: string; sizeBytes: number },
  ) {
    return this.request<{
      attachmentId: string;
      uploadUrl: string;
      requiredHeaders: Record<string, string>;
      expiresAt: string;
    }>('POST', `/arches/chat/channels/${channelId}/attachments/upload-init`, input);
  }

  async chatAttachmentFinalize(channelId: string, attachmentId: string) {
    return this.request<{
      attachmentId: string;
      fileName: string;
      mimeType: string;
      sizeBytes: number;
    }>('POST', `/arches/chat/channels/${channelId}/attachments/${attachmentId}/finalize`, {});
  }

  async chatAttachmentFromVault(channelId: string, vaultDocumentId: string) {
    return this.request<{
      attachmentId: string;
      fileName: string;
      mimeType: string;
      sizeBytes: number;
    }>('POST', `/arches/chat/channels/${channelId}/attachments/from-vault`, { vaultDocumentId });
  }

  async getChatAttachmentDownloadUrl(
    channelId: string,
    attachmentId: string,
    disposition?: 'inline' | 'attachment',
  ) {
    const qs = disposition ? `?disposition=${disposition}` : '';
    return this.request<{
      url: string;
      expiresAt: string;
      attachment: { attachmentId: string; fileName: string; mimeType: string; sizeBytes: number };
    }>('GET', `/arches/chat/channels/${channelId}/attachments/${attachmentId}/download${qs}`);
  }

  /** Save a copy of a channel attachment into the folder of their Documents
   *  the user chose in the Save dialog. Refusals as the Save dialog expects
   *  (409 `NAME_EXISTS` with `conflicts`, …). */
  async saveChatAttachmentToVault(
    channelId: string,
    attachmentId: string,
    save: VaultSaveTargetDto,
  ) {
    return this.request<{ documentId: string; name: string }>(
      'POST',
      `/arches/chat/channels/${channelId}/attachments/${attachmentId}/to-vault`,
      { save },
    );
  }

  /** Upload a file to the channel-scoped chat store: init -> direct S3 PUT -> finalize. */
  async uploadChatAttachment(
    channelId: string,
    file: File,
  ): Promise<{ attachmentId: string; fileName: string; mimeType: string; sizeBytes: number }> {
    // Guard the size bounds here so the user sees a plain message instead of the
    // server's raw Zod rejection (e.g. "sizeBytes: Too small: expected number to
    // be >=1") leaking into the upload toast. Cap is the shared
    // CHAT_ATTACHMENT_MAX_BYTES mirror (canonical value in @fias/db-types).
    if (file.size < 1) {
      throw new Error('File is empty (0 bytes) — pick a file with content.');
    }
    if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
      throw new Error('File is too large — the limit is 50 MB.');
    }
    // Type resolution comes AFTER the size guards so an oversized file still
    // reports its real problem rather than an unrelated type complaint.
    //
    // Send the canonical mime rather than `file.type`: browsers report the same
    // bytes inconsistently (a .zip arrives as application/zip,
    // application/x-zip-compressed, or nothing at all). The server re-resolves
    // and is authoritative — this just means the two agree.
    const resolved = resolveFileType(file.name, file.type, CHAT_FILE_TYPE_POLICY);
    if (!resolved.ok) {
      throw new Error(
        resolved.reason === 'blocked-type'
          ? "That kind of file can't be attached in chat."
          : "That file type isn't supported as a chat attachment.",
      );
    }
    const init = await this.chatAttachmentUploadInit(channelId, {
      fileName: file.name,
      mimeType: resolved.type.mimeType,
      sizeBytes: file.size,
    });
    if (!init.uploadUrl) {
      throw new Error('Upload init did not return an upload URL');
    }
    // Direct PUT to the presigned S3 URL (intentionally NOT API_BASE).
    const put = await fetch(init.uploadUrl, {
      method: 'PUT',
      headers: init.requiredHeaders,
      body: file,
    });
    if (!put.ok) {
      throw new Error(`Upload failed (HTTP ${put.status})`);
    }
    return this.chatAttachmentFinalize(channelId, init.attachmentId);
  }

  // ==========================================================================
  // Image Generation API
  // ==========================================================================

  /** `actingHolderId`: the workspace the user is acting in — the image is
   *  saved to its Arche Saves when they may write there, else to Personal
   *  (the `image_complete` message then carries `savedToPersonal`). */
  async generateImage(
    archeId: string,
    input: ImageGenerationInput,
    options?: { actingHolderId?: string | null },
  ) {
    return this.request<ImageGenerationResponse>('POST', `/arches/${archeId}/generate-image`, {
      input,
      ...(options?.actingHolderId ? { actingHolderId: options.actingHolderId } : {}),
    });
  }

  async cancelImageGeneration(archeId: string, requestId: string) {
    return this.request<{ status: string; requestId: string }>(
      'POST',
      `/arches/${archeId}/cancel-generation/${requestId}`,
    );
  }

  async listImageModels(archeId: string) {
    return this.request<ImageModelsResponse>('GET', `/arches/${archeId}/image-models`);
  }

  async getImageUrl(archeId: string, imageRef: string): Promise<string> {
    const result = await this.request<{ url: string }>(
      'GET',
      `/arches/${archeId}/image-url?imageRef=${encodeURIComponent(imageRef)}`,
    );
    return result.url;
  }

  async checkImageStatus(
    archeId: string,
    requestId: string,
    /** The workspace the generation was started in. */
    actingHolderId?: string | null,
  ): Promise<{
    status: 'pending' | 'complete' | 'error';
    imageRef?: string;
    fileId?: string;
    url?: string;
    error?: string;
  }> {
    return this.request<{
      status: 'pending' | 'complete' | 'error';
      imageRef?: string;
      fileId?: string;
      url?: string;
      error?: string;
    }>(
      'GET',
      `/arches/${archeId}/image-status/${requestId}` +
        (actingHolderId ? `?holder=${encodeURIComponent(actingHolderId)}` : ''),
    );
  }

  // ==========================================================================
  // Icon Generator API
  // ==========================================================================

  async generateIcon(input: {
    name: string;
    description: string;
    useDefaultStyle?: boolean;
    style?: string;
  }) {
    return this.request<{ requestId: string }>('POST', '/arches/icon-generator/generate', input);
  }

  async getIconGeneratorStatus(requestId: string) {
    return this.request<{
      status: 'pending' | 'complete' | 'error';
      previewUrl?: string;
      /** Vault document id of the generated icon. Present iff
       * status === 'complete'. Pass to `saveIcon` — the /save flow
       * keys off the durable vault id, not the in-flight requestId. */
      documentId?: string;
      requestId?: string;
      error?: string;
    }>('GET', `/arches/icon-generator/status/${requestId}`);
  }

  async saveIcon(input: { documentId: string; fileName?: string; attachToArcheId?: string }) {
    return this.request<{
      fileId: string;
      imageRef: string;
      attachedIconKey: string | null;
    }>('POST', '/arches/icon-generator/save', input);
  }

  // ==========================================================================
  // Voice Studio API
  // ==========================================================================

  async transcribeAudio(
    fileId: string,
    opts?: { language?: string },
  ): Promise<TranscriptionResult> {
    return this.request<TranscriptionResult>('POST', '/arches/voice-studio/transcribe', {
      fileId,
      ...(opts?.language ? { language: opts.language } : {}),
    });
  }

  async synthesizeSpeech(body: {
    text: string;
    voiceId: string;
    stability?: number;
    similarityBoost?: number;
  }): Promise<SynthesisResult> {
    return this.request<SynthesisResult>('POST', '/arches/voice-studio/synthesize', body);
  }

  async listVoiceModels(): Promise<{ voiceModels: VoiceModel[] }> {
    return this.request<{ voiceModels: VoiceModel[] }>('GET', '/arches/voice-studio/voice-models');
  }

  async createVoiceModel(body: {
    name: string;
    description?: string;
    sampleFileIds: string[];
  }): Promise<{ voiceModel: VoiceModel }> {
    return this.request<{ voiceModel: VoiceModel }>(
      'POST',
      '/arches/voice-studio/voice-models',
      body,
    );
  }

  async deleteVoiceModel(modelId: string): Promise<void> {
    return this.request<void>('DELETE', `/arches/voice-studio/voice-models/${modelId}`);
  }

  async listPlatformVoices(): Promise<{ voices: PlatformVoice[] }> {
    return this.request<{ voices: PlatformVoice[] }>('GET', '/arches/voice-studio/platform-voices');
  }

  // ==========================================================================
  // Image Editor API
  // ==========================================================================

  async processImage(request: {
    sourceFileId?: string;
    sourceDataUrl?: string;
    operations: Array<Record<string, unknown>>;
    output: {
      format: 'png' | 'jpeg' | 'webp' | 'avif';
      quality?: number;
      width?: number;
      height?: number;
      maintainAspectRatio?: boolean;
    };
    /** Save As into the user's Documents, where they chose in the Save dialog. */
    saveAs?: { target: VaultSaveTargetDto };
  }) {
    return this.request<{
      fileId?: string;
      /** The name a Fias save was stored under. */
      fileName?: string;
      downloadUrl?: string;
      width: number;
      height: number;
      sizeBytes: number;
      format: string;
    }>('POST', '/arches/image-editor/process', request);
  }

  /** Save an already-encoded structured artifact (PSD / SVG / PDF) to
   * Fias. These formats are built client-side and can't go through
   * `processImage` (Sharp re-encodes raster only), so the bytes are
   * uploaded verbatim. `sourceFileId` saves in place as a new version;
   * otherwise `target` — where the user chose in the Save dialog. */
  async saveImageEditorStructured(request: {
    format: 'psd' | 'svg' | 'pdf';
    bytesBase64: string;
    fileName: string;
    /** In place, as a new version of this document… */
    sourceFileId?: string;
    /** …or Save As, where the user chose in the Save dialog. */
    target?: VaultSaveTargetDto;
  }) {
    return this.request<{
      fileId: string;
      fileName: string;
      format: string;
      sizeBytes: number;
    }>('POST', '/arches/image-editor/save-structured', request);
  }

  async smartCreateImage(request: {
    prompt: string;
    /** Aspect ratio for the generated image. Aspect-ratio strings
     *  rather than pixel dimensions because every wired image provider
     *  accepts them, and this 5-element set is the intersection of
     *  natively-supported sizes across BFL / OpenAI / Google / Recraft
     *  / Stability. */
    size?: '1:1' | '4:3' | '3:4' | '16:9' | '9:16';
    /** Source PNG data URL for mask-aware inpainting. Must be paired
     *  with `maskDataUrl`. When omitted, falls back to text-to-image. */
    imageDataUrl?: string;
    /** Mask PNG data URL — same dimensions as `imageDataUrl`, with
     *  alpha=0 in the edit region and alpha=255 elsewhere. */
    maskDataUrl?: string;
    /** Model picker key for Insert mode (text-to-image). Ignored when
     *  imageDataUrl + maskDataUrl are present — inpaint is always
     *  routed through OpenAI gpt-image-1 since FLUX / Recraft /
     *  Stability text-to-image models don't accept masks. */
    model?:
      | 'flux-2-pro'
      | 'flux-ultra'
      | 'gpt-image-1'
      | 'nano-banana'
      | 'nano-banana-pro'
      | 'recraft-v3'
      | 'stable-ultra';
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-create',
      request,
    );
  }

  /**
   * Image Editor — Smart Erase. The escalation path from the free
   * MI-GAN-based Smart Delete: when the on-device model leaves
   * artifacts, this calls Stability AI's dedicated `/v2beta/stable-image/edit/erase`
   * endpoint. No prompt — the model fills the masked region from the
   * surrounding pixels. Costs roughly 4 credits per call (Stability's
   * 0.03 USD raw cost × 1.20 platform markup).
   */
  /**
   * Image Editor — Smart Inpaint. Mask-aware AI replacement: fills the
   * masked region with content matching the prompt. Three engines —
   * picked by the caller based on cost / quality tradeoffs:
   *   - `'stability'` (default, ~4 credits) — Stability's dedicated
   *     `/edit/inpaint`. Supports `negativePrompt` and `growMask`.
   *   - `'recraft'` (~5 credits) — Recraft's replacement-oriented inpaint.
   *     Strong with explicit prompts; supports prompt only.
   *   - `'openai'` (~5 credits) — gpt-image-1's `/edits` endpoint.
   *     Good text rendering; mask alpha inverted server-side.
   */
  async smartInpaintImage(request: {
    imageDataUrl: string;
    maskDataUrl: string;
    prompt: string;
    engine?: 'stability' | 'recraft' | 'openai';
    /** Optional negative prompt — what to avoid. Forwarded to engines
     *  that support it (Stability). Ignored by engines that don't. */
    negativePrompt?: string;
    /** Pixels to grow the mask outward server-side before sending to
     *  the engine. Range 0–64 px; defaults to 0. */
    growMask?: number;
    /** Optional engine-specific style preset:
     *    - Stability: one of 11 presets (`cinematic`, `anime`, etc.)
     *    - Recraft: one of 4 modes (`realistic_image`, `digital_illustration`,
     *      `vector_illustration`, `icon`)
     *    - OpenAI: ignored. */
    stylePreset?: string;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-inpaint',
      request,
    );
  }

  /**
   * Image Editor — Search & Replace. Stability auto-segments the
   * search target (no marquee required) and replaces it with content
   * matching `prompt`. Returns the full-source-sized result with the
   * replacement composited.
   */
  /**
   * Image Editor — Search & Recolor. Stability auto-segments via
   * `selectPrompt` and recolors the matching region with the color
   * or effect described in `prompt`. Shape stays fixed; only color
   * changes. Stability-only.
   */
  async smartImageToImageImage(
    request: {
      imageDataUrl: string;
      prompt: string;
      /** Engine slug: 'core' | 'ultra' | 'sd3-large'. */
      engine: 'core' | 'ultra' | 'sd3-large';
      /** Stability convention — 0 = identical to source, 1 = ignore source. */
      strength: number;
      negativePrompt?: string;
      /** Optional Stability style preset (only respected by Ultra). */
      stylePreset?: string;
      /** Optional seed for reproducibility (0..2^32-1). */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-image-to-image',
      request,
      options,
    );
  }

  async smartOutpaintImage(
    request: {
      imageDataUrl: string;
      /** Pixels to extend on each edge. ≥1 must be > 0. Each 0..2000. */
      left?: number;
      right?: number;
      up?: number;
      down?: number;
      /** Optional prompt to guide what fills the new area. */
      prompt?: string;
      /** 0..1; how much to invent (1.0) vs. extend literally (0.0). */
      creativity?: number;
      /** Optional negative prompt — features to avoid. */
      negativePrompt?: string;
      /** Optional Stability style preset (one of 11 named presets). */
      stylePreset?: string;
      /** Optional seed for reproducibility (0..2^32-1). */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-outpaint',
      request,
      options,
    );
  }

  async smartSearchRecolorImage(request: {
    imageDataUrl: string;
    /** Text describing the object to recolor. */
    selectPrompt: string;
    /** Text describing the new color/effect. */
    prompt: string;
    /** Optional negative prompt — features to avoid. */
    negativePrompt?: string;
    /** Pixels to grow the auto-segmented mask outward (0–64). */
    growMask?: number;
    /** Optional Stability style preset. */
    stylePreset?: string;
    /** Optional seed for reproducibility (0..2^32-1). */
    seed?: number;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-search-recolor',
      request,
    );
  }

  async smartSearchReplaceImage(request: {
    imageDataUrl: string;
    /** Text describing the existing object to replace. */
    searchPrompt: string;
    /** Text describing what should appear instead. */
    prompt: string;
    /** Optional negative prompt — features to avoid. */
    negativePrompt?: string;
    /** Pixels to grow the auto-segmented mask outward (0–64). */
    growMask?: number;
    /** Optional Stability style preset (one of 11 named presets:
     *  cinematic, anime, photographic, etc.). Empty/omitted = default. */
    stylePreset?: string;
    /** Optional seed for reproducibility. Same prompts + same seed →
     *  same output. Range 0..2^32-1. */
    seed?: number;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-search-replace',
      request,
    );
  }

  async smartEraseImage(request: {
    /** Source PNG data URL — the layer being edited. */
    imageDataUrl: string;
    /** Mask PNG data URL — alpha=255 in the region to erase, alpha=0
     *  elsewhere. Same dimensions as `imageDataUrl`. */
    maskDataUrl: string;
    /** Pixels to grow the mask outward server-side before sending.
     *  Helps when the marquee just barely catches the object and
     *  Stability otherwise leaves an edge halo. Range 0–64 px;
     *  defaults to 0 (no growth). */
    growMask?: number;
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-erase',
      request,
    );
  }

  /** Server-side background removal. Both engines bill through the
   *  entity gateway; `'recraft'` is the cheaper default. */
  async smartRemoveBackgroundImage(request: {
    /** Source PNG data URL — the layer being edited. */
    imageDataUrl: string;
    engine: 'stability' | 'recraft';
  }) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-remove-background',
      request,
    );
  }

  /** Control / Guided image generation. Four modes (from-sketch,
   *  match-structure, match-style, style-transfer) map to four
   *  Stability v2beta `/control/*` endpoints. The (mode, fields)
   *  tuple is validated server-side — see `smart-control.ts` for
   *  the dispatch table and required-field rules per mode. */
  async smartControlImage(
    request: {
      imageDataUrl: string;
      mode: 'from-sketch' | 'match-structure' | 'match-style' | 'style-transfer';
      /** Required for from-sketch / match-structure / match-style. */
      prompt?: string;
      negativePrompt?: string;
      /** 0..1, used by from-sketch + match-structure. */
      controlStrength?: number;
      /** 0..1, used by match-style. */
      fidelity?: number;
      /** Required for style-transfer. */
      styleImageDataUrl?: string;
      /** 0..1, style-transfer only. */
      styleStrength?: number;
      /** 0..1, style-transfer only. */
      compositionFidelity?: number;
      /** 0..1, style-transfer only. */
      changeStrength?: number;
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-control',
      request,
      options,
    );
  }

  /** Convert a raster image to SVG via Recraft's vectorize endpoint.
   *  Returns the SVG text, NOT a raster — caller should treat as a
   *  file to save, not pixels to draw. The editor's vectorize flow
   *  writes the result to the user's filesystem via showSaveFilePicker
   *  rather than rasterizing back into a layer (which would defeat
   *  the entire point of vectorizing). */
  async smartVectorizeImage(request: { imageDataUrl: string }, options?: { signal?: AbortSignal }) {
    return this.request<{ svg: string }>(
      'POST',
      '/arches/image-editor/smart-vectorize',
      request,
      options,
    );
  }

  /** Upscale an image. Three modes (fast/high-quality/reimagine) map to
   *  five entities behind the scenes. Engine + extra fields depend on
   *  the mode — see the route at smart-upscale.ts for the dispatch
   *  table. Server validates the (mode, engine) tuple. */
  async smartUpscaleImage(
    request: {
      /** Source PNG data URL — the active layer being upscaled. */
      imageDataUrl: string;
      mode: 'fast' | 'high-quality' | 'reimagine';
      engine: 'stability' | 'recraft';
      /** Required for stability HQ + reimagine; ignored otherwise. */
      prompt?: string;
      /** Stability HQ + reimagine only — 0..0.35. */
      creativity?: number;
      /** Recraft fast + reimagine only — 2 or 4. Stability fast is
       *  always 4×. Default behavior when omitted is provider-side
       *  default (4 for both Recraft endpoints). */
      factor?: 2 | 4;
      /** Stability HQ + reimagine only. */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-upscale',
      request,
      options,
    );
  }

  /** Replace the background of an image with a prompt-described scene
   *  (Recraft) or a prompt + optional reference image with subject
   *  relighting (Stability). Engines have very different feature
   *  surfaces — fields not honored by the chosen engine are dropped
   *  silently server-side. */
  async smartReplaceBackgroundImage(
    request: {
      /** Source PNG data URL — the full image, NOT a pre-cut subject;
       *  both engines segment internally. */
      imageDataUrl: string;
      engine: 'recraft' | 'stability';
      /** Required for Recraft. Required for Stability if no reference. */
      backgroundPrompt?: string;
      /** Stability only — reference image whose look the new BG should
       *  match. Either this or `backgroundPrompt` is required for
       *  Stability; if both are provided, both are sent and Stability
       *  blends. */
      backgroundReferenceDataUrl?: string;
      /** Stability only — bias the relighting from a specific direction.
       *  Omit (or use `'none'` client-side and don't forward) for no
       *  bias. */
      lightDirection?: 'above' | 'below' | 'left' | 'right';
      lightStrength?: number;
      lightReferenceDataUrl?: string;
      /** Stability only — 0..1, how much of the original subject's
       *  appearance to preserve through the relight. */
      preserveSubject?: number;
      /** Stability only — 0..1, blend factor with the original BG. */
      preserveOriginalBg?: number;
      /** Recraft only — one of 4 style presets. */
      style?: 'realistic_image' | 'digital_illustration' | 'vector_illustration' | 'icon';
      /** 0..2^32-1, both engines. */
      seed?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return this.request<{ imageDataUrl: string }>(
      'POST',
      '/arches/image-editor/smart-replace-background',
      request,
      options,
    );
  }

  // ==========================================================================
  // Builder Sessions API
  // ==========================================================================

  async createBuilderSession(params?: { templateId?: string; existingArchId?: string }) {
    return this.request<{
      sessionId: string;
      status: 'active';
      template?: BuilderTemplate;
      existingArche?: unknown;
    }>('POST', '/arches/builder/sessions', params || {});
  }

  async listBuilderSessions() {
    return this.request<{ sessions: BuilderSessionSummary[] }>('GET', '/arches/builder/sessions');
  }

  async getBuilderSession(sessionId: string) {
    return this.request<{ session: BuilderSession; currentPreview: unknown }>(
      'GET',
      `/arches/builder/sessions/${sessionId}`,
    );
  }

  async updateBuilderSession(
    sessionId: string,
    updates: {
      currentName?: string;
      currentDescription?: string;
      currentSettings?: unknown;
      status?: 'active' | 'paused' | 'completed' | 'abandoned';
    },
  ) {
    return this.request<{ session: BuilderSession }>(
      'PATCH',
      `/arches/builder/sessions/${sessionId}`,
      updates,
    );
  }

  async deleteBuilderSession(sessionId: string) {
    return this.request<void>('DELETE', `/arches/builder/sessions/${sessionId}`);
  }

  async resetBuilderContext(sessionId: string) {
    return this.request<{ session: BuilderSession; currentPreview: unknown }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/reset-context`,
    );
  }

  async checkArcheName(name: string, excludeArchId?: string) {
    const params = new URLSearchParams({ name });
    if (excludeArchId) {
      params.append('excludeArchId', excludeArchId);
    }
    return this.request<{ available: boolean }>(
      'GET',
      `/arches/builder/check-name?${params.toString()}`,
    );
  }

  async sendBuilderMessage(sessionId: string, message: string) {
    return this.request<{
      response: ClaudeMessageResponse;
      updatedPreview: unknown;
    }>('POST', `/arches/builder/sessions/${sessionId}/message`, { message });
  }

  async startBuilderMessageStream(
    sessionId: string,
    message: string,
    verbose?: boolean,
    useOpus?: boolean,
    guidedPhase?: string,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
      status: 'pending';
    }>('POST', `/arches/builder/sessions/${sessionId}/message/stream`, {
      message,
      verbose,
      useOpus,
      ...(guidedPhase ? { guidedPhase } : {}),
    });
  }

  async startGuidedFlow(sessionId: string, idea: string) {
    return this.request<{
      guidedFlow: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: unknown[];
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
    }>('POST', `/arches/builder/sessions/${sessionId}/guided/start`, { idea });
  }

  async saveBuilderSession(sessionId: string, params: { name: string; description: string }) {
    return this.request<{ archId: string; status: 'draft'; version: number }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/save`,
      params,
    );
  }

  async uploadPreviewScreenshot(sessionId: string, dataUrl: string): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/preview-screenshot`,
      { screenshot: dataUrl },
    );
  }

  async submitProblemReport(
    sessionId: string,
    report: {
      description: string;
      problemScope: number;
      conversationHistory?: unknown;
      currentUiDefinition?: unknown;
      currentSettings?: unknown;
      logEntries?: unknown;
      telemetryState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/report-problem`,
      report,
    );
  }

  // ==========================================================================
  // Plugin Builder API
  // ==========================================================================

  async createPluginBuilderSession(params?: { name?: string; mode?: 'chat' | 'guided' }) {
    return this.request<{
      sessionId: string;
      status: 'active';
      currentFiles: Record<string, unknown>;
    }>('POST', '/arches/plugin-builder/sessions', params || {});
  }

  async listPluginBuilderSessions() {
    return this.request<{ sessions: unknown[] }>('GET', '/arches/plugin-builder/sessions');
  }

  async getPluginBuilderSession(sessionId: string) {
    return this.request<{
      session: unknown;
      /** Present when the server healed a wedged build plan on this load —
       *  `repointed` means the pointer moved BACKWARD to a stranded step
       *  (the UI explains the step-number drop instead of staying silent). */
      buildPlanHealed?: {
        reasons: string[];
        repointed: boolean;
        toStepId?: string;
        toStepTitle?: string;
      };
    }>('GET', `/arches/plugin-builder/sessions/${sessionId}`);
  }

  async updatePluginBuilderSession(
    sessionId: string,
    updates: {
      currentName?: string;
      currentDescription?: string;
      currentExpandedDescription?: string;
      currentSettings?: unknown;
      status?: 'active' | 'paused' | 'completed' | 'abandoned';
    },
  ) {
    // PATCH returns the session response flat (no `session` wrapper, unlike GET),
    // including the post-write `version`. Callers thread that version back into
    // the session hook's optimistic-locking baseline via `noteServerVersion`.
    return this.request<{ version?: number }>(
      'PATCH',
      `/arches/plugin-builder/sessions/${sessionId}`,
      updates,
    );
  }

  /**
   * Upsert a single AI config through the scoped server route. AI configs are
   * NOT writable via the settings PATCH anymore — a whole-array replace from a
   * stale client snapshot silently reverted AI-written configs (save_ai_config
   * writes them server-side mid-turn; the two-writer clobber).
   */
  async putPluginBuilderAiConfig(
    sessionId: string,
    config: {
      configId: string;
      label: string;
      modelDefinitionId?: string;
      capabilityId?: string;
      systemPrompt: string;
      promptTemplate: string;
      parameters?: { temperature?: number; maxTokens?: number };
    },
  ) {
    const { configId, ...body } = config;
    return this.request<{ version?: number; aiConfigs?: unknown[] }>(
      'PUT',
      `/arches/plugin-builder/sessions/${sessionId}/ai-configs/${encodeURIComponent(configId)}`,
      body,
    );
  }

  /** Delete a single AI config through the scoped server route (see put above). */
  async deletePluginBuilderAiConfig(sessionId: string, configId: string) {
    return this.request<{ version?: number; aiConfigs?: unknown[] }>(
      'DELETE',
      `/arches/plugin-builder/sessions/${sessionId}/ai-configs/${encodeURIComponent(configId)}`,
    );
  }

  async updatePluginBuilderSessionFiles(
    sessionId: string,
    currentFiles: Record<string, unknown>,
    expectedVersion?: number,
  ) {
    // PATCH returns the session response flat (no `session` wrapper, unlike GET).
    return this.request<{ version: number }>(
      'PATCH',
      `/arches/plugin-builder/sessions/${sessionId}`,
      {
        // The server keeps its own node_modules/ copy (see withoutPackageFiles).
        currentSettings: { currentFiles: withoutPackageFiles(currentFiles) },
        // Optimistic-locking guard: server returns 409 if the row advanced past
        // this version on another device, so a stale tab can't clobber it.
        ...(expectedVersion !== undefined ? { expectedVersion } : {}),
      },
    );
  }

  async deletePluginBuilderSession(sessionId: string) {
    return this.request<void>('DELETE', `/arches/plugin-builder/sessions/${sessionId}`);
  }

  async startPluginBuilderMessageStream(
    sessionId: string,
    message: string,
    verbose?: boolean,
    useOpus?: boolean,
    guidedPhase?: string,
    autoApprove?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
      status: 'pending';
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/message/stream`, {
      message,
      verbose,
      useOpus,
      ...(guidedPhase ? { guidedPhase } : {}),
      ...(autoApprove !== undefined ? { autoApprove } : {}),
    });
  }

  async advancePluginBuilderStep(sessionId: string) {
    return this.request<{
      sessionId: string;
      buildPlan: unknown;
      allVerified: boolean;
      nextStep: { id: string; title: string } | null;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/guided/advance-step`);
  }

  async startPluginGuidedFlow(
    sessionId: string,
    idea: string,
    verbose?: boolean,
    signal?: AbortSignal,
    useOpus?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/start`,
      { idea, verbose, useOpus },
      { signal },
    );
  }

  async submitGuidedAnswers(
    sessionId: string,
    answers: Record<number, string>,
    verbose?: boolean,
    signal?: AbortSignal,
    comment?: string,
    useOpus?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/answers`,
      { answers, verbose, useOpus, ...(comment ? { comment } : {}) },
      { signal },
    );
  }

  async submitGuidedPlanDecision(
    sessionId: string,
    action: 'approve' | 'request-changes',
    verbose?: boolean,
    feedback?: string,
    signal?: AbortSignal,
    useOpus?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/plan-decision`,
      {
        action,
        verbose,
        useOpus,
        ...(feedback ? { feedback } : {}),
      },
      { signal },
    );
  }

  async submitGuidedAssumptionDecision(
    sessionId: string,
    action: 'approve' | 'suggest-changes',
    verbose?: boolean,
    comments?: Record<string, string>,
    signal?: AbortSignal,
    useOpus?: boolean,
  ) {
    // approve returns full result synchronously; suggest-changes returns { requestId } for streaming
    return this.request<{
      sessionId: string;
      requestId?: string;
      guidedFlow?: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: Array<{ question: string; answer: string | null }>;
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
      buildPlan?: unknown;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/assumption-decision`,
      {
        action,
        verbose,
        useOpus,
        ...(comments ? { comments } : {}),
      },
      { signal },
    );
  }

  /**
   * `confirmProgressLoss` acknowledges a rewind that deletes the plan or
   * resets build progress; without it the server answers 409
   * `REWIND_LOSES_PROGRESS` with `error.consequence` (see rewind-consequence).
   */
  async rewindGuidedPhase(
    sessionId: string,
    targetPhase: string,
    options?: { confirmProgressLoss?: boolean; expectedVersion?: number },
  ) {
    return this.request<{
      sessionId: string;
      guidedFlow: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: Array<{ question: string; answer: string | null }>;
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
      buildPlan: unknown;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/guided/rewind`, {
      targetPhase,
      ...(options?.confirmProgressLoss
        ? { confirmProgressLoss: true, expectedVersion: options.expectedVersion }
        : {}),
    });
  }

  async buildPluginPreview(
    sessionId: string,
    files?: Record<string, unknown>,
    previewMode?: 'light' | 'dark',
  ) {
    return this.request<{
      success: boolean;
      html: string;
      /**
       * THE permission set this build runs under — resolved server-side by the
       * same function the publish path uses (baseline ∪ derived-from-source ∪
       * valid-declared ∪ settings-provisioned). The webapp cannot compute it:
       * deriving from source needs `@fias/db-types`, which the client takes no
       * dependency on. Optional so a response from a pre-deploy server still
       * type-checks; the caller falls back to the manifest's declared array.
       */
      permissions?: PluginPermission[];
      fileCount: number;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/build`, {
      ...(files ? { files: withoutPackageFiles(files) } : {}),
      ...(previewMode ? { previewMode } : {}),
    });
  }

  async savePluginBuilderSession(
    sessionId: string,
    params: { name: string; description?: string },
  ) {
    return this.request<{ success: boolean; archId: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/save`,
      params,
    );
  }

  async getPluginBuilderIconUploadUrl(sessionId: string, mimeType: string, sizeBytes: number) {
    return this.request<{
      archId: string;
      uploadUrl: string;
      stagingKey: string;
      mimeType: string;
      expiresAt: string;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/icon/upload-url`, {
      mimeType,
      sizeBytes,
    });
  }

  async confirmPluginBuilderIcon(sessionId: string, stagingKey: string) {
    return this.request<{
      archId: string;
      iconKey: string;
      imageRef: string;
      variantSizes: number[];
    }>('PUT', `/arches/plugin-builder/sessions/${sessionId}/icon/confirm`, { stagingKey });
  }

  async deletePluginBuilderIcon(sessionId: string) {
    return this.request<void>('DELETE', `/arches/plugin-builder/sessions/${sessionId}/icon`);
  }

  /**
   * Cancel a pending icon change without republishing. The live arche
   * row was never touched (staging only mutates session settings), so
   * cancelling is a single settings rewrite — the previously-reviewed
   * icon stays on the marketplace listing.
   */
  async cancelPluginBuilderPendingIcon(sessionId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/plugin-builder/sessions/${sessionId}/icon/pending`,
    );
  }

  async uploadPluginPreviewScreenshot(sessionId: string, dataUrl: string): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/preview-screenshot`,
      { dataUrl },
    );
  }

  async uploadPluginPreviewLogs(
    sessionId: string,
    entries: Array<{ level: string; category: string; message: string; timestamp: number }>,
  ): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/preview-logs`,
      { entries },
    );
  }

  /**
   * Report a preview build request that was refused without reaching the
   * bundler (413 refused at body parsing, 429, gateway 5xx, network,
   * validation). The server records bundler errors itself; this fills in the
   * rest so the AI's preview tools can say "the request was refused — the code
   * was not compiled" instead of guessing from stale logs. `httpStatus` 0 = no
   * response at all; `elapsedMs` is how long ago the build was issued (monotonic
   * clock), so the server can order a delayed report before a newer build.
   */
  async reportPluginPreviewBuildFailure(
    sessionId: string,
    failure: { message: string; httpStatus: number; errorCode?: string; elapsedMs?: number },
  ): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/preview-build-failure`,
      failure,
    );
  }

  async resetPluginBuilderContext(sessionId: string) {
    return this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/reset-context`,
    );
  }

  async upgradePluginBuilderSdk(sessionId: string) {
    return this.request<{
      session: unknown;
      migrationNotes: string | null;
      triggerAiMigration: boolean;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/upgrade-sdk`);
  }

  async getPluginPublishCost(sessionId: string) {
    return this.request<{
      isFirstPublish: boolean;
      totalCents: number;
      reviewFeeCents: number;
      publishFeeCents: number;
      /**
       * Estimated AI security review cost (cents). Pass-through priced:
       * raw Claude cost × 1.20 markup, computed when the Lambda runs.
       * This value is the upfront reservation upper bound; the gateway
       * refunds the difference between this and the actual finalize.
       */
      estimatedReviewCents: number;
      /**
       * Preview→publish carry-over: eligible until a publish records a
       * durable decision (copied/declined). Counts are what a carry would
       * move; they may be absent when stats were momentarily unavailable
       * (the checkbox still shows — the copy itself is the authority).
       */
      previewDataCarry?: {
        eligible: boolean;
        collectionCount?: number;
        documentCount?: number;
        totalSizeBytes?: number;
        fileCount?: number;
      };
    }>('GET', `/arches/plugin-builder/sessions/${sessionId}/publish-cost`);
  }

  async publishPluginBuilder(sessionId: string, options?: { carryPreviewData?: boolean }) {
    return this.request<{
      submissionId: string;
      archId: string;
      reviewId: string;
      status: string;
      previewDataCarry?: {
        status: 'copied' | 'already_carried' | 'declined';
        collections?: number;
        documents?: number;
        workspaces?: number;
        files?: number;
      } | null;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/publish`,
      // Body only when the caller makes an explicit choice — older shape
      // (no body) still means "carry by default" server-side.
      options && options.carryPreviewData !== undefined
        ? { carryPreviewData: options.carryPreviewData }
        : undefined,
    );
  }

  async unpublishPluginSession(sessionId: string) {
    return this.request<{
      archId: string;
      status: string;
      isListed: boolean;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/unpublish`);
  }

  async submitPluginProblemReport(
    sessionId: string,
    report: {
      description: string;
      problemScope: number;
      conversationHistory?: unknown;
      currentFiles?: unknown;
      currentSettings?: unknown;
      guidedFlow?: unknown;
      buildPlan?: unknown;
      logEntries?: unknown;
      telemetryState?: unknown;
      guidedBuilderState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/report-problem`,
      report,
    );
  }

  async invokePluginAI(
    sessionId: string,
    input: string,
    // String config id, or an SDK selector object ({ capability }) — the
    // route resolves both (published-bridge parity).
    entityId: string | Record<string, unknown>,
    images?: Array<{ mediaType: string; dataBase64: string }>,
    systemPrompt?: string,
    // Multi-turn history and per-call parameters (the route reads
    // `parameters.reasoning`); validated server-side like the published bridge.
    chat?: { history?: unknown; parameters?: unknown },
  ): Promise<{ output: string }> {
    return this.request<{ output: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/ai-invoke`,
      // Optional fields are omitted entirely when absent, so the body shape
      // (and any request-size handling) is unchanged for plain calls.
      {
        input,
        entityId,
        ...(systemPrompt ? { systemPrompt } : {}),
        ...(images && images.length > 0 ? { images } : {}),
        ...(chat?.history !== undefined ? { history: chat.history } : {}),
        ...(chat?.parameters !== undefined ? { parameters: chat.parameters } : {}),
      },
    );
  }

  /**
   * Server-recorded invocation failures for a builder-preview session.
   *
   * Complements the live preview Log tab: these rows are the invocation
   * gateway's own audit trail, so they survive a page reload and include
   * failures the client never observed. Detail is available here because a
   * preview session is single-tenant — every row is the caller's own call.
   */
  async getBuilderInvocationFailures(
    sessionId: string,
    params?: { sinceHours?: number; limit?: number },
  ): Promise<{ failures: InvocationFailureDetail[] }> {
    const query = new URLSearchParams();
    if (params?.sinceHours) query.set('sinceHours', String(params.sinceHours));
    if (params?.limit) query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query}` : '';
    return this.request<{ failures: InvocationFailureDetail[] }>(
      'GET',
      `/plugins/builder-sessions/${sessionId}/invocation-failures${suffix}`,
    );
  }

  /**
   * Aggregate failure counts for a published arche.
   *
   * One total per entity and error class over a server-fixed 7-day window,
   * with no identifiers and no timestamps — the invoking users are the
   * plugin's END USERS, not the developer reading this, so there is
   * deliberately no per-invocation detail on this surface.
   */
  async getArcheInvocationFailures(
    archeId: string,
  ): Promise<{ buckets: InvocationFailureBucket[]; aggregation: string }> {
    // No window parameter: the server fixes it, so the endpoint cannot be
    // dialled down into an activity probe. See the route's rollupQuerySchema.
    return this.request<{ buckets: InvocationFailureBucket[]; aggregation: string }>(
      'GET',
      `/plugins/arches/${archeId}/invocation-failures`,
    );
  }

  async generatePluginImage(
    sessionId: string,
    payload: {
      entityId: string | Record<string, unknown>;
      prompt: string;
      size?: string;
      quality?: string;
      style?: string;
    },
  ): Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }> {
    return this.request(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/image-generate`,
      payload,
    );
  }

  /**
   * Mint a preview bridge capability token for a builder session. The
   * preview's PluginBridgeHost sends it as the Bearer credential to the live
   * plugins bridge (`POST /plugins/bridge/preview_<sessionId>`). Prefer the
   * cached wrapper in `arche/arches/plugin-builder/lib/preview-bridge-token`
   * over calling this directly — tokens are 5-minute credentials.
   */
  async mintBuilderPreviewBridgeToken(sessionId: string): Promise<{
    token: string;
    expiresIn: number;
    permissions: string[];
  }> {
    return this.request('POST', `/arches/plugin-builder/sessions/${sessionId}/bridge-token`, {});
  }

  /**
   * Wipe the session's preview tenant (sandbox storage + data store +
   * workspaces). Backs the preview pane's "Reset preview data" button.
   */
  async resetBuilderPreviewData(sessionId: string): Promise<{
    success: boolean;
    result: { storage: string; datastore: string; workspaces: string };
  }> {
    return this.request('DELETE', `/arches/plugin-builder/sessions/${sessionId}/preview-data`);
  }

  // ==========================================================================
  // Builder Templates API
  // ==========================================================================

  async listBuilderTemplates(options?: { category?: string; featured?: boolean }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.featured !== undefined) params.set('featured', String(options.featured));
    const query = params.toString();
    return this.request<{ templates: BuilderTemplate[] }>(
      'GET',
      `/arches/builder/templates${query ? `?${query}` : ''}`,
    );
  }

  async getBuilderTemplate(templateId: string) {
    return this.request<{ template: BuilderTemplate }>(
      'GET',
      `/arches/builder/templates/${templateId}`,
    );
  }

  // ==========================================================================
  // Builder Reviews API
  // ==========================================================================

  async requestArcheReview(archId: string) {
    return this.request<{
      reviewId: string;
      status: 'pending';
      estimatedCostCents: number;
    }>('POST', `/contributor/arches/${archId}/request-review`);
  }

  async listArcheReviews(archId: string) {
    return this.request<{ reviews: ArcheReviewSummary[] }>(
      'GET',
      `/contributor/arches/${archId}/reviews`,
    );
  }

  async getArcheReview(archId: string, reviewId: string) {
    return this.request<{ review: ArcheReviewDetail }>(
      'GET',
      `/contributor/arches/${archId}/reviews/${reviewId}`,
    );
  }

  // ==========================================================================
  // Chat API
  // ==========================================================================

  // Channel operations

  async listChatChannels(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<ChatChannelListResult>(
      'GET',
      `/arches/chat/channels${query ? `?${query}` : ''}`,
    );
  }

  async createChatChannel(input: CreateChatChannelInput) {
    return this.request<{ channel: ChatChannelResponse }>('POST', '/arches/chat/channels', input);
  }

  async getChatChannel(channelId: string) {
    return this.request<{ channel: ChatChannelResponse }>(
      'GET',
      `/arches/chat/channels/${channelId}`,
    );
  }

  async updateChatChannel(
    channelId: string,
    input: {
      name?: string;
      description?: string;
      imageRef?: string | null;
      avatarFileId?: string | null;
      isPrivate?: boolean;
      defaultPermissions?: ChatMemberPermissions;
    },
  ) {
    return this.request<{ channel: ChatChannelResponse }>(
      'PATCH',
      `/arches/chat/channels/${channelId}`,
      input,
    );
  }

  async deleteChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}`);
  }

  async getGlobalChatChannel() {
    return this.request<{ channel: ChatChannelResponse }>('GET', '/arches/chat/channels/global');
  }

  /**
   * Opens the DM with `target`. `options.guildId` selects the guild-scoped
   * conversation (work talk filed under that guild); omitting it opens the
   * personal one. The two are separate channels and never merge.
   */
  async getOrCreateDirectChannel(
    target: string,
    options?: { isUserId?: boolean; guildId?: string },
  ) {
    // A target picked from the user-search results is a userId (usr_<id> in
    // production, dev-user-NNN in dev); typed text is a username or email.
    // Auto-detect the userId shape so selecting a search result opens the DM
    // directly, instead of the server trying (and failing with a 400) to
    // resolve the raw id as a username.
    const looksLikeUserId = /^usr_[A-Za-z0-9]+$/.test(target) || /^dev-user-\d{3}$/.test(target);
    const isUserId = options?.isUserId === true || looksLikeUserId;
    const isEmail = !isUserId && target.includes('@');
    return this.request<{ channel: ChatChannelResponse }>('POST', '/arches/chat/channels/direct', {
      ...(isUserId
        ? { targetUserId: target }
        : isEmail
          ? { targetEmail: target }
          : { targetUsername: target }),
      ...(options?.guildId ? { guildId: options.guildId } : {}),
    });
  }

  async joinChatChannel(channelId: string) {
    return this.request<{ member: ChatChannelMemberResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/join`,
    );
  }

  async leaveChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/leave`);
  }

  async searchChatUsers(channelId: string, username: string) {
    return this.request<{
      users: Array<{ userId: string; username: string; displayName: string; isMember: boolean }>;
    }>(
      'GET',
      `/arches/chat/channels/${channelId}/users/search?username=${encodeURIComponent(username)}`,
    );
  }

  /**
   * Search active users by username prefix, for DM recipient autocomplete.
   * Global (not scoped to a channel); requires at least 2 characters server-side.
   */
  async searchUsersForDm(query: string) {
    return this.request<{
      users: Array<{ userId: string; username: string; displayName: string }>;
    }>('GET', `/arches/chat/users/search?username=${encodeURIComponent(query)}`);
  }

  async addChatMember(channelId: string, userId: string) {
    return this.request<{ channelId: string; userId: string; role: string; joinedAt: string }>(
      'POST',
      `/arches/chat/channels/${channelId}/members`,
      { userId },
    );
  }

  async getChatChannelMembers(
    channelId: string,
    options?: { limit?: number; offset?: number; search?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.search) params.set('search', options.search);
    const query = params.toString();
    return this.request<{ members: ChatChannelMemberResponse[]; total: number }>(
      'GET',
      `/arches/chat/channels/${channelId}/members${query ? `?${query}` : ''}`,
    );
  }

  async updateChatMemberRole(channelId: string, userId: string, role: string) {
    return this.request<{ member: ChatChannelMemberResponse }>(
      'PATCH',
      `/arches/chat/channels/${channelId}/members/${userId}`,
      { role },
    );
  }

  async updateChatMemberPermissions(
    channelId: string,
    userId: string,
    permissions: ChatMemberPermissions,
  ) {
    return this.request<{
      member: { channelId: string; userId: string; permissions: ChatMemberPermissions };
    }>('PATCH', `/arches/chat/channels/${channelId}/members/${userId}/permissions`, {
      permissions,
    });
  }

  async removeChatMember(channelId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/members/${userId}`);
  }

  async getChatChannelBans(channelId: string) {
    return this.request<{ bans: ChatChannelBanResponse[] }>(
      'GET',
      `/arches/chat/channels/${channelId}/bans`,
    );
  }

  async banChatMember(channelId: string, userId: string, reason?: string) {
    return this.request<{ ban: ChatChannelBanResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/bans`,
      { userId, reason },
    );
  }

  async unbanChatMember(channelId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/bans/${userId}`);
  }

  async generateChatChannelInvite(channelId: string, expiresInHours?: number) {
    return this.request<{ inviteCode: string; expiresAt: string | null }>(
      'POST',
      `/arches/chat/channels/${channelId}/invite`,
      { expiresInHours },
    );
  }

  async getChatChannelByInviteCode(code: string) {
    return this.request<{
      channel: {
        channelId: string;
        name: string | null;
        description: string | null;
        avatarFileId: string | null;
        imageRef: string | null;
      };
    }>('GET', `/arches/chat/channels/invite/${code}`);
  }

  async joinChatChannelByInvite(code: string) {
    return this.request<{ channelId: string; userId: string; role: string; joinedAt: string }>(
      'POST',
      `/arches/chat/channels/invite/${code}/join`,
    );
  }

  async getChatDmPartnerProfile(channelId: string) {
    return this.request<{ profile: ChatDmPartnerProfile }>(
      'GET',
      `/arches/chat/channels/${channelId}/partner-profile`,
    );
  }

  async getChatUserProfile(userId: string) {
    return this.request<{ profile: ChatDmPartnerProfile }>(
      'GET',
      `/arches/chat/users/${userId}/profile`,
    );
  }

  async muteChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/mute`);
  }

  async unmuteChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/mute`);
  }

  async pinChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/pin`);
  }

  async unpinChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/pin`);
  }

  async markChatChannelRead(channelId: string, messageId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/read`, { messageId });
  }

  /**
   * Mark the channel unread — the read pointer rolls back so unread messages
   * resurface. With `messageId` it rolls back to just before that message (the
   * per-message action). Without one it marks the whole channel unread from the
   * channel-list row (the server picks the latest message from someone else);
   * `markedUnread` is false when there was nothing to surface.
   */
  async markChatChannelUnread(channelId: string, messageId?: string) {
    return this.request<{ success: boolean; markedUnread?: boolean }>(
      'POST',
      `/arches/chat/channels/${channelId}/unread`,
      messageId ? { messageId } : {},
    );
  }

  async getChatUnreadCounts() {
    return this.request<{ channels: ChatUnreadCount[] }>('GET', '/arches/chat/unread');
  }

  // Cross-arche notification badge summary (worktop + header)

  async getNotificationSummary() {
    return this.request<NotificationSummaryResponse>('GET', '/notifications/summary');
  }

  /**
   * Clear the current user's unread notifications for an arche. Omit `groupKey`
   * to clear the whole arche's badge (the "opened the arche" case); pass one to
   * clear a single collapsed notification. Returns 204.
   */
  async markNotificationsRead(archeId: string, groupKey?: string) {
    return this.request<void>('POST', '/notifications/read', {
      archeId,
      ...(groupKey ? { groupKey } : {}),
    });
  }

  // Message operations

  async listChatMessages(
    channelId: string,
    options?: { before?: string; after?: string; limit?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.after) params.set('after', options.after);
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request<ChatMessageListResult>(
      'GET',
      `/arches/chat/channels/${channelId}/messages${query ? `?${query}` : ''}`,
    );
  }

  async sendChatMessage(
    channelId: string,
    input: {
      content: string;
      replyToId?: string;
      mentions?: Array<{ userId: string; displayName: string }>;
      attachmentRefs?: ChatAttachmentRef[];
    },
  ) {
    // Bound the request so a stalled network connection can't leave the send
    // pending forever — the composer disables itself until this settles, so an
    // unbounded request would wedge the input. On timeout the abort surfaces as a
    // thrown error the caller turns into a "failed to send" warning (text kept).
    const SEND_TIMEOUT_MS = 20_000;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);
    try {
      return await this.request<{ message: ChatMessageResponse }>(
        'POST',
        `/arches/chat/channels/${channelId}/messages`,
        input,
        { signal: controller.signal },
      );
    } finally {
      clearTimeout(timeout);
    }
  }

  async editChatMessage(messageId: string, content: string) {
    return this.request<{ message: ChatMessageResponse }>(
      'PATCH',
      `/arches/chat/messages/${messageId}`,
      {
        content,
      },
    );
  }

  /** The current user's cross-channel Mentions inbox (newest-first, cursor-paginated). */
  async getChatMentions(options?: { limit?: number; before?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);
    const query = params.toString();
    return this.request<{
      mentions: ChatMentionFeedItemResponse[];
      hasMore: boolean;
      nextCursor: string | null;
    }>('GET', `/arches/chat/mentions${query ? `?${query}` : ''}`);
  }

  async deleteChatMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/chat/messages/${messageId}`);
  }

  /**
   * Search one channel's messages by text, by sender, or both. `from` is a
   * USERNAME — the server resolves it to a userId; an unknown handle comes back
   * as zero matches rather than an error. At least one of `query` / `from` must
   * be non-empty or the server rejects the request.
   *
   * Cursor-paginated via `before` (a messageId), not offsets — a live channel
   * shifts under offset paging. `total` is only returned for the first page.
   */
  async searchChatMessages(
    channelId: string,
    query: string,
    options?: { from?: string; match?: ChatSearchMatchMode; limit?: number; before?: string },
  ) {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (options?.from) params.set('from', options.from);
    if (options?.match) params.set('match', options.match);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);
    return this.request<ChatMessageSearchResult>(
      'GET',
      `/arches/chat/channels/${channelId}/messages/search?${params.toString()}`,
    );
  }

  /**
   * Search every conversation the current user is an active member of. Same
   * query contract as {@link searchChatMessages} (text, `from` username, or
   * both; cursor via `before`) and the same server rate-limit bucket. Each hit
   * names its channel; `total` is capped server-side and `totalCapped` says
   * more exist.
   */
  async searchAllChatMessages(
    query: string,
    options?: { from?: string; match?: ChatSearchMatchMode; limit?: number; before?: string },
  ) {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (options?.from) params.set('from', options.from);
    if (options?.match) params.set('match', options.match);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);
    return this.request<ChatGlobalMessageSearchResult>(
      'GET',
      `/arches/chat/messages/search?${params.toString()}`,
    );
  }

  async getChatPinnedMessages(channelId: string) {
    return this.request<{ pins: ChatPinnedMessageResponse[] }>(
      'GET',
      `/arches/chat/channels/${channelId}/pins`,
    );
  }

  async pinChatMessage(messageId: string) {
    return this.request<void>('POST', `/arches/chat/messages/${messageId}/pin`);
  }

  async unpinChatMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/chat/messages/${messageId}/pin`);
  }

  async addChatReaction(messageId: string, emoji: string) {
    return this.request<void>('POST', `/arches/chat/messages/${messageId}/reactions`, { emoji });
  }

  async removeChatReaction(messageId: string, emoji: string) {
    return this.request<void>(
      'DELETE',
      `/arches/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    );
  }

  // Moderation operations

  /**
   * Files a moderation report against a message, a channel, or a person.
   *
   * At least one target is required. When reporting a message the caller should
   * pass `reportedUserId` too where it knows the sender — a moderator triaging
   * by account then sees the message reports against them as well.
   */
  async reportChatMessage(input: {
    messageId?: string;
    channelId?: string;
    reportedUserId?: string;
    reason: string;
    description?: string;
  }) {
    return this.request<{
      reportId: string;
      reason: string;
      status: string;
      createdAt: string;
    }>('POST', '/arches/chat/reports', input);
  }

  async blockChatUser(blockedId: string) {
    return this.request<void>('POST', '/arches/chat/blocks', { blockedId });
  }

  async unblockChatUser(blockedId: string) {
    return this.request<void>('DELETE', `/arches/chat/blocks/${blockedId}`);
  }

  async listChatBlockedUsers() {
    return this.request<{ blocks: ChatBlockResponse[] }>('GET', '/arches/chat/blocks');
  }

  async getChatSettings() {
    return this.request<{ settings: ChatUserSettingsResponse }>('GET', '/arches/chat/settings');
  }

  async updateChatSettings(input: UpdateChatUserSettingsInput) {
    return this.request<{ settings: ChatUserSettingsResponse }>(
      'PUT',
      '/arches/chat/settings',
      input,
    );
  }

  // ==========================================================================
  // Guild Methods
  // ==========================================================================

  async listGuilds(options?: {
    search?: string;
    guildType?: string;
    sortBy?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.guildType) params.set('guildType', options.guildType);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildListResult>('GET', `/arches/guilds${qs ? `?${qs}` : ''}`);
  }

  async getGuildDetail(guildId: string) {
    return this.request<GuildDetailResponse>('GET', `/arches/guilds/${guildId}/detail`);
  }

  async getGuildInviteInfo(inviteCode: string) {
    return this.request<{ guild: GuildSummary; invite: GuildInviteInfo }>(
      'GET',
      `/arches/guilds/invite/${inviteCode}`,
    );
  }

  async createGuild(input: CreateGuildPayload) {
    return this.request<GuildDetailResponse>('POST', '/arches/guilds', input);
  }

  async updateGuild(guildId: string, input: UpdateGuildPayload) {
    return this.request<GuildDetailResponse>('PATCH', `/arches/guilds/${guildId}`, input);
  }

  async deleteGuild(guildId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}`);
  }

  async listGuildRoles(guildId: string) {
    return this.request<{ roles: GuildRoleResponse[] }>('GET', `/arches/guilds/${guildId}/roles`);
  }

  async getMyGuildPermissions(guildId: string) {
    return this.request<GuildPermissionState>('GET', `/arches/guilds/${guildId}/permissions/me`);
  }

  async createGuildRole(
    guildId: string,
    input: { name: string; color?: string | null; permissions?: GuildPermission[] },
  ) {
    return this.request<{ role: GuildRoleResponse }>(
      'POST',
      `/arches/guilds/${guildId}/roles`,
      input,
    );
  }

  async updateGuildRole(
    guildId: string,
    roleId: string,
    input: { name?: string; color?: string | null; permissions?: GuildPermission[] },
  ) {
    return this.request<{ role: GuildRoleResponse }>(
      'PATCH',
      `/arches/guilds/${guildId}/roles/${roleId}`,
      input,
    );
  }

  async deleteGuildRole(guildId: string, roleId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/roles/${roleId}`);
  }

  async assignGuildRole(guildId: string, userId: string, roleId: string) {
    return this.request<void>('PUT', `/arches/guilds/${guildId}/members/${userId}/roles/${roleId}`);
  }

  async unassignGuildRole(guildId: string, userId: string, roleId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/guilds/${guildId}/members/${userId}/roles/${roleId}`,
    );
  }

  async getMyGuilds() {
    return this.request<{ guilds: GuildSummary[] }>('GET', '/arches/guilds/me');
  }

  async joinGuild(guildId: string) {
    return this.request<GuildMemberResponse>('POST', `/arches/guilds/${guildId}/join`);
  }

  async leaveGuild(guildId: string) {
    return this.request<void>('POST', `/arches/guilds/${guildId}/leave`);
  }

  async getGuildMembers(
    guildId: string,
    options?: { role?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.role) params.set('role', options.role);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildMemberListResult>(
      'GET',
      `/arches/guilds/${guildId}/members${qs ? `?${qs}` : ''}`,
    );
  }

  async updateGuildMemberRole(guildId: string, userId: string, role: string) {
    return this.request<GuildMemberResponse>(
      'PATCH',
      `/arches/guilds/${guildId}/members/${userId}`,
      {
        role,
      },
    );
  }

  async removeGuildMember(guildId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/members/${userId}`);
  }

  async transferGuildOwnership(guildId: string, newOwnerId: string) {
    return this.request<{ success: boolean }>('POST', `/arches/guilds/${guildId}/transfer`, {
      newOwnerId,
    });
  }

  async createGuildInvite(
    guildId: string,
    options?: { maxUses?: number; expiresInHours?: number },
  ) {
    return this.request<GuildInviteResponse>(
      'POST',
      `/arches/guilds/${guildId}/invites`,
      options || {},
    );
  }

  async listGuildInvites(guildId: string) {
    return this.request<{ invites: GuildInviteResponse[] }>(
      'GET',
      `/arches/guilds/${guildId}/invites`,
    );
  }

  async revokeGuildInvite(guildId: string, inviteId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/invites/${inviteId}`);
  }

  async acceptGuildInvite(inviteCode: string) {
    return this.request<GuildMemberResponse>('POST', `/arches/guilds/invite/${inviteCode}/accept`);
  }

  async banGuildMember(guildId: string, userId: string, reason?: string) {
    return this.request<GuildBanResponse>('POST', `/arches/guilds/${guildId}/bans`, {
      userId,
      reason,
    });
  }

  async unbanGuildMember(guildId: string, banId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/bans/${banId}`);
  }

  async listGuildBans(guildId: string) {
    return this.request<{ bans: GuildBanResponse[] }>('GET', `/arches/guilds/${guildId}/bans`);
  }

  async listGuildActivity(
    guildId: string,
    options?: { activityType?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.activityType) params.set('activityType', options.activityType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildActivityResult>(
      'GET',
      `/arches/guilds/${guildId}/activity${qs ? `?${qs}` : ''}`,
    );
  }

  async getGuildTreasury(guildId: string) {
    return this.request<GuildTreasuryResponse>('GET', `/arches/guilds/${guildId}/treasury`);
  }

  async listGuildTreasuryTransactions(
    guildId: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{ transactions: GuildTreasuryTransactionResponse[] }>(
      'GET',
      `/arches/guilds/${guildId}/treasury/transactions${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // DFE Inventory API
  // ==========================================================================

  async listDfeDefinitions(options?: {
    search?: string;
    fungibility?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.fungibility) params.set('fungibility', options.fungibility);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeDefinitionListResult>(
      'GET',
      `/arches/dfe/definitions${qs ? `?${qs}` : ''}`,
    );
  }

  /**
   * Generate randomised LitPet metadata for a species, for the admin mint
   * dialog's prefill.
   *
   * Server-side because the gene/species tables are far too large to ship to the
   * browser, and because generating it there means the dialog exercises the same
   * code that crafting does rather than a mirror that can drift.
   */
  async generateDfeLitpetTemplate(name: string) {
    return this.request<{ metadata: Record<string, unknown> }>(
      'GET',
      `/arches/dfe/admin/litpet-template?name=${encodeURIComponent(name)}`,
    );
  }

  async getDfeDefinition(itemDefId: string) {
    return this.request<DfeDefinitionDetail>('GET', `/arches/dfe/definitions/${itemDefId}`);
  }

  async getDfeInventory(options?: {
    search?: string;
    fungibility?: string;
    category?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.fungibility) params.set('fungibility', options.fungibility);
    if (options?.category !== undefined) params.set('category', options.category);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeInventoryListResult>(
      'GET',
      `/arches/dfe/inventory${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeInventoryItem(itemId: string) {
    return this.request<DfeInventoryItemDetail>('GET', `/arches/dfe/inventory/${itemId}`);
  }

  async transferDfeItem(input: TransferDfeItemInput) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/transfer', input);
  }

  async burnDfeItem(itemId: string) {
    return this.request<{ success: boolean }>('DELETE', `/arches/dfe/inventory/${itemId}`);
  }

  async burnDfeFungibleItems(input: {
    itemDefId: string;
    quantity: number;
    category?: string;
    position?: string;
  }) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/inventory/burn-fungible', input);
  }

  async createDfeDefinition(input: CreateDfeDefinitionInput) {
    return this.request<DfeDefinitionDetail>('POST', '/arches/dfe/admin/definitions', input);
  }

  async updateDfeDefinition(
    itemDefId: string,
    input: UpdateDfeDefinitionInput | Record<string, unknown>,
  ) {
    return this.request<DfeDefinitionDetail>(
      'PATCH',
      `/arches/dfe/admin/definitions/${itemDefId}`,
      input,
    );
  }

  async archiveDfeDefinition(itemDefId: string) {
    return this.request<{ itemDefId: string; name: string; status: string }>(
      'DELETE',
      `/arches/dfe/admin/definitions/${itemDefId}`,
    );
  }

  async mintDfeItems(input: {
    itemDefId: string;
    toUsername: string;
    quantity: number;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }) {
    return this.request<DfeMintResult>('POST', '/arches/dfe/admin/mint', input);
  }

  async listDfeTransfers(options?: {
    itemDefId?: string;
    userId?: string;
    transferType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.itemDefId) params.set('itemDefId', options.itemDefId);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.transferType) params.set('transferType', options.transferType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeEnrichedTransferListResult>(
      'GET',
      `/arches/dfe/admin/transfers${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeTransferHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeEnrichedTransferListResult>(
      'GET',
      `/arches/dfe/transfer-history${qs ? `?${qs}` : ''}`,
    );
  }

  async updateDfeItemPlacement(itemId: string, input: { category?: string; position?: string }) {
    return this.request<{ itemId: string; category: string; position: string }>(
      'PATCH',
      `/arches/dfe/inventory/${itemId}/placement`,
      input,
    );
  }

  async moveDfeFungibleStack(input: {
    itemDefId: string;
    quantity: number;
    fromCategory: string;
    fromPosition: string;
    toCategory: string;
    toPosition: string;
  }) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/inventory/move-fungible', input);
  }

  async getDfePositionConflicts() {
    return this.request<{ conflicts: DfePositionConflict[] }>(
      'GET',
      '/arches/dfe/inventory/position-conflicts',
    );
  }

  // =========================================================================
  // DFE Inventory Transition
  // =========================================================================

  async importDfeLegacyInventory(input: { username: string; password: string }) {
    return this.request<DfeLegacyImportResult>(
      'POST',
      '/arches/dfe-inventory-transition/import',
      input,
    );
  }

  async getDfeLegacyImportStatus() {
    return this.request<{ imports: DfeLegacyImportEntry[] }>(
      'GET',
      '/arches/dfe-inventory-transition/import/status',
    );
  }

  async retryDfeLegacyImport(importId: string, password: string) {
    return this.request<DfeLegacyImportResult>(
      'POST',
      `/arches/dfe-inventory-transition/import/${importId}/retry`,
      { password },
    );
  }

  async checkDfeLegacyUsername(username: string) {
    const params = new URLSearchParams({ username });
    return this.request<{ imported: boolean }>(
      'GET',
      `/arches/dfe-inventory-transition/import/check?${params.toString()}`,
    );
  }

  async listAllDfeLegacyImports() {
    return this.request<AdminDfeLegacyImportListResult>(
      'GET',
      '/arches/dfe-inventory-transition/admin/imports',
    );
  }

  async deleteDfeLegacyImport(importId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/dfe-inventory-transition/admin/imports/${importId}`,
    );
  }

  // ==========================================================================
  // LitSquad (arche_litsquad) — NFT inventory
  // ==========================================================================

  async listLitSquadDefinitions(options?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<LitSquadDefinitionListResult> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadDefinitionListResult>(
      'GET',
      `/arches/litsquad/definitions${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitSquadDefinition(itemDefId: string): Promise<LitSquadDefinitionDetail> {
    return this.request<LitSquadDefinitionDetail>(
      'GET',
      `/arches/litsquad/definitions/${itemDefId}`,
    );
  }

  async getLitSquadInventory(options?: {
    search?: string;
    category?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }): Promise<LitSquadInventoryListResult> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.category !== undefined) params.set('category', options.category);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadInventoryListResult>(
      'GET',
      `/arches/litsquad/inventory${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitSquadInventoryItem(itemId: string): Promise<LitSquadInventoryItemDetail> {
    return this.request<LitSquadInventoryItemDetail>('GET', `/arches/litsquad/inventory/${itemId}`);
  }

  async burnLitSquadItem(itemId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('DELETE', `/arches/litsquad/inventory/${itemId}`);
  }

  async updateLitSquadItemPlacement(
    itemId: string,
    input: { category?: string; position?: string },
  ): Promise<{ itemId: string; category: string; position: string }> {
    return this.request<{ itemId: string; category: string; position: string }>(
      'PATCH',
      `/arches/litsquad/inventory/${itemId}/placement`,
      input,
    );
  }

  async getLitSquadAvatar(): Promise<LitSquadAvatarResponse> {
    return this.request<LitSquadAvatarResponse>('GET', '/arches/litsquad/avatar');
  }

  /** Set the avatar: the server renders both images from the recipe. */
  async saveLitSquadAvatar(recipe: LitSquadAvatarRecipe): Promise<LitSquadAvatarSaveResult> {
    return this.request<LitSquadAvatarSaveResult>('PUT', '/arches/litsquad/avatar', recipe);
  }

  async transferLitSquadItem(input: TransferLitSquadItemInput): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('POST', '/arches/litsquad/transfer', input);
  }

  async getLitSquadTransferHistory(options?: {
    limit?: number;
    offset?: number;
  }): Promise<LitSquadEnrichedTransferListResult> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadEnrichedTransferListResult>(
      'GET',
      `/arches/litsquad/transfer-history${qs ? `?${qs}` : ''}`,
    );
  }

  async createLitSquadDefinition(
    input: CreateLitSquadDefinitionInput,
  ): Promise<LitSquadDefinitionDetail> {
    return this.request<LitSquadDefinitionDetail>(
      'POST',
      '/arches/litsquad/admin/definitions',
      input,
    );
  }

  async updateLitSquadDefinition(
    itemDefId: string,
    input: UpdateLitSquadDefinitionInput,
  ): Promise<LitSquadDefinitionDetail> {
    return this.request<LitSquadDefinitionDetail>(
      'PATCH',
      `/arches/litsquad/admin/definitions/${itemDefId}`,
      input,
    );
  }

  async archiveLitSquadDefinition(
    itemDefId: string,
  ): Promise<{ itemDefId: string; name: string; status: string; updatedAt: string }> {
    return this.request<{ itemDefId: string; name: string; status: string; updatedAt: string }>(
      'DELETE',
      `/arches/litsquad/admin/definitions/${itemDefId}`,
    );
  }

  async mintLitSquadItems(input: {
    itemDefId: string;
    toUserId?: string;
    toUsername?: string;
    quantity: number;
    displayName?: string;
    thumbnailUri?: string;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }): Promise<LitSquadMintResult> {
    return this.request<LitSquadMintResult>('POST', '/arches/litsquad/admin/mint', input);
  }

  // ==========================================================================
  // LitSquad Accessory Crafting
  // ==========================================================================

  async getLitSquadCraftIngredients(): Promise<LitSquadCraftIngredientsResult> {
    return this.request<LitSquadCraftIngredientsResult>(
      'GET',
      '/arches/litsquad/craft/ingredients',
    );
  }

  async previewLitSquadCraft(input: LitSquadCraftInput): Promise<LitSquadCraftPreviewResult> {
    return this.request<LitSquadCraftPreviewResult>(
      'POST',
      '/arches/litsquad/craft/preview',
      input,
    );
  }

  async executeLitSquadCraft(input: LitSquadCraftInput): Promise<LitSquadCraftResult> {
    return this.request<LitSquadCraftResult>('POST', '/arches/litsquad/craft', input);
  }

  async listLitSquadAdminTransfers(options?: {
    itemDefId?: string;
    userId?: string;
    transferType?: string;
    limit?: number;
    offset?: number;
  }): Promise<LitSquadEnrichedTransferListResult> {
    const params = new URLSearchParams();
    if (options?.itemDefId) params.set('itemDefId', options.itemDefId);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.transferType) params.set('transferType', options.transferType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitSquadEnrichedTransferListResult>(
      'GET',
      `/arches/litsquad/admin/transfers${qs ? `?${qs}` : ''}`,
    );
  }

  async getGuildChatChannel(guildId: string) {
    return this.request<{ channelId: string }>('GET', `/arches/guilds/${guildId}/channel`);
  }

  /** Lists the guild's channels the caller can see (public + private-where-member). */
  async listGuildChannels(guildId: string) {
    return this.request<{ channels: GuildChannelSummary[] }>(
      'GET',
      `/arches/guilds/${guildId}/channels`,
    );
  }

  /** Creates a new channel in a guild (guild admin+). */
  async createGuildChannel(
    guildId: string,
    input: { name: string; description?: string; isPrivate?: boolean; isReadOnly?: boolean },
  ) {
    return this.request<{ channelId: string }>('POST', `/arches/guilds/${guildId}/channels`, input);
  }

  /** Updates a guild channel (guild admin+). */
  async updateGuildChannel(
    guildId: string,
    channelId: string,
    input: {
      name?: string;
      description?: string | null;
      isPrivate?: boolean;
      isReadOnly?: boolean;
    },
  ) {
    return this.request<void>('PATCH', `/arches/guilds/${guildId}/channels/${channelId}`, input);
  }

  /** Archives a guild channel (guild admin+). The default channel cannot be deleted. */
  async deleteGuildChannel(guildId: string, channelId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/channels/${channelId}`);
  }

  // ==========================================================================
  // Game Rewards Methods
  // ==========================================================================

  // Public
  async getGameRewardsTiers() {
    return this.request<GrTierListResult>('GET', '/arches/game-rewards/tiers');
  }

  // User - Subscription
  async getGameRewardsSubscription() {
    return this.request<{ subscription: GrSubscriptionResponse | null }>(
      'GET',
      '/arches/game-rewards/subscription',
    );
  }

  async purchaseGameRewardsSubscription(tier: string) {
    return this.request<{ subscription: GrSubscriptionResponse }>(
      'POST',
      '/arches/game-rewards/subscription/purchase',
      { tier },
    );
  }

  async cancelGameRewardsSubscription() {
    return this.request<{ success: boolean }>('POST', '/arches/game-rewards/subscription/cancel');
  }

  async getGameRewardsSubscriptionHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrSubscriptionHistoryResult>(
      'GET',
      `/arches/game-rewards/subscription/history${qs ? `?${qs}` : ''}`,
    );
  }

  // User - LitCraft Store
  async getLitCraftStoreProducts() {
    return this.request<LitCraftStoreResponse>('GET', '/arches/game-rewards/store/products');
  }

  async purchaseLitCraftStoreProduct(productIdentifier: string) {
    return this.request<LitCraftStorePurchaseResponse>(
      'POST',
      '/arches/game-rewards/store/purchase',
      { productIdentifier },
    );
  }

  // User - Rewards
  async getGameRewardsHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardLogListResult>(
      'GET',
      `/arches/game-rewards/rewards${qs ? `?${qs}` : ''}`,
    );
  }

  // Admin - Games
  async listGameRewardsGames(options?: {
    isActive?: boolean;
    reviewStatus?: GrGameConfigReviewStatus;
    /** Include soft-deleted rows alongside live ones. */
    includeDeleted?: boolean;
    /** Return ONLY soft-deleted rows. The admin "Deleted" filter chip. */
    onlyDeleted?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.reviewStatus) params.set('reviewStatus', options.reviewStatus);
    if (options?.includeDeleted) params.set('includeDeleted', 'true');
    if (options?.onlyDeleted) params.set('onlyDeleted', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrGameConfigListResult>(
      'GET',
      `/arches/game-rewards/admin/games${qs ? `?${qs}` : ''}`,
    );
  }

  async approveGameRewardsGame(gameConfigId: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      reviewStatus: GrGameConfigReviewStatus;
      updatedAt: string;
    }>('POST', `/arches/game-rewards/admin/games/${gameConfigId}/approve`);
  }

  async rejectGameRewardsGame(gameConfigId: string, reason?: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      reviewStatus: GrGameConfigReviewStatus;
      updatedAt: string;
    }>('POST', `/arches/game-rewards/admin/games/${gameConfigId}/reject`, reason ? { reason } : {});
  }

  /**
   * Soft-delete a game config. The row is hidden from admin lists and
   * the claim path; historical reward-log entries still surface via
   * LEFT JOIN. Reversible via {@link restoreGameRewardsGame}. For
   * irreversible removal use {@link purgeGameRewardsGame}.
   */
  async deleteGameRewardsGame(gameConfigId: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      deletedAt: string | null;
    }>('DELETE', `/arches/game-rewards/admin/games/${gameConfigId}`);
  }

  /**
   * Reverse a soft-delete. The config reappears in admin lists and
   * accepts new claims again. Idempotent on already-live configs.
   */
  async restoreGameRewardsGame(gameConfigId: string) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      deletedAt: string | null;
    }>('POST', `/arches/game-rewards/admin/games/${gameConfigId}/restore`);
  }

  /**
   * Hard-delete a game config and all of its children (reward log,
   * rules, recipes, leaderboards, time-played). Irreversible. The
   * server requires `confirmGameName` to match the row's current
   * game_name — pass the name the admin typed into the UI's
   * confirmation field.
   *
   * If gr_reward_log has more rows than the server-side threshold, the
   * call returns a 409 CONFLICT with `{ rewardLogCount, threshold,
   * forceRequired: true }`. Re-call with `force: true` to override.
   */
  async purgeGameRewardsGame(
    gameConfigId: string,
    confirmGameName: string,
    options: { force?: boolean } = {},
  ) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      rewardLogRowsDeleted: number;
      purged: true;
    }>('DELETE', `/arches/game-rewards/admin/games/${gameConfigId}/purge`, {
      confirmGameName,
      ...(options.force ? { force: true } : {}),
    });
  }

  async getGameRewardsGame(gameConfigId: string) {
    return this.request<GrGameConfigDetail>(
      'GET',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
    );
  }

  async createGameRewardsGame(input: {
    gameName: string;
    displayName: string;
    description?: string;
    minScore?: number;
    maxScore?: number;
  }) {
    return this.request<GrGameConfigCreateResult>(
      'POST',
      '/arches/game-rewards/admin/games',
      input,
    );
  }

  async updateGameRewardsGame(
    gameConfigId: string,
    input: {
      displayName?: string;
      description?: string;
      minScore?: number;
      maxScore?: number;
      leaderboardType?: GrLeaderboardType;
      rewardMode?: GrRewardMode;
      itemListFormat?: GrItemListFormat;
      allowedItems?: string[] | null;
      allowedItemPrefixes?: string[] | null;
      claimCosts?: GrClaimCostRule[] | null;
      claimStateKeyword?: string | null;
      rewardLookupEvent?: string | null;
      isActive?: boolean;
      linkedArcheId?: string | null;
      leaderboardTargetGameId?: string | null;
      chatGuildId?: string | null;
      includeInLitcraftReset?: boolean;
    },
  ) {
    return this.request<GrGameConfigDetail>(
      'PATCH',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
      input,
    );
  }

  /**
   * Clone an existing game config + its reward rules into a new game config.
   * The clone resets gameName/displayName/api-key/linked-arche/review-status;
   * everything else copies (including leaderboardTargetGameId).
   */
  async cloneGameRewardsGame(input: {
    sourceGameConfigId: string;
    gameName: string;
    displayName: string;
  }) {
    return this.request<{
      gameConfigId: string;
      gameName: string;
      displayName: string;
      reviewStatus: GrGameConfigReviewStatus;
      rulesCopied: number;
      apiKey: string;
      apiKeyPrefix: string;
    }>('POST', '/arches/game-rewards/admin/games/clone', input);
  }

  async rotateGameRewardsApiKey(gameConfigId: string) {
    return this.request<{ gameConfigId: string; apiKey: string; apiKeyPrefix: string }>(
      'POST',
      `/arches/game-rewards/admin/games/${gameConfigId}/rotate-key`,
    );
  }

  // Admin - Reward Rules
  async listGameRewardsRules(options?: {
    gameConfigId?: string;
    gameEvent?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.gameEvent) params.set('gameEvent', options.gameEvent);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardRuleListResult>(
      'GET',
      `/arches/game-rewards/admin/rules${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsRule(input: {
    gameConfigId: string;
    gameEvent: string;
    rewardType: string;
    /** Item name (e.g. "Heptal"). Server resolves to item_def_id. */
    rewardItemName: string;
    m: number;
    b: number;
    maxRewardCount?: number;
    bronzeMultiplier?: number;
    silverMultiplier?: number;
    goldMultiplier?: number;
    steps?: GrStepDefinition[] | null;
    baseMultiplier?: number;
  }) {
    return this.request<GrRewardRuleResponse>('POST', '/arches/game-rewards/admin/rules', input);
  }

  async updateGameRewardsRule(
    ruleId: string,
    input: {
      m?: number;
      b?: number;
      maxRewardCount?: number;
      bronzeMultiplier?: number;
      silverMultiplier?: number;
      goldMultiplier?: number;
      steps?: GrStepDefinition[] | null;
      baseMultiplier?: number;
      isActive?: boolean;
      /** Item name to swap the rule's reward to. Server resolves to item_def_id. */
      rewardItemName?: string;
    },
  ) {
    return this.request<GrRewardRuleResponse>(
      'PATCH',
      `/arches/game-rewards/admin/rules/${ruleId}`,
      input,
    );
  }

  /**
   * List recent claim rejections (admin Recent Rejections panel).
   */
  async listGameRewardsRejections(options?: {
    archeId?: string;
    attemptedGameName?: string;
    /** Player-targeting filter — UIs send `username`; server resolves to userId. */
    username?: string;
    userId?: string;
    sinceHours?: number;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.attemptedGameName) params.set('attemptedGameName', options.attemptedGameName);
    if (options?.username) params.set('username', options.username);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.sinceHours) params.set('sinceHours', String(options.sinceHours));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrClaimRejectionListResult>(
      'GET',
      `/arches/game-rewards/admin/rejections${qs ? `?${qs}` : ''}`,
    );
  }

  async deleteGameRewardsRule(ruleId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/rules/${ruleId}`,
    );
  }

  // Admin - Rate Limits
  async listGameRewardsLimits(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardTypeLimitListResult>(
      'GET',
      `/arches/game-rewards/admin/limits${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsLimit(input: {
    rewardType: string;
    minSecondsBetweenRewards?: number;
    maxAmountPerDay?: number;
  }) {
    return this.request<GrRewardTypeLimitResponse>(
      'POST',
      '/arches/game-rewards/admin/limits',
      input,
    );
  }

  async updateGameRewardsLimit(
    limitId: string,
    input: { minSecondsBetweenRewards?: number; maxAmountPerDay?: number },
  ) {
    return this.request<GrRewardTypeLimitResponse>(
      'PATCH',
      `/arches/game-rewards/admin/limits/${limitId}`,
      input,
    );
  }

  async deleteGameRewardsLimit(limitId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/limits/${limitId}`,
    );
  }

  // Admin - Per-(game, reward type) Daily Caps
  //
  // The reserved-slice axis. Its sibling above (`…Limits`) is keyed by reward
  // type alone and is a SHARED pool across every game; these are keyed by
  // (game, reward type) and cannot be consumed by anything else. Both may
  // apply — enforcement takes the lower remaining.
  async listGameRewardsGameLimits(options?: {
    gameConfigId?: string;
    rewardType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.rewardType) params.set('rewardType', options.rewardType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrGameRewardLimitListResult>(
      'GET',
      `/arches/game-rewards/admin/game-limits${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsGameLimit(input: {
    gameConfigId: string;
    rewardType: string;
    maxAmountPerDay?: number;
  }) {
    return this.request<GrGameRewardLimitResponse>(
      'POST',
      '/arches/game-rewards/admin/game-limits',
      input,
    );
  }

  async updateGameRewardsGameLimit(limitId: string, input: { maxAmountPerDay?: number }) {
    return this.request<GrGameRewardLimitResponse>(
      'PATCH',
      `/arches/game-rewards/admin/game-limits/${limitId}`,
      input,
    );
  }

  async deleteGameRewardsGameLimit(limitId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/game-limits/${limitId}`,
    );
  }

  // Admin - Tier Config
  async listGameRewardsAdminTiers() {
    return this.request<GrAdminTierListResult>('GET', '/arches/game-rewards/admin/tiers');
  }

  async createGameRewardsTier(input: {
    tier: string;
    displayName: string;
    currencyItemDefId: string;
    priceAmount: number;
    durationDays: number;
    description?: string;
  }) {
    return this.request<GrTierConfigResponse>('POST', '/arches/game-rewards/admin/tiers', input);
  }

  async updateGameRewardsTier(
    tierId: string,
    input: {
      displayName?: string;
      currencyItemDefId?: string;
      priceAmount?: number;
      durationDays?: number;
      description?: string;
      isActive?: boolean;
    },
  ) {
    return this.request<GrTierConfigResponse>(
      'PATCH',
      `/arches/game-rewards/admin/tiers/${tierId}`,
      input,
    );
  }

  // Admin - Subscriptions
  async listGameRewardsSubscriptions(options?: {
    userId?: string;
    isActive?: boolean;
    tier?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.userId) params.set('userId', options.userId);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.tier) params.set('tier', options.tier);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrAdminSubscriptionListResult>(
      'GET',
      `/arches/game-rewards/admin/subscriptions${qs ? `?${qs}` : ''}`,
    );
  }

  async adminSetGameRewardsSubscription(input: {
    username: string;
    tier: string;
    durationDays: number;
  }) {
    return this.request<GrSubscriptionResponse>(
      'POST',
      '/arches/game-rewards/admin/subscriptions',
      input,
    );
  }

  async adminRemoveGameRewardsSubscription(subscriptionId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/subscriptions/${subscriptionId}`,
    );
  }

  // Admin - Reward Log
  async listGameRewardsLog(options?: {
    /** Player-targeting filter — UIs send `username`; server resolves to userId. */
    username?: string;
    userId?: string;
    gameConfigId?: string;
    sinceHours?: number;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.username) params.set('username', options.username);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.sinceHours) params.set('sinceHours', String(options.sinceHours));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardLogListResult>(
      'GET',
      `/arches/game-rewards/admin/reward-log${qs ? `?${qs}` : ''}`,
    );
  }

  // User - Time Played
  async getGameRewardsTimePlayed() {
    return this.request<GrTimePlayedAggregates>('GET', '/arches/game-rewards/time-played');
  }

  // User - Leaderboard
  async getGameRewardsLeaderboard(
    gameConfigId: string,
    gameEvent: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrLeaderboardResult>(
      'GET',
      `/arches/game-rewards/leaderboard/${gameConfigId}/${gameEvent}${qs ? `?${qs}` : ''}`,
    );
  }

  // User - My Scores
  async getGameRewardsMyScores(options?: {
    gameConfigId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrUserBestScoresResult>(
      'GET',
      `/arches/game-rewards/my-scores${qs ? `?${qs}` : ''}`,
    );
  }

  // Public - Leaderboard Games
  async getGameRewardsLeaderboardGames() {
    return this.request<{ games: GrLeaderboardGameInfo[] }>(
      'GET',
      '/arches/game-rewards/leaderboard/games',
    );
  }

  // User - Games with tracked time (Time / Rewards metric-selector options)
  async getGameRewardsTimeGames() {
    return this.request<{ games: GrTrackedTimeGame[] }>(
      'GET',
      '/arches/game-rewards/leaderboard/time-games',
    );
  }

  // User - Time-played leaderboard for a game
  async getGameRewardsTimeLeaderboard(
    gameConfigId: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrTimePlayedLeaderboardResult>(
      'GET',
      `/arches/game-rewards/leaderboard/time/${gameConfigId}${qs ? `?${qs}` : ''}`,
    );
  }

  // User - Distinct reward types (selector options)
  async getGameRewardsRewardTypes(gameConfigId?: string) {
    const params = new URLSearchParams();
    if (gameConfigId) params.set('gameConfigId', gameConfigId);
    const qs = params.toString();
    return this.request<{ rewardTypes: string[] }>(
      'GET',
      `/arches/game-rewards/reward-types${qs ? `?${qs}` : ''}`,
    );
  }

  // User - "Top earners of X" leaderboard (global, or per-game via gameConfigId)
  async getGameRewardsRewardLeaderboard(
    rewardType: string,
    options?: { gameConfigId?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    params.set('rewardType', rewardType);
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    return this.request<GrRewardEarningsLeaderboardResult>(
      'GET',
      `/arches/game-rewards/leaderboard/rewards?${params.toString()}`,
    );
  }

  // User - Leaderboard anonymity preference
  async getGameRewardsLeaderboardAnonymity() {
    return this.request<{ anonymous: boolean }>(
      'GET',
      '/arches/game-rewards/leaderboard/anonymity',
    );
  }

  async setGameRewardsLeaderboardAnonymity(anonymous: boolean) {
    return this.request<{ anonymous: boolean }>(
      'PUT',
      '/arches/game-rewards/leaderboard/anonymity',
      { anonymous },
    );
  }

  // ==========================================================================
  // DFE Game State API
  // ==========================================================================

  async getGameState(keyword: string) {
    return this.request<{ gameState: GameStateResponse }>(
      'GET',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}`,
    );
  }

  async setGameState(
    keyword: string,
    body: {
      dataType: string;
      data?: string;
      dataBinary?: string;
      version?: number | null;
      checksum?: string;
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ gameState: GameStateResponse }>(
      'PUT',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}`,
      body,
    );
  }

  async deleteGameState(keyword: string, version: number) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}?version=${version}`,
    );
  }

  async listGameStates(options?: { limit?: number; offset?: number; keyword?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.keyword) params.set('keyword', options.keyword);
    const qs = params.toString();
    return this.request<GameStateListResult>(
      'GET',
      `/arches/dfe-game-state/states${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Admin User API
  // ==========================================================================

  async adminLookupUser(username: string) {
    return this.request<AdminUserLookupResult>(
      'GET',
      `/admin/users/lookup?username=${encodeURIComponent(username)}`,
    );
  }

  async adminLookupUserByEmail(email: string) {
    return this.request<AdminUserLookupResult>(
      'GET',
      `/admin/users/lookup?email=${encodeURIComponent(email)}`,
    );
  }

  async adminLookupUserById(userId: string) {
    return this.request<AdminUserLookupResult>(
      'GET',
      `/admin/users/lookup?userId=${encodeURIComponent(userId)}`,
    );
  }

  /**
   * Look up a user by whatever identifier a support agent has on hand.
   * Detects the kind from the shape of the input: an `@` means email, a
   * `usr_` prefix means userId, otherwise it's treated as a username.
   */
  async adminLookupUserByAnyIdentifier(identifier: string) {
    const trimmed = identifier.trim();
    if (trimmed.includes('@')) return this.adminLookupUserByEmail(trimmed);
    if (trimmed.startsWith('usr_')) return this.adminLookupUserById(trimmed);
    return this.adminLookupUser(trimmed);
  }

  // ==========================================================================
  // Admin Arche Icons API
  // ==========================================================================

  async adminGetArcheIconEnvironments() {
    return this.request<{
      environments: string[];
      active: { environment: string; bucket: string; cdnUrl: string } | null;
    }>('GET', '/admin/arches/icon/environments');
  }

  async adminGetArcheIconUploadUrls(archId: string, mimeType: string, sizeBytes: number) {
    return this.request<{
      uploadUrl: string;
      stagingKey: string;
      mimeType: string;
      environment: string;
      expiresAt: string;
    }>('POST', `/admin/arches/${encodeURIComponent(archId)}/icon/upload-urls`, {
      mimeType,
      sizeBytes,
    });
  }

  async adminConfirmArcheIcon(archId: string, stagingKey: string) {
    return this.request<{
      arche: { archId: string; iconKey: string; imageRef: string };
      environment: string;
      iconKey: string;
      imageRef: string;
      variantSizes: number[];
    }>('PUT', `/admin/arches/${encodeURIComponent(archId)}/icon/confirm`, { stagingKey });
  }

  async adminDeleteArcheIcon(archId: string) {
    return this.request<void>('DELETE', `/admin/arches/${encodeURIComponent(archId)}/icon`);
  }

  // ==========================================================================
  // Admin Arche Management API
  // ==========================================================================

  async adminGetFeaturedArches() {
    return this.request<{ featured: { archId: string; displayOrder: number }[] }>(
      'GET',
      '/admin/arche-management/featured',
    );
  }

  async adminSetFeaturedArches(archIds: string[]) {
    return this.request<{ featured: { archId: string; displayOrder: number }[] }>(
      'PUT',
      '/admin/arche-management/featured',
      { archIds },
    );
  }

  async adminGetSuggestedArches(archId: string) {
    return this.request<{ archId: string; suggestedArches: string[] }>(
      'GET',
      `/admin/arche-management/${encodeURIComponent(archId)}/suggested`,
    );
  }

  async adminSetSuggestedArches(archId: string, suggestedArches: string[]) {
    return this.request<{ archId: string; suggestedArches: string[] }>(
      'PUT',
      `/admin/arche-management/${encodeURIComponent(archId)}/suggested`,
      { suggestedArches },
    );
  }

  async adminGetArchesSummary(opts?: {
    limit?: number;
    lifecycle?: 'all' | ArcheLifecycleState;
    search?: string;
    /** Restrict to platform-built arches or to developer plugins. */
    kind?: ArcheKindFilter;
    /** Drop Fias-owned rows in SQL, so the page is over annexable arches. */
    excludePlatformOwned?: boolean;
    /** Only company-owned, or only restricted, arches (ADR-026). */
    governance?: ArcheGovernanceFilter;
  }) {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.lifecycle) params.set('lifecycle', opts.lifecycle);
    if (opts?.search) params.set('search', opts.search);
    if (opts?.kind) params.set('kind', opts.kind);
    if (opts?.excludePlatformOwned) params.set('excludePlatformOwned', 'true');
    if (opts?.governance && opts.governance !== 'all') params.set('governance', opts.governance);
    const qs = params.toString();
    return this.request<{
      arches: ArcheManagementSummary[];
      /**
       * Whether THIS deployment can honour a review-skip designation. False on
       * production, where the publish path ignores the flag outright — the
       * console hides the "Skip review" action rather than offering one that
       * 400s, but keeps "Require review" so an existing designation is always
       * clearable. Optional so an older API response reads as "not available"
       * rather than crashing the page.
       */
      reviewSkipAvailable?: boolean;
    }>('GET', `/admin/arche-management/arches-summary${qs ? `?${qs}` : ''}`);
  }

  /**
   * Designate (or un-designate) an arche as first-party, so admin publishes of
   * it skip the paid AI security review.
   *
   * `reason` is REQUIRED — unlike the lifecycle actions, where it is optional.
   * The designation is a security decision and the audit row has to answer
   * "why does this arche skip review?" on its own.
   */
  async adminSetArcheReviewSkip(archId: string, enabled: boolean, reason: string) {
    return this.request<{
      archId: string;
      skipAiReview: boolean;
      skipAiReviewSetBy: string | null;
      skipAiReviewSetAt: string | null;
    }>('POST', `/admin/arche-management/${encodeURIComponent(archId)}/review-skip`, {
      enabled,
      reason,
    });
  }

  /**
   * Designate a plugin arche to save into users' `Arche Saves/<label>/`
   * automatically (it must ALSO declare `vault:arche-saves:write`), issuing it
   * `label` — its own forever. Refusals: 409 `LABEL_TAKEN` / `LABEL_RESERVED`,
   * 400 `LABEL_INVALID` / `NOT_A_PLUGIN`. `reason` is required and audited.
   */
  async adminDesignateArcheSaves(archId: string, label: string, reason: string) {
    return this.request<{ archId: string; archeSavesLabel: string }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/arche-saves`,
      { label, reason },
    );
  }

  /**
   * Approve the file kinds a plugin may open from "Open in…" (ADR-030). Each
   * kind must be declared in its published manifest's `fileIntake` (400
   * `KIND_NOT_DECLARED`). `reason` is required and audited.
   */
  async adminSetArcheFileIntake(archId: string, kinds: FileIntakeKind[], reason: string) {
    return this.request<{
      archId: string;
      fileIntakeKinds: FileIntakeKind[];
      fileIntakeTypes: string[] | null;
    }>('POST', `/admin/arche-management/${encodeURIComponent(archId)}/file-intake`, {
      kinds,
      reason,
    });
  }

  /** Withdraw a plugin's file-intake approval. */
  async adminClearArcheFileIntake(archId: string, reason: string) {
    return this.request<{ archId: string; fileIntakeKinds: null }>(
      'DELETE',
      `/admin/arche-management/${encodeURIComponent(archId)}/file-intake`,
      { reason },
    );
  }

  /** Stop an arche's automatic Arche Saves writes. Its label stays its own. */
  async adminRevokeArcheSaves(archId: string, reason: string) {
    return this.request<{ archId: string; archeSavesLabel: null }>(
      'DELETE',
      `/admin/arche-management/${encodeURIComponent(archId)}/arche-saves`,
      { reason },
    );
  }

  async adminDisableArche(archId: string, reason?: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/disable`,
      reason ? { reason } : {},
    );
  }

  async adminEnableArche(archId: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/enable`,
    );
  }

  async adminDeleteArche(archId: string, reason?: string) {
    return this.request<{
      archId: string;
      hardDeleted: boolean;
      lifecycleState?: ArcheLifecycleState;
      isListed?: boolean;
    }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/delete`,
      reason ? { reason } : {},
    );
  }

  async adminRestoreArche(archId: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/restore`,
    );
  }

  async adminUnlistArche(archId: string, reason?: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/unlist`,
      reason ? { reason } : {},
    );
  }

  async adminListArche(archId: string) {
    return this.request<{ archId: string; lifecycleState: ArcheLifecycleState; isListed: boolean }>(
      'POST',
      `/admin/arche-management/${encodeURIComponent(archId)}/list`,
    );
  }

  // ==========================================================================
  // Admin Invite Codes API
  // ==========================================================================

  async adminGetInviteCodeEnvironments() {
    return this.request<{ environments: string[] }>('GET', '/admin/invite-codes/environments');
  }

  async adminCreateInviteCodes(options: {
    count?: number;
    expiresAt?: string;
    grantRoles?: string[];
    metadata?: { description?: string; notes?: string };
    environment?: string;
  }) {
    return this.request<{
      inviteCode?: InviteCodeResponse;
      inviteCodes?: InviteCodeResponse[];
      count?: number;
    }>('POST', '/admin/invite-codes', options);
  }

  async adminListInviteCodes(options?: { status?: string; limit?: number; environment?: string }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.environment) params.set('environment', options.environment);
    const qs = params.toString();
    return this.request<{ inviteCodes: InviteCodeResponse[]; count: number }>(
      'GET',
      `/admin/invite-codes${qs ? `?${qs}` : ''}`,
    );
  }

  async adminRevokeInviteCode(code: string, environment?: string) {
    const params = new URLSearchParams();
    if (environment) params.set('environment', environment);
    const qs = params.toString();
    return this.request<{ message: string; code: string }>(
      'DELETE',
      `/admin/invite-codes/${encodeURIComponent(code)}${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // DFE Game State Admin API
  //
  // Player-targeting paths use the canonical /by-username/:username shape per
  // the platform "username in UI, resolve userId server-side" convention.
  // ==========================================================================

  /**
   * Resolve a username to the identity fields the game-state admin UI needs.
   *
   * Distinct from `adminLookupUser`, which hits the platform's support-facing
   * `/admin/users/lookup` — that route is gated by `requireAdmin` (platform
   * `admin` only), so a `litcraft_admin` got a 403 on every search even though
   * they can reach every other page in this arche. This one is gated by
   * `requireAdminOrLitcraftAdmin` like the rest of the arche, and returns only
   * what the lookup page renders.
   */
  async adminLookupGameStateUser(username: string) {
    return this.request<DfeGameStateUserLookupResult>(
      'GET',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}`,
    );
  }

  async adminListGameStates(
    username: string,
    options?: { limit?: number; offset?: number; keyword?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.keyword) params.set('keyword', options.keyword);
    const qs = params.toString();
    return this.request<GameStateListResult & { username: string }>(
      'GET',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/states${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetGameState(username: string, keyword: string) {
    return this.request<{ gameState: GameStateResponse }>(
      'GET',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/state/${encodeURIComponent(keyword)}`,
    );
  }

  async adminSetGameState(
    username: string,
    keyword: string,
    body: {
      dataType: string;
      data?: string;
      dataBinary?: string;
      checksum?: string;
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ gameState: GameStateResponse }>(
      'PUT',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/state/${encodeURIComponent(keyword)}`,
      body,
    );
  }

  async adminDeleteGameState(username: string, keyword: string) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/admin/users/by-username/${encodeURIComponent(username)}/state/${encodeURIComponent(keyword)}`,
    );
  }

  async adminListGameStateAuditLog(options?: {
    username?: string;
    keyword?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.username) params.set('username', options.username);
    if (options?.keyword) params.set('keyword', options.keyword);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GameStateAuditLogResult>(
      'GET',
      `/arches/dfe-game-state/admin/audit${qs ? `?${qs}` : ''}`,
    );
  }

  // --------------------------------------------------------------------------
  // DFE Game State — Plugin Resource Grants (admin)
  // --------------------------------------------------------------------------

  async adminListGameStateGrants(options?: {
    archeId?: string;
    resourceType?: GsResourceType;
    reviewStatus?: GsGrantReviewStatus;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.resourceType) params.set('resourceType', options.resourceType);
    if (options?.reviewStatus) params.set('reviewStatus', options.reviewStatus);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GameStateGrantListResult>(
      'GET',
      `/arches/dfe-game-state/admin/grants${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
  ) {
    return this.request<{ grant: GameStateGrant }>(
      'GET',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}`,
    );
  }

  async adminUpsertGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
    access: GsAccessLevel,
  ) {
    return this.request<SetGameStateGrantResult>(
      'PUT',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}`,
      { access },
    );
  }

  async adminDeleteGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
  ) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}`,
    );
  }

  async adminApproveGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
  ) {
    return this.request<ApproveRejectGameStateGrantResult>(
      'POST',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}/approve`,
    );
  }

  async adminRejectGameStateGrant(
    archeId: string,
    resourceType: GsResourceType,
    resourceName: string,
    reason?: string,
  ) {
    return this.request<ApproveRejectGameStateGrantResult>(
      'POST',
      `/arches/dfe-game-state/admin/grants/${encodeURIComponent(archeId)}/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceName)}/reject`,
      reason ? { reason } : {},
    );
  }

  // --------------------------------------------------------------------------
  // DFE Game State — Global Config (admin)
  // --------------------------------------------------------------------------

  async adminListGameStateConfigs(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GameStateConfigListResult>(
      'GET',
      `/arches/dfe-game-state/admin/config${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetGameStateConfig(keyword: string) {
    return this.request<{ config: GameStateConfig }>(
      'GET',
      `/arches/dfe-game-state/admin/config/${encodeURIComponent(keyword)}`,
    );
  }

  async adminUpsertGameStateConfig(keyword: string, data: unknown, description?: string | null) {
    return this.request<{ config: GameStateConfig }>(
      'PUT',
      `/arches/dfe-game-state/admin/config/${encodeURIComponent(keyword)}`,
      { data, description: description ?? null },
    );
  }

  async adminDeleteGameStateConfig(keyword: string) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/admin/config/${encodeURIComponent(keyword)}`,
    );
  }

  // ==========================================================================
  // Polls API
  // ==========================================================================

  async createPoll(input: CreatePollPayload) {
    return this.request<PollDetailResponse>('POST', '/arches/polls', input);
  }

  async listMyPolls(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<PollListResult>('GET', `/arches/polls${qs ? `?${qs}` : ''}`);
  }

  async getPoll(pollId: string) {
    return this.request<PollFullDetailResponse>('GET', `/arches/polls/${pollId}`);
  }

  async updatePoll(pollId: string, input: UpdatePollPayload) {
    return this.request<PollDetailResponse>('PUT', `/arches/polls/${pollId}`, input);
  }

  async deletePoll(pollId: string) {
    return this.request<void>('DELETE', `/arches/polls/${pollId}`);
  }

  async publishPoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/publish`,
    );
  }

  async closePoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/close`,
    );
  }

  async reopenPoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/reopen`,
    );
  }

  async submitPollResponse(pollId: string, input: { answers: PollAnswerPayload[] }) {
    return this.request<{ responseId: string; pollId: string; submittedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/respond`,
      input,
    );
  }

  async getMyPollResponse(pollId: string) {
    return this.request<PollResponseResult>('GET', `/arches/polls/${pollId}/my-response`);
  }

  async getPollResults(pollId: string) {
    return this.request<PollResultsResponse>('GET', `/arches/polls/${pollId}/results`);
  }

  async viewPoll(pollId: string) {
    return this.request<PollPublicViewResponse>('GET', `/arches/polls/${pollId}/view`);
  }

  // ==========================================================================
  // Plugin Submission API
  // ==========================================================================

  async getPluginUploadUrl(body: { fileName: string; sizeBytes: number; checksumSha256?: string }) {
    return this.request<{
      uploadUrl: string;
      submissionId: string;
      s3Key: string;
      expiresAt: string;
    }>('POST', '/plugins/submissions/upload-url', body);
  }

  async createPluginSubmission(body: {
    submissionId: string;
    sourceRef: string;
    manifest: Record<string, unknown>;
    archId?: string;
  }) {
    return this.request<{
      submissionId: string;
      archId: string;
      reviewId: string;
      status: string;
      createdAt: string;
    }>('POST', '/plugins/submissions', body);
  }

  async listPluginSubmissions(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<PluginSubmissionListResult>(
      'GET',
      `/plugins/submissions${query ? `?${query}` : ''}`,
    );
  }

  async getPluginSubmission(submissionId: string) {
    return this.request<PluginSubmissionDetailResult>(
      'GET',
      `/plugins/submissions/${submissionId}`,
    );
  }

  async deletePluginSubmission(submissionId: string) {
    return this.request<{ success: boolean; submissionId: string }>(
      'DELETE',
      `/plugins/submissions/${submissionId}`,
    );
  }

  // ========================================================================
  // Fias Forge (ADR-023) — hosted Claude Code build environments.
  // User routes live under /v1/forge/forges; every :forgeId route is
  // owner-scoped (404, never 403, for someone else's forge).
  // ========================================================================

  /**
   * `includeShared` also returns `shared`: the forges of arches the caller is
   * an active collaborator on (any role), projected to the team-visible
   * fields — enough to open the test build, never the owner's controls.
   */
  async listForges(options?: {
    includeArchived?: boolean;
    includeShared?: boolean;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.includeArchived) params.set('includeArchived', 'true');
    if (options?.includeShared) params.set('includeShared', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<{ forges: ForgeSummary[]; shared?: SharedForgeSummary[] }>(
      'GET',
      `/forge/forges${query ? `?${query}` : ''}`,
    );
  }

  /**
   * An arche's forge as its team sees it (contributor or active collaborator;
   * 404 `ARCHE_NOT_FOUND` for anyone else, `FORGE_NOT_FOUND` when the arche
   * has no live forge). Read-only: the id, status and current test build,
   * which is what an arche page needs to offer "open the test build".
   */
  async getForgeByArche(archeId: string) {
    return this.request<ForgeByArcheResult>('GET', `/forge/forges/by-arche/${archeId}`);
  }

  /**
   * Creates a draft arche + its forge. `name` is the arche's display name;
   * `size` defaults to `standard` (`large` is Pro-only → 403 FORGE_SIZE_REQUIRES_PRO).
   */
  async createForge(input: { name: string; size?: ForgeSize }) {
    return this.request<{
      forge: ForgeSummary;
      arche: { archId: string; name: string; slug: string | null; status: string };
    }>('POST', '/forge/forges', input);
  }

  async getForge(forgeId: string) {
    return this.request<ForgeDetailResult>('GET', `/forge/forges/${forgeId}`);
  }

  /**
   * PATCH — the model tier opt-in (`null` = tier default; `frontier` is
   * Pro-only → 403 FORGE_MODEL_TIER_REQUIRES_PRO) and/or the task size
   * (only while stopped/failed → 409 FORGE_NOT_STOPPED; `large` is Pro-only
   * → 403 FORGE_SIZE_REQUIRES_PRO). At least one field.
   */
  async updateForge(
    forgeId: string,
    input: { modelOptIn?: ForgeModelTier | null; size?: ForgeSize; name?: string },
  ) {
    return this.request<{ forge: ForgeSummary }>('PATCH', `/forge/forges/${forgeId}`, input);
  }

  async startForge(forgeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', `/forge/forges/${forgeId}/start`);
  }

  async stopForge(forgeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', `/forge/forges/${forgeId}/stop`);
  }

  /**
   * Deletes the forge (ADR-023 A8): stops its runtime if one is running and
   * removes the workspace. The arche is NOT deleted — a published arche stays
   * live. Idempotent. `storagePurged: false` means the server finishes the
   * cleanup later; the forge is gone from the user's point of view either way.
   */
  async deleteForge(forgeId: string) {
    return this.request<{ deleted: true; storagePurged: boolean; archeUnpublished: boolean }>(
      'DELETE',
      `/forge/forges/${forgeId}`,
    );
  }

  /**
   * Opens a turn. The browser subscribes to `requestId` on the WebSocket
   * exactly like the Arche Builder; the runtime's events arrive as `token`,
   * `activity`, `tool_call`, `tool_result`, `forge_screenshot`, `error` and
   * `complete` frames. Idempotent on `requestId` (`created: false` on replay).
   */
  async sendForgeTurn(
    forgeId: string,
    input: { prompt: string; requestId?: string; planMode?: boolean },
  ) {
    return this.request<{
      requestId: string;
      turnId: string;
      created: boolean;
      turn: ForgeTurnSummary;
    }>('POST', `/forge/forges/${forgeId}/turns`, input);
  }

  async stopForgeTurn(forgeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', `/forge/forges/${forgeId}/turns/stop`);
  }

  /** Answer the question Claude is waiting on (question text → answer). */
  async answerForgeQuestion(
    forgeId: string,
    turnId: string,
    body: { questionId: string; answers: Record<string, string> },
  ) {
    return this.request<{ turn: ForgeTurnSummary }>(
      'POST',
      `/forge/forges/${forgeId}/turns/${turnId}/answer`,
      body,
    );
  }

  /**
   * Approve the plan Claude is waiting on, or send it back with what to change
   * (ExitPlanMode, ADR-023 A14).
   */
  async decideForgePlan(
    forgeId: string,
    turnId: string,
    body: { questionId: string; approved: boolean; feedback?: string },
  ) {
    return this.request<{ turn: ForgeTurnSummary }>(
      'POST',
      `/forge/forges/${forgeId}/turns/${turnId}/plan`,
      body,
    );
  }

  async getForgePublishSettings(forgeId: string) {
    return this.request<ForgePublishSettingsView>(
      'GET',
      `/forge/forges/${forgeId}/publish-settings`,
    );
  }

  async saveForgePublishSettings(forgeId: string, settings: ForgePublishOptions) {
    return this.request<ForgePublishSettingsView>(
      'PUT',
      `/forge/forges/${forgeId}/publish-settings`,
      settings,
    );
  }

  async getForgeIconUploadUrl(forgeId: string, mimeType: string, sizeBytes: number) {
    return this.request<{ uploadUrl: string; stagingKey: string }>(
      'POST',
      `/forge/forges/${forgeId}/icon/upload-url`,
      { mimeType, sizeBytes },
    );
  }

  async confirmForgeIcon(forgeId: string, stagingKey: string) {
    return this.request<ForgePublishSettingsView>('PUT', `/forge/forges/${forgeId}/icon/confirm`, {
      stagingKey,
    });
  }

  async removeForgeIcon(forgeId: string) {
    return this.request<ForgePublishSettingsView>('DELETE', `/forge/forges/${forgeId}/icon`);
  }

  async submitForgeProblemReport(
    forgeId: string,
    body: {
      description: string;
      problemScope: 1 | 3 | 5 | 10;
      conversationHistory: Array<{
        role: 'user' | 'assistant';
        content: string;
        timestamp?: string;
      }>;
      toolEvents?: unknown;
      telemetryState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/forge/forges/${forgeId}/report-problem`,
      body,
    );
  }

  async getForgeCost(forgeId: string) {
    return this.request<{ cost: ForgeCost }>('GET', `/forge/forges/${forgeId}/cost`);
  }

  async listForgeTurns(forgeId: string, options?: { limit?: number }) {
    const query = options?.limit ? `?limit=${options.limit}` : '';
    return this.request<{ turns: ForgeTurnSummary[] }>(
      'GET',
      `/forge/forges/${forgeId}/turns${query}`,
    );
  }

  /** Queues a test-channel artifact build (never publishes). */
  async requestForgeTestBuild(forgeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', `/forge/forges/${forgeId}/test-build`);
  }

  async listForgeTestBuilds(forgeId: string, options?: { limit?: number }) {
    const query = options?.limit ? `?limit=${options.limit}` : '';
    return this.request<{ builds: ForgeTestBuildSummary[] }>(
      'GET',
      `/forge/forges/${forgeId}/test-builds${query}`,
    );
  }

  /**
   * Queues `publish` on the runtime: the controller packages the workspace
   * and submits it through the production publish pipeline. 202; the
   * submission id surfaces on `getForge` (`lastSubmissionId`) once the
   * runtime has submitted, and the page polls `getPluginSubmission` from
   * there. `carryTestData` asks THIS publish to copy the test channel's
   * DataStore rows and files into the live arche (first publish only; the
   * server skips a live arche that already has data).
   */
  async publishForge(
    forgeId: string,
    input: { carryTestData: boolean } = { carryTestData: false },
  ) {
    return this.request<{ forge: ForgeSummary }>('POST', `/forge/forges/${forgeId}/publish`, {
      carryTestData: input.carryTestData === true,
    });
  }

  /**
   * The current test build for an arche, as the test-channel host needs it:
   * the permission snapshot the bridge token is minted from and the build
   * hash the bundle is cache-keyed on. Owner-only; 404 `NO_TEST_BUILD` when
   * the forge has not produced a ready build yet.
   */
  async getForgeTestChannel(archeId: string) {
    return this.request<ForgeTestChannelInfo>('GET', `/forge/test-channel/${archeId}`);
  }

  /**
   * Move an Arche Builder session into a new forge (ADR-023 D6). ONE-WAY:
   * on success the session is read-only history (every builder mutation
   * route answers 409 SESSION_MIGRATED) and the arche lives in the forge.
   * 409 SESSION_ALREADY_MIGRATED / FORGE_EXISTS carry `forgeId` in the body.
   */
  async importForgeFromBuilder(sessionId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', '/forge/forges/import/builder', {
      sessionId,
    });
  }

  /**
   * Create a forge from a CLI-published plugin's last submitted source. The
   * caller must be the arche's contributor; the CLI stays a valid path.
   * 404 NO_SOURCE_AVAILABLE when nothing was ever submitted for it.
   */
  async importForgeFromPlugin(archeId: string) {
    return this.request<{ forge: ForgeSummary }>('POST', '/forge/forges/import/plugin', {
      archeId,
    });
  }

  /**
   * The caller's own Forge meter readings (ADR-023 D8): active seconds this
   * UTC month against the tier's included hours and hard cap, snapshot
   * storage, credits the daily settlement has collected this month, and any
   * hold (billing: a charge is failing; user: an admin decision).
   */
  async getForgeUsage() {
    return this.request<ForgeUsage>('GET', '/forge/usage');
  }

  // ========================================================================
  // Plugin Admin API
  // ========================================================================

  async listEscalatedPluginReviews(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      reviews: Array<{
        reviewId: string;
        submissionId: string;
        archId: string;
        manifestName: string;
        riskScore: number | null;
        criticalCount: number;
        highCount: number;
        filesAnalyzed: number;
        createdAt: string;
        /** Forensic publisher (may differ from the arche's financial owner). */
        submittedBy: string;
        /** Resolved server-side; null when the identity lookup is unavailable. */
        submittedByUsername: string | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/admin/plugins/reviews/escalated${qs ? `?${qs}` : ''}`);
  }

  /** Count of arches awaiting human review — for the admin "needs review" alert. */
  async getEscalatedPluginReviewCount() {
    return this.request<{ count: number }>('GET', '/admin/plugins/reviews/escalated/count');
  }

  async getPluginReviewDetail(reviewId: string) {
    return this.request<{
      review: {
        reviewId: string;
        submissionId: string;
        reviewerType: string;
        status: string;
        riskScore: number | null;
        codeFindings: unknown[] | null;
        dependencyFindings: unknown[] | null;
        policyFindings: unknown[] | null;
        qualityFindings: unknown[] | null;
        criticalCount: number;
        highCount: number;
        filesAnalyzed: number;
        dependenciesAnalyzed: number;
        reviewedBy: string | null;
        humanNotes: string | null;
        createdAt: string;
        completedAt: string | null;
      };
      submission: {
        submissionId: string;
        archId: string;
        manifestName: string;
        manifestVersion: string;
        status: string;
        sourceRef: string;
        createdAt: string;
      } | null;
    }>('GET', `/admin/plugins/reviews/${reviewId}`);
  }

  async approvePluginReview(reviewId: string, notes?: string) {
    return this.request<{ success: boolean; reviewId: string; status: string }>(
      'POST',
      `/admin/plugins/reviews/${reviewId}/approve`,
      { notes },
    );
  }

  async rejectPluginReview(reviewId: string, notes: string) {
    return this.request<{ success: boolean; reviewId: string; status: string }>(
      'POST',
      `/admin/plugins/reviews/${reviewId}/reject`,
      { notes },
    );
  }

  /** A short-lived presigned URL for the archive a submission was reviewed from. */
  async getPluginReviewSourceDownload(reviewId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string; fileName: string }>(
      'GET',
      `/admin/plugins/reviews/${reviewId}/source`,
    );
  }

  /**
   * Close an escalated review that a later submission of the same arche made
   * moot. The server verifies the later submission exists (409 NOT_SUPERSEDED
   * otherwise) and sends the contributor nothing.
   */
  async closeSupersededPluginReview(reviewId: string) {
    return this.request<{
      success: boolean;
      reviewId: string;
      status: string;
      supersededBy: SupersedingSubmission;
    }>('POST', `/admin/plugins/reviews/${reviewId}/close-superseded`);
  }

  // ==========================================================================
  // Contributor Asset Library API
  // ==========================================================================

  async getArcheAssetLibraryConfig(archeId: string) {
    return this.request<{
      config: {
        enabled: boolean;
        suspendedAt: string | null;
        suspendReason: string | null;
        effectiveCaps: {
          maxAssets: number;
          maxStorageBytes: number;
          maxUploadBytes: number;
          maxDecodedPixels: number;
        };
        usage: { activeStorageBytes: number; activeAssetCount: number };
      };
    }>('GET', `/contributor/arches/${archeId}/asset-library/config`);
  }

  async enableArcheAssetLibrary(archeId: string) {
    return this.request<{ config: ContributorAssetLibraryConfig }>(
      'POST',
      `/contributor/arches/${archeId}/asset-library/enable`,
    );
  }

  async disableArcheAssetLibrary(archeId: string) {
    return this.request<{ config: ContributorAssetLibraryConfig }>(
      'POST',
      `/contributor/arches/${archeId}/asset-library/disable`,
    );
  }

  /**
   * Queue an AI regeneration of the arche's help articles (the contributor's
   * high-level documentation command — article bodies are always written by
   * the platform generator, never by hand). Billed to the requesting
   * collaborator; owner/publisher-gated server-side.
   */
  async refreshArcheDocumentation(archeId: string, instruction?: string) {
    return this.request<{ success: boolean; status: 'queued' }>(
      'POST',
      `/contributor/arches/${archeId}/documentation/refresh`,
      instruction ? { instruction } : {},
    );
  }

  /** Content-pack storage + bandwidth bill (ADR-021 §11). 403 unless the caller pays or owns the team. */
  async getContentPackBilling(archeId: string) {
    return this.request<ContentPackBillingResponse>(
      'GET',
      `/contributor/arches/${archeId}/content-billing`,
    );
  }

  /** Daily bandwidth alert, in bytes; null restores the platform default. Payer only. */
  async setContentPackAlertThreshold(archeId: string, thresholdBytes: number | null) {
    return this.request<ContentPackBillingResponse>(
      'PUT',
      `/contributor/arches/${archeId}/content-billing/alert-threshold`,
      { thresholdBytes },
    );
  }

  /** Opt-in hard daily cap, in bytes; null turns it off. Payer only. */
  async setContentPackHardCap(archeId: string, capBytes: number | null) {
    return this.request<ContentPackBillingResponse>(
      'PUT',
      `/contributor/arches/${archeId}/content-billing/hard-cap`,
      { capBytes },
    );
  }

  async getArcheAssetBillingSummary(archeId: string) {
    return this.request<{
      summary: {
        yesterday: {
          storageChargeCredits: number;
          egressChargeCredits: number;
          storageByteDays: number;
          egressBytes: number;
          chargeStatus: string;
        } | null;
        monthToDate: {
          storageChargeCredits: number;
          egressChargeCredits: number;
          daysCharged: number;
          daysFailed: number;
        };
      };
    }>('GET', `/contributor/arches/${archeId}/billing/summary`);
  }

  /**
   * `source: 'shared'` returns ONLY the active assets of every workspace
   * library the arche is validly linked to (what a publish would pin from
   * workspaces) — never the arche's own rows; every row carries `holderId`.
   * Omit for the arche's own library only.
   */
  async listArcheAssets(
    archeId: string,
    opts?: {
      status?: string;
      tag?: string;
      cursor?: string;
      limit?: number;
      source?: 'own' | 'shared';
      sort?: AssetListSortValue;
      q?: string;
    },
  ) {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.q) params.set('q', opts.q);
    if (opts?.source) params.set('source', opts.source);
    const qs = params.toString();
    return this.request<{ assets: ContributorArcheAsset[]; nextCursor: string | null }>(
      'GET',
      `/contributor/arches/${archeId}/assets${qs ? `?${qs}` : ''}`,
    );
  }

  /**
   * Workspace libraries this arche is linked to, each with its derived
   * validity, plus the caller's workspaces that could be linked. Gated on
   * the collaborator model (`canPublishArche`), not contributor equality.
   */
  async getArcheWorkspaceLinks(archeId: string) {
    return this.request<ArcheWorkspaceLinksResult>(
      'GET',
      `/contributor/arches/${archeId}/workspaces`,
    );
  }

  // ==========================================================================
  // Admin — Fias-owned (platform-owned) plugins
  //
  // Server gate: `requireAdmin` on /v1/admin/plugins
  // (routes/plugins/platform-owned-admin.ts for the two reads;
  //  routes/plugins/ownership-admin.ts for the maintainer mutations).
  // ==========================================================================

  /** A page of Fias-owned plugin arches, newest first. */
  async listPlatformOwnedPlugins(opts?: {
    lifecycle?: 'all' | 'active' | 'disabled' | 'deleted';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (opts?.lifecycle) params.set('lifecycle', opts.lifecycle);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return this.request<PlatformOwnedPluginsResult>(
      'GET',
      `/admin/plugins/platform-owned${qs ? `?${qs}` : ''}`,
    );
  }

  /** Collaborator roster of one Fias-owned plugin (includes revoked/expired). */
  async listPluginMaintainers(archeId: string) {
    return this.request<{
      archeId: string;
      /** Scoped to the caller: which of this arche's links break if they leave. */
      removalImpact: MaintainerRemovalImpact[];
      maintainers: PluginMaintainer[];
    }>('GET', `/admin/plugins/${archeId}/maintainers`);
  }

  /**
   * Grants `publisher` on a Fias-owned plugin. This is what makes a workspace
   * asset-library link qualify — ADR-019 asks whether some active workspace
   * member can publish the arche.
   */
  async addPluginMaintainer(archeId: string, username: string) {
    return this.request<{
      archeId: string;
      userId: string;
      role: 'publisher';
      reactivated: boolean;
    }>('POST', `/admin/plugins/${archeId}/maintainers`, { username });
  }

  async removePluginMaintainer(archeId: string, username: string) {
    return this.request<{ archeId: string; userId: string; changed: boolean }>(
      'DELETE',
      `/admin/plugins/${archeId}/maintainers/${encodeURIComponent(username)}`,
    );
  }

  /**
   * Annex a developer-published plugin into Fias ownership, in place
   * (docs/plugin-annexation.md). `reason` is recorded in the audit row;
   * `consentRef` is the optional agreement / ticket reference.
   *
   * Errors propagate from the gated route through the global handler, so a
   * double-annex arrives as a bare 409 `CONFLICT` — key on status, not code.
   */
  async annexPlugin(archeId: string, input: { reason: string; consentRef?: string }) {
    return this.request<{
      archeId: string;
      contributorId: string;
      priorContributorId: string;
      transferredAt: string;
    }>('POST', `/admin/plugins/${encodeURIComponent(archeId)}/annex`, input);
  }

  /**
   * Set one account's role on a Fias-owned plugin: Read (`viewer`) or Publish
   * (`publisher`). An upsert — grants, reactivates, or changes the role as
   * needed; `changed: false` when the account already held that role.
   * A 409 means the roster moved under you (or the target is the Fias owner
   * row); refetch the roster and re-choose.
   */
  async setPluginTeamMember(archeId: string, input: { identifier: string; role: PluginTeamRole }) {
    return this.request<{
      archeId: string;
      userId: string;
      role: PluginTeamRole;
      previousRole: 'owner' | 'publisher' | 'viewer' | null;
      /** Echoed so a time-bound grant is never reported as simply "held". */
      expiresAt: string | null;
      reactivated: boolean;
      changed: boolean;
    }>('PUT', `/admin/plugins/${encodeURIComponent(archeId)}/team`, input);
  }

  /**
   * The account that pays for every Fias-owned plugin — publish-time docs and
   * embeddings, content packs, asset-library storage and egress. `userId` is
   * null while none is assigned (those charges then fail or are skipped;
   * nothing is ever platform-paid).
   */
  async getFiasPluginBillingAccount() {
    return this.request<{ billingAccount: FiasPluginBillingAccount }>(
      'GET',
      '/admin/plugins/billing-account',
    );
  }

  /**
   * Assign the Fias plugin billing account by user id, username or email. A
   * 400 `INVALID_BILLING_ACCOUNT` means the account cannot pay (a platform
   * account, a guest, or no credit account); 404 `USER_NOT_FOUND` that the
   * identifier names nobody.
   */
  async setFiasPluginBillingAccount(identifier: string) {
    return this.request<{ billingAccount: FiasPluginBillingAccount }>(
      'PUT',
      '/admin/plugins/billing-account',
      { identifier },
    );
  }

  /**
   * Revoke an existing collaborator row on a Fias-owned plugin by its stored
   * user id. The id is taken verbatim server-side — never re-resolved through
   * the users table — so a row whose account profile is gone can still be
   * removed. Idempotent.
   */
  async removePluginTeamMember(archeId: string, userId: string) {
    return this.request<{ archeId: string; userId: string; changed: boolean }>(
      'DELETE',
      `/admin/plugins/${encodeURIComponent(archeId)}/team/${encodeURIComponent(userId)}`,
    );
  }

  async initArcheAssetUpload(
    archeId: string,
    body: {
      contentType: ContributorArcheAssetContentType;
      sizeBytes: number;
      name?: string;
      altText?: string;
      tags?: string[];
    },
  ) {
    return this.request<{
      assetId: string;
      uploadUrl: string;
      expiresAt: string;
      storageKey: string;
    }>('POST', `/contributor/arches/${archeId}/assets/upload-init`, body);
  }

  async finalizeArcheAsset(
    archeId: string,
    assetId: string,
    body: { name?: string | null; altText?: string | null; tags?: string[] },
  ) {
    return this.request<{ asset: ContributorArcheAsset }>(
      'POST',
      `/contributor/arches/${archeId}/assets/${assetId}/finalize`,
      body,
    );
  }

  async patchArcheAsset(
    archeId: string,
    assetId: string,
    body: { name?: string | null; altText?: string | null; tags?: string[] },
  ) {
    return this.request<{ asset: ContributorArcheAsset }>(
      'PATCH',
      `/contributor/arches/${archeId}/assets/${assetId}`,
      body,
    );
  }

  async softDeleteArcheAsset(archeId: string, assetId: string) {
    return this.request<{ ok: true }>(
      'POST',
      `/contributor/arches/${archeId}/assets/${assetId}/soft-delete`,
    );
  }

  /**
   * Snapshot the arche's current active assets into its published
   * manifest — the manifest is what the plugin runtime reads, so this is
   * what makes new uploads visible to a LIVE published arche.
   *
   * Publishing the arche does this automatically (every publish
   * re-snapshots). This is the standalone path for a contributor who
   * uploaded art and does not want to republish code, and it is the only
   * browser-reachable equivalent of `fias-dev assets publish --refresh`.
   *
   * `refresh: true` re-snapshots an existing manifest in place, keeping
   * its manifest_id so signed URLs already in flight stay valid.
   */
  async snapshotArcheAssetManifest(archeId: string, refresh = true) {
    return this.request<{
      manifest: {
        manifestId: string;
        archeId: string;
        assetCount: number;
        totalBytes: number;
        createdAt: string;
      };
      preexisting: boolean;
      refreshed: boolean;
      /** Workspace libraries this version now serves from (ADR-019). */
      pinnedHolderIds: string[];
      /** Workspace libraries this version stopped serving (link no longer valid). */
      droppedHolderIds: string[];
      /** Validly linked workspaces that have never published a library version. */
      unpublishedHolderIds: string[];
    }>('POST', `/contributor/arches/${archeId}/manifests/snapshot`, { refresh });
  }

  /**
   * Build a contributor-only preview URL for an active asset.
   * Used by the portal grid to render thumbnails for assets that
   * aren't published yet. Auth via session cookie (caller must own
   * the arche).
   */
  buildArcheAssetPreviewUrl(archeId: string, assetId: string): string {
    return `${API_BASE}/contributor/arches/${archeId}/assets/${assetId}/preview`;
  }

  // ==========================================================================
  // Workspace (org Holder) Shared Asset Library API (ADR-019)
  //
  // Mounted at /v1/contributor/workspaces/:holderId/... — the "workspace" is
  // the existing Vault org Holder; membership is managed by the Vault
  // identity routes and the Workspace controls page, never here.
  // ==========================================================================

  async getWorkspaceAssetLibraryConfig(holderId: string) {
    return this.request<WorkspaceAssetLibraryConfigResult>(
      'GET',
      `/contributor/workspaces/${holderId}/asset-library/config`,
    );
  }

  async enableWorkspaceAssetLibrary(holderId: string) {
    return this.request<{ config: ContributorAssetLibraryConfig }>(
      'POST',
      `/contributor/workspaces/${holderId}/asset-library/enable`,
    );
  }

  async disableWorkspaceAssetLibrary(holderId: string) {
    return this.request<{ config: ContributorAssetLibraryConfig }>(
      'POST',
      `/contributor/workspaces/${holderId}/asset-library/disable`,
    );
  }

  async updateWorkspaceAssetLibrarySettings(holderId: string, body: { membersCanLink: boolean }) {
    return this.request<{ library: WorkspaceAssetLibrarySummary | null }>(
      'PATCH',
      `/contributor/workspaces/${holderId}/asset-library/settings`,
      body,
    );
  }

  async listWorkspaceAssets(
    holderId: string,
    opts?: {
      status?: string;
      tag?: string;
      cursor?: string;
      limit?: number;
      sort?: AssetListSortValue;
      q?: string;
    },
  ) {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.q) params.set('q', opts.q);
    const qs = params.toString();
    return this.request<{ assets: ContributorArcheAsset[]; nextCursor: string | null }>(
      'GET',
      `/contributor/workspaces/${holderId}/assets${qs ? `?${qs}` : ''}`,
    );
  }

  async initWorkspaceAssetUpload(
    holderId: string,
    body: {
      contentType: ContributorArcheAssetContentType;
      sizeBytes: number;
      name?: string;
      altText?: string;
      tags?: string[];
    },
  ) {
    return this.request<{
      assetId: string;
      uploadUrl: string;
      expiresAt: string;
      storageKey: string;
    }>('POST', `/contributor/workspaces/${holderId}/assets/upload-init`, body);
  }

  async finalizeWorkspaceAsset(
    holderId: string,
    assetId: string,
    body: { name?: string | null; altText?: string | null; tags?: string[] },
  ) {
    return this.request<{ asset: ContributorArcheAsset }>(
      'POST',
      `/contributor/workspaces/${holderId}/assets/${assetId}/finalize`,
      body,
    );
  }

  async patchWorkspaceAsset(
    holderId: string,
    assetId: string,
    body: { name?: string | null; altText?: string | null; tags?: string[] },
  ) {
    return this.request<{ asset: ContributorArcheAsset }>(
      'PATCH',
      `/contributor/workspaces/${holderId}/assets/${assetId}`,
      body,
    );
  }

  async softDeleteWorkspaceAsset(holderId: string, assetId: string) {
    return this.request<{ ok: true }>(
      'POST',
      `/contributor/workspaces/${holderId}/assets/${assetId}/soft-delete`,
    );
  }

  /** Member preview URL for an active workspace asset (session-cookie auth). */
  buildWorkspaceAssetPreviewUrl(holderId: string, assetId: string): string {
    return `${API_BASE}/contributor/workspaces/${holderId}/assets/${assetId}/preview`;
  }

  /**
   * Publish the workspace library: snapshots the current active assets into
   * a new immutable library version. Arches pin the LATEST version at their
   * own publish, so uploads after this call only affect future pins.
   */
  async snapshotWorkspaceAssetManifest(holderId: string) {
    return this.request<{
      manifest: {
        hmManifestId: string;
        holderId: string;
        assetCount: number;
        totalBytes: number;
        createdAt: string;
      };
      /** Older versions no arche pinned any more, garbage-collected in the same call. */
      gcManifestIds: string[];
    }>('POST', `/contributor/workspaces/${holderId}/manifests/snapshot`);
  }

  /**
   * Re-pin the arches that ALREADY serve this workspace's library onto its
   * latest published version, without asking each contributor to republish.
   *
   * Fast-forward only: an arche whose pinned version holds an asset the latest
   * one dropped is skipped, because moving its pin would 404 that asset in a
   * live plugin. An arche that does not already pin this workspace is also left
   * alone — a pin lands at the arche's own publish, and that is its publisher's
   * consent. Every arche comes back with an outcome; one failure does not
   * abort the rest.
   */
  async refreshWorkspaceAssetPins(holderId: string) {
    return this.request<{
      results: WorkspacePinRefreshResult[];
      /** True when the workspace has more linked arches than one run touches. */
      truncated: boolean;
    }>('POST', `/contributor/workspaces/${holderId}/pins/refresh`);
  }

  async listWorkspaceAssetManifests(holderId: string) {
    return this.request<{ manifests: WorkspaceAssetManifest[] }>(
      'GET',
      `/contributor/workspaces/${holderId}/manifests`,
    );
  }

  async listWorkspaceLinkedArches(holderId: string) {
    return this.request<{ links: WorkspaceLinkedArche[] }>(
      'GET',
      `/contributor/workspaces/${holderId}/arches`,
    );
  }

  async linkArcheToWorkspace(holderId: string, archeId: string) {
    return this.request<{
      link: {
        archeId: string;
        archeName: string;
        linkedAt: string;
        validity: WorkspaceLinkValidity;
      };
    }>('POST', `/contributor/workspaces/${holderId}/arches/${archeId}`);
  }

  /**
   * `soft` (default) — the link stops qualifying for future publishes; live
   * published versions keep serving until the arche republishes, and the
   * workspace keeps paying for the pinned bytes until then.
   * `hard` (workspace owner/admin only) — deletes the pins too, so live
   * versions stop serving the shared assets immediately.
   */
  async revokeWorkspaceArcheLink(
    holderId: string,
    archeId: string,
    body: { mode: 'soft' | 'hard'; reason?: string },
  ) {
    return this.request<{ ok: true; changed: boolean; unpinned: boolean }>(
      'DELETE',
      `/contributor/workspaces/${holderId}/arches/${archeId}`,
      body,
    );
  }

  async getWorkspaceAssetUsage(holderId: string) {
    return this.request<WorkspaceAssetUsage>('GET', `/contributor/workspaces/${holderId}/usage`);
  }

  /** Workspace owner only. */
  async getWorkspaceAssetBillingSummary(holderId: string) {
    return this.request<WorkspaceAssetBillingSummary>(
      'GET',
      `/contributor/workspaces/${holderId}/billing/summary`,
    );
  }

  // ==========================================================================
  // OAuth Consent API
  // ==========================================================================

  async getOAuthAuthorizeInfo(params: Record<string, string>) {
    const qs = new URLSearchParams(params).toString();
    return this.request<OAuthAuthorizeInfo>('GET', `/oauth/authorize?${qs}`);
  }

  async submitOAuthConsent(params: {
    clientId: string;
    redirectUri: string;
    responseType: string;
    scope: string;
    state: string;
    codeChallenge: string;
    codeChallengeMethod: string;
    consent: 'granted' | 'denied';
  }) {
    return this.request<{ redirectUrl: string }>('POST', '/oauth/authorize', params);
  }

  // ==========================================================================
  // Connected Apps API
  // ==========================================================================

  /** What this host is (ADR-024 §4.11): the platform, or a Company portal. */
  async getHostProfile() {
    return this.request<HostProfileDto>('GET', '/public/company-web/host-profile');
  }

  // ── Signing in ON a Company portal (ADR-024 rev 5, W5c) ───────────────────

  async getPortalSignInOptions() {
    return this.request<PortalSignInOptionsDto>('GET', '/auth/portal/sign-in-options');
  }

  /** Always `{ sent: true }`, whether or not an account exists. */
  async startPortalEmailCode(email: string, returnTo: string) {
    return this.request<{ sent: true }>('POST', '/auth/portal/code', { email, returnTo });
  }

  /** Answers the emailed code — a sign-in or sign-up code alike (the server knows which it sent). */
  async verifyPortalEmailCode(code: string, disclosureVersion: number) {
    return this.request<{ redirect: string }>('POST', '/auth/portal/code/verify', {
      code,
      disclosureVersion,
    });
  }

  /** Always `{ sent: true }` — whether or not the email already had an account. */
  async startPortalSignUp(input: {
    email: string;
    username: string;
    inviteCode: string;
    returnTo: string;
  }) {
    return this.request<{ sent: true }>('POST', '/auth/portal/sign-up', input);
  }

  /** On fias.io: a portal's brand for the Company-branded sign-in. */
  async getCompanyHostBrand(hostId: string) {
    return this.request<CompanyHostBrandDto>(
      'GET',
      `/public/company-web/hosts/${encodeURIComponent(hostId)}/brand`,
    );
  }

  /** Fias settings → Company portals (ADR-024 S14). */
  async getCompanyPortals() {
    return this.request<{ portals: CompanyPortalDto[]; sharedFields: string[] }>(
      'GET',
      '/settings/company-portals',
    );
  }

  async leaveCompanyPortal(hostId: string) {
    return this.request<void>('DELETE', `/settings/company-portals/${encodeURIComponent(hostId)}`);
  }

  async getConnectedApps() {
    return this.request<{ apps: ConnectedApp[] }>('GET', '/settings/connected-apps');
  }

  async revokeConnectedApp(appId: string) {
    return this.request<void>('DELETE', `/settings/connected-apps/${appId}`);
  }

  // ==========================================================================
  // Crafting API
  // ==========================================================================

  async createCraftingRecipe(input: Record<string, unknown>) {
    return this.request<{ recipe: { recipeId: string } }>(
      'POST',
      '/arches/crafting/admin/recipes',
      input,
    );
  }

  async updateCraftingRecipe(recipeId: string, input: Record<string, unknown>) {
    return this.request<{ recipe: { recipeId: string } }>(
      'PATCH',
      `/arches/crafting/admin/recipes/${recipeId}`,
      input,
    );
  }

  // ==========================================================================
  // Liquidity Caches
  // ==========================================================================

  async listLiquidityCachePools(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      pools: LcPoolResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/liquidity-caches${qs ? `?${qs}` : ''}`);
  }

  async getLiquidityCachePool(poolId: string) {
    return this.request<LcPoolResponse>('GET', `/arches/liquidity-caches/${poolId}`);
  }

  async getLiquidityCacheQuote(
    poolId: string,
    direction: string,
    inputAmount: string,
    niceMode?: boolean,
  ) {
    const params = new URLSearchParams();
    params.set('direction', direction);
    params.set('inputAmount', inputAmount);
    if (niceMode) params.set('niceMode', 'true');
    return this.request<{
      amountOut: string;
      feeAmount: string;
      priceImpactBps: number;
      niceMode?: { adjustedInput: string; cleanOutput: string; roundingSavings: string };
    }>('GET', `/arches/liquidity-caches/${poolId}/quote?${params.toString()}`);
  }

  async liquidityCacheInvest(
    poolId: string,
    input: { tokenAAmount: string; tokenBAmount: string },
  ) {
    return this.request<{
      position: { positionId: string; poolId: string; lpShares: string };
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/invest`, input);
  }

  async liquidityCacheWithdraw(poolId: string, input: { lpShares: string }) {
    return this.request<{
      amountA: string;
      amountB: string;
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/withdraw`, input);
  }

  async liquidityCacheTrade(
    poolId: string,
    input: {
      direction: string;
      inputAmount: string;
      minOutputAmount?: string;
      deadline?: string;
      niceMode?: boolean;
    },
  ) {
    return this.request<{
      amountOut: string;
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/trade`, input);
  }

  async getMyLiquidityCachePositions() {
    return this.request<{
      positions: Array<{
        positionId: string;
        poolId: string;
        lpShares: string;
        createdAt: string;
        updatedAt: string;
        pool?: {
          poolId: string;
          name: string;
          tokenASymbol: string;
          tokenBSymbol: string;
          reserveA: string;
          reserveB: string;
          totalLpShares: string;
          feeBps: number;
          status: string;
        };
      }>;
      total: number;
    }>('GET', '/arches/liquidity-caches/me/positions');
  }

  async getMyLiquidityCacheTransactions(options?: {
    type?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      transactions: LcTransactionResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/liquidity-caches/me/transactions${qs ? `?${qs}` : ''}`);
  }

  async getLiquidityCachePosition(poolId: string) {
    return this.request<{
      position: {
        positionId: string;
        poolId: string;
        lpShares: string;
        createdAt: string;
        updatedAt: string;
      } | null;
    }>('GET', `/arches/liquidity-caches/${poolId}/position`);
  }

  async createLiquidityCachePool(input: {
    name: string;
    description?: string;
    tokenA: { itemDefId?: string; symbol: string; name: string; decimals: number; source: string };
    tokenB: { itemDefId?: string; symbol: string; name: string; decimals: number; source: string };
    feeBps?: number;
    minLiquidity?: number;
    maxTradePct?: number;
  }) {
    return this.request<LcPoolResponse>('POST', '/arches/liquidity-caches/admin', input);
  }

  async updateLiquidityCachePool(
    poolId: string,
    input: {
      name?: string;
      description?: string;
      feeBps?: number;
      maxTradePct?: number;
      status?: string;
    },
  ) {
    return this.request<LcPoolResponse>('PATCH', `/arches/liquidity-caches/admin/${poolId}`, input);
  }

  // ==========================================================================
  // Business Methods
  // ==========================================================================

  async listBusinesses() {
    return this.request<{ businesses: BusinessResponse[] }>('GET', '/arches/businesses');
  }

  async getBusiness(businessId: string) {
    return this.request<BusinessResponse>('GET', `/arches/businesses/${businessId}`);
  }

  async createBusiness(input: { businessType: string; name: string }) {
    return this.request<BusinessResponse>('POST', '/arches/businesses', input);
  }

  async dissolveBusiness(businessId: string) {
    return this.request<void>('DELETE', `/arches/businesses/${businessId}`);
  }

  async collectBusinessRevenue(
    businessId: string,
    input: {
      goods: Record<string, number>;
      foods: Record<string, number>;
      /** Effician only: the item to manufacture. Quantity is server-derived. */
      productName?: string;
    },
  ) {
    return this.request<CollectRevenueResponse>(
      'POST',
      `/arches/businesses/${businessId}/collect`,
      input,
    );
  }

  async getBusinessParameters(type?: string) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    const qs = params.toString();
    return this.request<{ parameters: BusinessParameterResponse[] }>(
      'GET',
      `/arches/businesses/parameters${qs ? `?${qs}` : ''}`,
    );
  }

  async getBusinessHistory(businessId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<BusinessHistoryResponse>(
      'GET',
      `/arches/businesses/${businessId}/history${qs ? `?${qs}` : ''}`,
    );
  }

  async getBusinessStats() {
    return this.request<BusinessStatsResponse>('GET', '/arches/businesses/admin/stats');
  }

  // ==========================================================================
  // API Keys
  // ==========================================================================

  async createApiKey(params: { name: string; scopes?: string[]; expiresAt?: string }) {
    return this.request<{
      keyId: string;
      name: string;
      keyPrefix: string;
      secret: string;
      scopes: string[];
      createdAt: string;
    }>('POST', '/api-keys', params);
  }

  /**
   * Mint the API key for `fias-dev login`'s browser-redirect flow.
   *
   * Distinct from `createApiKey` on purpose: this endpoint applies the CLI key
   * policy (per-device name, revoke the prior key for that device, 90-day
   * expiry), and the generic key route refuses `FIAS CLI` names so the policy
   * cannot be bypassed. Only CliAuthPage should call this.
   */
  async createCliKey(params: { deviceLabel?: string | null }) {
    return this.request<{
      keyId: string;
      name: string;
      keyPrefix: string;
      secret: string;
      scopes: string[];
      expiresAt: string | null;
      createdAt: string;
    }>('POST', '/cli-auth/key', params.deviceLabel ? { deviceLabel: params.deviceLabel } : {});
  }

  /**
   * Claim a CLI auth code on behalf of the logged-in user, authorizing the
   * paste-code login flow used by `fias-dev login --manual` (Codespaces-
   * friendly). The CLI's poll endpoint returns the freshly-minted API key
   * once this resolves successfully. Throws ApiError on 404/409/410.
   */
  async claimCliAuthCode(code: string, scopes?: string[]) {
    return this.request<void>(
      'POST',
      `/cli-auth/code/${encodeURIComponent(code)}/claim`,
      scopes ? { scopes } : {},
    );
  }

  async listApiKeys() {
    return this.request<{
      keys: Array<{
        keyId: string;
        name: string;
        keyPrefix: string;
        scopes: string[];
        status: string;
        createdAt: string;
        lastUsedAt: string | null;
        expiresAt: string | null;
      }>;
    }>('GET', '/api-keys');
  }

  /**
   * Revoke an API key.
   *
   * NOT instant in a multi-task deployment. `getApiKeyByHash` serves from a
   * process-local 5-minute cache (`api-key-operations.ts`), and `revokeApiKey`
   * invalidates only the task that handled the DELETE. The container API runs
   * desiredCount 3 (min 3 / max 10), so the remaining tasks keep accepting the
   * key until their own TTL expires.
   *
   * Contrast workspace membership, which IS immediate: the agent-tools plane
   * re-reads it from the DB on every dispatch with no cache. Only the key
   * credential itself goes stale.
   */
  async revokeApiKey(keyId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api-keys/${encodeURIComponent(keyId)}`);
  }

  // ---- Which apps an agent key may use (ADR-025) ----------------------------
  // Unlike a key revocation, a grant change is immediate: the agent plane reads
  // the grant from the database on every call, uncached.

  /** Apps the user keeps data in that share data with agents. */
  async listAgentApps() {
    return this.request<{ apps: AgentAppSummary[] }>('GET', '/api-keys/agent-apps');
  }

  async listAgentKeyAppGrants(keyId: string) {
    return this.request<{ key: AgentKeyGrantCapabilities; grants: AgentKeyAppGrant[] }>(
      'GET',
      `/api-keys/${encodeURIComponent(keyId)}/app-grants`,
    );
  }

  async setAgentKeyAppGrant(
    keyId: string,
    archeId: string,
    body: { collections: AgentAppGrantEntry[]; disclosureHash: string },
  ) {
    return this.request<{ grant: Omit<AgentKeyAppGrant, 'app' | 'changedSinceGranted'> }>(
      'PUT',
      `/api-keys/${encodeURIComponent(keyId)}/app-grants/${encodeURIComponent(archeId)}`,
      body,
    );
  }

  async revokeAgentKeyAppGrant(keyId: string, archeId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/api-keys/${encodeURIComponent(keyId)}/app-grants/${encodeURIComponent(archeId)}`,
    );
  }

  // ===========================================================================
  // Beta Access
  // ===========================================================================

  async submitBetaAccessRequest(input: BetaAccessSubmitInput) {
    return this.request<BetaAccessSubmitResponse>('POST', '/arches/beta-access/requests', input);
  }

  async listBetaAccessRequests(options?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<BetaAccessListResponse>(
      'GET',
      `/arches/beta-access/admin/requests${qs ? `?${qs}` : ''}`,
    );
  }

  async getBetaAccessRequest(requestId: string) {
    return this.request<BetaAccessRequestResponse>(
      'GET',
      `/arches/beta-access/admin/requests/${requestId}`,
    );
  }

  async updateBetaAccessRequestStatus(
    requestId: string,
    input: { status: 'accepted' | 'rejected'; adminNotes?: string },
  ) {
    return this.request<BetaAccessRequestResponse>(
      'PATCH',
      `/arches/beta-access/admin/requests/${requestId}`,
      input,
    );
  }

  async getBetaAccessStats() {
    return this.request<BetaAccessStatsResponse>('GET', '/arches/beta-access/admin/stats');
  }

  // ==========================================================================
  // Learn About Fias
  // ==========================================================================

  async getLearnAboutFiasResources() {
    return this.request<LearnAboutFiasResourcesResponse>(
      'GET',
      '/arches/learn-about-fias/resources',
    );
  }

  async adminListLearnAboutFiasDocuments(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LearnAboutFiasDocumentListResponse>(
      'GET',
      `/arches/learn-about-fias/admin/documents${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetLearnAboutFiasDocument(docId: string) {
    return this.request<LearnAboutFiasDocumentDetailResponse>(
      'GET',
      `/arches/learn-about-fias/admin/documents/${docId}`,
    );
  }

  async adminCreateLearnAboutFiasDocument(
    input:
      | {
          mode: 'file';
          title: string;
          description?: string;
          fileBase64: string;
          fileName: string;
          sourceUrl?: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        }
      | {
          mode: 'text';
          title: string;
          description?: string;
          content: string;
          sourceType: string;
          sourceUrl?: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        }
      | {
          mode: 'link';
          title: string;
          description?: string;
          sourceUrl: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        },
  ) {
    return this.request<{ docId: string; chunksCreated: number; status: string }>(
      'POST',
      '/arches/learn-about-fias/admin/documents',
      input,
    );
  }

  async adminUpdateLearnAboutFiasDocument(
    docId: string,
    input: {
      title?: string;
      description?: string | null;
      sourceUrl?: string | null;
      showInResources?: boolean;
      ragEnabled?: boolean;
    },
  ) {
    return this.request<LearnAboutFiasDocumentResponse>(
      'PATCH',
      `/arches/learn-about-fias/admin/documents/${docId}`,
      input,
    );
  }

  async adminUpdateLearnAboutFiasDocumentContent(
    docId: string,
    input:
      | { mode: 'file'; fileBase64: string; fileName: string }
      | { mode: 'text'; content: string; sourceType?: string },
  ) {
    return this.request<{ docId: string; chunksCreated: number; status: string }>(
      'PATCH',
      `/arches/learn-about-fias/admin/documents/${docId}/content`,
      input,
    );
  }

  async adminDeleteLearnAboutFiasDocument(docId: string) {
    return this.request<void>('DELETE', `/arches/learn-about-fias/admin/documents/${docId}`);
  }

  async adminGetLearnAboutFiasStats() {
    return this.request<LearnAboutFiasStatsResponse>('GET', '/arches/learn-about-fias/admin/stats');
  }

  async adminReorderLearnAboutFiasDocuments(docIds: string[]) {
    return this.request<{ documents: LearnAboutFiasDocumentResponse[] }>(
      'PUT',
      '/arches/learn-about-fias/admin/documents/reorder',
      { docIds },
    );
  }

  // ==========================================================================
  // Storage Arches (DFE Avatars, DFE Icons, Litcraft Addressables)
  // ==========================================================================

  /**
   * Creates a StorageApi bound to a specific storage arche path.
   */
  private createStorageApi(archePath: string): StorageApi {
    return {
      listFiles: (
        prefix?: string,
        delimiter?: string,
        maxKeys?: number,
        continuationToken?: string,
      ) => {
        const params = new URLSearchParams();
        if (prefix) params.set('prefix', prefix);
        if (delimiter) params.set('delimiter', delimiter);
        if (maxKeys) params.set('maxKeys', String(maxKeys));
        if (continuationToken) params.set('continuationToken', continuationToken);
        const qs = params.toString();
        return this.request<StorageFileListResponse>(
          'GET',
          `/arches/${archePath}/admin/files${qs ? `?${qs}` : ''}`,
        );
      },
      getUploadUrl: (key: string, contentType: string, sizeBytes: number) => {
        return this.request<StorageUploadUrlResponse>(
          'POST',
          `/arches/${archePath}/admin/upload-url`,
          { key, contentType, sizeBytes },
        );
      },
      deleteFile: (key: string) => {
        return this.request<{ success: boolean; key: string }>(
          'DELETE',
          `/arches/${archePath}/admin/files?key=${encodeURIComponent(key)}`,
        );
      },
      deleteGroup: (prefix: string) => {
        return this.request<{
          success: boolean;
          prefix: string;
          deletedCount: number;
          failedCount?: number;
          failedKeys?: string[];
        }>('DELETE', `/arches/${archePath}/admin/groups?prefix=${encodeURIComponent(prefix)}`);
      },
      getMetadata: (key: string) => {
        return this.request<StorageFileMetadataResponse>(
          'GET',
          `/arches/${archePath}/admin/files/metadata?key=${encodeURIComponent(key)}`,
        );
      },
    };
  }

  readonly dfeAvatarsStorage = this.createStorageApi('dfe-avatars');
  readonly dfeIconsStorage = this.createStorageApi('dfe-icons');
  readonly litcraftAddressablesStorage = this.createStorageApi('litcraft-addressables');

  // ========================================================================
  // Todo Lists (W6 — Vault-resident, Holder-scoped)
  //
  // Every call carries `?holder=<holderId>` so the wrapper trust-check
  // can prove the caller can act for that Holder. Reads are explicit
  // GETs against /sections /items as top-level paths (not nested under
  // /lists/:listId) because the backend lets each verb live in its own
  // routelet for symmetric audit. Sharing is gone — list access is now
  // a Vault Membership grant on the Holder.
  // ========================================================================

  async listTodoLists(
    holder: string,
    options?: {
      includeArchived?: boolean;
      includeDeleted?: boolean;
      limit?: number;
      offset?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder });
    if (options?.includeArchived) qs.set('includeArchived', 'true');
    if (options?.includeDeleted) qs.set('includeDeleted', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    if (options?.offset) qs.set('offset', String(options.offset));
    return this.request<TodoListListResponse>('GET', `/arches/todo-lists?${qs.toString()}`);
  }

  /**
   * Visible-list counts for every workspace the caller can access
   * (Personal + active Org memberships). Drives the "you also have lists
   * in other workspaces" banner. Takes no holder — it's scoped server-
   * side to the caller's own accessible Holders.
   */
  async listTodoListWorkspaceSummary() {
    return this.request<TodoWorkspaceSummaryResponse>(
      'GET',
      `/arches/todo-lists/workspace-summary`,
    );
  }

  /**
   * The caller's unread assignment notifications, mapped to their lists so the
   * UI can paint per-list and per-item unread dots. Caller-scoped (no holder) —
   * results can span every workspace the caller belongs to.
   */
  async getTodoAssignmentNotifications() {
    return this.request<TodoAssignmentNotificationsResponse>(
      'GET',
      `/arches/todo-lists/assignments/unread`,
    );
  }

  async getTodoList(holder: string, listId: string) {
    return this.request<TodoListDetailResponse>(
      'GET',
      `/arches/todo-lists/${listId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async listTodoSections(holder: string, listId: string) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<TodoSectionListResponse>(
      'GET',
      `/arches/todo-lists/sections?${qs.toString()}`,
    );
  }

  async listTodoItems(
    holder: string,
    listId: string,
    options?: {
      sectionId?: string;
      status?: TodoItemStatus;
      completed?: boolean;
      /** List the sub-tasks of this parent item instead of top-level tasks. */
      parentItemId?: string;
    },
  ) {
    const qs = new URLSearchParams({ holder, listId });
    if (options?.sectionId) qs.set('sectionId', options.sectionId);
    if (options?.status) qs.set('status', options.status);
    if (options?.completed) qs.set('completed', 'true');
    if (options?.parentItemId) qs.set('parentItemId', options.parentItemId);
    return this.request<TodoItemListResponse>('GET', `/arches/todo-lists/items?${qs.toString()}`);
  }

  /** All sub-tasks of a list, projected to searchable text (title + description).
   * Powers sub-task search on the shared List view — a matching sub-task
   * surfaces its parent task. */
  async listTodoSubtaskIndex(holder: string, listId: string) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<TodoSubtaskIndexResponse>(
      'GET',
      `/arches/todo-lists/items/subtask-index?${qs.toString()}`,
    );
  }

  async createTodoList(
    holder: string,
    input: {
      name: string;
      description?: string;
      itemKey?: string;
      isPublic?: boolean;
      publicSlug?: string;
      reviewMode?: boolean;
    },
  ) {
    return this.request<{ list: TodoListResponse }>(
      'POST',
      `/arches/todo-lists?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  /**
   * Persist a manual drag-reorder of the Holder's list dashboard. `order` is the
   * full set of visible list ids in their new sequence. Owner/admin/self only —
   * the server gates the `reorder_lists` verb. Returns 204.
   */
  async reorderTodoLists(holder: string, order: string[]) {
    return this.request<void>(
      'POST',
      `/arches/todo-lists/reorder?holder=${encodeURIComponent(holder)}`,
      { order },
    );
  }

  async updateTodoList(
    holder: string,
    listId: string,
    input: {
      name?: string;
      description?: string | null;
      itemKey?: string;
      isPublic?: boolean;
      publicSlug?: string | null;
      reviewMode?: boolean;
      showProjectOnTasks?: boolean;
      showSectionPathOnTasks?: boolean;
    },
  ) {
    return this.request<{ list: TodoListResponse }>(
      'PATCH',
      `/arches/todo-lists/${listId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async archiveTodoList(holder: string, listId: string) {
    return this.request<{ list: TodoListResponse }>(
      'POST',
      `/arches/todo-lists/${listId}/archive?holder=${encodeURIComponent(holder)}`,
    );
  }

  async restoreTodoList(holder: string, listId: string) {
    return this.request<{ list: TodoListResponse }>(
      'POST',
      `/arches/todo-lists/${listId}/restore?holder=${encodeURIComponent(holder)}`,
    );
  }

  async deleteTodoList(holder: string, listId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/${listId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async createTodoSection(
    holder: string,
    input: {
      listId: string;
      name: string;
      description?: string;
      parentSectionId?: string | null;
      sectionType?: 'timeframe' | 'category';
      sortOrder?: number;
    },
  ) {
    return this.request<{ section: TodoSectionResponse }>(
      'POST',
      `/arches/todo-lists/sections?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async updateTodoSection(
    holder: string,
    sectionId: string,
    input: {
      name?: string;
      description?: string | null;
      sortOrder?: number;
      sectionType?: 'timeframe' | 'category';
      parentSectionId?: string | null;
      hiddenFromPublic?: boolean;
    },
  ) {
    return this.request<{ section: TodoSectionResponse }>(
      'PATCH',
      `/arches/todo-lists/sections/${sectionId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async deleteTodoSection(holder: string, sectionId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/sections/${sectionId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async restoreTodoSection(holder: string, sectionId: string) {
    return this.request<{ section: TodoSectionResponse }>(
      'POST',
      `/arches/todo-lists/sections/${sectionId}/restore?holder=${encodeURIComponent(holder)}`,
    );
  }

  async createTodoItem(
    holder: string,
    input: {
      listId: string;
      sectionId?: string | null;
      /** When set, create a sub-task under this parent item. */
      parentItemId?: string | null;
      title: string;
      description?: string;
      sortOrder?: number;
    },
  ) {
    return this.request<{ item: TodoItemResponse }>(
      'POST',
      `/arches/todo-lists/items?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async updateTodoItem(
    holder: string,
    itemId: string,
    input: {
      title?: string;
      description?: string | null;
      status?: TodoItemStatus;
      /** Doer's "finished my part" flag (review-list checkbox), orthogonal to
       *  status; the server couples completed⇒done and done⇒in_progress. */
      done?: boolean;
      sectionId?: string | null;
      sortOrder?: number;
      /** Set/clear the task's workspace project (null clears it). */
      projectId?: string | null;
      /** Only meaningful with status='in_review': route the review to one
       * reviewer (null/omitted = broadcast to all list reviewers). */
      reviewAssignedToPrincipalId?: string | null;
      /** Toggle this task's public visibility (owner/admin only). */
      hiddenFromPublic?: boolean;
    },
  ) {
    return this.request<{ item: TodoItemResponse }>(
      'PATCH',
      `/arches/todo-lists/items/${itemId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async listTodoListReviewers(holder: string, listId: string) {
    return this.request<TodoListReviewersResponse>(
      'GET',
      `/arches/todo-lists/${listId}/reviewers?holder=${encodeURIComponent(holder)}`,
    );
  }

  async setTodoListReviewers(holder: string, listId: string, principalIds: string[]) {
    return this.request<TodoListReviewersResponse>(
      'PUT',
      `/arches/todo-lists/${listId}/reviewers?holder=${encodeURIComponent(holder)}`,
      { principalIds },
    );
  }

  async reorderTodoItems(
    holder: string,
    listId: string,
    order: string[],
    sectionId?: string | null,
  ) {
    return this.request<void>(
      'POST',
      `/arches/todo-lists/items/reorder?holder=${encodeURIComponent(holder)}`,
      // Only include sectionId when moving across sections; omit for in-place reorder.
      { listId, order, ...(sectionId !== undefined ? { sectionId } : {}) },
    );
  }

  async deleteTodoItem(holder: string, itemId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/items/${itemId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async restoreTodoItem(holder: string, itemId: string) {
    return this.request<{ item: TodoItemResponse }>(
      'POST',
      `/arches/todo-lists/items/${itemId}/restore?holder=${encodeURIComponent(holder)}`,
    );
  }

  /** All sub-tasks of a list as full rows, for rendering them nested in the List view. */
  async listTodoListSubtasks(holder: string, listId: string) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<{ subtasks: TodoItemResponse[] }>(
      'GET',
      `/arches/todo-lists/items/subtasks?${qs.toString()}`,
    );
  }

  // ========================================================================
  // Checklist — lightweight text + checkbox breakdown of a task
  // ========================================================================

  async listTodoChecklist(holder: string, itemId: string) {
    return this.request<{ checklist: TodoChecklistItemResponse[] }>(
      'GET',
      `/arches/todo-lists/items/${itemId}/checklist?holder=${encodeURIComponent(holder)}`,
    );
  }

  async createTodoChecklistItem(holder: string, itemId: string, title: string) {
    return this.request<{ item: TodoChecklistItemResponse }>(
      'POST',
      `/arches/todo-lists/items/${itemId}/checklist?holder=${encodeURIComponent(holder)}`,
      { title },
    );
  }

  async updateTodoChecklistItem(
    holder: string,
    checklistItemId: string,
    input: { title?: string; isDone?: boolean },
  ) {
    return this.request<{ item: TodoChecklistItemResponse }>(
      'PATCH',
      `/arches/todo-lists/checklist/${checklistItemId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async deleteTodoChecklistItem(holder: string, checklistItemId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/checklist/${checklistItemId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async reorderTodoChecklist(holder: string, itemId: string, order: string[]) {
    return this.request<void>(
      'POST',
      `/arches/todo-lists/items/${itemId}/checklist/reorder?holder=${encodeURIComponent(holder)}`,
      { order },
    );
  }

  // ========================================================================
  // Personal "My Tasks" overlay (per-user private layer on a shared list)
  // ========================================================================

  async getTodoPersonalBoard(holder: string, listId: string) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<TodoPersonalBoardResponse>(
      'GET',
      `/arches/todo-lists/personal/board?${qs.toString()}`,
    );
  }

  /** The caller's completed/cancelled PRIVATE items — feeds the Completed page's
   *  "My tasks" view. Principal-scoped, so it only ever returns your own. */
  async listCompletedPersonalTodoItems(holder: string, listId: string) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<{ items: TodoPersonalItemResponse[] }>(
      'GET',
      `/arches/todo-lists/personal/completed?${qs.toString()}`,
    );
  }

  async createTodoPersonalSection(
    holder: string,
    listId: string,
    input: { name: string; description?: string; parentPersonalSectionId?: string | null },
  ) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<{ section: TodoPersonalSectionResponse }>(
      'POST',
      `/arches/todo-lists/personal/sections?${qs.toString()}`,
      input,
    );
  }

  async updateTodoPersonalSection(
    holder: string,
    id: string,
    input: { name?: string; description?: string | null; parentPersonalSectionId?: string | null },
  ) {
    return this.request<{ section: TodoPersonalSectionResponse }>(
      'PATCH',
      `/arches/todo-lists/personal/sections/${id}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async deleteTodoPersonalSection(holder: string, id: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/personal/sections/${id}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async reorderTodoPersonalSections(holder: string, listId: string, order: string[]) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<void>(
      'POST',
      `/arches/todo-lists/personal/sections/reorder?${qs.toString()}`,
      {
        order,
      },
    );
  }

  async createTodoPersonalItem(
    holder: string,
    listId: string,
    input: {
      personalSectionId?: string | null;
      parentPersonalItemId?: string | null;
      title: string;
      description?: string;
    },
  ) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<{ item: TodoPersonalItemResponse }>(
      'POST',
      `/arches/todo-lists/personal/items?${qs.toString()}`,
      input,
    );
  }

  async updateTodoPersonalItem(
    holder: string,
    id: string,
    input: {
      title?: string;
      description?: string | null;
      status?: TodoItemStatus;
      personalSectionId?: string | null;
      /** Set/clear the task's workspace project (null clears it). */
      projectId?: string | null;
    },
  ) {
    return this.request<{ item: TodoPersonalItemResponse }>(
      'PATCH',
      `/arches/todo-lists/personal/items/${id}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async deleteTodoPersonalItem(holder: string, id: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/personal/items/${id}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async restoreTodoPersonalItem(holder: string, id: string) {
    return this.request<{ item: TodoPersonalItemResponse }>(
      'POST',
      `/arches/todo-lists/personal/items/${id}/restore?holder=${encodeURIComponent(holder)}`,
    );
  }

  async reorderTodoPersonalEntries(
    holder: string,
    listId: string,
    personalSectionId: string | null,
    entries: TodoPersonalEntryRef[],
  ) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<void>(
      'POST',
      `/arches/todo-lists/personal/entries/reorder?${qs.toString()}`,
      {
        personalSectionId,
        entries,
      },
    );
  }

  // ---- Personal checklist (private mirror of the shared checklist) ----

  async getTodoPersonalChecklist(holder: string, personalItemId: string) {
    return this.request<{ checklist: TodoPersonalChecklistItemResponse[] }>(
      'GET',
      `/arches/todo-lists/personal/items/${personalItemId}/checklist?holder=${encodeURIComponent(holder)}`,
    );
  }

  async createTodoPersonalChecklistItem(holder: string, personalItemId: string, title: string) {
    return this.request<{ item: TodoPersonalChecklistItemResponse }>(
      'POST',
      `/arches/todo-lists/personal/items/${personalItemId}/checklist?holder=${encodeURIComponent(holder)}`,
      { title },
    );
  }

  async updateTodoPersonalChecklistItem(
    holder: string,
    personalChecklistItemId: string,
    input: { title?: string; isDone?: boolean },
  ) {
    return this.request<{ item: TodoPersonalChecklistItemResponse }>(
      'PATCH',
      `/arches/todo-lists/personal/checklist/${personalChecklistItemId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async deleteTodoPersonalChecklistItem(holder: string, personalChecklistItemId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/personal/checklist/${personalChecklistItemId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  async reorderTodoPersonalChecklist(holder: string, personalItemId: string, order: string[]) {
    return this.request<void>(
      'POST',
      `/arches/todo-lists/personal/items/${personalItemId}/checklist/reorder?holder=${encodeURIComponent(holder)}`,
      { order },
    );
  }

  /** Place a shared task on the caller's board (the "+S" flow) without assigning it. */
  /**
   * "+S" on the My Tasks board: create a shared sub-task under `parentItemId` and
   * place it in `personalSectionId` in one request. The sub-task is assigned to
   * the caller (the creator). Returns the created shared item.
   */
  async createSubtaskOnBoard(
    holder: string,
    listId: string,
    parentItemId: string,
    personalSectionId: string | null,
    title: string,
  ) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<{ item: TodoItemResponse }>(
      'POST',
      `/arches/todo-lists/personal/subtasks?${qs.toString()}`,
      { parentItemId, personalSectionId, title },
    );
  }

  async assignTodoItem(holder: string, itemId: string, assigneePrincipalId: string) {
    return this.request<{ item: TodoItemResponse }>(
      'POST',
      `/arches/todo-lists/items/${itemId}/assign?holder=${encodeURIComponent(holder)}`,
      { assigneePrincipalId },
    );
  }

  async unassignTodoItem(holder: string, itemId: string) {
    return this.request<{ item: TodoItemResponse }>(
      'POST',
      `/arches/todo-lists/items/${itemId}/unassign?holder=${encodeURIComponent(holder)}`,
    );
  }

  /** All access grants across a list's sections (one row per section+principal). */
  async listTodoSectionAccess(holder: string, listId: string) {
    const qs = new URLSearchParams({ holder, listId });
    return this.request<TodoSectionAccessResponse>(
      'GET',
      `/arches/todo-lists/sections/access?${qs.toString()}`,
    );
  }

  /** Replace one section's access grants (owner/admin). Returns the resulting set. */
  async setTodoSectionAccess(holder: string, sectionId: string, principalIds: string[]) {
    return this.request<{ access: string[] }>(
      'PUT',
      `/arches/todo-lists/sections/${sectionId}/access?holder=${encodeURIComponent(holder)}`,
      { principalIds },
    );
  }

  // --- Projects (workspace-scoped, cross-cutting task grouping) ---

  async listTodoProjects(holder: string) {
    return this.request<TodoProjectListResponse>(
      'GET',
      `/arches/todo-lists/projects?holder=${encodeURIComponent(holder)}`,
    );
  }

  async createTodoProject(holder: string, input: { name: string; color?: string | null }) {
    return this.request<{ project: TodoProjectResponse }>(
      'POST',
      `/arches/todo-lists/projects?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async updateTodoProject(
    holder: string,
    projectId: string,
    input: { name?: string; color?: string | null; sortOrder?: number },
  ) {
    return this.request<{ project: TodoProjectResponse }>(
      'PATCH',
      `/arches/todo-lists/projects/${projectId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async deleteTodoProject(holder: string, projectId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/projects/${projectId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  // --- Cross-list move + link shortcuts ---

  /** A source list's outbound "Move to list…" shortcuts. */
  async listTodoListLinks(holder: string, listId: string) {
    return this.request<TodoListLinksResponse>(
      'GET',
      `/arches/todo-lists/${listId}/links?holder=${encodeURIComponent(holder)}`,
    );
  }

  /** Link a list to a target list in the same workspace (owner/admin). */
  async createTodoListLink(
    holder: string,
    listId: string,
    input: { targetListId: string; defaultTargetSectionId?: string | null },
  ) {
    return this.request<{ link: TodoListLinkResponse }>(
      'POST',
      `/arches/todo-lists/${listId}/links?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  /** Patch a move shortcut's default landing section / order (owner/admin). */
  async updateTodoListLink(
    holder: string,
    linkId: string,
    input: { defaultTargetSectionId?: string | null; sortOrder?: number },
  ) {
    return this.request<{ link: TodoListLinkResponse }>(
      'PATCH',
      `/arches/todo-lists/links/${linkId}?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  /** Remove a move shortcut (owner/admin). */
  async deleteTodoListLink(holder: string, linkId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/todo-lists/links/${linkId}?holder=${encodeURIComponent(holder)}`,
    );
  }

  /** Re-home a top-level task (with its sub-tasks) into another list in the same
   * workspace, landing in a chosen section (owner/admin). */
  async moveTodoItemToList(
    holder: string,
    itemId: string,
    input: { targetListId: string; targetSectionId?: string | null },
  ) {
    return this.request<{ item: TodoItemResponse }>(
      'POST',
      `/arches/todo-lists/items/${itemId}/move?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  /** Re-home a whole section subtree into another list, attaching under a chosen
   * parent (or the root) (owner/admin). Returns the moved task count. */
  async moveTodoSectionToList(
    holder: string,
    sectionId: string,
    input: { targetListId: string; targetParentSectionId?: string | null },
  ) {
    return this.request<{ section: TodoSectionResponse; movedItemCount: number }>(
      'POST',
      `/arches/todo-lists/sections/${sectionId}/move?holder=${encodeURIComponent(holder)}`,
      input,
    );
  }

  async getPublicTodoList(slug: string) {
    return this.request<PublicTodoListResponse>('GET', `/arches/todo-lists/public/${slug}`);
  }

  async getPublicTodoListCompleted(slug: string, options?: { limit?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit) qs.set('limit', String(options.limit));
    const q = qs.toString();
    return this.request<PublicTodoCompletedResponse>(
      'GET',
      `/arches/todo-lists/public/${slug}/completed${q ? `?${q}` : ''}`,
    );
  }

  // ==========================================================================
  // Document Sign API
  // ==========================================================================

  async listDocumentSignEnvelopes(options: {
    /** Vault holder (`vh_…`) to scope the list to. Required. */
    holderId: string;
    /**
     * `'unarchived'` (default) — normal list. `'archived'` — Archived
     * filter pill. `'all'` — both, reserved for admin paths.
     */
    archiveScope?: 'unarchived' | 'archived' | 'all';
    status?: DocumentSignEnvelopeStatus[];
    search?: string;
    sortBy?: 'created_at' | 'sent_at' | 'completed_at';
    limit?: number;
    offset?: number;
  }): Promise<DocumentSignEnvelopeListResponse> {
    const qs = new URLSearchParams();
    qs.set('holderId', options.holderId);
    if (options.archiveScope) qs.set('archiveScope', options.archiveScope);
    if (options.status) {
      for (const s of options.status) qs.append('status', s);
    }
    if (options.search) qs.set('search', options.search);
    if (options.sortBy) qs.set('sortBy', options.sortBy);
    if (options.limit !== undefined) qs.set('limit', String(options.limit));
    if (options.offset !== undefined) qs.set('offset', String(options.offset));
    return this.request<DocumentSignEnvelopeListResponse>(
      'GET',
      `/arches/document-sign/envelopes?${qs.toString()}`,
    );
  }

  /** Archive an envelope — soft-hide from the default list. Reversible. */
  async archiveDocumentSignEnvelope(envelopeId: string) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/archive`,
    );
  }

  /** Reverse `archive` — return the envelope to the default list. */
  async unarchiveDocumentSignEnvelope(envelopeId: string) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/unarchive`,
    );
  }

  async getDocumentSignEnvelope(envelopeId: string) {
    return this.request<{ envelope: DocumentSignEnvelopeDetailResponse }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}`,
    );
  }

  /**
   * Distinct recipients the workspace has previously sent envelopes to,
   * most-recently-used first. Used by the builder's RecipientSuggestMenu
   * to autofill from history.
   */
  async listRecentDocumentSignRecipients(input: {
    holderId: string;
    limit?: number;
  }): Promise<{ recipients: Array<{ name: string; email: string; lastUsed: string }> }> {
    const qs = new URLSearchParams();
    qs.set('holderId', input.holderId);
    if (input.limit !== undefined) qs.set('limit', String(input.limit));
    return this.request<{
      recipients: Array<{ name: string; email: string; lastUsed: string }>;
    }>('GET', `/arches/document-sign/recipients/recent?${qs.toString()}`);
  }

  async createDocumentSignEnvelope(input: {
    /** Vault holder (`vh_…`) the envelope is created on behalf of. Required. */
    holderId: string;
    title: string;
    message?: string;
    signingOrder?: 'parallel' | 'sequential';
    assuranceTier?: DocumentSignAssuranceTier;
  }) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'POST',
      '/arches/document-sign/envelopes',
      input,
    );
  }

  /**
   * Resolve the draft for a cross-arche "Sign this document" handoff.
   *
   * Idempotent on `(holderId, originArcheId, requestId)` — re-presenting the
   * same request returns the SAME draft with `created: false`, which is what
   * makes a double-click or a retry safe. Recipients are seeded only on first
   * creation, so a repeat never overwrites edits made in the builder since.
   *
   * `needsSourceFile` answers "does the caller still have to attach the PDF?"
   * from the envelope's files, not from `created` — the copy happens after
   * this call, so a failed upload must leave a retry able to finish the job.
   */
  async createDocumentSignEnvelopeFromHandoff(input: {
    holderId: string;
    requestId: string;
    originArcheId: string;
    documentId: string;
    title?: string;
    message?: string;
    signingOrder?: 'parallel' | 'sequential';
    recipients?: Array<{ email: string; name?: string; role?: 'signer' | 'viewer' | 'approver' }>;
    correlationId?: string;
  }) {
    return this.request<{
      envelope: DocumentSignEnvelopeResponse;
      created: boolean;
      needsSourceFile: boolean;
    }>('POST', '/arches/document-sign/envelopes/from-handoff', input);
  }

  async updateDocumentSignEnvelope(
    envelopeId: string,
    input: {
      title?: string;
      message?: string | null;
      signingOrder?: 'parallel' | 'sequential';
      assuranceTier?: DocumentSignAssuranceTier;
    },
  ) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'PATCH',
      `/arches/document-sign/envelopes/${envelopeId}`,
      input,
    );
  }

  async deleteDocumentSignEnvelope(envelopeId: string) {
    return this.request<void>('DELETE', `/arches/document-sign/envelopes/${envelopeId}`);
  }

  async listDocumentSignFiles(envelopeId: string) {
    return this.request<{ files: DocumentSignFileResponse[] }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/files`,
    );
  }

  async createDocumentSignUploadUrl(
    envelopeId: string,
    input: { fileName: string; mimeType: string; sizeBytes: number; checksumSha256: string },
  ) {
    return this.request<DocumentSignUploadUrlResponse>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/files/upload-url`,
      input,
    );
  }

  async confirmDocumentSignUpload(
    envelopeId: string,
    input: { fileId: string; documentId: string; fileName: string; checksumSha256: string },
  ) {
    return this.request<{ file: DocumentSignFileResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/files/confirm`,
      input,
    );
  }

  async deleteDocumentSignFile(envelopeId: string, fileId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}`,
    );
  }

  async renameDocumentSignFile(envelopeId: string, fileId: string, input: { fileName: string }) {
    return this.request<{ file: DocumentSignFileResponse }>(
      'PATCH',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}`,
      input,
    );
  }

  async getDocumentSignFileDownloadUrl(
    envelopeId: string,
    fileId: string,
    disposition?: 'attachment' | 'inline',
  ) {
    const query = disposition ? `?disposition=${disposition}` : '';
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}/download-url${query}`,
    );
  }

  async listDocumentSignRecipients(envelopeId: string) {
    return this.request<{ recipients: DocumentSignRecipientResponse[] }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/recipients`,
    );
  }

  async setDocumentSignRecipients(
    envelopeId: string,
    input: { recipients: DocumentSignRecipientInput[] },
  ) {
    return this.request<{ recipients: DocumentSignRecipientResponse[] }>(
      'PUT',
      `/arches/document-sign/envelopes/${envelopeId}/recipients`,
      input,
    );
  }

  /**
   * Set a new access code on a recipient of a SENT envelope — the recovery
   * path when a signer is locked out or never received the code. Owner only;
   * the envelope must still be open and the recipient unsigned.
   */
  async resetDocumentSignRecipientAccessCode(
    envelopeId: string,
    recipientId: string,
    accessCode: string,
  ) {
    return this.request<{ recipient: DocumentSignRecipientResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/recipients/${encodeURIComponent(recipientId)}/access-code`,
      { accessCode },
    );
  }

  async listDocumentSignFields(envelopeId: string, fileId: string) {
    return this.request<{ fields: DocumentSignFieldResponse[] }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}/fields`,
    );
  }

  async setDocumentSignFields(
    envelopeId: string,
    fileId: string,
    input: { fields: DocumentSignFieldInput[] },
  ) {
    return this.request<{ fields: DocumentSignFieldResponse[] }>(
      'PUT',
      `/arches/document-sign/envelopes/${envelopeId}/files/${fileId}/fields`,
      input,
    );
  }

  async sendDocumentSignEnvelope(envelopeId: string) {
    return this.request<DocumentSignSendEnvelopeResponse>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/send`,
    );
  }

  /** Switch a draft between one shared envelope and a bulk send. */
  async setDocumentSignSendMode(envelopeId: string, sendMode: DocumentSignSendMode) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'PUT',
      `/arches/document-sign/envelopes/${envelopeId}/send-mode`,
      { sendMode },
    );
  }

  /** Replace the list of people a bulk envelope is sent to. */
  async setDocumentSignBulkRecipients(
    envelopeId: string,
    input: { recipients: DocumentSignBulkRecipient[] },
  ) {
    return this.request<{ recipients: DocumentSignBulkRecipient[] }>(
      'PUT',
      `/arches/document-sign/envelopes/${envelopeId}/bulk-recipients`,
      input,
    );
  }

  /**
   * Start a bulk send — one separate envelope per person. `reviewFingerprint` is the one the review page was
   * given with the envelope; the server refuses (409 BULK_SEND_CHANGED) if the
   * envelope changed in any way since, so what is sent and charged is what
   * was reviewed.
   */
  async bulkSendDocumentSignEnvelope(envelopeId: string, reviewFingerprint: string) {
    return this.request<DocumentSignBulkSendResponse>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/bulk-send`,
      { reviewFingerprint },
    );
  }

  /** Which optional Document Sign features are switched on. */
  async getDocumentSignFeatures() {
    return this.request<DocumentSignFeaturesResponse>('GET', '/arches/document-sign/features');
  }

  /**
   * The requester's own bulk sends in a workspace, newest first, a page at a
   * time: pass the previous page's `nextCursor` for the next.
   */
  async listDocumentSignBulkBatches(holderId: string, cursor?: string) {
    const params = new URLSearchParams({ holderId });
    if (cursor) params.set('cursor', cursor);
    return this.request<DocumentSignBulkBatchListResponse>(
      'GET',
      `/arches/document-sign/bulk-batches?${params.toString()}`,
    );
  }

  /** One bulk send and where each person in it stands. */
  async getDocumentSignBulkBatch(batchId: string) {
    return this.request<DocumentSignBulkBatchResponse>(
      'GET',
      `/arches/document-sign/bulk-batches/${batchId}`,
    );
  }

  async getDocumentSignSealedDownloadUrl(envelopeId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/arches/document-sign/envelopes/${envelopeId}/sealed/download-url`,
    );
  }

  async resealDocumentSignEnvelope(envelopeId: string) {
    return this.request<{
      envelopeId: string;
      alreadyExisted: boolean;
      sha256Hex: string;
      pageCount: number;
      sizeBytes: number;
    }>('POST', `/arches/document-sign/envelopes/${envelopeId}/reseal`);
  }

  async getDocumentSignSigningSealedUrl(token: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}/sealed/download-url`,
    );
  }

  async listDocumentSignEvents(envelopeId: string, options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit !== undefined) qs.set('limit', String(options.limit));
    if (options?.offset !== undefined) qs.set('offset', String(options.offset));
    const q = qs.toString();
    return this.request<{
      events: DocumentSignEventResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/document-sign/envelopes/${envelopeId}/events${q ? `?${q}` : ''}`);
  }

  async voidDocumentSignEnvelope(envelopeId: string, reason: string) {
    return this.request<{ envelope: DocumentSignEnvelopeResponse }>(
      'POST',
      `/arches/document-sign/envelopes/${envelopeId}/void`,
      { reason },
    );
  }

  // ==========================================================================
  // Fias Transfer — sender surface (authenticated)
  // ==========================================================================

  /**
   * List a holder's transfers. `archived` selects WHICH list — unarchived by
   * default, the archive when true. There is no combined view: the point of
   * archiving is that the default list stops growing.
   */
  async listFiasTransferSpaces(
    holderId: string,
    options?: { limit?: number; offset?: number; archived?: boolean },
  ) {
    const qs = new URLSearchParams({ holderId });
    if (options?.limit !== undefined) qs.set('limit', String(options.limit));
    if (options?.offset !== undefined) qs.set('offset', String(options.offset));
    if (options?.archived) qs.set('archived', 'true');
    return this.request<{ spaces: FiasTransferSpaceListItem[] }>(
      'GET',
      `/arches/fias-transfer/spaces?${qs.toString()}`,
    );
  }

  async getFiasTransferSpace(spaceId: string) {
    return this.request<FiasTransferSpaceDetailResponse>(
      'GET',
      `/arches/fias-transfer/spaces/${spaceId}`,
    );
  }

  async createFiasTransferSpace(input: { holderId: string; title: string; message?: string }) {
    return this.request<{ space: FiasTransferSpace }>(
      'POST',
      '/arches/fias-transfer/spaces',
      input,
    );
  }

  /**
   * Delete a transfer. Soft-deletes it AND revokes every one of its links in
   * the same transaction — this is the "stop sharing" action, as distinct from
   * archiving, which only moves it out of the default list.
   */
  async deleteFiasTransferSpace(spaceId: string) {
    return this.request<void>('DELETE', `/arches/fias-transfer/spaces/${spaceId}`);
  }

  /**
   * Wind a transfer down and file it away, or bring it back.
   *
   * Archiving REVOKES every live link in the same transaction — "archived"
   * reads as "put away", so leaving them serving would be the one lie that
   * leaks files. Restoring does not un-revoke; it returns the transfer and its
   * history, and the sender issues new links. `revokedLinkCount` reports what
   * was actually switched off.
   */
  async setFiasTransferSpaceArchived(spaceId: string, archived: boolean) {
    return this.request<{ space: FiasTransferSpace; revokedLinkCount: number }>(
      'POST',
      `/arches/fias-transfer/spaces/${spaceId}/archive`,
      { archived },
    );
  }

  /**
   * Attach Vault documents to a space (snapshot membership — adding a file to
   * a Vault folder later does NOT join it to an existing share).
   *
   * Partial success is expected, not exceptional: a document that has been
   * deleted, is arche-private, or exceeds the shareable sensitivity tier comes
   * back in `rejected` while the rest attach. The UI must surface both halves.
   */
  async attachFiasTransferDocuments(spaceId: string, documentIds: string[]) {
    return this.request<{
      attached: string[];
      rejected: Array<{ documentId: string; reason: string }>;
    }>('POST', `/arches/fias-transfer/spaces/${spaceId}/documents`, { documentIds });
  }

  /**
   * Per-document malware-scan state for a space.
   *
   * Its own call, not a field on the space detail: each entry costs one S3
   * `GetObjectTagging` server-side, which is exactly why membership resolution
   * skips scan verdicts entirely. Fetch it when the sender asks, not on every
   * dashboard load.
   */
  async getFiasTransferScanStatus(spaceId: string) {
    return this.request<FiasTransferScanStatusResponse>(
      'GET',
      `/arches/fias-transfer/spaces/${spaceId}/scan-status`,
    );
  }

  async detachFiasTransferDocument(spaceId: string, documentId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-transfer/spaces/${spaceId}/documents/${documentId}`,
    );
  }

  /**
   * Price a link BEFORE creating it — the whole reason bandwidth is priced at
   * creation rather than metered on download. `availableInCurrentPhase` is
   * false while Phase 1b (billing) is unbuilt and the quote is non-zero, so
   * the UI can disable the create button with an honest reason instead of
   * letting the POST fail.
   */
  async quoteFiasTransferLink(
    spaceId: string,
    input: { capabilities?: FiasTransferCapabilitySet; downloadAuthorizations: number },
  ) {
    return this.request<FiasTransferQuoteResponse>(
      'POST',
      `/arches/fias-transfer/spaces/${spaceId}/links/quote`,
      input,
    );
  }

  /**
   * Create a link. The response carries the raw share token, and this is the
   * ONLY time it crosses the API boundary on create — thereafter the sender
   * re-reads it from the dashboard, which decrypts the stored ciphertext.
   */
  async createFiasTransferLink(
    spaceId: string,
    input: {
      label?: string;
      capabilities?: FiasTransferCapabilitySet;
      downloadAuthorizations: number;
      recipientEmail?: string;
      recipientName?: string;
      password?: string;
      expiresInDays?: number;
    },
  ) {
    return this.request<{
      link: FiasTransferLink;
      token: string;
      quote: FiasTransferQuoteResponse;
    }>('POST', `/arches/fias-transfer/spaces/${spaceId}/links`, input);
  }

  /**
   * Recover the share URL for a link already issued. The raw token is returned
   * on create exactly once; this is how the sender copies it again later,
   * which is the reason the token is stored encrypted rather than hashed.
   */
  async getFiasTransferLinkUrl(linkId: string) {
    return this.request<{ token: string }>('GET', `/arches/fias-transfer/links/${linkId}/url`);
  }

  async revokeFiasTransferLink(linkId: string) {
    return this.request<void>('DELETE', `/arches/fias-transfer/links/${linkId}`);
  }

  async getFiasTransferLinkActivity(linkId: string) {
    return this.request<FiasTransferActivityResponse>(
      'GET',
      `/arches/fias-transfer/links/${linkId}/activity`,
    );
  }

  // ==========================================================================
  // Fias Transfer — public recipient surface (share-token auth)
  // ==========================================================================
  //
  // Same shape as the document-sign public endpoints: the token rides in the
  // URL path and the routes live under /v1/public/fias-transfer.
  //
  // Every one of these returns a UNIFORM 404 for an unknown, revoked, expired,
  // exhausted, unpaid, or deleted link. That is a security control, not an
  // error-handling gap — distinguishing them would turn the endpoint into an
  // existence oracle. UI copy must therefore say "this link is not available"
  // and must not speculate about why.

  async getFiasTransferPublicSummary(token: string) {
    return this.request<FiasTransferPublicSummary>(
      'GET',
      `/public/fias-transfer/${encodeURIComponent(token)}`,
    );
  }

  async unlockFiasTransfer(token: string, password: string) {
    return this.request<{ unlocked: boolean }>(
      'POST',
      `/public/fias-transfer/${encodeURIComponent(token)}/unlock`,
      { password },
    );
  }

  async acceptFiasTransferNda(token: string) {
    return this.request<{ accepted: boolean }>(
      'POST',
      `/public/fias-transfer/${encodeURIComponent(token)}/nda`,
    );
  }

  async recordFiasTransferEmail(token: string, email: string) {
    return this.request<{ recorded: boolean }>(
      'POST',
      `/public/fias-transfer/${encodeURIComponent(token)}/email`,
      { email },
    );
  }

  async getFiasTransferManifest(token: string) {
    return this.request<FiasTransferManifest>(
      'GET',
      `/public/fias-transfer/${encodeURIComponent(token)}/manifest`,
    );
  }

  /**
   * Consume one download authorization and open a session download window.
   * Idempotent while a window is already open (`consumed: false`), so a
   * double-click cannot burn two of the sender's authorizations.
   */
  async openFiasTransferWindow(token: string) {
    return this.request<{ windowExpiresAt: string; consumed: boolean }>(
      'POST',
      `/public/fias-transfer/${encodeURIComponent(token)}/window`,
    );
  }

  /**
   * Mint a download URL.
   *
   * `notScannable` means GuardDuty could not inspect this format at all —
   * the one verdict that is still served, because large and unusual files are
   * this product's core use case. It arrives HERE rather than on the manifest
   * because the verdict is read at the moment the recipient commits to the
   * file, so it cannot be stale.
   */
  async getFiasTransferFileUrl(token: string, documentId: string) {
    return this.request<{
      downloadUrl: string;
      expiresAt: string;
      fileName: string;
      proxied?: boolean;
      notScannable?: boolean;
    }>(
      'GET',
      `/public/fias-transfer/${encodeURIComponent(token)}/files/${documentId}/download-url`,
    );
  }

  /**
   * Server-rendered page images. Available on view-only links (where this IS
   * the delivery) and on page-analytics links (where the file is still
   * downloadable alongside it).
   */
  async getFiasTransferPreview(
    token: string,
    documentId: string,
    range?: { from?: number; to?: number },
  ) {
    // Paginated: the server renders at most `pageSpan` pages per request and
    // spends one unit of the session's per-window render budget doing it.
    // Requesting the whole document in one call is not merely slower — it was
    // the shape that put tens of megabytes of PNG data in one JSON response.
    const query = new URLSearchParams();
    if (range?.from !== undefined) query.set('from', String(range.from));
    if (range?.to !== undefined) query.set('to', String(range.to));
    const suffix = query.toString() ? `?${query.toString()}` : '';

    return this.request<FiasTransferPreview>(
      'GET',
      `/public/fias-transfer/${encodeURIComponent(token)}/files/${documentId}/preview${suffix}`,
    );
  }

  /**
   * Report page views from the viewer.
   *
   * Best-effort telemetry: a failure here must never surface to the reader or
   * block reading, which is why callers ignore the result.
   */
  async reportFiasTransferPages(
    token: string,
    documentId: string,
    pages: Array<{ pageNumber: number; dwellMs: number }>,
  ) {
    return this.request<{ recorded: number; capped: boolean }>(
      'POST',
      `/public/fias-transfer/${encodeURIComponent(token)}/files/${documentId}/pages`,
      { pages },
    );
  }

  async askFiasTransferQuestion(token: string, body: string, documentId?: string) {
    return this.request<{ question: FiasTransferQuestion }>(
      'POST',
      `/public/fias-transfer/${encodeURIComponent(token)}/questions`,
      documentId ? { body, documentId } : { body },
    );
  }

  /** The visitor's OWN thread. Session-scoped server-side — never other
   * holders of the same token. */
  async getFiasTransferQuestions(token: string) {
    return this.request<{ questions: FiasTransferQuestion[] }>(
      'GET',
      `/public/fias-transfer/${encodeURIComponent(token)}/questions`,
    );
  }

  // -- Sender-facing Phase 3 endpoints (authenticated) --

  async getFiasTransferPageStats(linkId: string) {
    return this.request<FiasTransferPageStats>(
      'GET',
      `/arches/fias-transfer/links/${linkId}/pages`,
    );
  }

  async getFiasTransferSpaceQuestions(spaceId: string, openOnly = false) {
    return this.request<{ questions: FiasTransferSenderQuestion[] }>(
      'GET',
      `/arches/fias-transfer/spaces/${spaceId}/questions${openOnly ? '?openOnly=true' : ''}`,
    );
  }

  async answerFiasTransferQuestion(questionId: string, body: string) {
    return this.request<{ question: FiasTransferSenderQuestion }>(
      'POST',
      `/arches/fias-transfer/questions/${questionId}/answer`,
      { body },
    );
  }

  async dismissFiasTransferQuestion(questionId: string) {
    return this.request<void>('POST', `/arches/fias-transfer/questions/${questionId}/dismiss`);
  }

  async setFiasTransferQuestionsEnabled(linkId: string, enabled: boolean) {
    return this.request<{ link: unknown }>(
      'PUT',
      `/arches/fias-transfer/links/${linkId}/questions-enabled`,
      { enabled },
    );
  }

  async getFiasTransferSpaceFolders(spaceId: string) {
    return this.request<{
      folders: FiasTransferSpaceFolder[];
      resolved: {
        fileCount: number;
        totalBytes: number;
        fromFolders: number;
        truncated: boolean;
      };
    }>('GET', `/arches/fias-transfer/spaces/${spaceId}/folders`);
  }

  async subscribeFiasTransferFolder(spaceId: string, folderId: string, includeSubfolders = true) {
    return this.request<{ folder: FiasTransferSpaceFolder }>(
      'POST',
      `/arches/fias-transfer/spaces/${spaceId}/folders`,
      { folderId, includeSubfolders },
    );
  }

  async unsubscribeFiasTransferFolder(spaceId: string, folderId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-transfer/spaces/${spaceId}/folders/${folderId}`,
    );
  }

  /**
   * How many active links currently expose a folder.
   *
   * Backs the standing badge in the Vault browser. A file becoming externally
   * visible with no explicit sharing action is the accidental-disclosure vector
   * dynamic membership introduces; this is how the sender notices.
   */
  async getFiasTransferFolderExposure(folderId: string) {
    return this.request<FiasTransferFolderExposure>(
      'GET',
      `/arches/fias-transfer/folders/${folderId}/exposure`,
    );
  }

  /**
   * Every exposed folder on a holder, in one call.
   *
   * SPARSE — folders with no exposure are absent from the map. That is what
   * lets the Vault browser badge a whole tree without sending a folder-id list
   * up, and without one request per node as the user expands it.
   */
  async getFiasTransferFolderExposures(holderId: string) {
    return this.request<{ exposures: Record<string, number> }>(
      'GET',
      `/arches/fias-transfer/folders/exposure?holderId=${encodeURIComponent(holderId)}`,
    );
  }

  // -- Public signing endpoints (token auth, used by SigningPage) --
  // These bypass the bearer-token machinery — the token rides in the URL
  // path, and the route lives under /v1/public/document-sign.
  async getDocumentSignSigningSession(token: string) {
    return this.request<DocumentSignSigningSessionResponse>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}`,
    );
  }

  async getDocumentSignSigningBranding(token: string) {
    return this.request<{ branding: DocumentSignPublicBranding | null }>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}/branding`,
    );
  }

  async getDocumentSignSigningFileUrl(token: string, fileId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/public/document-sign/sign/${encodeURIComponent(token)}/files/${fileId}/download-url`,
    );
  }

  /**
   * `disclosureVersion` is the version the page actually rendered. The server
   * refuses (409 `DISCLOSURE_UPDATED`) if it is no longer the live one, so a
   * signer never has an acceptance recorded against text they were not shown.
   */
  async acceptDocumentSignConsent(token: string, disclosureVersion?: number) {
    return this.request<{ success: true; alreadyAccepted: boolean; disclosureVersion: number }>(
      'POST',
      `/public/document-sign/sign/${encodeURIComponent(token)}/consent`,
      {
        acceptedDisclosure: true,
        ...(disclosureVersion === undefined ? {} : { disclosureVersion }),
      },
    );
  }

  /**
   * Second factor for a recipient whose sender set an access code. Uses the
   * pre-authentication resolver server-side, so it works while the session
   * endpoint is still answering 401 `ACCESS_CODE_REQUIRED`.
   */
  async verifyDocumentSignAccessCode(token: string, code: string) {
    return this.request<{ success: true }>(
      'POST',
      `/public/document-sign/sign/${encodeURIComponent(token)}/access-code`,
      { code },
    );
  }

  async submitDocumentSignFields(
    token: string,
    input: { fields: DocumentSignSigningFieldSubmission[] },
  ) {
    return this.request<{ success: true }>(
      'PUT',
      `/public/document-sign/sign/${encodeURIComponent(token)}/fields`,
      input,
    );
  }

  async submitDocumentSignSubmit(token: string) {
    return this.request<{
      success: true;
      envelopeStatus: DocumentSignEnvelopeStatus;
      envelopeCompleted: boolean;
    }>('POST', `/public/document-sign/sign/${encodeURIComponent(token)}/submit`);
  }

  /**
   * `declineKind` distinguishes refusing the AGREEMENT from refusing to sign
   * it ELECTRONICALLY (a paper-copy request). They are different acts and are
   * recorded differently; defaults to `agreement`.
   */
  async declineDocumentSignSignature(
    token: string,
    reason: string,
    declineKind: DocumentSignDeclineKind = 'agreement',
  ) {
    return this.request<{
      success: true;
      envelopeStatus: DocumentSignEnvelopeStatus;
      recipientStatus: string;
    }>('POST', `/public/document-sign/sign/${encodeURIComponent(token)}/decline`, {
      reason,
      declineKind,
    });
  }

  /**
   * Choose a Vault holder to receive the completed signed copy. Authenticated
   * (NOT on the public token surface) — the holder choice is validated against
   * a real Vault principal server-side. Only signers who are Fias users
   * (`recipient.deliveryEligible === true`) can call this.
   */
  async setDocumentSignDeliveryHolder(recipientId: string, holderId: string) {
    return this.request<{
      recipient: {
        recipientId: string;
        deliveryHolderId: string | null;
        deliveryHolderType: 'personal' | 'org' | null;
      };
    }>('POST', `/arches/document-sign/recipients/${recipientId}/delivery-holder`, { holderId });
  }

  // ==========================================================================
  // Customer Support API
  // ==========================================================================

  async trackArcheOpen(archeId: string) {
    return this.request<{ counted: boolean }>('POST', `/arches/${archeId}/track-open`);
  }

  async customerSupportChat(payload: {
    message: string;
    conversationId?: string;
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  }) {
    return this.request<{
      requestId: string;
      conversationId: string;
      selectedModel: {
        provider: string;
        modelName: string;
        displayName: string;
        tier: string;
      };
    }>('POST', '/arches/customer-support/chat', payload);
  }

  async createSuggestion(input: {
    title: string;
    description: string;
    archeId?: string | null;
    archeTagSource?: 'route' | 'user';
  }) {
    return this.request<{
      suggestionId: string;
      title: string;
      description: string;
      status: string;
      createdAt: string;
    }>('POST', '/arches/customer-support/suggestions', input);
  }

  async listSuggestionsAdmin(options?: {
    status?: string | string[];
    archeId?: string;
    awaitingReply?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    appendStatusParams(params, options?.status);
    if (options?.archeId) params.set('archeId', options.archeId);
    // Only sent when set — absent means "no filter", which is not the same as
    // asking for the items nobody is waiting on.
    if (options?.awaitingReply !== undefined)
      params.set('awaitingReply', String(options.awaitingReply));
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      suggestions: Array<{
        suggestionId: string;
        userId: string;
        user?: SupportUserRef | null;
        unread?: boolean;
        awaitingReply?: boolean;
        replyWaived?: boolean;
        archeId?: string | null;
        title: string;
        description: string;
        status: string;
        adminNotes: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/arches/customer-support/admin/suggestions${qs}`);
  }

  async updateSuggestionAdmin(
    suggestionId: string,
    input: { status?: string; adminNotes?: string | null },
  ) {
    return this.request<{
      suggestionId: string;
      title: string;
      description: string;
      status: string;
      adminNotes: string | null;
      updatedAt: string;
    }>('PUT', `/arches/customer-support/admin/suggestions/${suggestionId}`, input);
  }

  async deleteSuggestionAdmin(suggestionId: string) {
    return this.request<{ suggestionId: string }>(
      'DELETE',
      `/arches/customer-support/admin/suggestions/${suggestionId}`,
    );
  }

  async createBugReport(input: {
    title: string;
    description: string;
    stepsToReproduce?: string;
    archeId?: string | null;
    archeTagSource?: 'route' | 'user';
  }) {
    return this.request<{
      bugReportId: string;
      title: string;
      description: string;
      status: string;
      createdAt: string;
    }>('POST', '/arches/customer-support/bug-reports', input);
  }

  async listBugReportsAdmin(options?: {
    status?: string | string[];
    archeId?: string;
    awaitingReply?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    appendStatusParams(params, options?.status);
    if (options?.archeId) params.set('archeId', options.archeId);
    // Only sent when set — absent means "no filter", which is not the same as
    // asking for the items nobody is waiting on.
    if (options?.awaitingReply !== undefined)
      params.set('awaitingReply', String(options.awaitingReply));
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      bugReports: Array<{
        bugReportId: string;
        userId: string;
        user?: SupportUserRef | null;
        unread?: boolean;
        awaitingReply?: boolean;
        replyWaived?: boolean;
        archeId?: string | null;
        title: string;
        description: string;
        stepsToReproduce: string | null;
        status: string;
        adminNotes: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/arches/customer-support/admin/bug-reports${qs}`);
  }

  async updateBugReportAdmin(
    bugReportId: string,
    input: { status?: string; adminNotes?: string | null },
  ) {
    return this.request<{
      bugReportId: string;
      title: string;
      description: string;
      status: string;
      adminNotes: string | null;
      updatedAt: string;
    }>('PUT', `/arches/customer-support/admin/bug-reports/${bugReportId}`, input);
  }

  async deleteBugReportAdmin(bugReportId: string) {
    return this.request<{ bugReportId: string }>(
      'DELETE',
      `/arches/customer-support/admin/bug-reports/${bugReportId}`,
    );
  }

  async listProblemReportsAdmin(options?: {
    status?: string | string[];
    awaitingReply?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    appendStatusParams(params, options?.status);
    if (options?.awaitingReply !== undefined)
      params.set('awaitingReply', String(options.awaitingReply));
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      reports: Array<{
        reportId: string;
        sessionId: string | null;
        /** 'builder' (Arche Builder) or 'forge' (Fias Forge). */
        source?: 'builder' | 'forge';
        forgeId?: string | null;
        userId: string;
        user?: SupportUserRef | null;
        unread?: boolean;
        awaitingReply?: boolean;
        replyWaived?: boolean;
        description: string;
        problemScope: number;
        sessionStatus: string | null;
        archId: string | null;
        templateId: string | null;
        totalInputTokens: number;
        totalOutputTokens: number;
        totalCostCents: number;
        userAgent: string | null;
        pageUrl: string | null;
        status: string;
        adminNotes: string | null;
        reviewedBy: string | null;
        reviewedAt: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/arches/customer-support/admin/problem-reports${qs}`);
  }

  async updateProblemReportAdmin(
    reportId: string,
    input: { status?: string; adminNotes?: string | null },
  ) {
    return this.request<{
      reportId: string;
      status: string;
      adminNotes: string | null;
      reviewedBy: string | null;
      reviewedAt: string | null;
      updatedAt: string;
    }>('PUT', `/arches/customer-support/admin/problem-reports/${reportId}`, input);
  }

  async deleteProblemReportAdmin(reportId: string) {
    return this.request<{ reportId: string }>(
      'DELETE',
      `/arches/customer-support/admin/problem-reports/${reportId}`,
    );
  }

  async listFiasAiReportsAdmin(options?: {
    status?: string | string[];
    awaitingReply?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    appendStatusParams(params, options?.status);
    if (options?.awaitingReply !== undefined)
      params.set('awaitingReply', String(options.awaitingReply));
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      reports: Array<{
        reportId: string;
        conversationId: string | null;
        userId: string;
        user?: SupportUserRef | null;
        unread?: boolean;
        awaitingReply?: boolean;
        replyWaived?: boolean;
        description: string;
        modelInfo: unknown | null;
        userAgent: string | null;
        pageUrl: string | null;
        status: string;
        adminNotes: string | null;
        reviewedBy: string | null;
        reviewedAt: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('GET', `/arches/customer-support/admin/fias-ai-reports${qs}`);
  }

  async updateFiasAiReportAdmin(
    reportId: string,
    input: { status?: string; adminNotes?: string | null },
  ) {
    return this.request<{
      reportId: string;
      status: string;
      adminNotes: string | null;
      reviewedBy: string | null;
      reviewedAt: string | null;
      updatedAt: string;
    }>('PUT', `/arches/customer-support/admin/fias-ai-reports/${reportId}`, input);
  }

  async deleteFiasAiReportAdmin(reportId: string) {
    return this.request<{ reportId: string }>(
      'DELETE',
      `/arches/customer-support/admin/fias-ai-reports/${reportId}`,
    );
  }

  // --- Admin read/unread tracking ---

  async getSupportUnreadCounts() {
    return this.request<SupportAdminBadgeCounts>(
      'GET',
      '/arches/customer-support/admin/unread-counts',
    );
  }

  async markSuggestionReadAdmin(suggestionId: string) {
    return this.request<{ ok: boolean }>(
      'POST',
      `/arches/customer-support/admin/suggestions/${suggestionId}/read`,
    );
  }

  async markBugReportReadAdmin(bugReportId: string) {
    return this.request<{ ok: boolean }>(
      'POST',
      `/arches/customer-support/admin/bug-reports/${bugReportId}/read`,
    );
  }

  async markProblemReportReadAdmin(reportId: string) {
    return this.request<{ ok: boolean }>(
      'POST',
      `/arches/customer-support/admin/problem-reports/${reportId}/read`,
    );
  }

  async markFiasAiReportReadAdmin(reportId: string) {
    return this.request<{ ok: boolean }>(
      'POST',
      `/arches/customer-support/admin/fias-ai-reports/${reportId}/read`,
    );
  }

  // --- Awaiting-reply waivers ("no reply needed") ---

  /**
   * Set/clear the "no reply needed" waiver on any support record. `collection`
   * is the admin route segment for the record's class, so the five classes
   * share one call rather than five copies that can drift apart.
   */
  async setSupportReplyWaivedAdmin(
    collection: SupportAdminCollection,
    id: string,
    waived: boolean,
  ) {
    return this.request<{ waived: boolean }>(
      'POST',
      `/arches/customer-support/admin/${collection}/${id}/reply-waived`,
      { waived },
    );
  }

  // --- Roadmap automation (admin) ---

  async getRoadmapSettingsAdmin() {
    return this.request<{
      settings: {
        holderId: string | null;
        bugsListId: string | null;
        featuresListId: string | null;
      };
      workspaces: Array<{ holderId: string; holderType: 'personal' | 'org'; name: string }>;
      lists: Array<{ listId: string; name: string }>;
    }>('GET', '/arches/customer-support/admin/roadmap/settings');
  }

  /** Todo lists in a specific workspace, for the roadmap workspace switcher. */
  async getRoadmapListsAdmin(holderId: string) {
    return this.request<{ lists: Array<{ listId: string; name: string }> }>(
      'GET',
      `/arches/customer-support/admin/roadmap/lists?holderId=${encodeURIComponent(holderId)}`,
    );
  }

  async setRoadmapSettingsAdmin(input: {
    holderId: string;
    bugsListId?: string | null;
    featuresListId?: string | null;
  }) {
    return this.request<{
      holderId: string | null;
      bugsListId: string | null;
      featuresListId: string | null;
    }>('PUT', '/arches/customer-support/admin/roadmap/settings', input);
  }

  async runRoadmapSweepAdmin() {
    return this.request<{ processed: number; paused: number; failed: number }>(
      'POST',
      '/arches/customer-support/admin/roadmap/sweep',
    );
  }

  async createSupportTicket(input: {
    subject: string;
    description: string;
    categoryId?: string;
    priority?: string;
    archeId?: string | null;
    archeTagSource?: 'route' | 'user';
  }) {
    return this.request<SupportTicketResponse>('POST', '/arches/customer-support/tickets', input);
  }

  async listSupportTickets(options?: {
    status?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<SupportTicketListResult>(
      'GET',
      `/arches/customer-support/tickets${qs ? `?${qs}` : ''}`,
    );
  }

  async getSupportTicket(ticketId: string) {
    return this.request<SupportTicketDetailResponse>(
      'GET',
      `/arches/customer-support/tickets/${ticketId}`,
    );
  }

  async listSupportTicketMessages(ticketId: string) {
    return this.request<{ messages: SupportMessageResponse[] }>(
      'GET',
      `/arches/customer-support/tickets/${ticketId}/messages`,
    );
  }

  async createSupportTicketMessage(ticketId: string, input: { content: string }) {
    return this.request<SupportMessageResponse>(
      'POST',
      `/arches/customer-support/tickets/${ticketId}/messages`,
      input,
    );
  }

  async listSupportFaqs(categoryId?: string) {
    const qs = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request<{ faqs: SupportFaqResponse[] }>('GET', `/arches/customer-support/faq${qs}`);
  }

  async searchSupportFaqs(query: string) {
    return this.request<{ faqs: SupportFaqResponse[] }>(
      'GET',
      `/arches/customer-support/faq/search?q=${encodeURIComponent(query)}`,
    );
  }

  async listSupportCategoriesUser() {
    return this.request<{ categories: SupportCategoryResponse[] }>(
      'GET',
      '/arches/customer-support/categories',
    );
  }

  // Admin endpoints
  async listAllSupportTickets(options?: {
    status?: string | string[];
    priority?: string;
    categoryId?: string;
    assignedTo?: string;
    userId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    tagId?: string;
    archeId?: string;
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    appendStatusParams(params, options?.status);
    if (options?.priority) params.set('priority', options.priority);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.assignedTo) params.set('assignedTo', options.assignedTo);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.tagId) params.set('tagId', options.tagId);
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.includeDeleted) params.set('includeDeleted', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<SupportAdminTicketListResult>(
      'GET',
      `/arches/customer-support/admin/tickets${qs ? `?${qs}` : ''}`,
    );
  }

  async bulkUpdateSupportTickets(input: {
    ticketIds: string[];
    status?: string;
    priority?: string;
    assignedTo?: string | null;
  }) {
    return this.request<{ updated: number }>(
      'PUT',
      '/arches/customer-support/admin/tickets/bulk',
      input,
    );
  }

  async listSupportTicketsByUser(userId: string, options?: { limit?: number }) {
    const params = new URLSearchParams();
    params.set('userId', userId);
    if (options?.limit) params.set('limit', options.limit.toString());
    return this.request<SupportAdminTicketListResult>(
      'GET',
      `/arches/customer-support/admin/tickets?${params.toString()}`,
    );
  }

  async getSupportTicketAdmin(ticketId: string) {
    return this.request<SupportAdminTicketResponse>(
      'GET',
      `/arches/customer-support/admin/tickets/${ticketId}`,
    );
  }

  /** Admin: list a user's persisted support AI-chat conversations. */
  async getSupportUserChatConversations(userId: string) {
    return this.request<{ conversations: SupportChatConversationSummary[] }>(
      'GET',
      `/arches/customer-support/admin/users/${encodeURIComponent(userId)}/chat`,
    );
  }

  /** Admin: fetch the turns of a support AI-chat conversation. */
  async getSupportChatMessages(conversationId: string) {
    return this.request<{ messages: SupportChatMessageResponse[] }>(
      'GET',
      `/arches/customer-support/admin/chat/${conversationId}/messages`,
    );
  }

  async updateSupportTicketAdmin(
    ticketId: string,
    input: {
      status?: string;
      /** `null` clears the human priority back to unset. */
      priority?: string | null;
      aiPriority?: string | null;
      categoryId?: string;
      /** Admin arche retag; null = Fias system. Server stamps source='admin'. */
      archeId?: string | null;
    },
  ) {
    return this.request<SupportAdminTicketResponse>(
      'PUT',
      `/arches/customer-support/admin/tickets/${ticketId}`,
      input,
    );
  }

  /** Admin arche name-search for the ticket retag picker (searches all arches). */
  async searchArchesForSupportAdmin(q: string) {
    return this.request<{
      arches: Array<{ archeId: string; name: string; imageRef: string | null }>;
    }>('GET', `/arches/customer-support/admin/arche-search?q=${encodeURIComponent(q)}`);
  }

  async listSupportTicketMessagesAdmin(ticketId: string) {
    return this.request<{ messages: SupportAdminMessageResponse[] }>(
      'GET',
      `/arches/customer-support/admin/tickets/${ticketId}/messages`,
    );
  }

  async createSupportTicketMessageAdmin(
    ticketId: string,
    input: { content: string; isInternal?: boolean },
  ) {
    return this.request<SupportAdminMessageResponse>(
      'POST',
      `/arches/customer-support/admin/tickets/${ticketId}/messages`,
      input,
    );
  }

  async deleteSupportMessageAdmin(ticketId: string, messageId: string) {
    return this.request<{ messageId: string; deletedAt: string }>(
      'DELETE',
      `/arches/customer-support/admin/tickets/${ticketId}/messages/${messageId}`,
    );
  }

  /** Undo an unsend — puts the message back in the thread for both sides. */
  async restoreSupportMessageAdmin(ticketId: string, messageId: string) {
    return this.request<{ messageId: string; deletedAt: null }>(
      'POST',
      `/arches/customer-support/admin/tickets/${ticketId}/messages/${messageId}/restore`,
    );
  }

  async getSupportAnalytics() {
    return this.request<SupportAnalyticsResponse>(
      'GET',
      '/arches/customer-support/admin/analytics',
    );
  }

  async listSupportCategoriesAdmin() {
    return this.request<{ categories: SupportCategoryAdminResponse[] }>(
      'GET',
      '/arches/customer-support/admin/categories',
    );
  }

  async createSupportCategory(input: {
    name: string;
    description?: string;
    displayOrder?: number;
  }) {
    return this.request<SupportCategoryAdminResponse>(
      'POST',
      '/arches/customer-support/admin/categories',
      input,
    );
  }

  async updateSupportCategory(
    categoryId: string,
    input: {
      name?: string;
      description?: string | null;
      displayOrder?: number;
      isActive?: boolean;
    },
  ) {
    return this.request<SupportCategoryAdminResponse>(
      'PUT',
      `/arches/customer-support/admin/categories/${categoryId}`,
      input,
    );
  }

  async deleteSupportCategory(categoryId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/categories/${categoryId}`);
  }

  async listSupportFaqsAdmin(categoryId?: string) {
    const qs = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request<{ faqs: SupportFaqAdminResponse[] }>(
      'GET',
      `/arches/customer-support/admin/faq${qs}`,
    );
  }

  async createSupportFaq(input: {
    question: string;
    answer: string;
    categoryId?: string;
    displayOrder?: number;
    isPublished?: boolean;
  }) {
    return this.request<SupportFaqAdminResponse>(
      'POST',
      '/arches/customer-support/admin/faq',
      input,
    );
  }

  async updateSupportFaq(
    faqId: string,
    input: {
      question?: string;
      answer?: string;
      categoryId?: string | null;
      displayOrder?: number;
      isPublished?: boolean;
    },
  ) {
    return this.request<SupportFaqAdminResponse>(
      'PUT',
      `/arches/customer-support/admin/faq/${faqId}`,
      input,
    );
  }

  async deleteSupportFaq(faqId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/faq/${faqId}`);
  }

  async listSupportCannedResponses(categoryId?: string) {
    const qs = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request<{ responses: SupportCannedResponseResponse[] }>(
      'GET',
      `/arches/customer-support/admin/canned-responses${qs}`,
    );
  }

  async createSupportCannedResponse(input: {
    title: string;
    content: string;
    categoryId?: string;
  }) {
    return this.request<SupportCannedResponseResponse>(
      'POST',
      '/arches/customer-support/admin/canned-responses',
      input,
    );
  }

  async updateSupportCannedResponse(
    responseId: string,
    input: { title?: string; content?: string; categoryId?: string | null },
  ) {
    return this.request<SupportCannedResponseResponse>(
      'PUT',
      `/arches/customer-support/admin/canned-responses/${responseId}`,
      input,
    );
  }

  async deleteSupportCannedResponse(responseId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/customer-support/admin/canned-responses/${responseId}`,
    );
  }

  // Tag management
  async listSupportTags() {
    return this.request<{ tags: SupportTagResponse[] }>(
      'GET',
      '/arches/customer-support/admin/tags',
    );
  }

  async createSupportTag(input: { name: string; color?: string; description?: string }) {
    return this.request<SupportTagResponse>('POST', '/arches/customer-support/admin/tags', input);
  }

  async updateSupportTag(
    tagId: string,
    input: { name?: string; color?: string; description?: string | null },
  ) {
    return this.request<SupportTagResponse>(
      'PUT',
      `/arches/customer-support/admin/tags/${tagId}`,
      input,
    );
  }

  async deleteSupportTag(tagId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/tags/${tagId}`);
  }

  async assignSupportTicketTags(ticketId: string, tagIds: string[]) {
    return this.request<{ tags: SupportTagResponse[] }>(
      'POST',
      `/arches/customer-support/admin/tickets/${ticketId}/tags`,
      { tagIds },
    );
  }

  async removeSupportTicketTag(ticketId: string, tagId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/customer-support/admin/tickets/${ticketId}/tags/${tagId}`,
    );
  }

  // Knowledge Base management (admin)
  async listSupportKbArticlesAdmin(options?: {
    categoryId?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<{ articles: SupportKbArticleResponse[]; total: number }>(
      'GET',
      `/arches/customer-support/admin/knowledge-base${qs ? `?${qs}` : ''}`,
    );
  }

  async createSupportKbArticle(input: {
    title: string;
    content: string;
    categoryId?: string;
    keywords?: string[];
    isPublished?: boolean;
  }) {
    return this.request<SupportKbArticleResponse>(
      'POST',
      '/arches/customer-support/admin/knowledge-base',
      input,
    );
  }

  async updateSupportKbArticle(
    kbId: string,
    input: {
      title?: string;
      content?: string;
      categoryId?: string | null;
      keywords?: string[];
      isPublished?: boolean;
    },
  ) {
    return this.request<SupportKbArticleResponse>(
      'PUT',
      `/arches/customer-support/admin/knowledge-base/${kbId}`,
      input,
    );
  }

  async deleteSupportKbArticle(kbId: string) {
    return this.request<void>('DELETE', `/arches/customer-support/admin/knowledge-base/${kbId}`);
  }

  // Knowledge Base search (user)
  async searchSupportKbArticles(q: string, limit?: number) {
    const params = new URLSearchParams();
    params.set('q', q);
    if (limit) params.set('limit', limit.toString());
    return this.request<{ articles: SupportKbArticleResponse[] }>(
      'GET',
      `/arches/customer-support/knowledge-base/search?${params.toString()}`,
    );
  }

  // ==========================================================================
  // Fias Leads API (Phase 9)
  // ==========================================================================
  //
  // All endpoints are Holder-scoped — every method takes `holderId` as the
  // first argument and serializes it as `?holder=<vh_*>` per the
  // /v1/arches/fias-leads/* surface. Shared concepts (companies, contacts,
  // activities, tasks, email templates, contact relationships) lens over
  // Vault substrate rows; Leads-private concepts (deals, pipelines,
  // automations, custom fields, saved views) live in `fias_leads_*` tables.

  // --- Companies (vault_companies via fias-leads) ---

  async listFiasLeadsCompanies(
    holderId: string,
    options?: { includeArchived?: boolean; limit?: number },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.includeArchived) qs.set('includeArchived', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ companies: FiasLeadsCompanyDto[] }>(
      'GET',
      `/arches/fias-leads/companies?${qs}`,
    );
  }

  /**
   * Lightweight (id, name) refs of EVERY non-archived company,
   * alphabetized server-side. Backs dropdowns/selectors that must be
   * complete — listFiasLeadsCompanies caps at 500 full rows.
   */
  async listFiasLeadsCompanyRefs(holderId: string) {
    return this.request<{ companies: FiasLeadsCompanyRefDto[] }>(
      'GET',
      `/arches/fias-leads/companies/refs?holder=${holderId}`,
    );
  }

  async getFiasLeadsCompany(holderId: string, companyId: string) {
    return this.request<{ company: FiasLeadsCompanyDto }>(
      'GET',
      `/arches/fias-leads/companies/${companyId}?holder=${holderId}`,
    );
  }

  async findFiasLeadsCompanyByDomain(holderId: string, domain: string) {
    return this.request<{ company: FiasLeadsCompanyDto | null }>(
      'GET',
      `/arches/fias-leads/companies/lookup/by-domain?holder=${holderId}&domain=${encodeURIComponent(domain)}`,
    );
  }

  async createFiasLeadsCompany(holderId: string, input: FiasLeadsCreateCompanyInput) {
    return this.request<{ company: FiasLeadsCompanyDto }>(
      'POST',
      `/arches/fias-leads/companies?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsCompany(
    holderId: string,
    companyId: string,
    input: FiasLeadsUpdateCompanyInput,
  ) {
    return this.request<{ company: FiasLeadsCompanyDto }>(
      'PATCH',
      `/arches/fias-leads/companies/${companyId}?holder=${holderId}`,
      input,
    );
  }

  async archiveFiasLeadsCompany(holderId: string, companyId: string) {
    return this.request<{ company: FiasLeadsCompanyDto }>(
      'DELETE',
      `/arches/fias-leads/companies/${companyId}?holder=${holderId}`,
    );
  }

  async restoreFiasLeadsCompany(holderId: string, companyId: string) {
    return this.request<{ company: FiasLeadsCompanyDto }>(
      'POST',
      `/arches/fias-leads/companies/${companyId}/restore?holder=${holderId}`,
    );
  }

  async setOwnerFiasLeadsCompany(
    holderId: string,
    companyId: string,
    ownerPrincipalId: string | null,
  ) {
    return this.request<{ company: FiasLeadsCompanyDto }>(
      'PUT',
      `/arches/fias-leads/companies/${companyId}/owner?holder=${holderId}`,
      { ownerPrincipalId },
    );
  }

  // --- Contacts (vault_business_contacts + fias_leads_contact_extension) ---

  async listFiasLeadsContacts(
    holderId: string,
    options?: { companyId?: string; includeArchived?: boolean; limit?: number },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.companyId) qs.set('companyId', options.companyId);
    if (options?.includeArchived) qs.set('includeArchived', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{
      contacts: Array<FiasLeadsContactDto & { extension: FiasLeadsContactExtensionDto | null }>;
    }>('GET', `/arches/fias-leads/contacts?${qs}`);
  }

  async getFiasLeadsContact(holderId: string, contactId: string) {
    return this.request<{
      contact: FiasLeadsContactDto;
      extension: FiasLeadsContactExtensionDto | null;
    }>('GET', `/arches/fias-leads/contacts/${contactId}?holder=${holderId}`);
  }

  async findFiasLeadsContactByEmail(holderId: string, email: string) {
    return this.request<{ contact: FiasLeadsContactDto | null }>(
      'GET',
      `/arches/fias-leads/contacts/lookup/by-email?holder=${holderId}&email=${encodeURIComponent(email)}`,
    );
  }

  async createFiasLeadsContact(holderId: string, input: FiasLeadsCreateContactInput) {
    return this.request<{
      contact: FiasLeadsContactDto;
      extension: FiasLeadsContactExtensionDto | null;
    }>('POST', `/arches/fias-leads/contacts?holder=${holderId}`, input);
  }

  async updateFiasLeadsContact(
    holderId: string,
    contactId: string,
    input: FiasLeadsUpdateContactInput,
  ) {
    return this.request<{
      contact: FiasLeadsContactDto;
      extension: FiasLeadsContactExtensionDto | null;
    }>('PATCH', `/arches/fias-leads/contacts/${contactId}?holder=${holderId}`, input);
  }

  async archiveFiasLeadsContact(holderId: string, contactId: string) {
    return this.request<{ contact: FiasLeadsContactDto }>(
      'DELETE',
      `/arches/fias-leads/contacts/${contactId}?holder=${holderId}`,
    );
  }

  async restoreFiasLeadsContact(holderId: string, contactId: string) {
    return this.request<{ contact: FiasLeadsContactDto }>(
      'POST',
      `/arches/fias-leads/contacts/${contactId}/restore?holder=${holderId}`,
    );
  }

  async setOwnerFiasLeadsContact(
    holderId: string,
    contactId: string,
    ownerPrincipalId: string | null,
  ) {
    return this.request<{ contact: FiasLeadsContactDto }>(
      'PUT',
      `/arches/fias-leads/contacts/${contactId}/owner?holder=${holderId}`,
      { ownerPrincipalId },
    );
  }

  // --- Activities (vault_activities) ---

  async listFiasLeadsActivities(
    holderId: string,
    options?: {
      regardingType?: FiasLeadsActivityRegardingType;
      regardingId?: string;
      kind?: FiasLeadsActivityKind;
      before?: string;
      includeDeleted?: boolean;
      sortBy?: 'occurred_at' | 'created_at';
      sortDir?: 'asc' | 'desc';
      limit?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.regardingType) qs.set('regardingType', options.regardingType);
    if (options?.regardingId) qs.set('regardingId', options.regardingId);
    if (options?.kind) qs.set('kind', options.kind);
    if (options?.before) qs.set('before', options.before);
    if (options?.includeDeleted) qs.set('includeDeleted', 'true');
    if (options?.sortBy) qs.set('sortBy', options.sortBy);
    if (options?.sortDir) qs.set('sortDir', options.sortDir);
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ activities: FiasLeadsActivityDto[] }>(
      'GET',
      `/arches/fias-leads/activities?${qs}`,
    );
  }

  async getFiasLeadsActivity(holderId: string, activityId: string) {
    return this.request<{
      activity: FiasLeadsActivityDto;
      participants: FiasLeadsActivityParticipantDto[];
    }>('GET', `/arches/fias-leads/activities/${activityId}?holder=${holderId}`);
  }

  async createFiasLeadsActivity(holderId: string, input: FiasLeadsCreateActivityInput) {
    return this.request<{ activity: FiasLeadsActivityDto }>(
      'POST',
      `/arches/fias-leads/activities?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsActivity(
    holderId: string,
    activityId: string,
    input: Partial<FiasLeadsCreateActivityInput>,
  ) {
    return this.request<{ activity: FiasLeadsActivityDto }>(
      'PATCH',
      `/arches/fias-leads/activities/${activityId}?holder=${holderId}`,
      input,
    );
  }

  async deleteFiasLeadsActivity(holderId: string, activityId: string) {
    return this.request<{ activity: FiasLeadsActivityDto }>(
      'DELETE',
      `/arches/fias-leads/activities/${activityId}?holder=${holderId}`,
    );
  }

  async restoreFiasLeadsActivity(holderId: string, activityId: string) {
    return this.request<{ activity: FiasLeadsActivityDto }>(
      'POST',
      `/arches/fias-leads/activities/${activityId}/restore?holder=${holderId}`,
    );
  }

  // --- Tasks (vault_tasks) ---

  async listFiasLeadsTasks(
    holderId: string,
    options?: {
      regardingType?: FiasLeadsTaskRegardingType;
      regardingId?: string;
      status?: FiasLeadsTaskStatus;
      assigneePrincipalId?: string;
      dueBefore?: string;
      includeDeleted?: boolean;
      limit?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.regardingType) qs.set('regardingType', options.regardingType);
    if (options?.regardingId) qs.set('regardingId', options.regardingId);
    if (options?.status) qs.set('status', options.status);
    if (options?.assigneePrincipalId) qs.set('assigneePrincipalId', options.assigneePrincipalId);
    if (options?.dueBefore) qs.set('dueBefore', options.dueBefore);
    if (options?.includeDeleted) qs.set('includeDeleted', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ tasks: FiasLeadsTaskDto[] }>('GET', `/arches/fias-leads/tasks?${qs}`);
  }

  async getFiasLeadsTask(holderId: string, taskId: string) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'GET',
      `/arches/fias-leads/tasks/${taskId}?holder=${holderId}`,
    );
  }

  async createFiasLeadsTask(holderId: string, input: FiasLeadsCreateTaskInput) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'POST',
      `/arches/fias-leads/tasks?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsTask(holderId: string, taskId: string, input: FiasLeadsUpdateTaskInput) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'PATCH',
      `/arches/fias-leads/tasks/${taskId}?holder=${holderId}`,
      input,
    );
  }

  async completeFiasLeadsTask(holderId: string, taskId: string) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'POST',
      `/arches/fias-leads/tasks/${taskId}/complete?holder=${holderId}`,
    );
  }

  async reopenFiasLeadsTask(holderId: string, taskId: string) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'POST',
      `/arches/fias-leads/tasks/${taskId}/reopen?holder=${holderId}`,
    );
  }

  async cancelFiasLeadsTask(holderId: string, taskId: string) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'POST',
      `/arches/fias-leads/tasks/${taskId}/cancel?holder=${holderId}`,
    );
  }

  async deleteFiasLeadsTask(holderId: string, taskId: string) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'DELETE',
      `/arches/fias-leads/tasks/${taskId}?holder=${holderId}`,
    );
  }

  async restoreFiasLeadsTask(holderId: string, taskId: string) {
    return this.request<{ task: FiasLeadsTaskDto }>(
      'POST',
      `/arches/fias-leads/tasks/${taskId}/restore?holder=${holderId}`,
    );
  }

  /**
   * Send an email about a deal through the caller's own connected mailbox
   * and log it on the deal's activity timeline.
   *
   * `accountId` is an Email-arche connection id (see `getEmailConnections`),
   * because the mail goes out over that account's own SMTP server — this is
   * never a platform/system send. A `false` `activityLogged` means the mail
   * WAS sent but the timeline write failed; surface that rather than
   * treating it as a failed send, or the user will send twice.
   */
  async sendFiasLeadsDealEmail(
    holderId: string,
    dealId: string,
    input: FiasLeadsSendDealEmailInput,
  ) {
    return this.request<{ messageId: string; activityLogged: boolean }>(
      'POST',
      `/arches/fias-leads/deals/${dealId}/send-email?holder=${holderId}`,
      input,
    );
  }

  // --- Email Templates (vault_email_templates) ---

  async listFiasLeadsEmailTemplates(
    holderId: string,
    options?: {
      category?: FiasLeadsEmailTemplateCategory;
      includeArchived?: boolean;
      limit?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.category) qs.set('category', options.category);
    if (options?.includeArchived) qs.set('includeArchived', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ templates: FiasLeadsEmailTemplateDto[] }>(
      'GET',
      `/arches/fias-leads/email-templates?${qs}`,
    );
  }

  async getFiasLeadsEmailTemplate(holderId: string, templateId: string) {
    return this.request<{ template: FiasLeadsEmailTemplateDto }>(
      'GET',
      `/arches/fias-leads/email-templates/${templateId}?holder=${holderId}`,
    );
  }

  async createFiasLeadsEmailTemplate(holderId: string, input: FiasLeadsCreateEmailTemplateInput) {
    return this.request<{ template: FiasLeadsEmailTemplateDto }>(
      'POST',
      `/arches/fias-leads/email-templates?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsEmailTemplate(
    holderId: string,
    templateId: string,
    input: Partial<FiasLeadsCreateEmailTemplateInput>,
  ) {
    return this.request<{ template: FiasLeadsEmailTemplateDto }>(
      'PATCH',
      `/arches/fias-leads/email-templates/${templateId}?holder=${holderId}`,
      input,
    );
  }

  async archiveFiasLeadsEmailTemplate(holderId: string, templateId: string) {
    return this.request<{ template: FiasLeadsEmailTemplateDto }>(
      'DELETE',
      `/arches/fias-leads/email-templates/${templateId}?holder=${holderId}`,
    );
  }

  async restoreFiasLeadsEmailTemplate(holderId: string, templateId: string) {
    return this.request<{ template: FiasLeadsEmailTemplateDto }>(
      'POST',
      `/arches/fias-leads/email-templates/${templateId}/restore?holder=${holderId}`,
    );
  }

  async renderFiasLeadsEmailTemplate(
    holderId: string,
    templateId: string,
    variables: Record<string, string>,
  ) {
    return this.request<{ subject: string; bodyMarkdown: string }>(
      'POST',
      `/arches/fias-leads/email-templates/${templateId}/render?holder=${holderId}`,
      { variables },
    );
  }

  // --- Relationships (vault_contact_relationships) ---

  async listFiasLeadsRelationships(
    holderId: string,
    options?: {
      fromContactId?: string;
      toContactId?: string;
      relationshipType?: FiasLeadsContactRelationshipType;
      includeDeleted?: boolean;
      limit?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.fromContactId) qs.set('fromContactId', options.fromContactId);
    if (options?.toContactId) qs.set('toContactId', options.toContactId);
    if (options?.relationshipType) qs.set('relationshipType', options.relationshipType);
    if (options?.includeDeleted) qs.set('includeDeleted', 'true');
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ relationships: FiasLeadsContactRelationshipDto[] }>(
      'GET',
      `/arches/fias-leads/relationships?${qs}`,
    );
  }

  async createFiasLeadsRelationship(holderId: string, input: FiasLeadsCreateRelationshipInput) {
    return this.request<{ relationship: FiasLeadsContactRelationshipDto }>(
      'POST',
      `/arches/fias-leads/relationships?holder=${holderId}`,
      input,
    );
  }

  async deleteFiasLeadsRelationship(holderId: string, relationshipId: string) {
    return this.request<{ relationship: FiasLeadsContactRelationshipDto }>(
      'DELETE',
      `/arches/fias-leads/relationships/${relationshipId}?holder=${holderId}`,
    );
  }

  // --- Pipelines (fias_leads_pipelines) ---

  async listFiasLeadsPipelines(holderId: string) {
    return this.request<{ pipelines: FiasLeadsPipelineDto[] }>(
      'GET',
      `/arches/fias-leads/pipelines?holder=${holderId}`,
    );
  }

  async getFiasLeadsPipeline(holderId: string, pipelineId: string) {
    return this.request<{ pipeline: FiasLeadsPipelineDto }>(
      'GET',
      `/arches/fias-leads/pipelines/${pipelineId}?holder=${holderId}`,
    );
  }

  async createFiasLeadsPipeline(holderId: string, input: FiasLeadsCreatePipelineInput) {
    return this.request<{ pipeline: FiasLeadsPipelineDto }>(
      'POST',
      `/arches/fias-leads/pipelines?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsPipeline(
    holderId: string,
    pipelineId: string,
    input: FiasLeadsUpdatePipelineInput,
  ) {
    return this.request<{ pipeline: FiasLeadsPipelineDto }>(
      'PATCH',
      `/arches/fias-leads/pipelines/${pipelineId}?holder=${holderId}`,
      input,
    );
  }

  async deleteFiasLeadsPipeline(holderId: string, pipelineId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-leads/pipelines/${pipelineId}?holder=${holderId}`,
    );
  }

  async addFiasLeadsStage(
    holderId: string,
    pipelineId: string,
    input: {
      name: string;
      stageType?: FiasLeadsPipelineStageType;
      color?: string;
      displayOrder?: number;
    },
  ) {
    return this.request<{ stage: FiasLeadsPipelineStageDto }>(
      'POST',
      `/arches/fias-leads/pipelines/${pipelineId}/stages?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsStage(
    holderId: string,
    pipelineId: string,
    stageId: string,
    input: {
      name?: string;
      stageType?: FiasLeadsPipelineStageType;
      color?: string;
      displayOrder?: number;
    },
  ) {
    return this.request<{ stage: FiasLeadsPipelineStageDto }>(
      'PATCH',
      `/arches/fias-leads/pipelines/${pipelineId}/stages/${stageId}?holder=${holderId}`,
      input,
    );
  }

  async deleteFiasLeadsStage(holderId: string, pipelineId: string, stageId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-leads/pipelines/${pipelineId}/stages/${stageId}?holder=${holderId}`,
    );
  }

  async reorderFiasLeadsStages(
    holderId: string,
    pipelineId: string,
    stages: Array<{ stageId: string; displayOrder: number }>,
  ) {
    return this.request<void>(
      'POST',
      `/arches/fias-leads/pipelines/${pipelineId}/stages/reorder?holder=${holderId}`,
      { stages },
    );
  }

  // --- Deals (fias_leads_deals) ---

  async listFiasLeadsDeals(
    holderId: string,
    options?: {
      pipelineId?: string;
      stageId?: string;
      contactId?: string;
      companyId?: string;
      ownerPrincipalId?: string;
      status?: FiasLeadsDealStatus;
      sortBy?: FiasLeadsDealSortBy;
      sortDir?: 'asc' | 'desc';
      limit?: number;
    },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.pipelineId) qs.set('pipelineId', options.pipelineId);
    if (options?.stageId) qs.set('stageId', options.stageId);
    if (options?.contactId) qs.set('contactId', options.contactId);
    if (options?.companyId) qs.set('companyId', options.companyId);
    if (options?.ownerPrincipalId) qs.set('ownerPrincipalId', options.ownerPrincipalId);
    if (options?.status) qs.set('status', options.status);
    if (options?.sortBy) qs.set('sortBy', options.sortBy);
    if (options?.sortDir) qs.set('sortDir', options.sortDir);
    if (options?.limit) qs.set('limit', String(options.limit));
    return this.request<{ deals: FiasLeadsDealDto[] }>('GET', `/arches/fias-leads/deals?${qs}`);
  }

  async getFiasLeadsPipelineSummary(
    holderId: string,
    pipelineId: string,
    options?: { sortBy?: FiasLeadsDealSortBy; sortDir?: 'asc' | 'desc' },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options?.sortBy) qs.set('sortBy', options.sortBy);
    if (options?.sortDir) qs.set('sortDir', options.sortDir);
    return this.request<{ stages: FiasLeadsPipelineSummaryStageDto[] }>(
      'GET',
      `/arches/fias-leads/deals/pipeline-summary/${pipelineId}?${qs}`,
    );
  }

  async getFiasLeadsDeal(holderId: string, dealId: string) {
    return this.request<{ deal: FiasLeadsDealDto }>(
      'GET',
      `/arches/fias-leads/deals/${dealId}?holder=${holderId}`,
    );
  }

  async createFiasLeadsDeal(holderId: string, input: FiasLeadsCreateDealInput) {
    return this.request<{ deal: FiasLeadsDealDto }>(
      'POST',
      `/arches/fias-leads/deals?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsDeal(holderId: string, dealId: string, input: FiasLeadsUpdateDealInput) {
    return this.request<{ deal: FiasLeadsDealDto }>(
      'PATCH',
      `/arches/fias-leads/deals/${dealId}?holder=${holderId}`,
      input,
    );
  }

  async moveFiasLeadsDealStage(holderId: string, dealId: string, stageId: string) {
    return this.request<{ deal: FiasLeadsDealDto }>(
      'PATCH',
      `/arches/fias-leads/deals/${dealId}/stage?holder=${holderId}`,
      { stageId },
    );
  }

  async deleteFiasLeadsDeal(holderId: string, dealId: string) {
    return this.request<void>('DELETE', `/arches/fias-leads/deals/${dealId}?holder=${holderId}`);
  }

  async restoreFiasLeadsDeal(holderId: string, dealId: string) {
    return this.request<{ deal: FiasLeadsDealDto }>(
      'POST',
      `/arches/fias-leads/deals/${dealId}/restore?holder=${holderId}`,
    );
  }

  async listFiasLeadsDealContacts(holderId: string, dealId: string) {
    return this.request<{ contacts: FiasLeadsDealContactDto[] }>(
      'GET',
      `/arches/fias-leads/deals/${dealId}/contacts?holder=${holderId}`,
    );
  }

  async addFiasLeadsDealContact(
    holderId: string,
    dealId: string,
    input: { contactId: string; role?: string },
  ) {
    return this.request<{ contact: FiasLeadsDealContactDto }>(
      'POST',
      `/arches/fias-leads/deals/${dealId}/contacts?holder=${holderId}`,
      input,
    );
  }

  async removeFiasLeadsDealContact(holderId: string, dealId: string, contactId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-leads/deals/${dealId}/contacts/${contactId}?holder=${holderId}`,
    );
  }

  async setFiasLeadsDealContactPrimary(holderId: string, dealId: string, contactId: string) {
    return this.request<{ ok: boolean }>(
      'POST',
      `/arches/fias-leads/deals/${dealId}/contacts/${contactId}/primary?holder=${holderId}`,
    );
  }

  // --- Automations (fias_leads_automation_rules) ---

  async listFiasLeadsAutomations(holderId: string) {
    return this.request<{ rules: FiasLeadsAutomationRuleDto[] }>(
      'GET',
      `/arches/fias-leads/automations?holder=${holderId}`,
    );
  }

  async getFiasLeadsAutomation(holderId: string, ruleId: string) {
    return this.request<{ rule: FiasLeadsAutomationRuleDto }>(
      'GET',
      `/arches/fias-leads/automations/${ruleId}?holder=${holderId}`,
    );
  }

  async createFiasLeadsAutomation(holderId: string, input: FiasLeadsCreateAutomationInput) {
    return this.request<{ rule: { id: string } }>(
      'POST',
      `/arches/fias-leads/automations?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsAutomation(
    holderId: string,
    ruleId: string,
    input: Partial<FiasLeadsCreateAutomationInput>,
  ) {
    return this.request<{ rule: FiasLeadsAutomationRuleDto }>(
      'PUT',
      `/arches/fias-leads/automations/${ruleId}?holder=${holderId}`,
      input,
    );
  }

  async toggleFiasLeadsAutomation(holderId: string, ruleId: string) {
    return this.request<{ rule: FiasLeadsAutomationRuleDto }>(
      'POST',
      `/arches/fias-leads/automations/${ruleId}/toggle?holder=${holderId}`,
    );
  }

  async deleteFiasLeadsAutomation(holderId: string, ruleId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-leads/automations/${ruleId}?holder=${holderId}`,
    );
  }

  async getFiasLeadsAutomationExecutions(holderId: string, ruleId: string, limit?: number) {
    const qs = new URLSearchParams({ holder: holderId });
    if (limit) qs.set('limit', String(limit));
    return this.request<{ executions: FiasLeadsAutomationExecutionDto[] }>(
      'GET',
      `/arches/fias-leads/automations/${ruleId}/executions?${qs}`,
    );
  }

  // --- Custom Fields (fias_leads_custom_field_definitions) ---

  async listFiasLeadsCustomFields(holderId: string, entityType?: FiasLeadsCustomFieldEntityType) {
    const qs = new URLSearchParams({ holder: holderId });
    if (entityType) qs.set('entityType', entityType);
    return this.request<{ fields: FiasLeadsCustomFieldDefinitionDto[] }>(
      'GET',
      `/arches/fias-leads/custom-fields?${qs}`,
    );
  }

  async createFiasLeadsCustomField(holderId: string, input: FiasLeadsCreateCustomFieldInput) {
    return this.request<{ field: { id: string } }>(
      'POST',
      `/arches/fias-leads/custom-fields?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsCustomField(
    holderId: string,
    fieldId: string,
    input: Partial<Omit<FiasLeadsCreateCustomFieldInput, 'entityType' | 'fieldName'>>,
  ) {
    return this.request<{ field: FiasLeadsCustomFieldDefinitionDto }>(
      'PUT',
      `/arches/fias-leads/custom-fields/${fieldId}?holder=${holderId}`,
      input,
    );
  }

  async deleteFiasLeadsCustomField(holderId: string, fieldId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/fias-leads/custom-fields/${fieldId}?holder=${holderId}`,
    );
  }

  // --- Saved Views (fias_leads_saved_views) ---

  async listFiasLeadsSavedViews(holderId: string, entityType?: FiasLeadsSavedViewEntityType) {
    const qs = new URLSearchParams({ holder: holderId });
    if (entityType) qs.set('entityType', entityType);
    return this.request<{ views: FiasLeadsSavedViewDto[] }>(
      'GET',
      `/arches/fias-leads/views?${qs}`,
    );
  }

  async createFiasLeadsSavedView(holderId: string, input: FiasLeadsCreateSavedViewInput) {
    return this.request<{ view: { id: string } }>(
      'POST',
      `/arches/fias-leads/views?holder=${holderId}`,
      input,
    );
  }

  async updateFiasLeadsSavedView(
    holderId: string,
    viewId: string,
    input: Partial<Omit<FiasLeadsCreateSavedViewInput, 'entityType'>>,
  ) {
    return this.request<{ view: FiasLeadsSavedViewDto }>(
      'PUT',
      `/arches/fias-leads/views/${viewId}?holder=${holderId}`,
      input,
    );
  }

  async deleteFiasLeadsSavedView(holderId: string, viewId: string) {
    return this.request<void>('DELETE', `/arches/fias-leads/views/${viewId}?holder=${holderId}`);
  }

  // --- Dashboard / Reports / Deal Risk ---

  async getFiasLeadsDashboard(holderId: string, pipelineId?: string) {
    const qs = new URLSearchParams({ holder: holderId });
    if (pipelineId) qs.set('pipelineId', pipelineId);
    return this.request<FiasLeadsDashboardMetricsDto>(
      'GET',
      `/arches/fias-leads/dashboard/metrics?${qs}`,
    );
  }

  async getFiasLeadsPipelineVelocity(holderId: string, pipelineId?: string) {
    const qs = new URLSearchParams({ holder: holderId });
    if (pipelineId) qs.set('pipelineId', pipelineId);
    return this.request<{ stages: FiasLeadsPipelineVelocityRowDto[] }>(
      'GET',
      `/arches/fias-leads/reports/pipeline-velocity?${qs}`,
    );
  }

  async getFiasLeadsForecast(holderId: string, pipelineId?: string) {
    const qs = new URLSearchParams({ holder: holderId });
    if (pipelineId) qs.set('pipelineId', pipelineId);
    return this.request<{ forecast: FiasLeadsForecastRowDto[] }>(
      'GET',
      `/arches/fias-leads/reports/forecast?${qs}`,
    );
  }

  async getFiasLeadsActivityTrends(holderId: string, days?: number) {
    const qs = new URLSearchParams({ holder: holderId });
    if (days) qs.set('days', String(days));
    return this.request<{ trends: FiasLeadsActivityTrendsRowDto[] }>(
      'GET',
      `/arches/fias-leads/reports/activity-trends?${qs}`,
    );
  }

  async getFiasLeadsLostDeals(holderId: string, pipelineId?: string) {
    const qs = new URLSearchParams({ holder: holderId });
    if (pipelineId) qs.set('pipelineId', pipelineId);
    return this.request<{ lostDeals: FiasLeadsLostDealRowDto[] }>(
      'GET',
      `/arches/fias-leads/reports/lost-deals?${qs}`,
    );
  }

  async getFiasLeadsDealRisk(holderId: string) {
    return this.request<{ deals: FiasLeadsDealRiskAssessmentDto[] }>(
      'GET',
      `/arches/fias-leads/deal-risk?holder=${holderId}`,
    );
  }

  async getFiasLeadsDealRiskAlerts(holderId: string) {
    return this.request<{ alerts: FiasLeadsDealRiskAssessmentDto[] }>(
      'GET',
      `/arches/fias-leads/deal-risk/alerts?holder=${holderId}`,
    );
  }

  async refreshFiasLeadsDealRisk(holderId: string, dealId: string) {
    return this.request<{ deal: FiasLeadsDealRiskAssessmentDto }>(
      'POST',
      `/arches/fias-leads/deal-risk/refresh/${dealId}?holder=${holderId}`,
    );
  }

  // --- Timeline (merged activities + tasks) ---

  async getFiasLeadsTimeline(
    holderId: string,
    regardingType: FiasLeadsActivityRegardingType,
    regardingId: string,
    limit?: number,
  ) {
    const qs = new URLSearchParams({
      holder: holderId,
      regardingType,
      regardingId,
    });
    if (limit) qs.set('limit', String(limit));
    return this.request<{
      items: Array<
        | { type: 'activity'; occurredAt: string; item: FiasLeadsActivityDto }
        | { type: 'task'; occurredAt: string; item: FiasLeadsTaskDto }
      >;
    }>('GET', `/arches/fias-leads/timeline?${qs}`);
  }

  // --- AI ---

  async fiasLeadsScoreLead(holderId: string, contactId: string) {
    return this.request<FiasLeadsLeadScoreResultDto>(
      'POST',
      `/arches/fias-leads/ai/score-lead/${contactId}?holder=${holderId}`,
    );
  }

  async fiasLeadsWinLikelihood(holderId: string, dealId: string) {
    return this.request<FiasLeadsWinLikelihoodResultDto>(
      'POST',
      `/arches/fias-leads/ai/win-likelihood/${dealId}?holder=${holderId}`,
    );
  }

  async fiasLeadsSummarize(holderId: string, entityType: 'contact' | 'company', entityId: string) {
    return this.request<FiasLeadsAiSummaryResultDto>(
      'POST',
      `/arches/fias-leads/ai/summarize/${entityType}/${entityId}?holder=${holderId}`,
    );
  }

  async fiasLeadsSearch(
    holderId: string,
    entityType: 'contact' | 'company',
    q: string,
    limit?: number,
  ) {
    const qs = new URLSearchParams({ holder: holderId, q });
    if (limit) qs.set('limit', String(limit));
    return this.request<{
      entityType: 'contact' | 'company';
      results: unknown[];
      idColumn: string;
    }>('GET', `/arches/fias-leads/ai/search/${entityType}?${qs}`);
  }

  /**
   * Global cross-entity search: one query returns matching contacts,
   * companies, and deals — each with its related records (a contact's
   * company + deals, a company's counts, a deal's contact + company).
   */
  async fiasLeadsGlobalSearch(holderId: string, q: string, limit?: number) {
    const qs = new URLSearchParams({ holder: holderId, q });
    if (limit) qs.set('limit', String(limit));
    return this.request<FiasLeadsGlobalSearchResultDto>('GET', `/arches/fias-leads/search?${qs}`);
  }

  async fiasLeadsSimilar(
    holderId: string,
    entityType: 'contact' | 'company',
    entityId: string,
    limit?: number,
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (limit) qs.set('limit', String(limit));
    return this.request<{ entityType: 'contact' | 'company'; results: unknown[] }>(
      'GET',
      `/arches/fias-leads/ai/similar/${entityType}/${entityId}?${qs}`,
    );
  }

  // --- Bulk Actions ---

  async fiasLeadsBulkContactsArchive(holderId: string, ids: string[]) {
    return this.request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      'POST',
      `/arches/fias-leads/bulk/contacts/archive?holder=${holderId}`,
      { ids },
    );
  }

  async fiasLeadsBulkContactsRestore(holderId: string, ids: string[]) {
    return this.request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      'POST',
      `/arches/fias-leads/bulk/contacts/restore?holder=${holderId}`,
      { ids },
    );
  }

  async fiasLeadsBulkContactsUpdate(
    holderId: string,
    ids: string[],
    payload: FiasLeadsUpdateContactInput,
  ) {
    return this.request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      'POST',
      `/arches/fias-leads/bulk/contacts/update?holder=${holderId}`,
      { ids, payload },
    );
  }

  async fiasLeadsBulkCompaniesArchive(holderId: string, ids: string[]) {
    return this.request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      'POST',
      `/arches/fias-leads/bulk/companies/archive?holder=${holderId}`,
      { ids },
    );
  }

  async fiasLeadsBulkCompaniesRestore(holderId: string, ids: string[]) {
    return this.request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      'POST',
      `/arches/fias-leads/bulk/companies/restore?holder=${holderId}`,
      { ids },
    );
  }

  async fiasLeadsBulkCompaniesUpdate(
    holderId: string,
    ids: string[],
    payload: FiasLeadsUpdateCompanyInput,
  ) {
    return this.request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      'POST',
      `/arches/fias-leads/bulk/companies/update?holder=${holderId}`,
      { ids, payload },
    );
  }

  /**
   * Permanent delete of ARCHIVED contacts (irreversible; the records
   * are shared with Fias 360). Per-item `code` carries the machine
   * reason when an item is refused (e.g. CONTACT_REFERENCED_BY_DEALS).
   */
  async fiasLeadsBulkContactsDelete(holderId: string, ids: string[]) {
    return this.request<{ results: FiasLeadsBulkDeleteResult[] }>(
      'POST',
      `/arches/fias-leads/bulk/contacts/delete?holder=${holderId}`,
      { ids },
    );
  }

  /** Company counterpart of fiasLeadsBulkContactsDelete. */
  async fiasLeadsBulkCompaniesDelete(holderId: string, ids: string[]) {
    return this.request<{ results: FiasLeadsBulkDeleteResult[] }>(
      'POST',
      `/arches/fias-leads/bulk/companies/delete?holder=${holderId}`,
      { ids },
    );
  }

  // --- Import / Export ---

  async fiasLeadsExport(
    holderId: string,
    entityType: 'contact' | 'company' | 'deal',
    limit?: number,
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (limit) qs.set('limit', String(limit));
    const response = await fetch(`${API_BASE}/arches/fias-leads/export/${entityType}?${qs}`, {
      credentials: sessionCredentials(),
    });
    if (!response.ok) throw new Error(`Export failed: ${response.status}`);
    return response.text();
  }

  async fiasLeadsImportPreview(
    holderId: string,
    entityType: FiasLeadsImportEntityType,
    body: {
      rows: Array<Record<string, string>>;
      dedupStrategy?: FiasLeadsImportDedupStrategy;
      nameFallback?: FiasLeadsImportNameFallback;
      pipelineId?: string;
      defaultStageId?: string;
      startIndex?: number;
      rowIndexes?: number[];
      knownKeys?: string[];
    },
  ) {
    return this.request<FiasLeadsImportPreviewResponse>(
      'POST',
      `/arches/fias-leads/import/${entityType}/preview?holder=${holderId}`,
      body,
    );
  }

  async fiasLeadsImportExecute(
    holderId: string,
    entityType: FiasLeadsImportEntityType,
    body: {
      batchId: string;
      rows: Array<Record<string, string>>;
      startIndex?: number;
      rowIndexes?: number[];
    },
  ) {
    return this.request<{
      entityType: string;
      created: number;
      skipped: number;
      failed: Array<{ rowIndex: number; error: string }>;
      batch: FiasLeadsImportBatchDto | null;
    }>('POST', `/arches/fias-leads/import/${entityType}/execute?holder=${holderId}`, body);
  }

  async fiasLeadsImportCreateBatch(
    holderId: string,
    input: {
      entityType: FiasLeadsImportEntityType;
      fileName: string;
      dedupStrategy?: FiasLeadsImportDedupStrategy;
      nameFallback?: FiasLeadsImportNameFallback;
      pipelineId?: string;
      defaultStageId?: string;
      totalRows?: number;
    },
  ) {
    return this.request<{ batch: FiasLeadsImportBatchDto }>(
      'POST',
      `/arches/fias-leads/import/batches?holder=${holderId}`,
      input,
    );
  }

  async fiasLeadsImportBatches(holderId: string) {
    return this.request<{ batches: FiasLeadsImportBatchDto[] }>(
      'GET',
      `/arches/fias-leads/import/batches?holder=${holderId}`,
    );
  }

  async fiasLeadsImportBatchFinalize(holderId: string, batchId: string) {
    return this.request<{ batch: FiasLeadsImportBatchDto }>(
      'POST',
      `/arches/fias-leads/import/batches/${batchId}/finalize?holder=${holderId}`,
      {},
    );
  }

  async fiasLeadsImportBatchArchive(holderId: string, batchId: string) {
    return this.request<FiasLeadsImportBatchLifecycleResponse>(
      'POST',
      `/arches/fias-leads/import/batches/${batchId}/archive?holder=${holderId}`,
      {},
    );
  }

  async fiasLeadsImportBatchRestore(holderId: string, batchId: string) {
    return this.request<FiasLeadsImportBatchLifecycleResponse>(
      'POST',
      `/arches/fias-leads/import/batches/${batchId}/restore?holder=${holderId}`,
      {},
    );
  }

  // --- Dedup ---

  async findFiasLeadsDuplicates(holderId: string, limit?: number) {
    const qs = new URLSearchParams({ holder: holderId });
    if (limit) qs.set('limit', String(limit));
    return this.request<{ duplicates: FiasLeadsDuplicateGroupDto[] }>(
      'GET',
      `/arches/fias-leads/dedup/contacts?${qs}`,
    );
  }

  async mergeFiasLeadsContacts(
    holderId: string,
    canonicalId: string,
    mergedId: string,
    fields?: Record<string, unknown>,
  ) {
    return this.request<{ canonicalId: string; mergedId: string; ok: boolean }>(
      'POST',
      `/arches/fias-leads/dedup/contacts/merge?holder=${holderId}`,
      { canonicalId, mergedId, fields },
    );
  }

  // --- Cross-arche presence ---

  async getFiasLeadsCrossArchePresence(
    holderId: string,
    options: { contactIds?: string[]; companyIds?: string[] },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (options.contactIds?.length) qs.set('contactIds', options.contactIds.join(','));
    if (options.companyIds?.length) qs.set('companyIds', options.companyIds.join(','));
    return this.request<FiasLeadsCrossArchePresenceDto>(
      'GET',
      `/arches/fias-leads/cross-arche-presence?${qs}`,
    );
  }

  // ─── External Sync ───────────────────────────────────────────────────────

  // Calendar sync
  async listCalendarSyncConnections() {
    return this.request<{ connections: SyncConnection[] }>(
      'GET',
      '/arches/calendar/sync/connections',
    );
  }

  async initCalendarSyncOAuth(provider: 'google' | 'microsoft') {
    return this.request<{ authorizationUrl: string; state: string }>(
      'POST',
      '/arches/calendar/sync/connections/oauth/init',
      { provider },
    );
  }

  async calendarSyncOAuthCallback(code: string, state: string) {
    return this.request<{ connection: SyncConnection }>(
      'GET',
      `/arches/calendar/sync/connections/oauth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    );
  }

  async connectCalendarApple(username: string, appPassword: string) {
    return this.request<{ connection: SyncConnection }>(
      'POST',
      '/arches/calendar/sync/connections/apple',
      { username, appPassword },
    );
  }

  async disconnectCalendarSync(connectionId: string, mode: 'keep' | 'remove' = 'keep') {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/sync/connections/${connectionId}?mode=${mode}`,
    );
  }

  /** Counts for the disconnect confirm dialog: how many events a "remove"
   * would delete (remote-imported only) vs how many always stay local. */
  async calendarSyncDisconnectPreview(connectionId: string) {
    return this.request<{ removable: number; keptLocal: number }>(
      'GET',
      `/arches/calendar/sync/connections/${connectionId}/disconnect-preview`,
    );
  }

  // ── Email arche — external account connections ──
  async listEmailConnections() {
    return this.request<{ accounts: EmailConnection[] }>('GET', '/arches/email/connections');
  }

  async initEmailConnectionOAuth(provider: 'gmail' | 'microsoft') {
    return this.request<{ authorizationUrl: string }>(
      'POST',
      '/arches/email/connections/oauth/init',
      { provider },
    );
  }

  async emailConnectionOAuthCallback(code: string, state: string) {
    return this.request<EmailConnection>(
      'GET',
      `/arches/email/connections/oauth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    );
  }

  async connectEmailImap(input: EmailImapConnectInput) {
    return this.request<EmailConnection>('POST', '/arches/email/connections/imap', input);
  }

  async disconnectEmailConnection(accountId: string) {
    return this.request<void>('DELETE', `/arches/email/connections/${accountId}`);
  }

  /** Set the display order of connected mailboxes (full ordered list of account ids). */
  async reorderEmailConnections(order: string[]) {
    return this.request<{ accounts: EmailConnection[] }>(
      'POST',
      '/arches/email/connections/reorder',
      { order },
    );
  }

  /** Edit a connected account's display name and/or make it the primary mailbox. */
  async updateEmailConnection(
    accountId: string,
    input: { displayName?: string | null; makePrimary?: boolean },
  ) {
    return this.request<EmailConnection>('PATCH', `/arches/email/connections/${accountId}`, input);
  }

  /** Edit a generic account's IMAP/POP3/SMTP transport (re-probed server-side). */
  async updateEmailConnectionTransport(
    accountId: string,
    input: {
      incomingProtocol: 'imap' | 'pop3';
      incomingHost: string;
      incomingPort: number;
      incomingSecurity: 'ssl_tls' | 'starttls' | 'none';
      outgoingHost: string;
      outgoingPort: number;
      outgoingSecurity: 'ssl_tls' | 'starttls' | 'none';
    },
  ) {
    return this.request<EmailConnection>(
      'PATCH',
      `/arches/email/connections/${accountId}/transport`,
      input,
    );
  }

  /** Re-enter the username/password for a generic account (re-probed server-side). */
  async updateEmailConnectionCredentials(
    accountId: string,
    input: { username?: string; password: string },
  ) {
    return this.request<EmailConnection>(
      'POST',
      `/arches/email/connections/${accountId}/credentials`,
      input,
    );
  }

  async getEmailAutoconfig(email: string) {
    return this.request<{ suggestion: EmailAutoconfigSuggestion | null }>(
      'GET',
      `/arches/email/connections/autoconfig?email=${encodeURIComponent(email)}`,
    );
  }

  // ── Email arche — mailbox read + inline sync ──
  // folderId scopes the pull to one folder (folder-open auto-sync); omit it for
  // a full-account sync across every folder. backfill (with folderId) pulls one
  // batch of OLDER mail. Always send an object body (even `{}`) — the route
  // declares a body schema and Fastify rejects an absent body against
  // `type: 'object'` with a 400 "Validation failed".
  async syncEmailAccount(accountId: string, folderId?: string, backfill?: boolean) {
    const body: { folderId?: string; backfill?: boolean } = {};
    if (folderId) body.folderId = folderId;
    if (backfill) body.backfill = true;
    return this.request<{
      foldersUpserted: number;
      messagesUpserted: number;
      writebacksApplied?: number;
      /** Backfill-only: true once the folder has been walked to the bottom. */
      complete?: boolean;
    }>('POST', `/arches/email/connections/${accountId}/sync`, body);
  }

  /**
   * Full-account sync streamed over SSE for live "folder X of Y — N messages"
   * progress. Returns the raw Response; consume it with `consumeSSEFrames`.
   * The plain `syncEmailAccount` (JSON) remains for background/auto-sync.
   * `backfillAll` streams one OLDER-mail pass across every folder (the "Load all
   * messages" action) — the done frame's `complete` says whether to repeat.
   */
  async streamEmailAccountSync(
    accountId: string,
    options?: { signal?: AbortSignal; backfillAll?: boolean },
  ): Promise<Response> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    return fetch(
      `${API_BASE}/arches/email/connections/${encodeURIComponent(accountId)}/sync/stream`,
      {
        method: 'POST',
        headers,
        credentials: sessionCredentials(),
        signal: options?.signal,
        // Always send an object body — the route declares a body schema.
        body: JSON.stringify(options?.backfillAll ? { backfillAll: true } : {}),
      },
    );
  }

  // Drain pending local→server mutations (read/star/move/delete) for all of the
  // holder's accounts. Called debounced after write actions so they reach the
  // server promptly instead of waiting for the next sync.
  async flushEmailWritebacks() {
    return this.request<{ writebacksApplied: number }>('POST', `/arches/email/flush`, {});
  }

  // Permanently delete everything in the account's Trash folder (server +
  // local). Irreversible.
  async emptyEmailTrash(accountId: string) {
    return this.request<{ purged: number }>(
      'POST',
      `/arches/email/connections/${accountId}/trash/empty`,
      {},
    );
  }

  async listEmailMailboxFolders(accountId: string) {
    return this.request<{ folders: EmailFolderDto[] }>(
      'GET',
      `/arches/email/folders?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  // ── Email arche — custom-folder management (server-backed over IMAP) ──
  // `parentFolderId` nests the new folder under an existing one (a subfolder).
  async createEmailMailboxFolder(accountId: string, name: string, parentFolderId?: string) {
    return this.request<{ folder: EmailFolderDto }>('POST', `/arches/email/folders`, {
      accountId,
      name,
      ...(parentFolderId ? { parentFolderId } : {}),
    });
  }

  async renameEmailMailboxFolder(folderId: string, accountId: string, name: string) {
    return this.request<{ folder: EmailFolderDto }>(
      'PATCH',
      `/arches/email/folders/${encodeURIComponent(folderId)}`,
      { accountId, name },
    );
  }

  async deleteEmailMailboxFolder(folderId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/folders/${encodeURIComponent(folderId)}?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  // accountId omitted = unified (all-accounts) view; the backend stays
  // holder-scoped so this only ever spans the caller's own accounts.
  async listEmailMailboxThreads(accountId?: string, folderId?: string) {
    const q = new URLSearchParams();
    if (accountId) q.set('accountId', accountId);
    if (folderId) q.set('folderId', folderId);
    return this.request<{ threads: EmailThreadDto[] }>('GET', `/arches/email/threads?${q}`);
  }

  /** The holder's editable category set (seeds the four defaults on first read). */
  async listEmailMailboxCategories() {
    return this.request<{ categories: EmailCategoryDto[] }>('GET', `/arches/email/categories`);
  }

  async createEmailMailboxCategory(input: { name: string; color?: string; sortOrder?: number }) {
    return this.request<{ category: EmailCategoryDto }>('POST', `/arches/email/categories`, input);
  }

  async updateEmailMailboxCategory(
    categoryId: string,
    input: { name?: string; color?: string; sortOrder?: number },
  ) {
    return this.request<{ category: EmailCategoryDto }>(
      'PATCH',
      `/arches/email/categories/${encodeURIComponent(categoryId)}`,
      input,
    );
  }

  async deleteEmailMailboxCategory(categoryId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/categories/${encodeURIComponent(categoryId)}`,
    );
  }

  // ── Email arche — labels (account-scoped, many-per-message) ──
  async listEmailMailboxLabels(accountId: string) {
    return this.request<{ labels: EmailLabelMailboxDto[] }>(
      'GET',
      `/arches/email/labels?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  async createEmailMailboxLabel(input: {
    accountId: string;
    name: string;
    color?: string;
    sortOrder?: number;
  }) {
    return this.request<{ label: EmailLabelMailboxDto }>('POST', `/arches/email/labels`, input);
  }

  async updateEmailMailboxLabel(
    labelId: string,
    input: { accountId: string; name?: string; color?: string; sortOrder?: number },
  ) {
    return this.request<{ label: EmailLabelMailboxDto }>(
      'PATCH',
      `/arches/email/labels/${encodeURIComponent(labelId)}`,
      input,
    );
  }

  async deleteEmailMailboxLabel(labelId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/labels/${encodeURIComponent(labelId)}?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  /** Replace a message's full label set; returns the resulting chip refs. */
  async applyEmailMailboxMessageLabels(messageId: string, accountId: string, labelIds: string[]) {
    return this.request<{ labels: EmailMessageLabelRefDto[] }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/labels`,
      { accountId, labelIds },
    );
  }

  /** RFC 8058 one-click unsubscribe (server-side POST). Only for messages whose
   *  List-Unsubscribe-Post advertises one-click; the mailto/URL fallback opens
   *  client-side. */
  async unsubscribeEmailMailboxMessage(messageId: string) {
    return this.request<{ ok: boolean; status: number }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/unsubscribe`,
    );
  }

  /** Add a `.ics` invite attachment to the Vault calendar (idempotent on
   *  ical_uid). Targets the holder's default calendar when `calendarId` is omitted. */
  async addEmailCalendarInvite(messageId: string, attachmentId: string, calendarId?: string) {
    return this.request<EmailCalendarInviteAddResult>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(
        attachmentId,
      )}/calendar`,
      calendarId ? { calendarId } : {},
    );
  }

  /**
   * Parse a `.ics` invite attachment and return its event details for an in-app
   * preview (no writes). Used to render a meeting-invite card instead of a raw
   * iCalendar blob, which browsers can't display inline.
   */
  async getEmailCalendarInvitePreview(messageId: string, attachmentId: string) {
    return this.request<CalendarInvitePreview>(
      'GET',
      `/arches/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(
        attachmentId,
      )}/calendar`,
    );
  }

  async listEmailMailboxMessages(
    accountId: string | undefined,
    opts: {
      folderId?: string;
      folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
      threadId?: string;
      /** Drill-down to one smart category ('uncategorized' = threads with none). */
      categoryId?: string;
      /** Drill-down to one label (message-level). */
      labelId?: string;
      snoozed?: 'exclude' | 'only' | 'include';
      screener?: boolean;
      /** Unified inbox: account ids the (per-account) screener applies to. */
      screenerAccountIds?: string[];
      /** The dedicated Screener inbox: only held (pending-sender) mail. */
      screenerView?: boolean;
      isRead?: boolean;
      isStarred?: boolean;
      hasAttachments?: boolean;
      limit?: number;
      offset?: number;
    } & EmailAdvancedFilters = {},
  ) {
    const q = new URLSearchParams();
    if (accountId) q.set('accountId', accountId);
    if (opts.folderId) q.set('folderId', opts.folderId);
    if (opts.folderSystemRole) q.set('folderSystemRole', opts.folderSystemRole);
    if (opts.threadId) q.set('threadId', opts.threadId);
    if (opts.categoryId) q.set('categoryId', opts.categoryId);
    if (opts.labelId) q.set('labelId', opts.labelId);
    if (opts.snoozed) q.set('snoozed', opts.snoozed);
    if (opts.screener) q.set('screener', 'true');
    if (opts.screenerAccountIds && opts.screenerAccountIds.length > 0)
      q.set('screenerAccountIds', opts.screenerAccountIds.join(','));
    if (opts.screenerView) q.set('screenerView', 'true');
    if (opts.isRead !== undefined) q.set('isRead', String(opts.isRead));
    if (opts.isStarred !== undefined) q.set('isStarred', String(opts.isStarred));
    if (opts.hasAttachments !== undefined) q.set('hasAttachments', String(opts.hasAttachments));
    if (opts.answered !== undefined) q.set('answered', String(opts.answered));
    if (opts.inAddressBook !== undefined) q.set('inAddressBook', String(opts.inAddressBook));
    if (opts.receivedAfter) q.set('receivedAfter', opts.receivedAfter);
    if (opts.receivedBefore) q.set('receivedBefore', opts.receivedBefore);
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.offset) q.set('offset', String(opts.offset));
    return this.request<{ messages: EmailMessageDto[] }>('GET', `/arches/email/messages?${q}`);
  }

  // ── Fias-AI "current list" (dynamic AI-defined list shown in the mailbox) ──
  async showAiEmailListFromFilter(filter: EmailAiListFilterInput, label: string) {
    return this.request<{
      listId: string;
      count: number;
      label: string;
      source: 'filter';
      filter: EmailAiListFilterInput;
      /** The criteria the filter actually narrowed on, shown in the banner. */
      criteria: string[];
      /** True when more messages matched than the list holds. */
      truncated: boolean;
      messages: EmailMessageDto[];
    }>('POST', '/arches/email/ai-list/from-filter', { filter, label });
  }

  async getCurrentAiEmailList() {
    return this.request<{
      list: {
        listId: string;
        count: number;
        label: string;
        source: 'filter' | 'curated';
        filter: EmailAiListFilterInput | null;
        criteria: string[];
        truncated: boolean;
      } | null;
      messages: EmailMessageDto[];
    }>('GET', '/arches/email/ai-list/current');
  }

  async clearAiEmailList() {
    return this.request<{ ok: boolean }>('DELETE', '/arches/email/ai-list');
  }

  async getEmailMailboxMessage(messageId: string) {
    return this.request<EmailMessageDetailDto>(
      'GET',
      `/arches/email/messages/${encodeURIComponent(messageId)}`,
    );
  }

  /** The verbatim RFC-822 source, fetched live from the IMAP server. */
  async getEmailMailboxMessageSource(messageId: string) {
    return this.request<{ source: string; truncated: boolean; bytes: number }>(
      'GET',
      `/arches/email/messages/${encodeURIComponent(messageId)}/source`,
    );
  }

  async listEmailMailboxMessageAttachments(messageId: string) {
    return this.request<{ attachments: EmailMailboxAttachmentDto[] }>(
      'GET',
      `/arches/email/messages/${encodeURIComponent(messageId)}/attachments`,
    );
  }

  async getEmailMailboxAttachmentDownloadUrl(
    messageId: string,
    attachmentId: string,
    disposition?: 'attachment' | 'inline',
  ) {
    const qs = disposition ? `?disposition=${disposition}` : '';
    return this.request<{ url: string; expiresAt: string; filename: string; mimeType: string }>(
      'GET',
      `/arches/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(
        attachmentId,
      )}/download${qs}`,
    );
  }

  // ── Email arche — AI assist (billed via invokeEntity) ──
  async summarizeEmailMailboxMessage(messageId: string) {
    return this.request<{ summary: string }>('POST', `/arches/email/ai/summarize`, { messageId });
  }
  /**
   * Streams an AI-drafted email body as SSE. Returns the raw Response for the
   * caller to feed into `consumeSSE` — the same `{ text }` token / `{ done,
   * output }` / `{ error }` shape the plugin bridge's entity_invoke emits, so
   * the consumer is identical when this arche ports to a plugin. Cookie auth +
   * CSRF, same as `request()`.
   */
  async streamEmailMailboxDraft(
    input: { instructions: string; replyToMessageId?: string },
    options?: { signal?: AbortSignal },
  ): Promise<Response> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    return fetch(`${API_BASE}/arches/email/ai/draft`, {
      method: 'POST',
      headers,
      credentials: sessionCredentials(),
      body: JSON.stringify(input),
      signal: options?.signal,
    });
  }
  async categorizeEmailMailboxMessage(messageId: string) {
    return this.request<{ category: 'primary' | 'feed' | 'paper_trail' | 'notifications' }>(
      'POST',
      `/arches/email/ai/categorize`,
      { messageId },
    );
  }

  /**
   * Full-text + substring search over synced mail (one account, or all when
   * omitted). `fields` is a combinable subset of from / recipient / subject /
   * body (empty = all fields); `folderId` restricts to a single folder ("this
   * folder" scope); `filters` apply the same quick filters as the list view.
   */
  async searchEmailMailbox(
    q: string,
    accountId?: string,
    fields?: EmailSearchField[],
    folderId?: string,
    filters?: {
      isRead?: boolean;
      isStarred?: boolean;
      hasAttachments?: boolean;
    } & EmailAdvancedFilters,
    limit?: number,
  ) {
    const params = new URLSearchParams({ q });
    if (accountId) params.set('accountId', accountId);
    if (fields && fields.length > 0) params.set('fields', fields.join(','));
    if (folderId) params.set('folderId', folderId);
    if (filters?.isRead !== undefined) params.set('isRead', String(filters.isRead));
    if (filters?.isStarred !== undefined) params.set('isStarred', String(filters.isStarred));
    if (filters?.hasAttachments !== undefined)
      params.set('hasAttachments', String(filters.hasAttachments));
    if (filters?.answered !== undefined) params.set('answered', String(filters.answered));
    if (filters?.inAddressBook !== undefined)
      params.set('inAddressBook', String(filters.inAddressBook));
    if (filters?.receivedAfter) params.set('receivedAfter', filters.receivedAfter);
    if (filters?.receivedBefore) params.set('receivedBefore', filters.receivedBefore);
    if (limit !== undefined) params.set('limit', String(limit));
    return this.request<{ messages: EmailMessageDto[] }>('GET', `/arches/email/search?${params}`);
  }

  /** Nested AND/OR advanced search over synced mail. */
  async advancedSearchEmailMailbox(
    query: AdvancedSearchGroup,
    opts?: {
      accountIds?: string[];
      accountId?: string;
      folderId?: string;
      limit?: number;
      offset?: number;
    },
  ) {
    return this.request<{ messages: EmailMessageDto[] }>(
      'POST',
      `/arches/email/messages/search/advanced`,
      { query, ...opts },
    );
  }

  /**
   * Group the (scoped) folder view by sender for the "By Sender" overview. The
   * exclude toggles narrow the groups AND the per-group action sets identically.
   */
  async listEmailMailboxBySender(
    accountId: string | undefined,
    opts: {
      folderId?: string;
      folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
      screener?: boolean;
      excludeStarred?: boolean;
      excludeUnread?: boolean;
      excludeAttachments?: boolean;
      /** Narrow the overview to what the search box matches (same match as search). */
      search?: { query: string; fields?: EmailSearchField[] };
    } & EmailAdvancedFilters = {},
  ) {
    const q = new URLSearchParams();
    if (accountId) q.set('accountId', accountId);
    if (opts.folderId) q.set('folderId', opts.folderId);
    if (opts.folderSystemRole) q.set('folderSystemRole', opts.folderSystemRole);
    if (opts.screener) q.set('screener', 'true');
    if (opts.excludeStarred) q.set('excludeStarred', 'true');
    if (opts.excludeUnread) q.set('excludeUnread', 'true');
    if (opts.excludeAttachments) q.set('excludeAttachments', 'true');
    if (opts.answered !== undefined) q.set('answered', String(opts.answered));
    if (opts.inAddressBook !== undefined) q.set('inAddressBook', String(opts.inAddressBook));
    if (opts.receivedAfter) q.set('receivedAfter', opts.receivedAfter);
    if (opts.receivedBefore) q.set('receivedBefore', opts.receivedBefore);
    if (opts.search?.query) {
      q.set('q', opts.search.query);
      if (opts.search.fields && opts.search.fields.length > 0) {
        q.set('fields', opts.search.fields.join(','));
      }
    }
    return this.request<{ groups: EmailSenderGroupDto[] }>(
      'GET',
      `/arches/email/messages/by-sender?${q}`,
    );
  }

  /**
   * Group the (scoped) folder view by smart category for the "By Category"
   * overview. Same scope + exclude semantics as {@link listEmailMailboxBySender};
   * the result includes an Uncategorized group (`categoryId: null`) when present.
   */
  async listEmailMailboxByCategory(
    accountId: string | undefined,
    opts: {
      folderId?: string;
      folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
      screener?: boolean;
      excludeStarred?: boolean;
      excludeUnread?: boolean;
      excludeAttachments?: boolean;
      /** Narrow the overview to what the search box matches (same match as search). */
      search?: { query: string; fields?: EmailSearchField[] };
    } & EmailAdvancedFilters = {},
  ) {
    const q = new URLSearchParams();
    if (accountId) q.set('accountId', accountId);
    if (opts.folderId) q.set('folderId', opts.folderId);
    if (opts.folderSystemRole) q.set('folderSystemRole', opts.folderSystemRole);
    if (opts.screener) q.set('screener', 'true');
    if (opts.excludeStarred) q.set('excludeStarred', 'true');
    if (opts.excludeUnread) q.set('excludeUnread', 'true');
    if (opts.excludeAttachments) q.set('excludeAttachments', 'true');
    if (opts.answered !== undefined) q.set('answered', String(opts.answered));
    if (opts.inAddressBook !== undefined) q.set('inAddressBook', String(opts.inAddressBook));
    if (opts.receivedAfter) q.set('receivedAfter', opts.receivedAfter);
    if (opts.receivedBefore) q.set('receivedBefore', opts.receivedBefore);
    if (opts.search?.query) {
      q.set('q', opts.search.query);
      if (opts.search.fields && opts.search.fields.length > 0) {
        q.set('fields', opts.search.fields.join(','));
      }
    }
    return this.request<{ groups: EmailCategoryGroupDto[] }>(
      'GET',
      `/arches/email/messages/by-category?${q}`,
    );
  }

  // ── Email arche — mailbox message write actions (enqueue server writeback) ──
  async markEmailMailboxMessageRead(messageId: string, value: boolean) {
    return this.request<{ message: EmailMessageDto }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/read`,
      { value },
    );
  }

  async starEmailMailboxMessage(messageId: string, value: boolean) {
    return this.request<{ message: EmailMessageDto }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/star`,
      { value },
    );
  }

  async moveEmailMailboxMessage(messageId: string, targetFolderId: string) {
    return this.request<{ message: EmailMessageDto }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/move`,
      { targetFolderId },
    );
  }

  /**
   * Copy a message into another folder of the SAME account (IMAP COPY). The
   * original stays put; the copy appears once the target folder next syncs.
   */
  async copyEmailMailboxMessage(messageId: string, targetFolderId: string) {
    return this.request<{ copiedUid: number | null }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/copy`,
      { targetFolderId },
    );
  }

  /**
   * Move or copy a message into a folder of a DIFFERENT account. `move` removes
   * the original (to its account's Trash) after the copy is confirmed on the
   * target; `copy` leaves the original in place. Both accounts must be IMAP.
   */
  async moveEmailMailboxMessageToAccount(
    messageId: string,
    input: { targetAccountId: string; targetFolderId: string; mode: 'move' | 'copy' },
  ) {
    return this.request<{
      appendedUid: number | null;
      removedFromSource: boolean;
      attachmentDocumentIds: string[];
    }>('POST', `/arches/email/messages/${encodeURIComponent(messageId)}/move-to-account`, input);
  }

  async deleteEmailMailboxMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/email/messages/${encodeURIComponent(messageId)}`);
  }

  // ── Email arche — bulk message actions (one round-trip over a set) ──
  /**
   * The server caps every id-based bulk email request at 500 ids (an AJV
   * `maxItems` that rejects the WHOLE request — nothing is done if you exceed
   * it). Split a larger selection into sequential ≤500-id batches so "select all
   * → delete/move/mark" works past the cap; results are summed.
   */
  private chunkEmailBulkIds(messageIds: string[]): string[][] {
    const MAX = 500;
    const batches: string[][] = [];
    for (let i = 0; i < messageIds.length; i += MAX) {
      batches.push(messageIds.slice(i, i + MAX));
    }
    return batches;
  }

  async bulkMarkEmailMailboxRead(messageIds: string[], value: boolean) {
    let updated = 0;
    for (const ids of this.chunkEmailBulkIds(messageIds)) {
      const res = await this.request<{ updated: number }>(
        'POST',
        `/arches/email/messages/bulk/read`,
        { messageIds: ids, value },
      );
      updated += res.updated;
    }
    return { updated };
  }

  async bulkStarEmailMailbox(messageIds: string[], value: boolean) {
    let updated = 0;
    for (const ids of this.chunkEmailBulkIds(messageIds)) {
      const res = await this.request<{ updated: number }>(
        'POST',
        `/arches/email/messages/bulk/star`,
        { messageIds: ids, value },
      );
      updated += res.updated;
    }
    return { updated };
  }

  /**
   * Move a set. Supply EITHER `targetFolderId` (single-account selection) OR
   * `targetSystemRole` (resolved per message against its own account — the safe
   * path for a cross-account selection, e.g. Archive over search results).
   */
  async bulkMoveEmailMailbox(
    messageIds: string[],
    target:
      | { targetFolderId: string; targetSystemRole?: never }
      | {
          targetSystemRole: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
          targetFolderId?: never;
        },
  ) {
    let moved = 0;
    for (const ids of this.chunkEmailBulkIds(messageIds)) {
      const res = await this.request<{ moved: number }>(
        'POST',
        `/arches/email/messages/bulk/move`,
        { messageIds: ids, ...target },
      );
      moved += res.moved;
    }
    return { moved };
  }

  async bulkDeleteEmailMailbox(messageIds: string[]) {
    let deleted = 0;
    for (const ids of this.chunkEmailBulkIds(messageIds)) {
      const res = await this.request<{ deleted: number }>(
        'POST',
        `/arches/email/messages/bulk/delete`,
        { messageIds: ids },
      );
      deleted += res.deleted;
    }
    return { deleted };
  }

  // ── Email arche — bulk-by-filter ("select all in folder") ──
  // Act on the ENTIRE matching set server-side, not just loaded rows. `scope`
  // must name a folder (folderId XOR folderSystemRole) and mirrors the list
  // filters so it hits exactly the set the user sees. `capped` is true when more
  // matched than one call moved/updated (the caller can repeat).
  async bulkMoveByFilterEmailMailbox(
    scope: EmailBulkFilterScope,
    target:
      | { targetFolderId: string; targetSystemRole?: never }
      | {
          targetSystemRole: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
          targetFolderId?: never;
        },
  ) {
    return this.request<{ moved: number; capped: boolean }>(
      'POST',
      `/arches/email/messages/bulk/move-by-filter`,
      { scope, ...target },
    );
  }

  async bulkFlagByFilterEmailMailbox(
    scope: EmailBulkFilterScope,
    field: 'read' | 'star',
    value: boolean,
  ) {
    return this.request<{ updated: number; capped: boolean }>(
      'POST',
      `/arches/email/messages/bulk/flag-by-filter`,
      { scope, field, value },
    );
  }

  async sendEmailMailboxMessage(input: {
    accountId: string;
    to: EmailAddressDto[];
    cc?: EmailAddressDto[];
    bcc?: EmailAddressDto[];
    subject?: string;
    text?: string;
    html?: string;
    inReplyToMessageId?: string;
    attachmentDocumentIds?: string[];
    /** Vault document ids for inline (CID) images referenced by the HTML body. */
    inlineImageDocumentIds?: string[];
    /** A resumed draft to remove once the send succeeds. */
    replacesDraftId?: string;
  }) {
    return this.request<{ messageId: string; rfc822MessageId: string }>(
      'POST',
      `/arches/email/messages/send`,
      input,
    );
  }

  /** Persist the composition to the Drafts folder. `replacesDraftId` supersedes
   *  a draft being resumed so re-saving doesn't pile up duplicates. */
  async saveEmailMailboxDraft(input: {
    accountId: string;
    to: EmailAddressDto[];
    cc?: EmailAddressDto[];
    bcc?: EmailAddressDto[];
    subject?: string;
    text?: string;
    html?: string;
    inReplyToMessageId?: string;
    attachmentDocumentIds?: string[];
    inlineImageDocumentIds?: string[];
    replacesDraftId?: string;
  }) {
    return this.request<{ messageId: string }>('POST', `/arches/email/messages/draft`, input);
  }

  /** Discard a draft — hard-removes the local + server-side draft copy so it
   *  disappears from Drafts immediately and doesn't reappear on the next sync. */
  async discardEmailMailboxDraft(messageId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/messages/draft/${encodeURIComponent(messageId)}`,
    );
  }

  /** Set which server folder maps to each operational role (Sent / Drafts /
   *  Trash / Spam / Archive) for an account. A null value clears that override
   *  (falling back to SPECIAL-USE auto-detection). Returns the updated account. */
  async setEmailFolderRoleOverrides(
    accountId: string,
    overrides: Partial<Record<'sent' | 'drafts' | 'trash' | 'spam' | 'archive', string | null>>,
  ) {
    return this.request<EmailConnection>('POST', `/arches/email/accounts/folder-roles`, {
      accountId,
      overrides,
    });
  }

  async scheduleEmailMailboxMessage(input: {
    accountId: string;
    to: EmailAddressDto[];
    cc?: EmailAddressDto[];
    bcc?: EmailAddressDto[];
    subject?: string;
    text?: string;
    html?: string;
    inReplyToMessageId?: string;
    attachmentDocumentIds?: string[];
    inlineImageDocumentIds?: string[];
    replacesDraftId?: string;
    /** ISO-8601 future date-time. */
    sendAt: string;
  }) {
    return this.request<{ id: string; sendAt: string }>(
      'POST',
      `/arches/email/messages/schedule`,
      input,
    );
  }

  async listEmailScheduledSends() {
    return this.request<{ scheduled: EmailScheduledSendDto[] }>('GET', `/arches/email/scheduled`);
  }

  async cancelEmailScheduledSend(actionId: string) {
    return this.request<void>(
      'POST',
      `/arches/email/scheduled/${encodeURIComponent(actionId)}/cancel`,
    );
  }

  async snoozeEmailMailboxMessage(messageId: string, snoozeUntil: string) {
    return this.request<{ snoozed: unknown }>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/snooze`,
      { snoozeUntil },
    );
  }

  async unsnoozeEmailMailboxMessage(messageId: string) {
    return this.request<void>(
      'POST',
      `/arches/email/messages/${encodeURIComponent(messageId)}/unsnooze`,
    );
  }

  // ── Email arche — composer assets (signatures / templates / snippets) ──
  async listEmailMailboxSignatures(accountId: string) {
    return this.request<{ signatures: MailboxSignatureDto[] }>(
      'GET',
      `/arches/email/signatures?accountId=${encodeURIComponent(accountId)}`,
    );
  }
  async createEmailMailboxSignature(input: {
    accountId: string;
    name: string;
    bodyHtml: string;
    isDefault?: boolean;
    sortOrder?: number;
  }) {
    return this.request<{ signature: MailboxSignatureDto }>(
      'POST',
      `/arches/email/signatures`,
      input,
    );
  }
  async updateEmailMailboxSignature(
    signatureId: string,
    input: {
      accountId: string;
      name?: string;
      bodyHtml?: string;
      isDefault?: boolean;
      sortOrder?: number;
    },
  ) {
    return this.request<{ signature: MailboxSignatureDto }>(
      'PATCH',
      `/arches/email/signatures/${encodeURIComponent(signatureId)}`,
      input,
    );
  }
  async deleteEmailMailboxSignature(signatureId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/signatures/${encodeURIComponent(signatureId)}?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  async listEmailMailboxTemplates(accountId: string) {
    return this.request<{ templates: MailboxTemplateDto[] }>(
      'GET',
      `/arches/email/templates?accountId=${encodeURIComponent(accountId)}`,
    );
  }
  async createEmailMailboxTemplate(input: {
    accountId: string;
    name: string;
    subject?: string | null;
    bodyHtml: string;
    category?: string | null;
  }) {
    return this.request<{ template: MailboxTemplateDto }>('POST', `/arches/email/templates`, input);
  }
  async updateEmailMailboxTemplate(
    templateId: string,
    input: {
      accountId: string;
      name?: string;
      subject?: string | null;
      bodyHtml?: string;
      category?: string | null;
    },
  ) {
    return this.request<{ template: MailboxTemplateDto }>(
      'PATCH',
      `/arches/email/templates/${encodeURIComponent(templateId)}`,
      input,
    );
  }
  async deleteEmailMailboxTemplate(templateId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/templates/${encodeURIComponent(templateId)}?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  async listEmailMailboxSnippets(accountId: string) {
    return this.request<{ snippets: MailboxSnippetDto[] }>(
      'GET',
      `/arches/email/snippets?accountId=${encodeURIComponent(accountId)}`,
    );
  }
  async createEmailMailboxSnippet(input: { accountId: string; trigger: string; body: string }) {
    return this.request<{ snippet: MailboxSnippetDto }>('POST', `/arches/email/snippets`, input);
  }
  async updateEmailMailboxSnippet(
    snippetId: string,
    input: { accountId: string; trigger?: string; body?: string },
  ) {
    return this.request<{ snippet: MailboxSnippetDto }>(
      'PATCH',
      `/arches/email/snippets/${encodeURIComponent(snippetId)}`,
      input,
    );
  }
  async deleteEmailMailboxSnippet(snippetId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/snippets/${encodeURIComponent(snippetId)}?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  // ── Email arche — contacts (address book) ──
  // Omit accountId to list harvested contacts across ALL accounts (deduped).
  async listEmailMailboxContacts(accountId?: string, search?: string) {
    const q = new URLSearchParams();
    if (accountId) q.set('accountId', accountId);
    if (search) q.set('search', search);
    const qs = q.toString();
    return this.request<{ contacts: MailboxContactDto[] }>(
      'GET',
      `/arches/email/contacts${qs ? `?${qs}` : ''}`,
    );
  }
  async createEmailMailboxContact(input: {
    accountId: string;
    emailAddress: string;
    displayName?: string | null;
    company?: string | null;
  }) {
    return this.request<{ contact: MailboxContactDto }>('POST', `/arches/email/contacts`, input);
  }
  async updateEmailMailboxContact(
    contactId: string,
    input: {
      accountId: string;
      displayName?: string | null;
      company?: string | null;
      isFavorite?: boolean;
    },
  ) {
    return this.request<{ contact: MailboxContactDto }>(
      'PATCH',
      `/arches/email/contacts/${encodeURIComponent(contactId)}`,
      input,
    );
  }
  async deleteEmailMailboxContact(contactId: string) {
    return this.request<void>('DELETE', `/arches/email/contacts/${encodeURIComponent(contactId)}`);
  }

  // ── Email arche — filters (rules) ──
  async listEmailMailboxFilters(accountId: string) {
    return this.request<{ filters: MailboxFilterDto[] }>(
      'GET',
      `/arches/email/filters?accountId=${encodeURIComponent(accountId)}`,
    );
  }
  async createEmailMailboxFilter(input: {
    accountId: string;
    name: string;
    conditions: MailboxFilterCondition[];
    actions: MailboxFilterAction[];
    isEnabled?: boolean;
    runAfterDays?: number | null;
  }) {
    return this.request<{ filter: MailboxFilterDto }>('POST', `/arches/email/filters`, input);
  }
  async updateEmailMailboxFilter(
    filterId: string,
    input: {
      accountId: string;
      name?: string;
      conditions?: MailboxFilterCondition[];
      actions?: MailboxFilterAction[];
      isEnabled?: boolean;
      runAfterDays?: number | null;
    },
  ) {
    return this.request<{ filter: MailboxFilterDto }>(
      'PATCH',
      `/arches/email/filters/${encodeURIComponent(filterId)}`,
      input,
    );
  }
  async deleteEmailMailboxFilter(filterId: string, accountId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/email/filters/${encodeURIComponent(filterId)}?accountId=${encodeURIComponent(accountId)}`,
    );
  }

  // ── Email arche — screener ──
  async listEmailMailboxScreener(accountId: string, status?: 'pending' | 'approved' | 'rejected') {
    const q = new URLSearchParams({ accountId });
    if (status) q.set('status', status);
    return this.request<{ senders: MailboxScreenedSenderDto[] }>(
      'GET',
      `/arches/email/screener?${q}`,
    );
  }
  async decideEmailMailboxScreener(
    screenedId: string,
    accountId: string,
    decision: 'approved' | 'rejected',
  ) {
    // `trashed` = how many of the sender's messages were moved to Trash on a
    // reject (rejecting actively trashes their mail, not just hides it).
    return this.request<{ sender: MailboxScreenedSenderDto; trashed?: number }>(
      'POST',
      `/arches/email/screener/${encodeURIComponent(screenedId)}/decide`,
      { accountId, decision },
    );
  }
  /** Approve/reject a screened sender by ADDRESS (from the Screener inbox view,
   *  where the row carries the address but not the screenedId). */
  async decideEmailMailboxScreenerBySender(
    accountId: string,
    senderAddress: string,
    decision: 'approved' | 'rejected',
  ) {
    return this.request<{ sender: MailboxScreenedSenderDto; trashed?: number }>(
      'POST',
      `/arches/email/screener/decide-by-sender`,
      { accountId, senderAddress, decision },
    );
  }
  async setEmailMailboxScreenerEnabled(accountId: string, enabled: boolean) {
    return this.request<EmailConnection>('POST', `/arches/email/screener/settings`, {
      accountId,
      enabled,
    });
  }

  /** Per-account opt-in to run the screener before the AI spam filter. */
  async setEmailMailboxScreenerFirst(accountId: string, screenerFirst: boolean) {
    return this.request<EmailConnection>('POST', `/arches/email/screener/settings`, {
      accountId,
      screenerFirst,
    });
  }

  /** Per-account opt-in for the Snooze / Scheduled-send UI (off by default). */
  async setEmailMailboxFeatures(
    accountId: string,
    patch: { snoozeEnabled?: boolean; scheduleEnabled?: boolean; autoCategorizeEnabled?: boolean },
  ) {
    return this.request<EmailConnection>(
      'PUT',
      `/arches/email/connections/${encodeURIComponent(accountId)}/features`,
      patch,
    );
  }

  // ── Email arche — AI bulk categorize (backlog cleanup) ──
  async getEmailMailboxUncategorizedCount(opts: {
    accountId?: string;
    folderId?: string;
    folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
  }) {
    const q = new URLSearchParams();
    if (opts.accountId) q.set('accountId', opts.accountId);
    if (opts.folderId) q.set('folderId', opts.folderId);
    if (opts.folderSystemRole) q.set('folderSystemRole', opts.folderSystemRole);
    return this.request<{ count: number }>('GET', `/arches/email/ai/uncategorized-count?${q}`);
  }

  async bulkCategorizeEmailMailbox(input: {
    accountId?: string;
    folderId?: string;
    folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
    limit?: number;
  }) {
    return this.request<{ categorized: number; remaining: number }>(
      'POST',
      `/arches/email/ai/bulk-categorize`,
      input,
    );
  }

  // ── Email arche — holder-wide global settings (cross-account) ──
  async getEmailMailboxUserSettings() {
    return this.request<{ settings: EmailUserSettingsDto }>('GET', `/arches/email/user-settings`);
  }
  async setEmailMailboxUserSettings(settings: EmailUserSettingsDto) {
    return this.request<{ settings: EmailUserSettingsDto }>(
      'PUT',
      `/arches/email/user-settings`,
      settings,
    );
  }

  // ── Email arche — vacation responder / out-of-office ──
  async getEmailMailboxVacationSettings(accountId: string) {
    const q = new URLSearchParams({ accountId });
    return this.request<{ enabled: boolean; settings: EmailVacationSettingsDto }>(
      'GET',
      `/arches/email/vacation/settings?${q}`,
    );
  }
  async setEmailMailboxVacationSettings(
    accountId: string,
    patch: {
      enabled?: boolean;
      subject?: string;
      body?: string;
      startAt?: string | null;
      endAt?: string | null;
    },
  ) {
    return this.request<{ enabled: boolean; settings: EmailVacationSettingsDto }>(
      'PUT',
      `/arches/email/vacation/settings`,
      { accountId, ...patch },
    );
  }

  // ── Email arche — AI spam filter ──
  async getEmailMailboxSpamSettings(accountId: string) {
    const q = new URLSearchParams({ accountId });
    return this.request<MailboxSpamSettingsDto>('GET', `/arches/email/spam/settings?${q}`);
  }
  /** What the AI spam filter has cost this user (credits) by rolling period. */
  async getEmailMailboxSpamSpend() {
    const { spend } = await this.request<{ spend: EmailSpamSpendDto }>(
      'GET',
      `/arches/email/spam/spend`,
    );
    return spend;
  }
  async setEmailMailboxSpamSettings(
    accountId: string,
    patch: { enabled?: boolean; rules?: string | null; spamRetention?: MailboxSpamRetentionDto },
  ) {
    return this.request<EmailConnection>('PUT', `/arches/email/spam/settings`, {
      accountId,
      ...patch,
    });
  }
  /** Block the target's senders and move ALL of their mail to Trash. One sweep
   *  batch per call — repeat while `capped`. */
  async reportEmailMailboxSpam(target: EmailSpamReportTarget) {
    return this.request<EmailSpamReportResult>('POST', `/arches/email/spam/report`, target);
  }
  /** Sender + message counts a report would hit (for the multi-sender confirm). */
  async previewEmailSpamReport(target: EmailSpamReportTarget) {
    return this.request<EmailSpamReportPreview>(
      'POST',
      `/arches/email/spam/report/preview`,
      target,
    );
  }
  /** Put reported mail back where it was and restore the senders' dispositions. */
  async undoEmailSpamReport(body: {
    moved: EmailSpamMovedMessage[];
    blocked: EmailSpamBlockedSender[];
  }) {
    return this.request<{ restored: number; unblocked: number }>(
      'POST',
      `/arches/email/spam/report/undo`,
      body,
    );
  }
  async notEmailMailboxSpam(messageId: string) {
    return this.request<{ moved: number; trashed: EmailSenderTrashedMail[] }>(
      'POST',
      `/arches/email/spam/not-spam`,
      { messageId },
    );
  }

  /** One page of the allow/block list — one mailbox or all (accountId omitted). */
  async listEmailSpamSenderPage(opts: {
    disposition: 'approved' | 'rejected';
    accountId?: string;
    decidedBy?: 'user' | 'automated';
    q?: string;
    limit: number;
    offset: number;
  }) {
    const q = new URLSearchParams({
      disposition: opts.disposition,
      limit: String(opts.limit),
      offset: String(opts.offset),
    });
    if (opts.accountId) q.set('accountId', opts.accountId);
    if (opts.decidedBy) q.set('decidedBy', opts.decidedBy);
    if (opts.q) q.set('q', opts.q);
    return this.request<{ groups: EmailSenderListGroup[]; total: number }>(
      'GET',
      `/arches/email/spam/senders?${q}`,
    );
  }
  /** Allow or block a sender by hand (one mailbox, or every mailbox). */
  async addEmailSpamSender(input: {
    senderAddress: string;
    disposition: 'approved' | 'rejected';
    accountId?: string;
    allMailboxes?: boolean;
  }) {
    return this.request<{ entries: MailboxScreenedSenderDto[] }>(
      'POST',
      `/arches/email/spam/senders`,
      input,
    );
  }
  /** Remove several allow/block entries at once. */
  async removeEmailSpamSenders(entries: { screenedId: string; accountId: string }[]) {
    return this.request<{ removed: number; trashed: EmailSenderTrashedMail[] }>(
      'POST',
      `/arches/email/spam/senders/remove`,
      { entries },
    );
  }
  /** Move senders' mail back from Trash to the Inbox (after unblocking). */
  async restoreEmailSpamSenders(senders: { accountId: string; senderAddress: string }[]) {
    return this.request<{ restored: number; capped?: boolean }>(
      'POST',
      `/arches/email/spam/senders/restore`,
      { senders },
    );
  }
  /** Who is trusted without being listed (emailed addresses, contacts). */
  async getEmailSpamTrustSummary(accountId?: string) {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.request<{ emailed: number; contacts: number; trustContacts: boolean }>(
      'GET',
      `/arches/email/spam/trust-summary${q}`,
    );
  }

  async listRemoteCalendars(connectionId: string) {
    return this.request<{ calendars: RemoteCalendar[] }>(
      'GET',
      `/arches/calendar/sync/connections/${connectionId}/calendars`,
    );
  }

  async listCalendarSyncJobs() {
    return this.request<{ jobs: SyncJob[] }>('GET', '/arches/calendar/sync/jobs');
  }

  async createCalendarSyncJob(data: {
    connectionId: string;
    localCalendarId: string;
    remoteCalendarId: string;
    remoteCalendarName?: string;
    direction?: string;
    autoSyncEnabled?: boolean;
    conflictStrategy?: string;
  }) {
    return this.request<{ job: SyncJob }>('POST', '/arches/calendar/sync/jobs', data);
  }

  /** How many events would `mode=remove` delete for this job? */
  async calendarSyncJobDeletePreview(jobId: string) {
    return this.request<{ removable: number; keptLocal: number }>(
      'GET',
      `/arches/calendar/sync/jobs/${jobId}/delete-preview`,
    );
  }

  /**
   * Stop syncing a calendar. `mode=remove` also deletes the events this job
   * imported from the provider; events created in Fias always survive.
   */
  async deleteCalendarSyncJob(jobId: string, mode: 'keep' | 'remove' = 'keep') {
    return this.request<{ deleted: boolean; removed: number }>(
      'DELETE',
      `/arches/calendar/sync/jobs/${jobId}?mode=${mode}`,
    );
  }

  async triggerCalendarSync(jobId: string) {
    // Stamp-and-enqueue: the run executes on the worker tier; job status is
    // polled via useSyncJobs while it runs.
    return this.request<{ triggered: boolean; mode: 'enqueued' | 'inline' }>(
      'POST',
      `/arches/calendar/sync/jobs/${jobId}/trigger`,
    );
  }

  async updateCalendarSyncJobSettings(jobId: string, settings: Partial<SyncJobSettings>) {
    return this.request<{ job: SyncJob }>(
      'PATCH',
      `/arches/calendar/sync/jobs/${jobId}/settings`,
      settings,
    );
  }

  // Contact book sync
  async listContactSyncConnections() {
    return this.request<{ connections: SyncConnection[] }>(
      'GET',
      '/arches/contact-book/sync/connections',
    );
  }

  async initContactSyncOAuth(provider: 'google' | 'microsoft') {
    return this.request<{ authorizationUrl: string; state: string }>(
      'POST',
      '/arches/contact-book/sync/connections/oauth/init',
      { provider },
    );
  }

  async contactSyncOAuthCallback(code: string, state: string) {
    return this.request<{ connection: SyncConnection }>(
      'GET',
      `/arches/contact-book/sync/connections/oauth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    );
  }

  async connectContactApple(username: string, appPassword: string) {
    return this.request<{ connection: SyncConnection }>(
      'POST',
      '/arches/contact-book/sync/connections/apple',
      { username, appPassword },
    );
  }

  async disconnectContactSync(connectionId: string) {
    return this.request<void>('DELETE', `/arches/contact-book/sync/connections/${connectionId}`);
  }

  async listRemoteContactGroups(connectionId: string) {
    return this.request<{ groups: RemoteContactGroup[] }>(
      'GET',
      `/arches/contact-book/sync/connections/${connectionId}/groups`,
    );
  }

  async listContactSyncJobs() {
    return this.request<{ jobs: SyncJob[] }>('GET', '/arches/contact-book/sync/jobs');
  }

  async createContactSyncJob(data: {
    connectionId: string;
    remoteGroupId?: string;
    remoteGroupName?: string;
    direction?: string;
  }) {
    return this.request<{ job: SyncJob }>('POST', '/arches/contact-book/sync/jobs', data);
  }

  async deleteContactSyncJob(jobId: string) {
    return this.request<void>('DELETE', `/arches/contact-book/sync/jobs/${jobId}`);
  }

  async triggerContactSync(jobId: string) {
    return this.request<{ stats: SyncStats }>(
      'POST',
      `/arches/contact-book/sync/jobs/${jobId}/trigger`,
    );
  }

  async updateContactSyncJobSettings(jobId: string, settings: Partial<SyncJobSettings>) {
    return this.request<{ job: SyncJob }>(
      'PATCH',
      `/arches/contact-book/sync/jobs/${jobId}/settings`,
      settings,
    );
  }

  // ==========================================================================
  // Vault Documents (Phase 12)
  // ==========================================================================
  // Holder-scoped document storage. Coexists with platform RAG corpus
  // (System A) and per-user My Data (System B). Routes live at
  // /vault/documents/* — the route layer calls
  // assertPrincipalCanActForHolder + the ops module directly per the
  // adjunct-route pattern.

  async listVaultDocuments(
    holderId: string,
    options?: {
      status?: VaultDocumentStatusDto;
      tags?: string[];
      includeArchived?: boolean;
      includeDeleted?: boolean;
      /** When true, return ONLY soft-deleted rows (Trash view). */
      deletedOnly?: boolean;
      /** `null` = Holder root (folder_id IS NULL). A `vdf_*` id =
       * that folder. Omit to list across all folders. */
      folderId?: string | null;
      limit?: number;
      /** Rows to skip — the next page. */
      offset?: number;
    } & VaultDocumentMetadataFilters,
  ) {
    const params = vaultDocumentListParams(holderId, options);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    return this.request<{ documents: VaultDocumentDto[]; total: number }>(
      'GET',
      `/vault/documents?${params.toString()}`,
    );
  }

  /** The workspace's By Type summary: the same filters as the list, grouped
   *  by file kind over EVERY matching file (not a page). Free — no entity. */
  async groupVaultDocumentsByKind(
    holderId: string,
    options?: VaultDocumentMetadataFilters & { includeArchived?: boolean },
  ) {
    const params = vaultDocumentListParams(holderId, options);
    return this.request<VaultDocumentKindGroupsDto>(
      'GET',
      `/vault/documents/by-kind?${params.toString()}`,
    );
  }

  /** The workspace's By Arche summary: grouped by the arche that made each
   *  file, with files no arche made split into "uploads" and "fias". */
  async groupVaultDocumentsByArche(
    holderId: string,
    options?: VaultDocumentMetadataFilters & { includeArchived?: boolean },
  ) {
    const params = vaultDocumentListParams(holderId, options);
    return this.request<VaultDocumentArcheGroupsDto>(
      'GET',
      `/vault/documents/by-arche?${params.toString()}`,
    );
  }

  /** One person's Desktop arrangement in a workspace. */
  async getVaultDesktopLayout(holderId: string) {
    return this.request<{ layout: VaultDesktopLayoutDto; revision: number }>(
      'GET',
      `/vault/desktop-layout?holder=${encodeURIComponent(holderId)}`,
    );
  }

  /** Merge changes into the arrangement; returns the whole merged layout.
   *  Sent `keepalive`, so a save flushed as the tab closes still arrives (each
   *  body is bounded far under the browser's 64 KB keepalive cap). */
  async patchVaultDesktopLayout(input: VaultDesktopLayoutPatchDto) {
    return this.request<{ layout: VaultDesktopLayoutDto; revision: number }>(
      'PATCH',
      '/vault/desktop-layout',
      input,
      { keepalive: true },
    );
  }

  async getVaultDocument(documentId: string) {
    return this.request<{ document: VaultDocumentDto }>('GET', `/vault/documents/${documentId}`);
  }

  /**
   * The host file dialogs' listing (Open, Save, the cross-arche pickers): the
   * user's own files in Documents and / or Arche Saves — never My-Fias, never
   * arche-private rows — searched, filtered by kind, sorted and paged
   * server-side, with a `total`. See `docs/vault/arche-files-policy.md`.
   *
   * `folderId`: `null` = the Holder root; a `vdf_*` id = that folder; omitted
   * = the whole of the chosen areas (search, Recent). `forArche` caps
   * sensitivity at what an arche may be given (never proprietary).
   */
  async listVaultFileDialogDocuments(holderId: string, query: VaultFileDialogQuery = {}) {
    const params = new URLSearchParams({ holder: holderId });
    if (query.areas?.length) params.set('areas', query.areas.join(','));
    if (query.folderId === null) params.set('folderId', 'root');
    else if (typeof query.folderId === 'string') params.set('folderId', query.folderId);
    if (query.search) params.set('search', query.search);
    if (query.kinds?.length) params.set('kinds', query.kinds.join(','));
    if (query.sort) params.set('sort', query.sort);
    if (query.direction) params.set('direction', query.direction);
    if (query.forArche) params.set('forArche', 'true');
    if (query.limit) params.set('limit', String(query.limit));
    if (query.offset) params.set('offset', String(query.offset));
    return this.request<{ documents: VaultFileDialogDocumentDto[]; total: number }>(
      'GET',
      `/vault/documents/picker?${params.toString()}`,
    );
  }

  /**
   * Save to Documents, step 1 — the host Save dialog, after the user
   * confirmed. Session-only: a plugin's bridge token cannot reach it.
   * Refusals the dialog handles: 409 `NAME_EXISTS` (with `conflicts`, see
   * `vaultSaveConflictsOf`), 409 `REPLACE_TARGET_CHANGED`, 413
   * `REPLACE_TOO_LARGE`, 422 `ARCHE_CANNOT_SAVE`.
   */
  async initVaultSaveToDocuments(body: VaultSaveToDocumentsInitBody) {
    return this.request<VaultSaveToDocumentsInitDto>('POST', '/vault/documents/save/init', body);
  }

  /** Save to Documents, step 2 — after the bytes were PUT to `uploadUrl`. */
  async finalizeVaultSaveToDocuments(body: VaultSaveToDocumentsFinalizeBody) {
    return this.request<{ document: VaultDocumentDto; access: VaultContinuedAccess }>(
      'POST',
      '/vault/documents/save/finalize',
      body,
    );
  }

  /**
   * Arche Access panel: per-document grants the caller has given plugin
   * arches (vault_consents, grantee_kind='arche'), plus a display-name
   * join for the granting UI. `documentId` filters to one document.
   *
   * `holderId` selects the workspace whose grants to list; omitted it means
   * the caller's Personal Holder. Server-side the holder is a scope, not a
   * claim — the Vault wrapper authorizes it.
   *
   * `dataClass` tells the two kinds apart: `arche_document_grant` carries a
   * `scopeJson.documentId` and means "this one document"; the Document Sign
   * class carries an EMPTY scope and means "envelopes this arche started".
   * Render them differently — a row with no document name is not a broken
   * per-document grant.
   */
  async listVaultArcheConsents(options?: {
    archeId?: string;
    documentId?: string;
    holderId?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.documentId) params.set('documentId', options.documentId);
    if (options?.holderId) params.set('holderId', options.holderId);
    const query = params.toString();
    return this.request<{
      consents: Array<{
        consentId: string;
        granteeArcheId: string | null;
        dataClass: string;
        /** `access` is absent on grants made before edit access existed —
         *  treat absent as 'read' (`archeGrantAccess`). */
        scopeJson: { documentId?: string; access?: 'read' | 'write' };
        grantedAt: string;
        status: 'active' | 'expired' | 'revoked';
      }>;
      archeNames: Record<string, string | null>;
      /** Names of the granted files the caller can see. A grant whose file
       *  is gone (or hidden from their role) has no entry. */
      documentNames?: Record<string, string>;
    }>('GET', `/vault/consents/arche${query ? `?${query}` : ''}`);
  }

  /** Give a published app access to documents of yours. `access: 'write'`
   *  additionally lets it SAVE CHANGES to them; the server refuses that for
   *  an app that may not edit user files (`ARCHE_CANNOT_EDIT`). The grant is
   *  made on the workspace the documents live in — resolved server-side from
   *  the ids, never named here — at the caller's role there; a viewer is
   *  refused (`ROLE_FORBIDDEN_FOR_ARCHE_GRANT`), and a document the caller
   *  cannot act for reads as not found. */
  async grantVaultArcheConsent(body: {
    granteeArcheId: string;
    documentIds: string[];
    access?: 'read' | 'write';
  }) {
    return this.request<{
      grants: Array<{ consentId: string; documentId: string; outcome: string }>;
    }>('POST', '/vault/consents/arche', body);
  }

  /** Lower an app's access to view-only. RAISING it is not offered here: that
   *  goes through a consent sheet, which shows the user what they are agreeing to. */
  async setVaultArcheConsentAccess(consentId: string, access: 'read') {
    return this.request<{ consent: { consentId: string } }>(
      'POST',
      `/vault/consents/${consentId}/access`,
      { access },
    );
  }

  /** Who changed this file, newest first. `cursor` = the previous page's
   *  `nextCursor`. */
  async getVaultDocumentActivity(
    documentId: string,
    options?: { limit?: number; cursor?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);
    const query = params.toString();
    return this.request<{
      entries: VaultDocumentActivityEntryDto[];
      nextCursor: string | null;
    }>('GET', `/vault/documents/${documentId}/activity${query ? `?${query}` : ''}`);
  }

  /** Rebuild a document's search index after its content was changed in
   *  place. Uses the uploader's AI credits, exactly like a fresh upload. */
  async reindexVaultDocument(documentId: string) {
    return this.request<{ documentId: string; status: 'queued' }>(
      'POST',
      `/vault/documents/${documentId}/reindex`,
    );
  }

  /** Revoke a consent the caller granted (Arche Access / sharing UIs). */
  async revokeVaultConsent(consentId: string) {
    return this.request<void>('DELETE', `/vault/consents/${consentId}`);
  }

  async listVaultDocumentVersions(documentId: string) {
    return this.request<{ versions: VaultDocumentDto[] }>(
      'GET',
      `/vault/documents/${documentId}/versions`,
    );
  }

  /** Returns a signed 1-hour download URL. The route resolves
   * documentId → row → trust+sensitivity → s3_key server-side; the
   * client NEVER sees the raw S3 key. */
  /** A fresh signed URL. `inline` asks for one the browser renders in place
   *  (a PDF in the viewer's frame); the server grants it only for types that
   *  can't run script, and reports which it gave in `disposition`. */
  async getVaultDocumentDownloadUrl(documentId: string, options?: { inline?: boolean }) {
    const qs = options?.inline ? '?disposition=inline' : '';
    return this.request<VaultDocumentDownloadDto>(
      'GET',
      `/vault/documents/${documentId}/download${qs}`,
    );
  }

  /** Reserve quota + create pending row + return presigned PUT URL.
   * Caller PUTs bytes direct to S3 with the returned headers, then
   * calls finalizeVaultDocumentUpload. */
  async initVaultDocumentUpload(input: {
    holderId: string;
    name: string;
    mimeType: string;
    sizeBytes: number;
    checksumSha256: string;
    sensitivity?: VaultDocumentSensitivityDto;
    description?: string;
    tags?: string[];
    /** Folder this document lives in. NULL or omitted = Holder root.
     * The folder must belong to the same Holder. */
    folderId?: string | null;
    replacesDocumentId?: string;
    filename?: string;
    /** An internal arche's input: Fias files it into that purpose's
     * My-Fias folder. Excludes `folderId` and `replacesDocumentId`. */
    purpose?: VaultInputPurposeDto;
  }) {
    return this.request<VaultDocumentUploadInitDto>('POST', `/vault/documents/upload-init`, input);
  }

  async finalizeVaultDocumentUpload(documentId: string, input: { checksumSha256: string }) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/finalize`,
      input,
    );
  }

  async updateVaultDocument(
    documentId: string,
    input: Partial<{
      name: string;
      description: string | null;
      tags: string[];
      path: string | null;
    }>,
  ) {
    return this.request<{ document: VaultDocumentDto }>(
      'PATCH',
      `/vault/documents/${documentId}`,
      input,
    );
  }

  async archiveVaultDocument(documentId: string) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/archive`,
    );
  }

  async unarchiveVaultDocument(documentId: string) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/unarchive`,
    );
  }

  /** Owner/admin only on org Holders; always allowed on personal. */
  async setVaultDocumentSensitivity(documentId: string, sensitivity: VaultDocumentSensitivityDto) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/sensitivity`,
      { sensitivity },
    );
  }

  async softDeleteVaultDocument(documentId: string) {
    return this.request<{ document: VaultDocumentDto }>('DELETE', `/vault/documents/${documentId}`);
  }

  async restoreVaultDocument(documentId: string) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/restore`,
    );
  }

  /** Bring an older version's bytes forward as the new current content.
   * `documentId` identifies the chain (any live member resolves it);
   * `versionId` is the older version to restore. Returns the NEW chain
   * head. The server collapses the prior head out of file lists. */
  async restoreVaultDocumentVersion(documentId: string, versionId: string) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/versions/${versionId}/restore`,
    );
  }

  // Version history the user decides (file areas §4.12). User-session only —
  // never reachable from a plugin.

  /** The file's history choice, the workspace default, what applies, and
   *  what the history costs. */
  async getVaultDocumentHistory(documentId: string) {
    return this.request<VaultDocumentHistoryDto>('GET', `/vault/documents/${documentId}/history`);
  }

  /** The file's own choice: 'keep', 'off', or null = the workspace default. */
  async setVaultDocumentHistoryChoice(documentId: string, policy: VaultHistoryPolicyDto | null) {
    return this.request<{ document: VaultDocumentDto }>(
      'PUT',
      `/vault/documents/${documentId}/history-policy`,
      { policy },
    );
  }

  /** Delete one earlier version (row and bytes). */
  async deleteVaultDocumentVersion(documentId: string, versionId: string) {
    return this.request<{ deletedVersionIds: string[] }>(
      'DELETE',
      `/vault/documents/${documentId}/versions/${versionId}`,
    );
  }

  /** "Delete older versions" — everything but the current version. */
  async deleteEarlierVaultDocumentVersions(documentId: string) {
    return this.request<{ deletedVersionIds: string[] }>(
      'POST',
      `/vault/documents/${documentId}/versions/delete-earlier`,
    );
  }

  /** What arches keep for the signed-in user outside their files — My
   *  Data's "Arche data" (file areas §4.9). View-only. `holderId` picks the
   *  workspace whose metered Vault data is reported (Personal by default). */
  async getVaultArcheData(holderId?: string | null) {
    return this.request<VaultArcheDataDto>(
      'GET',
      `/vault/arche-data${holderId ? `?holder=${encodeURIComponent(holderId)}` : ''}`,
    );
  }

  /** What is using a workspace's file storage (file areas §4.9). */
  async getVaultStorageBreakdown(holderId: string) {
    return this.request<VaultStorageBreakdownDto>(
      'GET',
      `/vault/documents/storage/breakdown?holder=${encodeURIComponent(holderId)}`,
    );
  }

  /** A workspace's version-history default. */
  async getWorkspaceFileSettings(holderId: string) {
    return this.request<{ settings: WorkspaceFileSettingsDto; canChange: boolean }>(
      'GET',
      `/vault/holders/${holderId}/file-settings`,
    );
  }

  async updateWorkspaceFileSettings(
    holderId: string,
    patch: Partial<Pick<WorkspaceFileSettingsDto, 'historyEnabled' | 'historyMaxVersions'>>,
  ) {
    return this.request<{ settings: WorkspaceFileSettingsDto; canChange: boolean }>(
      'PUT',
      `/vault/holders/${holderId}/file-settings`,
      patch,
    );
  }

  /** Semantic search. Burns the caller's credits on the query embedding
   * (AI Markup Invariant — see Phase 12c-3). */
  async searchVaultDocuments(input: {
    holderId: string;
    query: string;
    topK?: number;
    minSimilarity?: number;
    includeContent?: boolean;
  }) {
    return this.request<{ matches: VaultDocumentSearchMatchDto[] }>(
      'POST',
      `/vault/documents/search`,
      input,
    );
  }

  // ==========================================================================
  // Vault Documents — cross-arche attachment (Phase 12e-1)
  // ==========================================================================

  async attachVaultDocument(
    documentId: string,
    input: { regardingType: VaultDocumentReferenceTargetTypeDto; regardingId: string },
  ) {
    return this.request<{ reference: VaultDocumentReferenceDto }>(
      'POST',
      `/vault/documents/${documentId}/attach`,
      input,
    );
  }

  async detachVaultDocumentReference(documentId: string, referenceId: string) {
    return this.request<{ reference: VaultDocumentReferenceDto }>(
      'DELETE',
      `/vault/documents/${documentId}/attach/${referenceId}`,
    );
  }

  /**
   * Documents attached to one target ("what's on this row?"), the inverse of
   * {@link listVaultDocumentAttachmentsForDocument}.
   *
   * Returns the references AND the hydrated documents in one round-trip. The
   * server trust-checks the caller against the TARGET's Holder and applies
   * sensitivity gating, so `documents` can be shorter than `references` — a
   * proprietary document attached by someone else is referenced but not
   * returned. Render around the gap rather than assuming a 1:1 pairing.
   */
  async listVaultDocumentAttachmentsForTarget(
    regardingType: VaultDocumentReferenceTargetTypeDto,
    regardingId: string,
  ) {
    return this.request<{
      references: VaultDocumentReferenceDto[];
      documents: VaultDocumentDto[];
    }>(
      'GET',
      `/vault/documents/attachments?regardingType=${encodeURIComponent(regardingType)}&regardingId=${encodeURIComponent(regardingId)}`,
    );
  }

  async listVaultDocumentAttachmentsForDocument(documentId: string) {
    return this.request<{ references: VaultDocumentReferenceDto[] }>(
      'GET',
      `/vault/documents/${documentId}/attachments`,
    );
  }

  /** Per-Holder storage usage (Phase 12e review fix #9). Returns
   * bytes used + quota + file count so the UI can render a usage
   * bar. Trust-checked on the Holder. */
  /** List folders for a Holder. Returns the full flat list; the caller
   * assembles the tree from `parentFolderId` pointers. */
  async listVaultDocumentFolders(holderId: string, options?: { parentFolderId?: string | null }) {
    const params = new URLSearchParams({ holderId });
    if (options?.parentFolderId === null) params.set('parentFolderId', '');
    else if (typeof options?.parentFolderId === 'string')
      params.set('parentFolderId', options.parentFolderId);
    return this.request<{ folders: VaultDocumentFolderDto[] }>(
      'GET',
      `/vault/folders?${params.toString()}`,
    );
  }

  /** Recovery endpoint — re-runs ensureSystemFolders for the active
   * Holder. Used by the My Data UI's "repair system folders" button. */
  async ensureVaultSystemFolders(holderId: string) {
    return this.request<{ folders: VaultDocumentFolderDto[] }>(
      'POST',
      `/vault/folders/ensure-system`,
      { holderId },
    );
  }

  /** Create a user-managed folder. The route rejects writes that
   * would land under `/My-Fias/`, so protected-folder paths are
   * unreachable from the client. */
  async createVaultDocumentFolder(input: {
    holderId: string;
    parentFolderId?: string | null;
    name: string;
  }) {
    return this.request<{ folder: VaultDocumentFolderDto }>('POST', `/vault/folders`, input);
  }

  /** Rename a folder. Hits the same PATCH endpoint that handles
   * folder-moves — the server picks the action by which field is
   * supplied. Protected folders return 403 from the ops layer. */
  async renameVaultDocumentFolder(folderId: string, name: string) {
    return this.request<{ folder: VaultDocumentFolderDto }>('PATCH', `/vault/folders/${folderId}`, {
      name,
    });
  }

  /** Re-parent a folder. `parentFolderId: null` lifts it to the Holder
   * root. Shares the PATCH endpoint with rename — the server picks the
   * action by which field is supplied — and rewrites `path` on every
   * descendant, so callers must invalidate the whole Holder branch.
   * Server rejects protected folders and protected destinations (403),
   * cross-Holder targets (400), and moves into a own descendant (409
   * `FOLDER_CONFLICT`). */
  async moveVaultDocumentFolder(folderId: string, parentFolderId: string | null) {
    return this.request<{ folder: VaultDocumentFolderDto }>('PATCH', `/vault/folders/${folderId}`, {
      parentFolderId,
    });
  }

  /** Delete an empty user-managed folder. Server returns 409
   * `FOLDER_CONFLICT` when the folder still has documents (including
   * soft-deleted rows whose `folder_id` FK still pins them) or any
   * child folder. */
  async deleteVaultDocumentFolder(folderId: string, options?: { force?: boolean }) {
    if (options?.force) {
      return this.request<{ foldersDeleted: number; documentsPurged: number }>(
        'DELETE',
        `/vault/folders/${folderId}?force=true`,
      );
    }
    return this.request<void>('DELETE', `/vault/folders/${folderId}`);
  }

  /** Move a document into a different folder. `targetFolderId: null`
   * lands the document at the Holder root. Protected folders are valid
   * destinations — only the folder itself is immutable, not its
   * contents. Server returns 422 `VALIDATION` if the target belongs to
   * a different Holder. */
  async moveVaultDocument(documentId: string, targetFolderId: string | null) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/move`,
      { targetFolderId },
    );
  }

  /** Copy a file into a Documents folder of the same workspace (the bytes
   *  are copied server-side). `keep-both` (the default) numbers the name on
   *  a clash; `fail` answers 409 `NAME_EXISTS`. */
  async copyVaultDocument(
    documentId: string,
    input: {
      holderId: string;
      folderId: string | null;
      name?: string;
      onConflict?: 'keep-both' | 'fail';
    },
  ) {
    return this.request<{ document: VaultDocumentDto }>(
      'POST',
      `/vault/documents/${documentId}/copy`,
      {
        holder: input.holderId,
        folderId: input.folderId,
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.onConflict ? { onConflict: input.onConflict } : {}),
      },
    );
  }

  /** Hard-delete a single soft-deleted document. Server returns 409
   * `CONFLICT` (`MUST_SOFT_DELETE_FIRST`) for non-trashed docs — the
   * pipeline refuses to bypass the trash window. */
  async purgeVaultDocument(documentId: string) {
    return this.request<{ purged: boolean }>('DELETE', `/vault/documents/${documentId}/purge`);
  }

  /** Hard-delete every soft-deleted document in the Holder, up to the
   * server's per-call batch cap. Returns `purgedCount` for what just
   * ran, `remainingCount` for what's still trashed (UI loops while
   * > 0), and `failures` for any per-doc errors that didn't block the
   * batch. */
  async emptyVaultTrash(holderId: string) {
    return this.request<{
      purgedCount: number;
      remainingCount: number;
      failures: Array<{ documentId: string; reason: string }>;
    }>('POST', `/vault/documents/empty-trash`, { holderId });
  }

  async getVaultDocumentsStorage(holderId: string) {
    const params = new URLSearchParams({ holder: holderId });
    return this.request<{
      storage: VaultDocumentsStorageDto;
      principal: VaultDocumentsPrincipalUsageDto;
    }>('GET', `/vault/documents/storage?${params.toString()}`);
  }

  async listVaultDocumentsForTarget(
    regardingType: VaultDocumentReferenceTargetTypeDto,
    regardingId: string,
  ) {
    const params = new URLSearchParams({
      regardingType,
      regardingId,
    });
    return this.request<{
      references: VaultDocumentReferenceDto[];
      documents: VaultDocumentDto[];
    }>('GET', `/vault/documents/attachments?${params.toString()}`);
  }

  // ==========================================================================
  // Vault Documents — consent-grant document_share (Phase 12e-2)
  // ==========================================================================

  async shareVaultDocument(
    documentId: string,
    input: { granteeUserId: string; expiresAt?: string },
  ) {
    return this.request<{ consent: { consentId: string }; reactivated: boolean }>(
      'POST',
      `/vault/documents/${documentId}/share`,
      input,
    );
  }

  async revokeVaultDocumentShare(documentId: string, consentId: string) {
    return this.request<{ revoked: boolean }>(
      'DELETE',
      `/vault/documents/${documentId}/share/${consentId}`,
    );
  }

  async listVaultDocumentShares(documentId: string) {
    return this.request<{ shares: VaultDocumentShareDto[] }>(
      'GET',
      `/vault/documents/${documentId}/shares`,
    );
  }

  // ==========================================================================
  // Banking Transform API
  // ==========================================================================

  async transformBankingCsv(params: BankingTransformRequest) {
    return this.request<BankingTransformResponse>(
      'POST',
      '/arches/upload-banking/transform',
      params,
    );
  }

  // --- Advertising Optimization ---

  async adoptListCampaigns(
    holderId: string,
    filters?: { status?: string; promotedArcheId?: string; limit?: number },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.promotedArcheId) qs.set('promotedArcheId', filters.promotedArcheId);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    return this.request<{ campaigns: AdoptCampaignDto[] }>(
      'GET',
      `/arches/advertising-optimization/campaigns?${qs}`,
    );
  }

  async adoptGetCampaign(holderId: string, campaignId: string) {
    return this.request<{
      campaign: AdoptCampaignDto;
      funnelGoals: AdoptFunnelGoalDto[];
      /** Absent from an older server; null only for a campaign that vanished mid-read. */
      trials?: AdoptCampaignTrialsDto | null;
    }>('GET', `/arches/advertising-optimization/campaigns/${campaignId}?holder=${holderId}`);
  }

  async adoptCreateCampaign(holderId: string, body: AdoptCreateCampaignRequest) {
    return this.request<{ campaign: AdoptCampaignDto }>(
      'POST',
      `/arches/advertising-optimization/campaigns?holder=${holderId}`,
      body,
    );
  }

  async adoptUpdateCampaign(
    holderId: string,
    campaignId: string,
    body: AdoptUpdateCampaignRequest,
  ) {
    return this.request<{ campaign: AdoptCampaignDto }>(
      'PATCH',
      `/arches/advertising-optimization/campaigns/${campaignId}?holder=${holderId}`,
      body,
    );
  }

  async adoptCampaignLifecycle(
    holderId: string,
    campaignId: string,
    verb: 'activate' | 'pause' | 'complete' | 'archive',
  ) {
    // pause / complete / archive also pause the campaign's placements on the
    // channel; a 207 means some of those pauses are still pending.
    return this.request<{ campaign: AdoptCampaignDto; channelPause: AdoptChannelPauseDto[] }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/${verb}?holder=${holderId}`,
    );
  }

  async adoptSetKillSwitch(holderId: string, campaignId: string, enabled: boolean) {
    return this.request<{
      campaign: AdoptCampaignDto;
      engaged: boolean;
      channelPause: AdoptChannelPauseDto[];
    }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/kill-switch?holder=${holderId}`,
      { enabled },
    );
  }

  async adoptPutFunnelGoals(holderId: string, campaignId: string, goals: AdoptFunnelGoalInput[]) {
    return this.request<{ funnelGoals: AdoptFunnelGoalDto[] }>(
      'PUT',
      `/arches/advertising-optimization/campaigns/${campaignId}/funnel-goals?holder=${holderId}`,
      { goals },
    );
  }

  /**
   * The campaign's guest trials (owner / admin only). Changes ONLY the fields
   * sent — unlike the strategy PUT, which resets every engine tunable it is not
   * sent. `version` is the campaign version last read (409 if it moved).
   */
  async adoptPutTrialSettings(
    holderId: string,
    campaignId: string,
    body: AdoptPutTrialSettingsRequest,
  ) {
    return this.request<{ campaign: AdoptCampaignDto; trials: AdoptCampaignTrialsDto | null }>(
      'PUT',
      `/arches/advertising-optimization/campaigns/${campaignId}/trial-settings?holder=${holderId}`,
      body,
    );
  }

  async adoptPutStrategy(holderId: string, campaignId: string, strategy: AdoptStrategyDto) {
    return this.request<{ campaign: AdoptCampaignDto }>(
      'PUT',
      `/arches/advertising-optimization/campaigns/${campaignId}/strategy?holder=${holderId}`,
      strategy,
    );
  }

  async adoptGetSettings(holderId: string) {
    return this.request<{ settings: AdoptHolderSettingsDto }>(
      'GET',
      `/arches/advertising-optimization/settings?holder=${holderId}`,
    );
  }

  async adoptPutSettings(holderId: string, body: AdoptPutSettingsRequest) {
    return this.request<{ settings: AdoptHolderSettingsDto }>(
      'PUT',
      `/arches/advertising-optimization/settings?holder=${holderId}`,
      body,
    );
  }

  /** The workspace daily ad limit, what is committed against it, and today's spend. */
  async adoptGetDailyAdLimit(holderId: string) {
    return this.request<{ dailyAdLimit: AdoptDailyAdLimitDto }>(
      'GET',
      `/arches/advertising-optimization/settings/daily-ad-limit?holder=${holderId}`,
    );
  }

  /**
   * Sets the workspace daily ad limit. Browser session + owner/admin only; a
   * first set or a raise needs `confirmCents === dailyLimitCents`. 207 when a
   * lower paused live placements and a channel pause is still pending.
   */
  async adoptPutDailyAdLimit(holderId: string, body: AdoptPutDailyAdLimitRequest) {
    return this.request<AdoptPutDailyAdLimitResponse>(
      'PUT',
      `/arches/advertising-optimization/settings/daily-ad-limit?holder=${holderId}`,
      body,
    );
  }

  /** The optional spend caps (monthly, managed wallet), and the last spend reading. */
  async adoptGetSpendCaps(holderId: string) {
    return this.request<{ spendCaps: AdoptSpendCapsDto }>(
      'GET',
      `/arches/advertising-optimization/settings/spend-caps?holder=${holderId}`,
    );
  }

  /**
   * Adds, changes or removes (`capCents: null`) an optional spend cap. Browser
   * session + owner/admin only; a raise needs `confirmCents === capCents`, a
   * removal `confirmRemoval: 'REMOVE'`. 207 when a cap at or below what was
   * already spent paused placements and a channel pause is still pending.
   */
  async adoptPutSpendCap(holderId: string, cap: AdoptSpendCapParam, body: AdoptPutSpendCapRequest) {
    return this.request<AdoptPutSpendCapResponse>(
      'PUT',
      `/arches/advertising-optimization/settings/spend-caps/${cap}?holder=${holderId}`,
      body,
    );
  }

  async adoptListLinks(holderId: string, filters?: { status?: 'active' | 'archived' }) {
    const qs = new URLSearchParams({ holder: holderId });
    if (filters?.status) qs.set('status', filters.status);
    return this.request<{ links: AdoptTrackedLinkDto[] }>(
      'GET',
      `/arches/advertising-optimization/links?${qs}`,
    );
  }

  async adoptCreateLink(holderId: string, body: AdoptCreateLinkRequest) {
    return this.request<{ link: AdoptTrackedLinkDto }>(
      'POST',
      `/arches/advertising-optimization/links?holder=${holderId}`,
      body,
    );
  }

  async adoptArchiveLink(holderId: string, linkId: string) {
    return this.request<{ archived: boolean }>(
      'DELETE',
      `/arches/advertising-optimization/links/${linkId}?holder=${holderId}`,
    );
  }

  async adoptListGoals(holderId: string) {
    return this.request<{ goals: AdoptGoalDto[] }>(
      'GET',
      `/arches/advertising-optimization/goals?holder=${holderId}`,
    );
  }

  async adoptListAccounts(holderId: string) {
    return this.request<{ accounts: AdoptChannelAccountDto[] }>(
      'GET',
      `/arches/advertising-optimization/accounts?holder=${holderId}`,
    );
  }

  /**
   * Begins the BYO OAuth flow. Returns the Google consent URL the browser must
   * navigate to — the caller assigns it to `location`, it is not fetched.
   *
   * `targetCustomerId` pre-picks the operating account. Without it a grant
   * seeing exactly one non-manager account auto-connects; seeing several comes
   * back as `?connect=choose` with the candidates, and the user's pick is
   * re-submitted here.
   */
  async adoptOAuthInit(holderId: string, targetCustomerId?: string) {
    return this.request<{ authorizationUrl: string; state: string }>(
      'POST',
      `/arches/advertising-optimization/accounts/oauth/init?holder=${holderId}`,
      targetCustomerId ? { targetCustomerId } : {},
    );
  }

  /** Disconnects a channel account and erases its stored tokens. */
  async adoptDisconnectAccount(holderId: string, accountId: string) {
    return this.request<{ disconnected: boolean }>(
      'DELETE',
      `/arches/advertising-optimization/accounts/${accountId}?holder=${holderId}`,
    );
  }

  async adoptListPlacements(holderId: string, campaignId: string) {
    return this.request<{ placements: AdoptPlacementDto[] }>(
      'GET',
      `/arches/advertising-optimization/campaigns/${campaignId}/placements?holder=${holderId}`,
    );
  }

  /** Edit a placement after create; a deployed one changes on Google too. */
  async adoptEditPlacement(holderId: string, placementId: string, body: AdoptEditPlacementRequest) {
    return this.request<{ placement: AdoptPlacementDto }>(
      'PATCH',
      `/arches/advertising-optimization/placements/${placementId}?holder=${holderId}`,
      body,
    );
  }

  async adoptCreatePlacement(
    holderId: string,
    campaignId: string,
    body: AdoptCreatePlacementRequest,
  ) {
    return this.request<{ placement: AdoptPlacementDto }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/placements?holder=${holderId}`,
      body,
    );
  }

  async adoptDeployPlacement(holderId: string, placementId: string) {
    return this.request<{ placement: AdoptPlacementDto }>(
      'POST',
      `/arches/advertising-optimization/placements/${placementId}/deploy?holder=${holderId}`,
    );
  }

  async adoptPlacementLifecycle(holderId: string, placementId: string, verb: 'pause' | 'resume') {
    return this.request<{ placement: AdoptPlacementDto; channelPause?: AdoptChannelPauseDto[] }>(
      'POST',
      `/arches/advertising-optimization/placements/${placementId}/${verb}?holder=${holderId}`,
    );
  }

  async adoptListCreatives(
    holderId: string,
    campaignId: string,
    filters?: { status?: string; placementId?: string },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.placementId) qs.set('placementId', filters.placementId);
    return this.request<{ creatives: AdoptCreativeDto[] }>(
      'GET',
      `/arches/advertising-optimization/campaigns/${campaignId}/creatives?${qs}`,
    );
  }

  async adoptCreateCreative(
    holderId: string,
    campaignId: string,
    body: AdoptCreateCreativeRequest,
  ) {
    return this.request<{ creative: AdoptCreativeDto }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/creatives?holder=${holderId}`,
      body,
    );
  }

  async adoptUpdateCreative(holderId: string, creativeId: string, content: AdoptRsaContentDto) {
    return this.request<{ creative: AdoptCreativeDto }>(
      'PATCH',
      `/arches/advertising-optimization/creatives/${creativeId}?holder=${holderId}`,
      { content },
    );
  }

  async adoptCreativeAction(
    holderId: string,
    creativeId: string,
    verb: 'approve' | 'reject' | 'pause' | 'resume',
  ) {
    return this.request<{ creative: AdoptCreativeDto }>(
      'POST',
      `/arches/advertising-optimization/creatives/${creativeId}/${verb}?holder=${holderId}`,
    );
  }

  async adoptDeployCreative(
    holderId: string,
    creativeId: string,
    body: { placementId?: string; experimentId?: string; variantKey?: string },
  ) {
    return this.request<{ creative: AdoptCreativeDto }>(
      'POST',
      `/arches/advertising-optimization/creatives/${creativeId}/deploy?holder=${holderId}`,
      body,
    );
  }

  async adoptKillCreative(holderId: string, creativeId: string, reason: string) {
    return this.request<{ creative: AdoptCreativeDto }>(
      'POST',
      `/arches/advertising-optimization/creatives/${creativeId}/kill?holder=${holderId}`,
      { reason },
    );
  }

  async adoptGenerateCopy(
    holderId: string,
    campaignId: string,
    body: {
      count?: number;
      angles?: string[];
      instructions?: string;
      placementId?: string;
      landingVariantKey?: string;
    },
  ) {
    // 202: the generation runs in the background — poll adoptListGenerations
    // until its status is no longer 'running'.
    return this.request<{ generation: AdoptGenerationDto }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/generate/copy?holder=${holderId}`,
      body,
    );
  }

  async adoptGenerateKeywords(
    holderId: string,
    campaignId: string,
    body: { seedTerms?: string[]; count?: number },
  ) {
    return this.request<{ generationId: string; keywords: AdoptKeywordIdeaDto[] }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/generate/keywords?holder=${holderId}`,
      body,
    );
  }

  async adoptGenerateChallengers(
    holderId: string,
    campaignId: string,
    body: { count?: number; placementId?: string },
  ) {
    // 202, like adoptGenerateCopy.
    return this.request<{ generation: AdoptGenerationDto }>(
      'POST',
      `/arches/advertising-optimization/campaigns/${campaignId}/generate/challengers?holder=${holderId}`,
      body,
    );
  }

  async adoptListGenerations(holderId: string, campaignId: string) {
    return this.request<{ generations: AdoptGenerationDto[] }>(
      'GET',
      `/arches/advertising-optimization/campaigns/${campaignId}/generations?holder=${holderId}`,
    );
  }

  async adoptListExperiments(holderId: string) {
    return this.request<{ experiments: AdoptExperimentDto[] }>(
      'GET',
      `/arches/advertising-optimization/experiments?holder=${holderId}`,
    );
  }

  async adoptCreateExperiment(holderId: string, body: AdoptCreateExperimentRequest) {
    return this.request<{ experiment: AdoptExperimentDto }>(
      'POST',
      `/arches/advertising-optimization/experiments?holder=${holderId}`,
      body,
    );
  }

  async adoptGetExperiment(holderId: string, experimentId: string) {
    return this.request<{ experiment: AdoptExperimentDto; variants: AdoptExperimentVariantDto[] }>(
      'GET',
      `/arches/advertising-optimization/experiments/${experimentId}?holder=${holderId}`,
    );
  }

  async adoptGetExperimentResults(holderId: string, experimentId: string) {
    return this.request<AdoptExperimentResultsDto>(
      'GET',
      `/arches/advertising-optimization/experiments/${experimentId}/results?holder=${holderId}`,
    );
  }

  async adoptExperimentLifecycle(
    holderId: string,
    experimentId: string,
    verb: 'start' | 'stop' | 'complete' | 'archive',
  ) {
    return this.request<{ experiment: AdoptExperimentDto }>(
      'POST',
      `/arches/advertising-optimization/experiments/${experimentId}/${verb}?holder=${holderId}`,
    );
  }

  async adoptListActions(
    holderId: string,
    filters?: { status?: string; campaignId?: string; limit?: number },
  ) {
    const qs = new URLSearchParams({ holder: holderId });
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.campaignId) qs.set('campaignId', filters.campaignId);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    return this.request<{ actions: AdoptActionDto[] }>(
      'GET',
      `/arches/advertising-optimization/actions?${qs}`,
    );
  }

  async adoptReviewAction(holderId: string, actionId: string, verb: 'approve' | 'reject') {
    return this.request<{
      action: AdoptActionDto | null;
      execution?: { ok: boolean; error?: string };
    }>('POST', `/arches/advertising-optimization/actions/${actionId}/${verb}?holder=${holderId}`);
  }

  async adoptGetAutoUnlock(holderId: string) {
    return this.request<{ autoUnlock: AdoptAutoUnlockDto; guardrails: AdoptGuardrailsDto }>(
      'GET',
      `/arches/advertising-optimization/actions/auto-unlock?holder=${holderId}`,
    );
  }

  /** Platform admin: approve automatic mode for a workspace (audited). */
  async adoptAdminGrantAutoUnlock(holderId: string, note: string) {
    return this.request<{ autoUnlock: AdoptAutoUnlockDto }>(
      'PUT',
      `/arches/advertising-optimization/admin/holders/${holderId}/auto-unlock`,
      { acknowledged: true, note },
    );
  }

  /** Platform admin: withdraw that approval. */
  async adoptAdminRevokeAutoUnlock(holderId: string) {
    return this.request<{ revoked: boolean; autoUnlock: AdoptAutoUnlockDto }>(
      'DELETE',
      `/arches/advertising-optimization/admin/holders/${holderId}/auto-unlock`,
    );
  }

  /** May the caller use AdOpt in this workspace, and with what powers? Never 403s. */
  async adoptGetAccess(holderId: string) {
    return this.request<{ access: AdoptAccessDto }>(
      'GET',
      `/arches/advertising-optimization/access?holder=${holderId}`,
    );
  }

  /** Platform admin: is AdOpt open to this org workspace's members? */
  async adoptAdminGetWorkspaceAccess(holderId: string) {
    return this.request<{ workspaceAccess: AdoptWorkspaceAccessDto | null }>(
      'GET',
      `/arches/advertising-optimization/admin/holders/${holderId}/adopt-access`,
    );
  }

  /** Platform admin: open AdOpt to an org workspace's members (audited). */
  async adoptAdminGrantWorkspaceAccess(holderId: string, note: string) {
    return this.request<{ workspaceAccess: AdoptWorkspaceAccessDto }>(
      'PUT',
      `/arches/advertising-optimization/admin/holders/${holderId}/adopt-access`,
      { note },
    );
  }

  /** Platform admin: close it again (stops nothing already running). */
  async adoptAdminRevokeWorkspaceAccess(holderId: string) {
    return this.request<{ revoked: boolean; workspaceAccess: null }>(
      'DELETE',
      `/arches/advertising-optimization/admin/holders/${holderId}/adopt-access`,
    );
  }
}

// ==========================================================================
// Advertising Optimization Types
// ==========================================================================

/**
 * The caller's AdOpt standing in a workspace (GET /access). `allowed` is
 * platform admin OR a workspace a platform admin opened; `canWrite` excludes
 * viewers; `canManageSpend` is owner / admin (limits, caps, trials, the Google
 * connection, automatic mode).
 */
export interface AdoptAccessDto {
  allowed: boolean;
  platformAdmin: boolean;
  workspaceOpen: boolean;
  canWrite: boolean;
  canManageSpend: boolean;
}

/** A platform admin's grant opening AdOpt to an org workspace. */
export interface AdoptWorkspaceAccessDto {
  enabledBy: string;
  enabledAt: string;
  note: string;
}

export type AdoptObjectiveDto = 'cpa' | 'roas' | 'ltv_90d' | 'conversions' | 'clicks' | 'awareness';
export type AdoptAutonomyLevelDto = 'manual' | 'propose' | 'auto';
export type AdoptCampaignStatusDto = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export interface AdoptStrategyDto {
  explorationMinSpendPerVariantCents?: number;
  explorationMaxVariantsLive?: number;
  maxDailyBudgetDeltaPct?: number;
  maxDailyBudgetDeltaCents?: number;
  promoteConfidence?: number;
  killConfidence?: number;
  minObservationHours?: number;
  challengerBatchSize?: number;
  actionApprovalExpiryHours?: number;
  scaleStepPct?: number;
  cooldownHoursAfterBudgetChange?: number;
  // The three guest-trial fields are KEPT by a save that leaves them out.
  /**
   * Guest-trial credits per ad-click mint; absent = the platform default. On a
   * save, `null` removes the override (back to the platform default).
   */
  trialCreditAmount?: number | null;
  /** Guest trials the campaign may mint per UTC day. */
  maxTrialsPerDay?: number;
  /** Set by the click-verification sweep; only saving an explicit `false` lifts it. */
  trialsSuspended?: boolean;
}

/** Why and when a campaign's guest trials were suspended (`strategy.trialsSuspension`). */
export type AdoptTrialSuspensionDto =
  | {
      /** The click-verification sweep: Google did not log enough of `day`'s claimed clicks. */
      reason: 'unverified_clicks';
      at: string;
      /** The UTC claim day judged (YYYY-MM-DD). */
      day: string;
      checked: number;
      notFound: number;
    }
  | { reason: 'manual'; at: string };

/** A campaign's guest trials, as `GET /campaigns/:id` reports them (read the way the mint reads them). */
export interface AdoptCampaignTrialsDto {
  /** The campaign's own amount; null = none (visitors get `defaultCredits`). */
  creditOverride: number | null;
  /** What a visitor minting against this campaign gets now. */
  effectiveCredits: number;
  /** The platform default trial. */
  defaultCredits: number;
  /** The ceiling on a campaign's amount. */
  maxCampaignCredits: number;
  /** Trials a UTC day (the claim gate's cap). */
  maxTrialsPerDay: number;
  /** The ceiling on `maxTrialsPerDay`. */
  maxTrialsPerDayLimit: number;
  suspended: boolean;
  /** Null when not suspended, or suspended before the record existed. */
  suspension: AdoptTrialSuspensionDto | null;
  /** Trials minted since UTC midnight. */
  claimedToday: number;
  /** A workspace owner or admin: may change these. */
  canEdit: boolean;
}

export interface AdoptPutTrialSettingsRequest {
  version: number;
  /** null = back to the platform default trial. */
  trialCreditAmount?: number | null;
  maxTrialsPerDay?: number;
  liftSuspension?: true;
}

/** A strategy with every default applied (`trialCreditAmount` has no default). */
export type AdoptResolvedStrategyDto = Required<Omit<AdoptStrategyDto, 'trialCreditAmount'>> & {
  trialCreditAmount?: number;
};

/**
 * A landing-page variant of a campaign's promoted target, served at
 * `<landingUrl>/<path>`. Mirrors `AdoptLandingVariant` (@fias/db-types):
 * `key` and `path` are 1–40 chars of [a-z0-9-], `brief` ≤ 500 chars.
 */
export interface AdoptLandingVariantDto {
  key: string;
  path: string;
  brief: string;
}

export interface AdoptPromotedTargetDto {
  kind: 'arche' | 'capability' | 'url';
  label: string;
  landingUrl: string;
  landingVariants?: AdoptLandingVariantDto[];
}

/**
 * A promoted target as SENT on create / update. An `arche` target carries no
 * landing URL: the server derives it from the arche (its own page, at its
 * slug for a plugin arche), so a client never builds one.
 */
export type AdoptPromotedTargetInputDto =
  | (Omit<AdoptPromotedTargetDto, 'kind' | 'landingUrl'> & { kind: 'arche' })
  | (AdoptPromotedTargetDto & { kind: 'capability' | 'url' });

/**
 * A campaign's copy guidance (`AdoptCopyGuidance`, @fias/db-types): facts the
 * ad copy may rely on, and claims no ad may contain — the server refuses a
 * creative with a banned claim (422 BANNED_CLAIM) on create, edit, approve and
 * deploy. Bounds: ≤ 20 facts × 200 chars, ≤ 50 banned claims × 60 chars.
 */
export interface AdoptCopyGuidanceDto {
  facts: string[];
  bannedClaims: string[];
}

export interface AdoptCampaignDto {
  id: string;
  holderId: string;
  name: string;
  promotedArcheId: string | null;
  promotedTarget: AdoptPromotedTargetDto | Record<string, never>;
  objective: AdoptObjectiveDto;
  objectiveTargetValue: number | null;
  totalBudgetCents: number;
  dailyBudgetCeilingCents: number;
  currency: string;
  startsAt: string;
  endsAt: string | null;
  autonomyLevel: AdoptAutonomyLevelDto;
  strategy: AdoptStrategyDto;
  /** Null (or absent from an older server) = the campaign has no copy guidance. */
  copyGuidance?: AdoptCopyGuidanceDto | null;
  status: AdoptCampaignStatusDto;
  killSwitch: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdoptFunnelGoalDto {
  id: string;
  campaignId: string;
  stageOrder: number;
  name: string;
  goalType:
    | 'impression'
    | 'click'
    | 'landing_view'
    | 'signup'
    | 'activation'
    | 'purchase'
    | 'custom_event';
  eventKey: string;
  valueCents: number | null;
  isPrimary: boolean;
  createdAt: string;
}

export type AdoptFunnelGoalInput = Omit<
  AdoptFunnelGoalDto,
  'id' | 'campaignId' | 'createdAt' | 'valueCents' | 'isPrimary'
> & {
  valueCents?: number;
  isPrimary?: boolean;
};

export interface AdoptCreateCampaignRequest {
  name: string;
  promotedArcheId?: string;
  promotedTarget?: AdoptPromotedTargetInputDto;
  objective: AdoptObjectiveDto;
  objectiveTargetValue?: number;
  totalBudgetCents: number;
  dailyBudgetCeilingCents: number;
  startsAt: string;
  endsAt?: string;
  autonomyLevel?: AdoptAutonomyLevelDto;
  strategy?: AdoptStrategyDto;
  copyGuidance?: AdoptCopyGuidanceDto;
}

export interface AdoptUpdateCampaignRequest {
  version: number;
  name?: string;
  promotedArcheId?: string | null;
  promotedTarget?: AdoptPromotedTargetInputDto;
  objective?: AdoptObjectiveDto;
  objectiveTargetValue?: number | null;
  totalBudgetCents?: number;
  dailyBudgetCeilingCents?: number;
  startsAt?: string;
  endsAt?: string | null;
  autonomyLevel?: AdoptAutonomyLevelDto;
  strategy?: AdoptStrategyDto;
  /** Replaces the whole guidance; null removes it. */
  copyGuidance?: AdoptCopyGuidanceDto | null;
}

export interface AdoptHolderSettingsDto {
  holderId: string;
  /** Only an `org` workspace can be opened to members (WorkspaceAccessCard). */
  holderType: 'personal' | 'org' | 'guild';
  defaultAutonomyLevel: AdoptAutonomyLevelDto;
  currency: string;
  settings: Record<string, unknown>;
  version: number;
  updatedAt: string;
}

export interface AdoptPutSettingsRequest {
  version: number;
  defaultAutonomyLevel?: AdoptAutonomyLevelDto;
  settings?: Record<string, unknown>;
}

/** GET/PUT /settings/daily-ad-limit — the human-only workspace daily ad limit. */
export interface AdoptDailyAdLimitDto {
  /**
   * Null = never set: nothing can be deployed or resumed. 0 = set to "no ads
   * may run" (an explicit freeze; also nothing can be deployed or resumed).
   */
  limitCents: number | null;
  updatedBy: string | null;
  updatedAt: string | null;
  /** Daily budgets of placements that can spend (live + pause pending). */
  committedCents: number;
  headroomCents: number | null;
  /** The spend-sync sweep's last reading of today's cost (account time zone). */
  todaySpend: { cents: number; day: string; observedAt: string; fresh: boolean } | null;
  /** Google's worst case for this limit: 2× on a day, 30.4× a month. */
  worstCase: { dayCents: number; monthCents: number } | null;
  /** Owner / admin of the workspace — others see it read-only. */
  canEdit: boolean;
  bounds: { minCents: number; maxCents: number };
}

export interface AdoptPutDailyAdLimitRequest {
  dailyLimitCents: number;
  /** The amount typed a second time — required to raise the limit or first set it above $0. */
  confirmCents?: number;
}

export interface AdoptPutDailyAdLimitResponse {
  dailyAdLimit: AdoptDailyAdLimitDto;
  change: {
    previousLimitCents: number | null;
    limitCents: number;
    direction: 'set' | 'raised' | 'lowered' | 'unchanged';
    pausedPlacements: number;
  };
  channelPause: AdoptChannelPauseDto[];
  ownerNotification: { recipients: number; sent: number; failed: number };
}

/** The optional caps, as the route names them. */
export type AdoptSpendCapParam = 'monthly' | 'wallet-daily' | 'wallet-monthly';

/** GET /settings/spend-caps — the optional caps (null = no cap; 0 = no spend in its scope). */
export interface AdoptSpendCapsDto {
  /** Owner / admin of the workspace — others see them read-only. */
  canEdit: boolean;
  /** The spend-sync's last reading (each ad account's own day and month). */
  reading: {
    observedAt: string;
    /** Recent enough for the caps to decide on (3h). */
    fresh: boolean;
    day: string | null;
    month: string | null;
    todayCents: number;
    monthCents: number;
    managedTodayCents: number;
    managedMonthCents: number;
  } | null;
  monthly: {
    capCents: number | null;
    updatedBy: string | null;
    updatedAt: string | null;
    bounds: { minCents: number; maxCents: number };
  };
  /** Null when the workspace has no ad wallet (wallet caps cover managed accounts only). */
  wallet: {
    dailyCapCents: number | null;
    monthlyCapCents: number | null;
    updatedBy: string | null;
    updatedAt: string | null;
    bounds: {
      daily: { minCents: number; maxCents: number };
      monthly: { minCents: number; maxCents: number };
    };
  } | null;
  /** The word to type to remove a cap. */
  removeConfirmation: string;
}

export interface AdoptPutSpendCapRequest {
  /** Null removes the cap. */
  capCents: number | null;
  /** The new amount typed a second time — required to raise. */
  confirmCents?: number;
  /** The removal word, typed — required to remove. */
  confirmRemoval?: string;
}

export interface AdoptPutSpendCapResponse {
  spendCaps: AdoptSpendCapsDto;
  change: {
    cap: 'monthly' | 'wallet_daily' | 'wallet_monthly';
    previousCents: number | null;
    capCents: number | null;
    direction: 'added' | 'raised' | 'lowered' | 'removed' | 'unchanged';
    pausedPlacements: number;
  };
  channelPause: AdoptChannelPauseDto[];
  ownerNotification: { recipients: number; sent: number; failed: number };
}

export interface AdoptTrackedLinkDto {
  linkId: string;
  holderId: string;
  token: string;
  destinationUrl: string;
  campaignId: string | null;
  creativeId: string | null;
  channel: string | null;
  utmDefaults: Record<string, string> | null;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface AdoptCreateLinkRequest {
  destinationUrl: string;
  campaignId?: string;
  creativeId?: string;
  channel?: string;
  utmDefaults?: Record<string, string>;
}

export interface AdoptGoalDto {
  goalId: string;
  holderId: string;
  key: string;
  name: string;
  goalType: string;
  eventMatch: { eventType: string; eventName: string; archeId?: string } | null;
  ltvHorizonDays: number | null;
  attributionWindowDays: number;
  valueSemantics: 'binary' | 'revenue_cents';
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface AdoptChannelAccountDto {
  channelAccountId: string;
  channel: string;
  mode: 'managed' | 'byo';
  externalCustomerId: string | null;
  descriptiveName: string | null;
  currencyCode: string;
  status: string;
  lastError: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

export interface AdoptPlacementDto {
  id: string;
  campaignId: string;
  channel: string;
  channelAccountId: string;
  externalCampaignRef: string | null;
  externalAdGroupRef: string | null;
  dailyBudgetCents: number;
  targeting: AdoptPlacementTargetingDto | null;
  status: string;
  pauseReason: string | null;
  observedStatus: string | null;
  lastAppliedAt: string | null;
  lastDeployError: string | null;
  /** Paused here, but the channel has not confirmed it yet — retried by the spend-sync sweep. */
  pausePending: boolean;
  lastPauseError: string | null;
  /**
   * Ad-account local date of a self-resuming spend-limit pause: a daily
   * reason resumes after this day, a monthly one after this month.
   */
  spendCapPausedOn?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Per-placement result of a stop's channel pause. */
export interface AdoptChannelPauseDto {
  placementId: string;
  ok: boolean;
  error?: string;
}

/** `adopt_placements.targeting` — what the deploy sends to the ad channel. */
export interface AdoptPlacementTargetingDto {
  keywords?: Array<{ text: string; matchType: 'exact' | 'phrase' | 'broad'; negative?: boolean }>;
  /** Plain negative terms (deployed as broad-match negatives). */
  negativeKeywords?: string[];
  /** Google geo target constant ids ('2840' = US). */
  locations?: string[];
  /** Google language constant ids ('1000' = English). */
  languages?: string[];
  /** Maximize Clicks bid ceiling. Absent on placements created before it existed. */
  maxCpcCents?: number;
}

/** Any of the create's fields but the account; absent = unchanged. */
export type AdoptEditPlacementRequest = Partial<
  Omit<AdoptCreatePlacementRequest, 'channelAccountId'>
>;

export interface AdoptCreatePlacementRequest {
  channelAccountId: string;
  dailyBudgetCents: number;
  keywords: Array<{ text: string; matchType: 'exact' | 'phrase' | 'broad'; negative?: boolean }>;
  negativeKeywords?: string[];
  locations?: string[];
  languages?: string[];
  /** Required: a placement without a CPC cap cannot be deployed. */
  maxCpcCents: number;
}

export interface AdoptRsaContentDto {
  headlines: Array<{ text: string; pinnedPosition?: 1 | 2 | 3 }>;
  descriptions: Array<{ text: string }>;
  paths: string[];
  finalUrl: string;
  /** The campaign landing variant this ad points at; absent = the base landing page. */
  landingVariantKey?: string;
}

export type AdoptCreativeAngleDto =
  | 'feature_led'
  | 'pain_led'
  | 'social_proof'
  | 'price_led'
  | 'urgency'
  | 'curiosity'
  | 'unspecified';

export type AdoptCreativeStatusDto =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'deploying'
  | 'live'
  | 'paused'
  | 'killed'
  | 'rejected'
  | 'error';

export interface AdoptCreativeDto {
  id: string;
  campaignId: string;
  placementId: string | null;
  format: 'rsa' | 'text_ad' | 'image' | 'video';
  angle: AdoptCreativeAngleDto;
  content: AdoptRsaContentDto;
  status: AdoptCreativeStatusDto;
  externalAdRef: string | null;
  experimentVariantKey: string | null;
  generationId: string | null;
  parentCreativeId: string | null;
  createdBy: 'human' | 'ai';
  critique: {
    scores?: { compliance: number; distinctiveness: number; landingMatch: number };
    notes?: string;
    passed?: boolean;
    /** Spelling the reviewer found; always empty on a stored creative (any entry rejects). */
    spellingErrors?: string[];
    /** Claims the reviewer judged untrue or against the campaign's facts. */
    claimViolations?: string[];
  };
  killedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdoptCreateCreativeRequest {
  format: 'rsa';
  angle?: AdoptCreativeAngleDto;
  placementId?: string;
  content: AdoptRsaContentDto;
}

export interface AdoptKeywordIdeaDto {
  text: string;
  matchType: 'exact' | 'phrase' | 'broad';
  negative?: boolean;
}

/** A generation's lifecycle: copy / challengers run in the background. */
export type AdoptGenerationStatusDto = 'running' | 'succeeded' | 'failed';

export interface AdoptGenerationDto {
  id: string;
  kind: 'copy' | 'keywords' | 'challengers' | 'critique' | 'image';
  status: AdoptGenerationStatusDto;
  requestedBy: 'human' | 'engine';
  provider: string;
  model: string;
  acceptedCreativeIds: string[];
  rejectedCount: number;
  requestId: string | null;
  /** Why it failed (status 'failed'), e.g. a timed-out run. */
  error: string | null;
  createdAt: string;
  completedAt: string | null;
  /** The angles asked for (copy / challengers). */
  angles: string[];
  /** The landing variant the copy is for (null = the base landing page). */
  landingVariantKey: string | null;
  /** How the run ended, candidate by candidate (null while running and on older rows). */
  summary: { requested: number; accepted: number; rejected: number; failed: number } | null;
}

export type AdoptExperimentStageDto = 'ad_creative' | 'landing' | 'onboarding_path' | 'in_product';

export interface AdoptExperimentDto {
  experimentId: string;
  holderId: string;
  key: string;
  name: string;
  hypothesis: string | null;
  stage: AdoptExperimentStageDto;
  assignmentMode: 'internal' | 'external';
  evidenceClass: 'randomized' | 'observational';
  unitType: 'visitor' | 'user';
  status: 'draft' | 'running' | 'paused' | 'completed' | 'archived';
  trafficPct: number;
  targeting: { landingPathPrefix?: string; archeId?: string; campaignIds?: string[] };
  primaryGoalId: string | null;
  denominatorPolicy: 'exposed_only' | 'intent_to_treat';
  autonomyLevel: 'manual' | 'recommend' | 'auto_promote';
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
}

export interface AdoptExperimentVariantDto {
  variantKey: string;
  name: string;
  isControl: boolean;
  weight: number;
  config: Record<string, unknown>;
  externalRef: { channel?: string; externalAdRef?: string; creativeId?: string } | null;
  status: 'active' | 'retired';
}

export interface AdoptCreateExperimentRequest {
  key: string;
  name: string;
  hypothesis?: string;
  stage: AdoptExperimentStageDto;
  unitType: 'visitor' | 'user';
  trafficPct?: number;
  /** `campaignIds` (exactly one) is required when a variant's arm is a landing variant. */
  targeting?: { landingPathPrefix?: string; archeId?: string; campaignIds?: string[] };
  primaryGoalId?: string;
  denominatorPolicy?: 'exposed_only' | 'intent_to_treat';
  autonomyLevel?: 'manual' | 'recommend' | 'auto_promote';
  variants: Array<{
    variantKey: string;
    name: string;
    isControl?: boolean;
    weight?: number;
    /** `{ landingVariantKey }` makes the arm every creative of that campaign landing variant. */
    config?: Record<string, unknown>;
    externalRef?: { channel: 'google_ads'; externalAdRef: string; creativeId?: string };
  }>;
}

export interface AdoptExperimentResultRowDto {
  variantKey: string;
  computedAt: string;
  exposedUnits: number;
  conversions: number;
  revenueCents: number;
  spendMicros: number | null;
  posterior: Record<string, unknown>;
  pBest: number;
  expectedLoss: number;
  ciLow: number | null;
  ciHigh: number | null;
  evidence: Record<string, unknown>;
}

export interface AdoptExperimentResultsDto {
  evidenceClass: 'randomized' | 'observational';
  results: AdoptExperimentResultRowDto[];
  decisions: Array<Record<string, unknown>>;
}

export type AdoptActionStatusDto =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'executing'
  | 'executed'
  | 'failed'
  | 'expired';

export interface AdoptActionDto {
  id: string;
  campaignId: string;
  actionClass: 'optimization' | 'safety';
  actionType: string;
  targetType: string;
  targetId: string;
  params: Record<string, unknown>;
  rationale: string;
  evidence: Record<string, unknown>;
  status: AdoptActionStatusDto;
  decidedBy: 'engine' | 'human';
  reviewedAt: string | null;
  expiresAt: string | null;
  executedAt: string | null;
  executionError: string | null;
  createdAt: string;
}

export interface AdoptAutoUnlockDto {
  unlocked: boolean;
  /** What unlocked it: a platform admin's approval, the clean-history ladder, or nothing. */
  via: 'admin' | 'history' | null;
  executed: number;
  failed: number;
  required: number;
  windowDays: number;
  adminUnlock: { unlockedBy: string; unlockedAt: string; note: string } | null;
}

/** What an automatic-mode approval accepts: strategy defaults + each campaign's ceilings. */
export interface AdoptGuardrailsDto {
  defaultStrategy: AdoptResolvedStrategyDto;
  /** Platform bounds on every campaign's guest trials (credits are cents). */
  trial: {
    /** Credits a guest gets when its campaign sets no `trialCreditAmount`. */
    defaultCredits: number;
    /** Ceiling on a campaign's `trialCreditAmount`. */
    maxCampaignCredits: number;
    /** Ceiling on a campaign's `maxTrialsPerDay`. */
    maxTrialsPerDayLimit: number;
  };
  campaigns: Array<{
    id: string;
    name: string;
    status: AdoptCampaignStatusDto;
    autonomyLevel: AdoptAutonomyLevelDto;
    totalBudgetCents: number;
    dailyBudgetCeilingCents: number;
    strategy: AdoptResolvedStrategyDto;
  }>;
}

// ==========================================================================
// Liquidity Cache Types
// ==========================================================================

export interface LcPoolResponse {
  poolId: string;
  name: string;
  description: string | null;
  tokenA: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  tokenB: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  reserveA: string;
  reserveB: string;
  totalLpShares: string;
  feeBps: number;
  minLiquidity: number;
  maxTradePct: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface LcTransactionResponse {
  transactionId: string;
  poolId: string;
  transactionType: string;
  tokenAAmount: string | null;
  tokenBAmount: string | null;
  lpSharesAmount: string | null;
  tradeDirection: string | null;
  feeAmount: string | null;
  priceAtTrade: string | null;
  slippageBps: number | null;
  roundingAdjusted: boolean;
  originalAmount: string | null;
  adjustedAmount: string | null;
  roundingSavings: string | null;
  inventoryTransfersExecuted: boolean;
  createdAt: string;
}

// ==========================================================================
// Conversation Types
// ==========================================================================

export interface ConversationResponse {
  conversationId: string;
  archeId: string;
  title: string | null;
  messageCount: number;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface ConversationListResult {
  conversations: ConversationResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationMessageInput {
  role: 'user' | 'assistant';
  content: string;
  thinking?: string;
  provider?: ModelProvider;
  modelName?: string;
  modelEntityId?: string;
  inputTokens?: number;
  outputTokens?: number;
  thinkingTokens?: number;
  costUsd?: number;
  latencyMs?: number;
  metadata?: Record<string, unknown>;
}

export interface ConversationMessageResponse {
  messageId: string;
  sequenceNum: number;
  role: 'user' | 'assistant';
  content: string;
  thinking: string | null;
  provider: ModelProvider | null;
  modelName: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  latencyMs: number | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ConversationWithMessages extends ConversationResponse {
  messages: ConversationMessageResponse[];
}

export interface DeleteAllConversationsResult {
  success: boolean;
  deletedCount: number;
}

export interface ShareConversationResponse {
  shareId: string;
  expiresAt: string;
}

export interface SharedMessage {
  role: 'user' | 'assistant';
  content: string;
  provider: ModelProvider | null;
  modelName: string | null;
  timestamp: string;
}

export interface SharedConversationResponse {
  shareId: string;
  archeId: string;
  title: string | null;
  messages: SharedMessage[];
  createdAt: string;
  expiresAt: string;
}

// ==========================================================================
// Arche Session Types
// ==========================================================================

export interface SingleAIPreferences {
  activeConversationId: string | null;
  selectedProvider: ModelProvider;
  selectedModelEntityId: string | null;
  includeContext: boolean;
  showThinking: boolean;
  providerSettings: {
    openai?: {
      gpt4MaxOutputTokens?: number;
      gpt5Reasoning?: boolean;
      reasoningEffort?: 'low' | 'medium' | 'high' | 'xhigh';
      verbosity?: 'low' | 'medium' | 'high';
      gptMaxOutputTokens?: number;
    };
    anthropic?: {
      maxTokens?: number;
      budgetTokens?: number;
      temperature?: number;
    };
    google?: {
      maxOutputTokens?: number;
      temperature?: number;
      thinkingLevel?: 'LOW' | 'HIGH';
    };
  };
}

export interface MultiAIColumnConfig {
  provider: ModelProvider;
  modelEntityId: string;
  showThinking: boolean;
  gpt5Reasoning?: boolean;
  providerSettings: Record<string, unknown>;
}

export interface MultiAIPreferences {
  activeConversationId: string | null;
  columnConfigs: MultiAIColumnConfig[];
  selectedTier: 'fast' | 'balanced' | 'powerful' | null;
  includeContext: boolean;
  enabledPanels: boolean[];
}

export interface SingleAISessionResponse {
  type: 'single-ai';
  archeId: string;
  preferences: SingleAIPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface MultiAISessionResponse {
  type: 'multi-ai';
  archeId: string;
  preferences: MultiAIPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface ImageGeneratorPreferences {
  selectedProvider: 'openai' | 'google' | 'bfl' | 'recraft' | 'xai';
  selectedModelId: string | null;
  imageSettings: {
    size: string;
    quality: string;
    style: string;
  };
}

export interface ImageGeneratorSessionResponse {
  type: 'image-generator';
  archeId: string;
  preferences: ImageGeneratorPreferences;
  createdAt: string;
  updatedAt: string;
}

export type ArcheSessionResponse =
  | SingleAISessionResponse
  | MultiAISessionResponse
  | ImageGeneratorSessionResponse;

export type ArcheSessionUpdateInput =
  | Partial<SingleAIPreferences>
  | Partial<MultiAIPreferences>
  | Partial<ImageGeneratorPreferences>;

// ==========================================================================
// Arche Metadata Types
// ==========================================================================

/**
 * Lightweight arche metadata for route protection and embed eligibility
 */
export interface ArcheMetadata {
  archId: string;
  isPublic: boolean;
  isFullScreen: boolean;
  hasWorkflow: boolean;
  /** Whether the arche can be embedded in an iframe (public and no workflow) */
  isEmbeddable: boolean;
  renderMode: string;
  sessionType: string;
  isTrusted: boolean;
  /**
   * 'tool' | 'site' | 'internal' — the MARKETPLACE taxonomy behind the store's
   * Tools/Sites filters.
   *
   * NOT a signal that an arche serves a prerendered snapshot: the Arche Store
   * and Learn About Fias are `'site'` and serve none. Whether a slug URL is
   * canonical is decided by the SITE_ARCHES registry (`isSiteArcheSlug`).
   */
  archeType: string;
  suggestedArches: string[];
  redirectPath: string | null;
  /**
   * The shell theme this arche asked to run on (`fias-plugin.json`'s
   * `colorScheme`). 'platform' — the default, and what every arche published
   * before the field existed reports — means follow the user's own Fias theme.
   *
   * Optional on the wire so a client running against an older API, or a test
   * fixture written before this existed, does not have to carry it.
   */
  colorScheme?: 'dark' | 'light' | 'platform';
  /**
   * The arche's display name, and its icon as web-app-manifest entries.
   *
   * Both feed the per-page manifest (`lib/page-manifest.ts`): a page added to
   * a home screen should be the ARCHE — its name, its artwork — rather than a
   * second copy of Fias. `icons` is empty for an arche that has none, and only
   * carries sizes the icon pipeline actually wrote.
   *
   * Optional on the wire, like `colorScheme` above, so an older API or a
   * fixture written before this existed still type-checks.
   */
  name?: string;
  icons?: { src: string; sizes: string; type?: string }[];
  /**
   * The arche's URL slug — the name-based segment `/a/<slug>` people share.
   * The route guard rewrites a raw `/a/arc_<id>` address onto it
   * (`canonicalPluginArcheSegment`). Optional on the wire, like the two fields
   * above, so an older API or an older fixture still type-checks; absent or
   * null means "keep the URL as it is".
   */
  slug?: string | null;
}

// ==========================================================================
// Favorites Types
// ==========================================================================

/**
 * Lightweight arche display metadata for the worktop "Add Featured" chooser —
 * just enough to render a card (name, one-line description, resolved icon URL).
 */
/** A Vault file kind a plugin can declare it opens (`other` only with file types). */
export type FileIntakeKind = VaultFileKind;

/** A plugin app that can open Vault files (ADR-030). */
export interface FileIntakeDestination {
  archeId: string;
  label: string;
  kinds: FileIntakeKind[];
  /** null = whole kinds; otherwise the file's extension or MIME type must be listed. */
  types: string[] | null;
}

export interface ArcheDisplayMeta {
  archId: string;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
}

export interface UserFavoriteWithArche {
  userId: string;
  archId: string;
  /** Cell index in the 7-col desktop grid. */
  desktopPosition: number;
  /** Cell index in the 5-col mobile grid. */
  mobilePosition: number;
  createdAt: string;
  archeName: string;
  archeSlug: string | null;
  archeSimpleDescription: string;
  archeImageRef: string | null;
}

/** Which worktop grid a reorder applies to. Each layout has independent memory. */
export type WorktopLayout = 'mobile' | 'desktop';

// ==========================================================================
// Marketplace Types
// ==========================================================================

export type ArcheType = 'tool' | 'site';

export interface MarketplaceArche {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  tags: string[];
  fitnessScore: number;
  invocationCount: number;
  pricing: ArchePricing;
  actionPricing: ActionPricing | null;
  hasPerUseCredits: boolean;
  isStatic: boolean;
  archeType: ArcheType;
  relevance?: number;
  contributorId: string | null; // nullable: legacy/placeholder arches have no contributor
  isPublic: boolean;
  isFullScreen: boolean;
}

export interface MarketplaceSearchResult {
  arches: MarketplaceArche[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface MarketplaceArcheDetail {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  expandedDescription: string | null;
  imageRef: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  tags: string[];
  fitnessScore: number;
  successRate: number;
  avgCompletionTimeMs: number | null;
  invocationCount: number;
  pricing: ArchePricing;
  actionPricing: ActionPricing | null;
  hasPerUseCredits: boolean;
  costPerformanceConfig: CostPerformanceConfig;
  isStatic: boolean;
  archeType: ArcheType;
  inputSchema: Record<string, unknown> | null;
  contributorId: string | null; // nullable: legacy/placeholder arches have no contributor
  /**
   * Who published the arche, resolved server-side: the contributor's username
   * for third-party plugins, the Fias legal name for platform-built arches.
   * Null when a contributor id has no resolvable profile — the store hides the
   * row rather than showing a placeholder.
   */
  publisherName: string | null;
  publishedAt: string | null;
  isPublic: boolean;
  isFullScreen: boolean;
  /** Present for plugin arches — the store's Permissions section renders
   * `permissions` (the manifest scopes reviewed at publish). */
  pluginConfig?: { permissions?: string[] } | null;
}

export interface MarketplaceCategory {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  parentCategoryId: string | null;
  depth: number;
  archeCount: number;
}

export interface ArchePricing {
  model: 'free' | 'fixed' | 'per_use' | 'tiered' | 'no_base_cost' | 'included';
  currency: 'usd';
  priceCents?: number;
  baseFeeCents?: number;
  platformFeePercent?: number;
  tiers?: Array<{ upTo: number; priceCents: number }>;
}

export interface ActionPrice {
  costCredits: number;
  platformFeePercent: number;
}

export type ActionPricing = Record<string, ActionPrice>;

export interface CostPerformanceConfig {
  defaultWeight: number;
  userAdjustable: boolean;
  minWeight: number | null;
  maxWeight: number | null;
}

// ==========================================================================
// Marketplace Entity Types
// ==========================================================================

export type EntityType =
  | 'specialized-model'
  | 'human'
  | 'integration'
  | 'code-block'
  | 'model-definition'
  | 'service';

export interface EntityPricing {
  model: 'free' | 'fixed' | 'per_invocation' | 'no_base_cost' | 'included';
  currency: 'usd';
  priceCents?: number;
}

export interface MarketplaceEntity {
  entityId: string;
  type: EntityType;
  name: string;
  description: string;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  fitnessScore: number;
  invocationCount: number;
  avgLatencyMs: number | null;
  successRate: number;
  pricing: EntityPricing;
  contributorId: string;
  relevance?: number;
  semanticSimilarity?: number;
}

export interface MarketplaceEntityDetail extends MarketplaceEntity {
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  publishedAt: string | null;
  modelProvider?: string;
  modelName?: string;
}

export interface MarketplaceEntitySearchResult {
  entities: MarketplaceEntity[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface MarketplaceCategoryWithEntityCount {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  parentCategoryId: string | null;
  depth: number;
  entityCount: number;
}

// ==========================================================================
// Contributor Types
// ==========================================================================

export interface ContributorMetricsSummary {
  totalEntities: number;
  publishedEntities: number;
  totalInvocations: number;
  avgFitness: number;
}

export interface ContributorEntityMetric {
  entityId: string;
  name: string;
  type: string;
  status: string;
  fitnessScore: number;
  invocationCount: number;
  successRate: number;
  avgLatencyMs: number | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  lastInvokedAt: string | null;
}

export interface ContributorMetricsResult {
  summary: ContributorMetricsSummary;
  entities: ContributorEntityMetric[];
}

export interface ContributorArcheDashboardSummary {
  totalArches: number;
  publishedArches: number;
  totalInvocations: number;
  estimatedRevenueCents: number;
  avgFitness: number;
}

export interface ContributorArcheMetric {
  archId: string;
  name: string;
  simpleDescription: string;
  status: string;
  pricingModel: string;
  pricingLabel: string;
  invocationCount: number;
  successRate: number;
  fitnessScore: number;
  estimatedRevenueCents: number;
  publishedAt: string | null;
  updatedAt: string;
  editSessionId: string | null;
  /** The user's Fias Forge for this arche, when it has one (not deleted). */
  forgeId: string | null;
}

export interface ContributorArcheDashboardResult {
  summary: ContributorArcheDashboardSummary;
  arches: ContributorArcheMetric[];
}

// ==========================================================================
// Credits Types
// ==========================================================================

export type CreditBucket = 'subscription' | 'fias' | 'system';

export interface CreditBalance {
  userId: string;
  balance: number;
  buckets: {
    subscription: number;
    fias: number;
    system: number;
  };
  subscription: {
    tier: 'free' | 'standard' | 'pro';
    status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
    isOnSubscription: boolean;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    monthlyCreditAllocation: number;
  };
  lifetimeUsed: number;
  lifetimeGranted: number;
  lastUpdated: string;
}

export interface CreditTransaction {
  transactionId: string;
  type:
    | 'grant'
    | 'usage'
    | 'refund'
    | 'adjustment'
    | 'system_grant'
    | 'welcome_grant'
    | 'subscription_reset'
    | 'system_reset'
    | 'purchase'
    | 'subscription_credit'
    | string;
  amount: number;
  balanceAfter: number;
  bucket?: 'subscription' | 'fias' | 'system' | null;
  requestId: string | null;
  description: string | null;
  expenseCategory: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreditTransactionsResult {
  transactions: CreditTransaction[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * A user-facing charge group from GET /credits/transactions?view=grouped.
 * Every ledger row sharing one gateway request_id (a reservation plus its
 * finalize/cancel refunds) collapses into one group.
 */
export interface CreditTransactionGroup {
  groupId: string;
  requestId: string | null;
  /** SUM of the group's row amounts (negative = user paid). */
  netAmount: number;
  /**
   * fully_refunded: net 0 with >=1 usage and >=1 refund row (cancelled or
   * finalized at zero cost — nothing was charged). charged: net < 0.
   * simple: everything else (grants, purchases, standalone refunds).
   */
  status: 'fully_refunded' | 'charged' | 'simple';
  type: CreditTransaction['type'];
  description: string | null;
  expenseCategory: string | null;
  createdAt: string;
  /** Raw ledger rows, oldest first — the auditable detail. */
  transactions: CreditTransaction[];
}

export interface CreditTransactionGroupsResult {
  groups: CreditTransactionGroup[];
  /** Count of GROUPS, not raw ledger rows. */
  total: number;
  limit: number;
  offset: number;
}

export interface SpendingCategorySummary {
  category: string;
  totalCents: number;
  count: number;
}

/**
 * A user's net spend on a single arche/plugin. `archeId` (and `name`) are null
 * for the aggregate of spend with no arche context — the client owns that
 * row's label (e.g. "Platform / Other"). For real arches `name` is the
 * human-readable arche name, resolved server-side with an archived-arche
 * fallback (only a hard-deleted arche ever yields its bare id); `imageRef` is
 * the resolved icon URL (null when the arche is unpublished/deleted).
 */
export interface SpendByArche {
  archeId: string | null;
  name: string | null;
  imageRef: string | null;
  totalCents: number;
  count: number;
}

export interface CreditGrantResult {
  success: boolean;
  newBalance: number;
  transactionId: string;
}

// ==========================================================================
// Payment Types
// ==========================================================================

export interface PaymentConfig {
  publishableKey: string;
  purchaseAmounts: Array<{
    amountCents: number;
    displayPrice: string;
    credits: number;
  }>;
  minAmountCents: number;
  maxAmountCents: number;
  currency: string;
}

export interface CheckoutSessionResult {
  sessionId: string;
  url: string;
  publishableKey: string;
}

export interface CryptoCheckoutResult {
  chargeId: string;
  hostedUrl: string;
  expiresAt: string;
}

export interface PaymentHistoryItem {
  paymentId: string;
  amountCents: number;
  displayAmount: string;
  currency: string;
  creditsPurchased: number;
  paymentProvider: 'stripe' | 'coinbase';
  status: 'succeeded' | 'pending' | 'failed' | 'refunded' | 'partially_refunded';
  description: string | null;
  createdAt: string;
}

export interface PaymentHistoryResult {
  payments: PaymentHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Admin Arche Management Types
// ==========================================================================

export type ArcheLifecycleState = 'active' | 'disabled' | 'deleted';

/**
 * Origin filter for the admin arche pickers. 'internal' is every arche the
 * platform ships itself; 'plugin' is every developer-submitted one.
 */
export type ArcheKindFilter = 'all' | 'internal' | 'plugin';
/** Dev Console governance view (ADR-026): company-owned or restricted arches only. */
export type ArcheGovernanceFilter = 'all' | 'company-owned' | 'restricted';
/** The two creation-only governance kill switches (ADR-026). */
export type ArcheGovernanceSettingKey =
  | 'arche_restriction_enabled'
  | 'company_arche_transfer_enabled';
export type ArcheGovernanceSettingsDto = Record<ArcheGovernanceSettingKey, boolean>;

export interface ArcheManagementSummary {
  archId: string;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
  archeType: string;
  /** `'plugin'` = developer-submitted; anything else = platform-built. */
  renderMode: string;
  status: string;
  isListed: boolean;
  lifecycleState: ArcheLifecycleState;
  lifecycleChangedAt: string | null;
  lifecycleChangeReason: string | null;
  contributorId: string | null; // nullable: legacy/placeholder arches have no contributor
  contributorUsername: string | null;
  /**
   * Whether the contributor is the Fias platform account. `null` when the
   * server could not verify it (platform-account lookup failed); absent on an
   * older API build. Both read as "unknown" — never as "not Fias-owned" — so the
   * annex picker disables the row instead of offering an annex the server will
   * refuse.
   */
  isPlatformOwned?: boolean | null;
  suggestedArches: string[];
  /**
   * First-party designation: admin publishes of this arche skip the paid AI
   * security review. See `services/api-service/src/services/first-party-review-skip.ts`.
   */
  skipAiReview: boolean;
  skipAiReviewSetBy: string | null;
  skipAiReviewSetAt: string | null;
  /** Its Arche Saves label when it may save there automatically; absent on
   *  an older API build. */
  archeSavesLabel?: string | null;
  /** File kinds an admin approved this plugin to open (ADR-030); null when
   *  not approved; absent on an older API build. */
  fileIntakeKinds?: FileIntakeKind[] | null;
  /** File kinds its published manifest declares in `fileIntake`. */
  fileIntakeDeclared?: FileIntakeKind[];
  /** File types the manifest narrows to (null = whole kinds). */
  fileIntakeDeclaredTypes?: string[] | null;
  /** What takes effect now: approved ∩ declared. */
  fileIntakeEffective?: { kinds: FileIntakeKind[]; types: string[] | null };
  /** Governance (ADR-026); absent on an older API build. */
  owningCompanyId?: string | null;
  owningCompanyName?: string | null;
  isRestricted?: boolean;
}

// ==========================================================================
// Admin Payments Types
// ==========================================================================

export interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
  enabledAt: string | null;
  enabledBy: string | null;
}

/** `GET/PUT /v1/admin/guest-mode` — the stored state, read through the server's cache. */
export interface AdminGuestModeState {
  /** The guest-mode kill switch (`guest_mode_enabled`); off when never set. */
  enabled: boolean;
  /** The paid-arrivals gate (`guest_require_ad_click`); required when never set. Read-only. */
  adClickRequired: boolean;
  /** When the switch was last changed; null if it never has been on this environment. */
  updatedAt: string | null;
  /** User id of whoever last changed the switch; null if never changed. */
  updatedBy: string | null;
  /** Local dev only: the server skips the switch and the ad-click gate entirely. */
  devModeBypass: boolean;
}

export interface AdminPaymentsDashboard {
  period: string;
  profitAndLoss: {
    revenue: {
      totalCents: number;
      display: string;
      purchases: { totalCents: number; display: string; count: number };
      subscriptions: { totalCents: number; display: string; activeCount: number };
      serviceFees: { totalCents: number; display: string };
      aiMarkup: { totalCents: number; display: string };
    };
    // True provider cash-out (COGS). `costTrackingSince` is the date raw-cost
    // tracking began — earlier usage contributes $0 (no raw cost recorded).
    cost: {
      totalCents: number;
      display: string;
      transactionCount: number;
      costTrackingSince: string;
    };
    systemCreditsAbsorbed: { totalCredits: number; display: string; grantCount: number };
    systemCreditUsage: {
      recipients: number;
      byGrantType: Array<{
        grantType: string;
        totalCredits: number;
        display: string;
        count: number;
      }>;
      recipientUsage: { totalConsumed: number; display: string; transactionCount: number };
      byCategory: Array<{ category: string; totalCredits: number; display: string; count: number }>;
    };
    grossMargin: { totalCents: number; display: string; percent: string; isPositive: boolean };
  };
  platform: {
    totalUsers: number;
    activeSubscribers: number;
    averageRevenuePerUser: string;
  };
  expenseByCategory: Array<{
    category: string;
    totalCredits: number;
    display: string;
    count: number;
  }>;
  topSpendingUsers: Array<{
    userId: string;
    username: string | null;
    displayName: string | null;
    totalSpent: number;
    displaySpent: string;
    transactionCount: number;
  }>;
}

export interface CreditActivityPerson {
  userId: string;
  username: string | null;
  displayName: string | null;
}

export interface CreditActivityGrantItem {
  transactionId: string;
  kind: 'grant' | 'deduct';
  amountCredits: number;
  display: string;
  bucket: string | null;
  description: string | null;
  reason: string | null;
  target: CreditActivityPerson | null;
  actor: CreditActivityPerson | null;
  createdAt: string;
}

export interface CreditActivityRefundItem {
  paymentId: string;
  status: string;
  amountCents: number;
  refundedCents: number;
  display: string;
  reason: string | null;
  target: CreditActivityPerson | null;
  actor: CreditActivityPerson | null;
  createdAt: string;
}

// ── Providers cost/safety control center ──────────────────────────────
// Caps + spend are RAW CREDITS (1 credit = $0.01, pre-markup). UI converts $.
export interface AdminProviderOverviewItem {
  provider: string;
  enabled: boolean;
  /**
   * The `provider_config` row's OWN values (defaults when no row exists) — the
   * only correct source for the edit dialog. `caps` below is display-shaped:
   * it folds in the balance ceiling, the emergency override and the fail-safe
   * default, so round-tripping it through the form silently redefines the cap
   * an admin set — and a balance-derived 0 ("account empty") would be saved as
   * 0 ("unlimited"), removing the cap entirely.
   */
  configuredCaps: {
    enabled: boolean;
    dailyCapRawCredits: number;
    monthlyCapRawCredits: number;
    softAlertPercent: number;
  };
  caps: {
    dailyCapRawCredits: number;
    monthlyCapRawCredits: number;
    softAlertPercent: number;
    /**
     * Where the DISPLAYED cap came from. `balance` means a fresh remaining
     * balance is lower than the configured cap and is what the console shows
     * (only prepaid providers with a balance API or a manual snapshot reach
     * this); `default` means no config row exists and the $2,000 fail-safe
     * applies — visually distinct so an unconfigured provider is never
     * mistaken for one with real headroom.
     */
    capSource: 'balance' | 'config' | 'default' | 'emergency';
    /**
     * Provenance of the MONTHLY cap. Never `balance`: the balance ceiling
     * bounds the daily window only, because reconstructing it as
     * `spent + remaining` assumes no mid-period top-up — true over a day,
     * false over a month. Reported separately so the month cell can still flag
     * an unconfigured `default` cap.
     */
    monthlyCapSource: 'config' | 'default' | 'emergency';
    /**
     * The account has NO funds available this period. Distinguishes a
     * balance-derived 0 from the "0 = unlimited" configured-cap convention —
     * without it an empty prepaid account renders as an infinite cap.
     */
    capExhausted: boolean;
    /** Remaining balance (USD) behind a fresh observation, else null. */
    balanceUsd: number | null;
    /**
     * The cap the GATEWAY actually enforces. Differs from dailyCapRawCredits
     * only when a balance is the lower, displayed number — balances are
     * reported on but never blocked on, so both are surfaced rather than
     * leaving "why didn't my cap stop this" unanswerable on screen.
     */
    enforcedDailyCapRawCredits: number;
  };
  spend: {
    daySpentRawCredits: number;
    dayReservedRawCredits: number;
    monthSpentRawCredits: number;
    monthReservedRawCredits: number;
  };
  dayUsagePct: number | null;
  monthUsagePct: number | null;
  hasConfig: boolean;
  accountLabel: string | null;
  consoleUrl: string | null;
  billingUrl: string | null;
  notes: string | null;
  setup: {
    /** An inference key source is configured (env presence only). */
    inferenceKeyConfigured: boolean;
    /** Admin/management key configured; null = provider has no admin variant. */
    managementKeyConfigured: boolean | null;
    /** A live balance/cost fetcher exists for this provider. */
    balanceFetcherAvailable: boolean;
  };
}

export interface AdminProvidersOverview {
  providers: AdminProviderOverviewItem[];
  generatedAt: string;
}

export interface AdminProviderConfigInput {
  enabled: boolean;
  dailyCapRawCredits: number;
  monthlyCapRawCredits: number;
  softAlertPercent: number;
  lowBalanceThresholdUsd?: number | null;
  driftToleranceUsd?: number;
  accountLabel?: string | null;
  consoleUrl?: string | null;
  billingUrl?: string | null;
  notes?: string | null;
}

export type ProviderBalanceEntryType = 'observation' | 'top_up' | 'refund' | 'adjustment';
export type ProviderBalanceUnit = 'usd' | 'credits' | 'characters';

export interface AdminProviderBalanceSnapshot {
  snapshotId: string;
  provider: string;
  entryType: ProviderBalanceEntryType;
  balanceAmount: number;
  balanceUnit: ProviderBalanceUnit;
  balanceUsdEstimate: number | null;
  conversionRateSource: string | null;
  observedAt: string;
  fetchMethod: 'manual' | 'api';
  expectedUsd: number | null;
  driftUsd: number | null;
  note: string | null;
  recordedBy: string | null;
  createdAt: string;
}

export interface AdminProviderReconciliation {
  provider: string;
  lastObservation: AdminProviderBalanceSnapshot | null;
  recordedSpendSinceUsd: number;
  adjustmentsSinceUsd: number;
  expectedBalanceUsd: number | null;
  observedBalanceUsd: number | null;
  driftUsd: number | null;
  flagged: boolean;
}

export interface AdminProviderDetail {
  provider: string;
  config: {
    accountLabel: string | null;
    consoleUrl: string | null;
    billingUrl: string | null;
    notes: string | null;
    driftToleranceUsd: number;
  } | null;
  caps: { dailyCapRawCredits: number; monthlyCapRawCredits: number; softAlertPercent: number };
  spend: AdminProviderOverviewItem['spend'];
  reconciliation: AdminProviderReconciliation;
  snapshots: AdminProviderBalanceSnapshot[];
}

export interface AdminProviderSnapshotInput {
  entryType: ProviderBalanceEntryType;
  balanceAmount: number;
  balanceUnit?: ProviderBalanceUnit;
  balanceUsdEstimate?: number | null;
  conversionRateSource?: string | null;
  observedAt?: string;
  note?: string | null;
}

export interface AdminProviderBalanceResult {
  provider: string;
  kind: 'balance' | 'cost';
  amount: number | null;
  unit: ProviderBalanceUnit;
  usdEstimate: number | null;
  conversionRateSource: string | null;
  fetchedAt: string;
  ok: boolean;
  error?: string;
}

// ============================================================================
// Fias Forge admin (dev-console) — mirrors routes/admin/forge.ts responses.
// ============================================================================

export interface AdminForgeListParams {
  status?: ForgeStatus;
  userId?: string;
  limit?: number;
  offset?: number;
}

/**
 * A forge as the admin views see it. They list deleted forges too, and a
 * deleted forge's arche may have been deleted with it (ADR-023 A17).
 */
export type AdminForgeSummary = Omit<ForgeSummary, 'archeId'> & { archeId: string | null };

export interface AdminForgeListResult {
  forges: AdminForgeSummary[];
  /** Rows matching the filters, ignoring limit/offset. */
  total: number;
  limit: number;
  offset: number;
}

export interface AdminForgeDetailResult {
  forge: AdminForgeSummary;
  turns: ForgeTurnSummary[];
  builds: ForgeTestBuildSummary[];
  monthActiveSeconds: number;
  ownerMonthActiveSeconds: number;
  chargedCredits30d: number;
}

export interface AdminForgeMetrics {
  countsByStatus: Record<ForgeStatus, number>;
  runningNow: number;
  activeSecondsToday: number;
  activeSecondsMonth: number;
  openTurns: number;
  creditsCharged30d: { compute: number; storage: number; total: number };
  snapshotBytesTotal: number;
}

/** `forge_user_holds` row: the user cannot start a forge or send a turn while held. */
export interface AdminForgeUserHold {
  userId: string;
  reason: string;
  createdBy: string;
  createdAt: string;
}

export interface AdminAwsCostReport {
  period: 'mtd' | '30d';
  totalUsd: number;
  byService: { service: string; amountUsd: number }[];
  daily: { date: string; amountUsd: number }[];
  dataAsOf: string | null;
  fetchedAt: string;
  ok: boolean;
  error?: string;
}

export interface AdminProviderSpendTotal {
  provider: string;
  rawCostCredits: number;
  invocationCount: number;
}

export interface AdminProviderSpendDailyPoint {
  provider: string;
  date: string; // 'YYYY-MM-DD'
  rawCostCredits: number;
  invocationCount: number;
}

export interface AdminProviderSpendHistory {
  from: string;
  to: string;
  totals: AdminProviderSpendTotal[];
  daily: AdminProviderSpendDailyPoint[];
  generatedAt: string;
}

export type AdminProviderHealthWindow = '24h' | '7d' | '30d';

export interface AdminProviderHealthRow {
  provider: string;
  totalInvocations: number;
  successfulInvocations: number;
  failedInvocations: number;
  successRate: number; // 0-1
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  errorCounts: Record<string, number>;
}

export interface AdminProviderHealth {
  window: AdminProviderHealthWindow;
  providers: AdminProviderHealthRow[];
  /** When attribution began (MIN attributed invoked_at); null before any attributed rows. */
  attributionSince: string | null;
  generatedAt: string;
}

export interface AdminProviderModelHealthRow {
  entityId: string;
  entityName: string;
  totalInvocations: number;
  successfulInvocations: number;
  failedInvocations: number;
  successRate: number; // 0-1
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
  errorCounts: Record<string, number>;
}

export interface AdminProviderModelHealth {
  provider: string;
  window: AdminProviderHealthWindow;
  models: AdminProviderModelHealthRow[];
  generatedAt: string;
}

export type AdminProviderStatusIndicator = 'none' | 'minor' | 'major' | 'critical';

export interface AdminProviderStatusReading {
  provider: string;
  ok: boolean;
  indicator: AdminProviderStatusIndicator | null;
  description: string | null;
  error: string | null;
  statusPageUrl: string;
  checkedAt: string;
}

export interface AdminProviderStatus {
  provider: string;
  /** false = no known public status page for this provider (render nothing). */
  supported: boolean;
  status: AdminProviderStatusReading | null;
  generatedAt: string;
}

export interface AdminCreditActivity {
  kind: 'grants' | 'refunds';
  total: number;
  limit: number;
  offset: number;
  items: Array<CreditActivityGrantItem | CreditActivityRefundItem>;
}

export interface AdminPaymentsCosts {
  period: string;
  byCategory: Array<{ category: string; totalCredits: number; displayCost: string; count: number }>;
  byArche: Array<{
    archeId: string;
    archeName: string;
    totalCredits: number;
    displayCost: string;
    count: number;
  }>;
}

// ==========================================================================
// Admin Payment Management Types
// ==========================================================================

export interface AdminUserPaymentDetail {
  userId: string;
  credits: { balance: number; lifetimeUsed: number; lifetimeGranted: number };
  systemCreditsGranted: number;
  subscription: { tier: string; status: string };
  recentPayments: Array<{
    paymentId: string;
    amountCents: number;
    currency: string;
    status: string;
    description: string | null;
    createdAt: string;
  }>;
  recentTransactions: Array<{
    transactionId: string;
    type: string;
    amount: number;
    balanceAfter: number;
    bucket: string | null;
    description: string;
    createdAt: string;
  }>;
}

export interface AdminPaymentSearchParams {
  status?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  minAmountCents?: number;
  maxAmountCents?: number;
  limit?: number;
  offset?: number;
}

export interface AdminPaymentRecord {
  paymentId: string;
  userId: string;
  amountCents: number;
  displayAmount: string;
  currency: string;
  creditsPurchased: number;
  paymentProvider: string;
  providerPaymentId: string | null;
  providerChargeId: string | null;
  status: string;
  description: string | null;
  createdAt: string;
}

export interface AdminPaymentSearchResult {
  payments: AdminPaymentRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminSubscriptionListParams {
  tier?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface AdminSubscriptionRecord {
  subscriptionId: string;
  userId: string;
  tier: string;
  status: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  monthlyCreditAllocation: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSubscriptionListResult {
  subscriptions: AdminSubscriptionRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminStripeEventListParams {
  eventType?: string;
  startDate?: string;
  endDate?: string;
  processedOk?: boolean;
  limit?: number;
  offset?: number;
}

export interface AdminStripeEvent {
  eventId: string;
  eventType: string;
  receivedAt: string;
  processedAt: string | null;
  processedOk: boolean | null;
  errorMessage: string | null;
}

export interface AdminStripeEventDetail extends AdminStripeEvent {
  payload: unknown;
}

export interface AdminStripeEventListResult {
  events: AdminStripeEvent[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Admin Platform Usage Analytics — Layer 2 dashboard response shapes
// ==========================================================================

export type AnalyticsPeriod = '7d' | '30d' | '90d';

export interface AdminAnalyticsOverview {
  period: AnalyticsPeriod;
  dau: number;
  wau: number;
  mau: number;
  totalUsers: number;
  newUsersPeriod: number;
  activeSubscribers: number;
  totalSpendCredits: number;
  totalSpendDisplay: string;
  dailySeries: Array<{
    date: string;
    activeUsers: number;
    sessions: number;
    pageViews: number;
  }>;
  dailySpendSeries: Array<{
    date: string;
    spendCredits: number;
    invocations: number;
  }>;
  topPagesByViews: Array<{ pageName: string; views: number }>;
  topArchesByOpens: Array<{ archeId: string; opens: number; uniqueUsers: number }>;
  topInvocationCategories: Array<{
    category: string;
    invocations: number;
    totalCredits: number;
  }>;
}

export interface AdminAnalyticsEngagement {
  period: AnalyticsPeriod;
  topArchesByUniqueUsers: Array<{ archeId: string; uniqueUsers: number }>;
  topArchesByEstimatedActiveTime: Array<{ archeId: string; estimatedActiveMinutes: number }>;
  topArchesByOpens: Array<{ archeId: string; opens: number }>;
  topArchesByCredits: Array<{ archeId: string; totalCredits: number; count: number }>;
  featureAdoption: Array<{ feature: string; uniqueUsers: number; totalUses: number }>;
  workflowFunnel: Array<{
    workflow: string;
    started: number;
    completed: number;
    abandoned: number;
    completionRate: number;
  }>;
  bucketSpendDaily: Array<{
    date: string;
    subscription: number;
    fias: number;
    system: number;
  }>;
  expenseByCategory: Array<{ category: string; totalCredits: number; count: number }>;
}

export interface AdminAnalyticsHealth {
  period: AnalyticsPeriod;
  dailyBurnCapCredits: number;
  entityHealth: Array<{
    entityId: string;
    entityName: string;
    invocations: number;
    successes: number;
    failures: number;
    successRate: number;
    p95LatencyMs: number;
  }>;
  dailyInvocationHealth: Array<{ date: string; success: number; failure: number }>;
  usersNearBurnCap: Array<{
    userId: string;
    peakDay: string;
    peakSpendCredits: number;
    capCredits: number;
    daysOverEighty: number;
  }>;
  systemBucketByFeature: Array<{ feature: string; totalCredits: number; count: number }>;
  analyticsIngestionHealth: { hourly: Array<{ hour: string; eventCount: number }> };
}

export interface AdminAnalyticsUserDetail {
  userId: string;
  profile: {
    balance: number;
    subscriptionCreditBalance: number;
    fiasCreditBalance: number;
    systemCreditBalance: number;
    lifetimeUsed: number;
    lifetimeGranted: number;
    subscription: { tier: string; status: string };
  };
  summary: {
    activeDaysInPeriod: number;
    totalSessions: number;
    estimatedActiveMinutes: number;
    totalSpendCredits: number;
    totalInvocations: number;
    topArche: string | null;
  };
  dailyActivity: Array<{
    date: string;
    sessions: number;
    estimatedActiveMinutes: number;
    spendCredits: number;
  }>;
  eventTimeline: Array<{
    occurredAt: string;
    eventType: string;
    eventName: string;
    archeId: string | null;
    pagePath: string | null;
    sessionId: string;
  }>;
  recentTransactions: unknown[];
  recentInvocations: Array<{
    invocationId: string;
    requestId: string;
    entityId: string;
    verb: string;
    outcome: string;
    startedAt: string;
    finalizedAt: string | null;
    latencyMs: number | null;
    errorCode: string | null;
    costCents: number;
  }>;
  recentBuilderSessions: Array<{
    sessionId: string;
    archId: string | null;
    status: string;
    totalCostCents: number | null;
    lastClaudeResponseAt: string | null;
    updatedAt: string;
  }>;
}

// ==========================================================================
// Subscription Types
// ==========================================================================

export interface SubscriptionTier {
  tier: 'free' | 'standard' | 'pro';
  name: string;
  priceMonthly: number;
  displayPrice: string;
  monthlyCreditAllocation: number;
  storageLimitGb: number;
  features: string[];
}

export interface SubscriptionTiersResult {
  tiers: SubscriptionTier[];
}

export interface SubscriptionInfo {
  subscriptionId: string;
  tier: 'free' | 'standard' | 'pro';
  tierName: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  monthlyCreditAllocation: number;
  storageLimitGb: number;
  features: string[];
}

// ==========================================================================
// Models Types
// ==========================================================================

export type ModelProvider = 'openai' | 'anthropic' | 'google' | 'xai';
export type ModelTier = 'fast' | 'balanced' | 'powerful';

/**
 * Pass-through cost spec as delivered on the wire by `GET /v1/models` and
 * `GET /v1/models/definitions`. Rates are RAW provider USD (pre-markup); the
 * user-facing charge is `raw × (1 + markupPercent/100)`. Mirrors the server's
 * `PassThroughCostSpec` (packages/db-types/src/entity-pricing.ts) — kept as a
 * client-local type because api.ts hand-maintains its own type mirrors.
 */
export type ModelCostSpec =
  | { kind: 'per_call'; rawCostUsd: number }
  | {
      kind: 'per_token';
      inputUsdPerToken: number;
      outputUsdPerToken: number;
      highTierThresholdInputTokens?: number;
      highTierInputUsdPerToken?: number;
      highTierOutputUsdPerToken?: number;
    }
  | { kind: 'per_character'; usdPerChar: number }
  | { kind: 'per_second'; usdPerSecond: number }
  | {
      kind: 'per_tier';
      rawCostUsdByTier: Record<string, number>;
      inputUsdPerToken?: number;
      outputUsdPerToken?: number;
    };

/**
 * The `pass_through` `EntityPricing` shape carried by model rows. This is what
 * text models (`/v1/models`) and image models (`/v1/models/definitions`) return;
 * `costSpec` holds the raw per-unit provider rate and `markupPercent` the platform
 * markup applied at billing time. Consumed by `getModelCostInfo` / `ModelCostLabel`.
 */
export interface ModelPassThroughPricing {
  model: 'pass_through';
  currency: 'usd';
  markupPercent: number;
  platformFeePercent?: number;
  contributorFeePercent?: number;
  costSpec: ModelCostSpec;
}

export interface ModelCapabilities {
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  temperatureRange: { min: number; max: number } | null;
  defaultTemperature: number;
  isReasoningModel: boolean;
  apiEndpoint: 'chat' | 'responses';
}

export interface ModelEntity {
  entityId: string;
  name: string;
  description: string;
  provider: ModelProvider;
  providerDisplayName: string;
  modelName: string;
  tier: ModelTier;
  isRecommended: boolean;
  capabilities: ModelCapabilities;
  /**
   * Text model rows come back as `pass_through` pricing (per-token cost spec).
   * The `ArchePricing` arm is retained for defensiveness against non-pass-through
   * rows; `getModelCostInfo` narrows on `pricing.model === 'pass_through'`.
   */
  pricing: ModelPassThroughPricing | ArchePricing;
}

export interface ProviderInfo {
  key: ModelProvider;
  displayName: string;
  models: string[];
  recommendedModelId: string;
}

export interface ModelListResponse {
  models: ModelEntity[];
  providers: ProviderInfo[];
}

/**
 * A platform text-capability a plugin can request (text-fast / text-standard /
 * text-advanced). `modelDefinitionId` is the current concrete model the
 * capability resolves to (used as the builder's pinned fallback).
 */
export interface TextCapability {
  id: string;
  label: string;
  description: string;
  modelDefinitionId: string;
}

// ==========================================================================
// Model Definition Types (Entity-driven parameter schema)
// ==========================================================================

/**
 * Parameter data types for model definition schema
 */
export type ParameterType = 'number' | 'enum' | 'boolean' | 'string';

/**
 * UI control types for parameter rendering
 */
export type ParameterControl = 'slider' | 'input' | 'select' | 'checkbox' | 'toggle' | 'textarea';

/**
 * Parameter categories for UI grouping
 */
export type ParameterCategory = 'output' | 'reasoning' | 'behavior' | 'advanced';

/**
 * Numeric range constraint for parameters
 */
export interface ParameterRange {
  min: number;
  max: number;
  step?: number;
  default: number;
}

/**
 * Option for enum parameters
 */
export interface ParameterOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Conditional dependency for parameter visibility
 */
export interface ParameterDependency {
  parameter: string;
  value: unknown;
}

/**
 * Conditional default values based on another parameter's value
 */
export interface ConditionalDefault {
  dependsOn: string;
  values: Record<string, unknown>;
}

/**
 * Complete parameter definition from Model Definition Entity
 *
 * Defines everything needed to:
 * 1. Validate parameter values
 * 2. Render UI controls
 * 3. Apply constraints
 * 4. Show/hide conditionally
 */
export interface ParameterDefinition {
  name: string;
  type: ParameterType;
  label: string;
  description: string;
  category: ParameterCategory;
  range?: ParameterRange;
  options?: ParameterOption[];
  defaultValue?: unknown;
  required: boolean;
  dependsOn?: ParameterDependency;
  conditionalDefault?: ConditionalDefault;
  control: ParameterControl;
  apiName?: string;
}

/**
 * API endpoint configuration for model definitions
 */
export interface ApiEndpointConfig {
  url: string;
  method: 'POST';
  format: 'chat' | 'responses' | 'completions' | 'custom';
  streaming: boolean;
}

/**
 * Model definition capabilities
 */
export interface ModelDefinitionCapabilities {
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  supportsStreaming: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  isReasoningModel: boolean;
  inputTokenCostPer1k?: number;
  outputTokenCostPer1k?: number;
  /** Allow provider-specific capability extensions (e.g., providerParams for image models) */
  [key: string]: unknown;
}

/**
 * Model type discriminator
 */
export type ModelType = 'text' | 'image';

/**
 * Model Definition Entity response from API
 */
export interface ModelDefinition {
  entityId: string;
  name: string;
  description: string;
  status: string;
  provider: ModelProvider;
  modelName: string;
  modelType: ModelType;
  displayName: string;
  shortDescription: string;
  apiEndpoint: ApiEndpointConfig;
  parameterSchema: ParameterDefinition[];
  executionConfig: unknown;
  capabilities: ModelDefinitionCapabilities;
}

/**
 * Model Definition list item (lighter weight for lists)
 */
export interface ModelDefinitionListItem {
  entityId: string;
  name: string;
  displayName: string;
  shortDescription: string;
  provider: ModelProvider;
  modelName: string;
  modelType: ModelType;
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  maxOutputTokens: number;
  status: string;
  capabilities: ModelDefinitionCapabilities;
  /**
   * Present on the LIST endpoint (`GET /v1/models/definitions`) only — image model
   * rows carry `per_call` pass-through pricing. Optional because the single-fetch
   * `/definitions/:id` + `/definitions/lookup` responses deliberately strip pricing.
   */
  pricing?: ModelPassThroughPricing;
}

/**
 * Model Definition list response
 */
export interface ModelDefinitionListResponse {
  definitions: ModelDefinitionListItem[];
  total: number;
}

// ==========================================================================
// File Management Types
// ==========================================================================

export type FileCategory = 'general' | 'document' | 'financial' | 'media' | 'generated';
export type FileStatus = 'active' | 'processing' | 'archived' | 'deleted';
export type StorageClass = 'standard' | 'infrequent' | 'glacier';
export type StorageTier = 'free' | 'basic' | 'premium';

export interface PresignedUploadResponse {
  uploadUrl: string;
  fileId: string;
  s3Key: string;
  expiresAt: string;
}

export interface MultipartUploadResponse {
  uploadId: string;
  fileId: string;
  s3Key: string;
  partSize: number;
  totalParts: number;
}

export interface PresignedPartResponse {
  uploadUrl: string;
  partNumber: number;
  expiresAt: string;
}

export interface UserFileResponse {
  fileId: string;
  fileName: string;
  fileExtension: string | null;
  mimeType: string | null;
  sizeBytes: number;
  checksumSha256?: string | null;
  category: FileCategory;
  sensitivity?: string;
  folderId: string | null;
  tags: string[];
  metadata?: Record<string, unknown>;
  status: FileStatus;
  storageClass: StorageClass;
  sourceType?: string;
  sourceArcheId?: string | null;
  createdAt: string;
  updatedAt: string;
  accessedAt: string;
  /** Lifecycle state of the small WebP thumbnail variant. */
  thumbnailStatus?: 'pending' | 'ready' | 'failed' | 'skipped';
  /** Inlined presigned URL for the WebP thumbnail (only when thumbnailStatus === 'ready'). */
  thumbnailUrl?: string;
  /** Pre-minted presigned URL for the original image, inlined by /files to avoid N+1 fetches. */
  downloadUrl?: string;
}

export interface FileListResult {
  files: UserFileResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FolderResponse {
  folderId: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ContextFolderResponse {
  folderId: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  createdAt: string;
  isProtected: boolean;
}

export interface StorageUsageBreakdown {
  category: FileCategory;
  bytes: number;
  formatted: string;
  fileCount: number;
  percentage: number;
}

export interface StorageUsageResponse {
  userId: string;
  totalBytes: number;
  totalFormatted: string;
  quotaBytes: number;
  quotaFormatted: string;
  usedPercentage: number;
  fileCount: number;
  storageTier: StorageTier;
  breakdown: StorageUsageBreakdown[];
  lastCalculatedAt: string;
}

export interface FileOperationResponse {
  operationId: string;
  fileId: string | null;
  operationType: string;
  bytesTransferred: number;
  bytesFormatted: string;
  costCredits: number;
  archeId: string | null;
  createdAt: string;
}

export interface FileOperationsListResult {
  operations: FileOperationResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Image Generation Types
// ==========================================================================

export interface ImageGenerationInput {
  prompt: string;
  provider?: string;
  model?: string;
  size?: string;
  quality?: string;
  style?: string;
  providerParams?: Record<string, unknown>;
  referenceImageRef?: string;
  referenceImageFileId?: string;
  referenceStrength?: number;
}

export interface ImageGenerationResponse {
  requestId: string;
  archId: string;
  status: 'pending';
}

export interface ImageModel {
  id: string;
  name: string;
  provider: string;
  supportedSizes: string[];
  supportedQualities?: string[];
  supportedStyles?: string[];
  supportsReferenceImage?: boolean;
}

export interface ImageProviderInfo {
  key: string;
  displayName: string;
  models: string[];
  recommendedModelId: string;
}

export interface ImageModelsResponse {
  models: ImageModel[];
  providers: ImageProviderInfo[];
}

// ============================================================================
// Voice Studio Types
// ============================================================================

export interface TranscriptionResult {
  transcriptionId: string;
  text: string;
  detectedLanguage: string | null;
  durationSeconds: number | null;
}

export interface SynthesisResult {
  /** vault_documents row id; undefined if the optional document registration failed. */
  fileId?: string;
  /** Vault Documents S3 key for the stored audio; usable to re-presign through the Vault flow. */
  audioRef: string;
  /** Short-lived presigned URL for immediate playback / download. */
  downloadUrl: string;
}

export interface VoiceModel {
  voiceModelId: string;
  name: string;
  description: string | null;
  provider: string;
  providerVoiceId: string;
  status: string;
  sampleFileIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PlatformVoice {
  voiceId: string;
  name: string;
  description: string | null;
  labels: Record<string, string>;
}

// ==========================================================================
// Banking Transform Types
// ==========================================================================

export interface BankingTransformRequest {
  sourceType: 'my-fias' | 'upload';
  fileId?: string;
  csvContent?: string;
  /** Where the user chose to save the result (the Save dialog). */
  save: VaultSaveTargetDto;
  adapter?: string;
}

export interface BankingTransformResponse {
  fileId: string;
  fileName: string;
  rowCount: number;
  preview: string[];
  adapterName: string;
}

export const api = new ApiClient();
export default api;

// ==========================================================================
// Contributor Asset Library Types
// ==========================================================================

export interface ContributorAssetLibraryConfig {
  enabled: boolean;
  suspendedAt: string | null;
  suspendReason: string | null;
  effectiveCaps: {
    maxAssets: number;
    maxStorageBytes: number;
    maxUploadBytes: number;
    maxDecodedPixels: number;
  };
  usage: {
    activeStorageBytes: number;
    activeAssetCount: number;
  };
}

/** Server-side list orderings for asset libraries (mirrors the route enum). */
export type AssetListSortValue =
  | 'newest'
  | 'oldest'
  | 'name_asc'
  | 'name_desc'
  | 'size_asc'
  | 'size_desc';

/** Content types the asset library accepts: images, and short sound effects. */
export type ContributorArcheAssetContentType =
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'audio/mpeg'
  | 'audio/wav';

export interface ContributorArcheAsset {
  assetId: string;
  /** Owning arche, or null when the asset belongs to a workspace library (ADR-019). */
  archeId: string | null;
  /** Owning workspace (org Holder), or null for a per-arche asset. */
  holderId: string | null;
  contributorId: string;
  storageKey: string;
  contentType: ContributorArcheAssetContentType;
  contentHash: string;
  sizeBytes: number;
  /** Pixel dimensions for images; null for audio. */
  widthPx: number | null;
  heightPx: number | null;
  decodedPixels: number | null;
  name: string | null;
  altText: string | null;
  tags: string[];
  status: string;
  statusReason: string | null;
  statusChangedAt: string;
  uploadedBy: string;
  uploadedAt: string;
}

// ==========================================================================
// Workspace (org Holder) Shared Asset Library Types (ADR-019)
// ==========================================================================

/** The caller's role in an org workspace — the existing Vault org roles. */
export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface WorkspaceAssetLibrarySummary {
  enabled: boolean;
  suspendedAt: string | null;
  membersCanLink: boolean;
}

export interface WorkspaceAssetLibraryConfigResult {
  config: ContributorAssetLibraryConfig;
  membersCanLink: boolean;
  role: WorkspaceRole;
  holder: { holderId: string; displayName: string };
}

export interface WorkspaceAssetManifest {
  hmManifestId: string;
  assetCount: number;
  totalBytes: number;
  createdAt: string;
  /** How many published arche versions currently pin this library version. */
  pinCount: number;
}

/**
 * What a pin refresh did to one linked arche. Mirrors `RepinArcheOutcome` in
 * `@fias/db-operations` — the client cannot import it, so the union is
 * restated. Only `repinned` changed anything.
 */
export type WorkspacePinRefreshOutcome =
  | 'repinned'
  | 'already_current'
  | 'skipped_would_drop'
  | 'not_pinned'
  | 'no_manifest'
  | 'link_invalid'
  | 'unpublished'
  | 'failed';

export interface WorkspacePinRefreshResult {
  archeId: string;
  archeName: string | null;
  outcome: WorkspacePinRefreshOutcome;
  hmManifestId: string | null;
  /** For `skipped_would_drop`: a sample of the asset ids the latest version lost. */
  missingAssetIds?: string[];
  /** For `failed`: a fixed, caller-safe reason. Never the underlying error text. */
  error?: string;
}

export type WorkspaceLinkInvalidReason =
  | 'revoked'
  | 'holder_archived'
  | 'arche_deleted'
  | 'library_disabled'
  | 'library_suspended'
  | 'no_qualifying_member';

/** Derived at read time by the server — never stored, never cached client-side. */
export interface WorkspaceLinkValidity {
  valid: boolean;
  reason: WorkspaceLinkInvalidReason | null;
}

/** One arche linked to a workspace library (workspace-side view). */
export interface WorkspaceLinkedArche {
  archeId: string;
  archeName: string | null;
  linkedAt: string;
  revokedAt: string | null;
  revokeReason: string | null;
  validity: WorkspaceLinkValidity;
  /**
   * A published version of the arche still pins this library. Stays true
   * after a soft revoke until the arche republishes (or is hard-revoked),
   * which is why `canHardRevoke` can be true on a revoked row.
   */
  pinned: boolean;
  /** Server-derived: the caller may soft-unlink this arche (always false once revoked). */
  canUnlink: boolean;
  /** Server-derived: the caller may hard-revoke (delete pins, stop serving immediately). */
  canHardRevoke: boolean;
}

/** One workspace an arche is linked to (arche-side view). */
export interface ArcheWorkspaceLink {
  holderId: string;
  displayName: string;
  linkedAt: string;
  revokedAt: string | null;
  validity: WorkspaceLinkValidity;
  /** Latest published library version, or null if the workspace never published. */
  latestManifestId: string | null;
  /**
   * A published version of this arche still pins the workspace's library.
   * Stays true after a soft unlink until the arche republishes (or the
   * workspace hard-revokes).
   */
  pinned: boolean;
  /** Server-derived: the caller may soft-unlink this arche (always false once revoked). */
  canUnlink: boolean;
  /** Server-derived: the caller may hard-revoke (delete pins, stop serving immediately). */
  canHardRevoke: boolean;
}

export interface ArcheWorkspaceLinksResult {
  archeId: string;
  links: ArcheWorkspaceLink[];
  /** Workspaces the caller could link this arche to right now. */
  eligible: Array<{ holderId: string; displayName: string; role: WorkspaceRole }>;
}

/**
 * One Fias-owned (platform-owned) plugin arche, as listed by the admin-only
 * section of the contributor dashboard. `isAnnexed` distinguishes a plugin
 * annexed in place (docs/plugin-annexation.md) from one born first-party.
 */
/** The account that pays for every Fias-owned plugin (admin Dev Console). */
export interface FiasPluginBillingAccount {
  userId: string | null;
  username: string | null;
}

export interface PlatformOwnedPluginArche {
  archeId: string;
  name: string;
  slug: string | null;
  status: string;
  lifecycleState: 'active' | 'disabled' | 'deleted';
  isListed: boolean;
  isAnnexed: boolean;
  contributorTransferredAt: string | null;
  /** Internal-only provenance. Never render on a public/marketplace surface. */
  priorContributorId: string | null;
  priorContributorUsername: string | null;
  publishedAt: string | null;
  updatedAt: string;
  /**
   * Whether the CALLER holds an active `can_publish` collaborator row. This is
   * what qualifies a workspace asset-library link under ADR-019.
   */
  youAreMaintainer: boolean;
  workspaceLinks: PlatformOwnedWorkspaceLink[];
}

/** Active workspace-library link on a Fias-owned plugin, with derived validity. */
export interface PlatformOwnedWorkspaceLink {
  holderId: string;
  displayName: string;
  valid: boolean;
  reason: WorkspaceLinkInvalidReason | null;
  pinned: boolean;
}

export interface PlatformOwnedPluginsResult {
  arches: PlatformOwnedPluginArche[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Per-link answer to "what breaks if I remove myself as a maintainer?".
 *
 * Server-derived, and it has to be: link validity joins workspace membership on
 * a SPECIFIC holder, so the number of other publishers says nothing about which
 * links survive — another publisher preserves only the workspaces they belong to.
 */
export interface MaintainerRemovalImpact {
  holderId: string;
  displayName: string;
  validNow: boolean;
  wouldRemainValid: boolean;
}

/**
 * The roles the Dev Console "Fias Plugins" page hands out. Read = `viewer`
 * (sees the team roster), Publish = `publisher` (can `fias-dev submit`). There
 * is no separate "write": the plugin's source lives in the developer's repo and
 * the platform holds only published bundles, so publishing IS the write.
 * `owner` is deliberately not offered (see platform-owned-admin.ts).
 */
export type PluginTeamRole = 'viewer' | 'publisher';

/** A collaborator row on a Fias-owned plugin, including revoked/expired rows. */
export interface PluginMaintainer {
  userId: string;
  username: string | null;
  isPlatformAccount: boolean;
  role: 'owner' | 'publisher' | 'viewer';
  canPublish: boolean;
  canManageTeam: boolean;
  status: 'active' | 'expired' | 'revoked';
  grantedBy: string;
  grantedAt: string;
  updatedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
}

export interface WorkspaceAssetUsage {
  holderId: string;
  draft: { activeBytes: number; activeCount: number };
  published: {
    hmManifestId: string;
    assetCount: number;
    totalBytes: number;
    createdAt: string;
  } | null;
  /** Bytes of library versions still pinned by at least one published arche. */
  pinnedBytes: number;
}

export interface WorkspaceAssetBillingSummary {
  summary: {
    yesterday: {
      storageChargeCredits: number;
      storageByteDays: number;
      chargeStatus: string;
    } | null;
    monthToDate: {
      storageChargeCredits: number;
      daysCharged: number;
      /** Days a charge was attempted and failed (failed-only; abandoned days are separate). */
      daysFailed: number;
      /** Days the daily charge was abandoned (e.g. suspension) rather than failed. */
      daysAbandoned: number;
    };
  };
}

// ==========================================================================
// Plugin Types
// ==========================================================================

export interface PluginSubmissionSummary {
  submissionId: string;
  archId: string;
  manifestName: string;
  manifestVersion: string;
  status: string;
  reviewId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PluginSubmissionListResult {
  submissions: PluginSubmissionSummary[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Fias Forge (ADR-023) — wire shapes. Mirror `ForgeWire` / `ForgeTurnWire` /
// `ForgeTestBuildWire` in services/api-service/src/routes/forge/serialize.ts
// (the db-types rows with Dates as ISO strings).
// ============================================================================

export type ForgeStatus =
  | 'provisioning'
  | 'running'
  | 'stopping'
  | 'stopped'
  | 'archived'
  | 'failed'
  /** Terminal: the owner deleted it. Never listed; only admin views show it. */
  | 'deleted';
/**
 * Model tier a forge runs (mirrors FORGE_MODEL_TIERS): every tier includes
 * the ones below it; `frontier` (Anthropic's most capable model) is Pro-only.
 */
export type ForgeModelTier = 'balanced' | 'powerful' | 'frontier';
/** Task size (mirrors FORGE_SIZES); `large` is Pro-only and changes only while stopped. */
export type ForgeSize = 'standard' | 'large';
export type ForgeTurnStatus = 'open' | 'completed' | 'stopped' | 'failed' | 'expired';
export type ForgeTestBuildStatus = 'building' | 'ready' | 'failed';
export type ForgePendingCommand = 'stop_turn' | 'snapshot' | 'shutdown' | 'test_build' | 'publish';

/** The publishing options a forge lays over its manifest (the Arche Builder's, for Forge). */
export interface ForgePublishOptions {
  name: string;
  description: string;
  /** The long "About" text; '' = the short description. */
  about: string;
  isListed: boolean;
  isPublic: boolean;
  isFullScreen: boolean;
  pricing: { model: 'free' } | { model: 'fixed'; priceCents: number };
}

export interface ForgePublishSettingsView {
  settings: ForgePublishOptions;
  /** False until the user saves options: the workspace manifest decides meanwhile. */
  saved: boolean;
  archeStatus: string;
  icon: {
    imageRef: string | null;
    /** A new icon (or removal) waiting for the next publish of a live arche. */
    pending: { state: 'set'; iconKey: string; imageRef: string } | { state: 'delete' } | null;
  };
}

/** What a forge has cost so far, in credits (GET /v1/forge/forges/:id/cost). */
export interface ForgeCost {
  /** Claude's model usage across every turn. */
  modelCredits: number;
  /** Running time at the hourly rate (settled daily). */
  computeCredits: number;
  activeSeconds: number;
  totalCredits: number;
  /** The turn in progress: settled so far, and reserved for requests in flight. */
  currentTurn: { credits: number; reservedCredits: number } | null;
}

export interface ForgeSummary {
  id: string;
  userId: string;
  archeId: string;
  /** The user's name for the forge; null on forges that predate names. */
  name?: string | null;
  status: ForgeStatus;
  size: ForgeSize;
  origin: 'new' | 'builder' | 'plugin';
  originRef: string | null;
  /** null = tier default (balanced). */
  modelOptIn: ForgeModelTier | null;
  runtimeGeneration: number;
  runtimeRef: string | null;
  leaseExpiresAt: string | null;
  snapshotRef: string | null;
  snapshotBytes: number;
  currentTestBuildId: string | null;
  pendingCommand: ForgePendingCommand | null;
  failureReason: string | null;
  lastActiveAt: string | null;
  activeSecondsBilled: number;
  /** Set by the metering job when a charge failed; starts and turns are refused (402). Absent on older servers. */
  billingHold?: boolean;
  /** One-shot: the pending publish will carry the test channel's data into the live arche. */
  publishCarryTestData: boolean;
  /**
   * Object key of a source tarball waiting to be overlaid on the runtime's
   * first boot (ADR-023 D6: a forge made from a builder session or a
   * published plugin). Null once applied or for a forge made from scratch.
   */
  importRef?: string | null;
  /** ISO time the runtime reported the import applied; null until then. */
  importAppliedAt?: string | null;
  /** ISO time the owner deleted the forge; set only on `status: 'deleted'` (admin views). */
  deletedAt?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * A forge shared with the caller through the arche's collaborator roster
 * (`GET /forge/forges?includeShared=true` → `shared[]`). Mirrors
 * `SharedForgeWire` in services/api-service/src/routes/forge/forges.ts.
 */
export interface SharedForgeSummary {
  id: string;
  archeId: string;
  status: ForgeStatus;
  currentTestBuildId: string | null;
  /** The forge's owner — who to ask for a new test build. */
  ownerUserId: string;
}

/** `GET /forge/forges/by-arche/:archeId` — mirrors `ForgeByArcheWire` on the server. */
export interface ForgeByArcheResult {
  forge: {
    id: string;
    status: ForgeStatus;
    currentTestBuild: {
      id: string;
      status: ForgeTestBuildStatus;
      buildHash: string | null;
      readyAt: string | null;
    } | null;
  };
}

export interface ForgeDetailResult {
  forge: ForgeSummary;
  /**
   * The newest plugin submission of the forge's arche (by any surface), so
   * the page can follow a "Deploy to production" through
   * `getPluginSubmission`. Null until the arche has been submitted once;
   * absent on a server predating the publish route.
   */
  lastSubmissionId?: string | null;
}

export interface ForgeTurnSummary {
  id: string;
  forgeId: string;
  requestId: string;
  generation: number;
  status: ForgeTurnStatus;
  prompt: string;
  modelTier: ForgeModelTier;
  /** Claude was asked to plan first (ADR-023 A14). Absent from servers predating it. */
  planMode?: boolean;
  reservedCents: number;
  costCents: number;
  toolCalls: number;
  dispatchedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  /** What Claude is waiting on the owner for in this (open) turn, if anything (ADR-023 A12, A14). */
  openQuestion: ForgeOpenQuestion | null;
}

/**
 * What a turn waits on: questions Claude asked with AskUserQuestion, or a
 * plan it asks the owner to approve (ExitPlanMode).
 */
export type ForgeOpenQuestion =
  | {
      kind: 'questions';
      questionId: string;
      questions: Array<{
        question: string;
        header: string;
        options: Array<{ label: string; description: string }>;
        multiSelect: boolean;
      }>;
    }
  | { kind: 'plan'; questionId: string; plan: string };

export interface ForgeTestBuildSummary {
  id: string;
  forgeId: string;
  archeId: string;
  status: ForgeTestBuildStatus;
  bundleRef: string | null;
  buildHash: string | null;
  manifestHash: string | null;
  permissionsSnapshot: string[] | null;
  findings: unknown[];
  errorMessage: string | null;
  createdAt: string | null;
  readyAt: string | null;
}

/** GET /forge/test-channel/:archeId — what the test-channel host renders from. */
export interface ForgeTestChannelInfo {
  buildId: string;
  permissions: string[];
  buildHash: string | null;
  readyAt: string | null;
  /**
   * The sample answers Claude wrote for this build (`fias-dev.config.json`
   * `mockEntities`), used by the test window's Mock mode (ADR-023 A16).
   * Absent from servers predating it.
   */
  mockEntities?: Record<string, { response: string }>;
}

/** GET /forge/usage — mirrors `ForgeUsageWire` in services/api-service/src/routes/forge/usage.ts. */
export interface ForgeUsage {
  /** The platform kill switch — Forge is offered only when on. */
  enabled: boolean;
  /** The user's plan may run forges (Standard/Pro). */
  eligible: boolean;
  tier: 'free' | 'standard' | 'pro';
  month: {
    activeSeconds: number;
    hardCapSeconds: number;
    chargedCredits: number;
  };
  /** Credits per active hour by forge size; no hours are included with any plan. */
  computeCreditsPerHour: { standard: number; large: number };
  storageBytes: number;
  billingHold: boolean;
  userHold: { reason: string; createdAt: string } | null;
}

/** A later submission of the same arche that makes an earlier one moot. */
export interface SupersedingSubmission {
  submissionId: string;
  status: string;
  manifestVersion: string | null;
  createdAt: string;
}

export interface PluginSubmissionDetailResult {
  /**
   * The newest later, non-rejected submission of the same arche, or null.
   * Server-decided; the page renders its obsolescence notice from it.
   */
  supersededBy: SupersedingSubmission | null;
  submission: {
    submissionId: string;
    archId: string;
    sourceRef: string;
    manifest: Record<string, unknown>;
    status: string;
    reviewId: string | null;
    buildRef: string | null;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
  };
  review: {
    reviewId: string;
    status: string;
    reviewerType: string;
    riskScore: number | null;
    /**
     * Why the review escalated, as the server recorded it. Null on reviews
     * predating the column (2026-08-13) and on reviews that never escalated.
     */
    escalationReason: string | null;
    /**
     * The human reviewer's note to the contributor, when one was written.
     * Rendered on an approval; a rejection's note is already carried in
     * `submission.errorMessage`.
     */
    humanNotes: string | null;
    criticalCount: number;
    highCount: number;
    filesAnalyzed: number;
    dependenciesAnalyzed: number;
    codeFindings: unknown[] | null;
    dependencyFindings: unknown[] | null;
    policyFindings: unknown[] | null;
    qualityFindings: unknown[] | null;
    createdAt: string;
    completedAt: string | null;
  } | null;
}

// ==========================================================================
// Chat Types
// ==========================================================================

export interface ChatMemberPermissions {
  sendMessages?: boolean;
  sendImages?: boolean;
  sendVideos?: boolean;
  sendAudio?: boolean;
  sendFiles?: boolean;
  sendLinks?: boolean;
  sendStickers?: boolean;
  addUsers?: boolean;
  pinMessages?: boolean;
  changeGroupInfo?: boolean;
  banUsers?: boolean;
  deleteMessages?: boolean;
  addAdmins?: boolean;
}

export interface ChatChannelResponse {
  channelId: string;
  channelType: 'direct' | 'global' | 'guild' | 'group';
  name: string | null;
  description: string | null;
  imageRef: string | null;
  avatarFileId: string | null;
  guildId: string | null;
  createdBy: string;
  maxMembers: number | null;
  isReadOnly: boolean;
  isPrivate: boolean;
  inviteCode: string | null;
  defaultPermissions: ChatMemberPermissions;
  status: 'active' | 'archived' | 'suspended';
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  unreadCount?: number;
  /** Unread messages in this channel that @-mention the current user. */
  mentionCount?: number;
  /** True when the current user has muted this channel. */
  isMuted?: boolean;
  hasLeft?: boolean;
  isPinned?: boolean;
  dmPartnerId?: string | null;
  /** True for a self-DM ("Saved Messages") — a direct channel with only the current user. */
  isSelfDm?: boolean;
  dmPartnerDisplayName?: string | null;
  lastMessage?: {
    content: string;
    senderId: string;
    senderName: string | null;
    createdAt: string;
  } | null;
}

export interface ChatChannelMemberResponse {
  channelId: string;
  userId: string;
  role: 'owner' | 'admin' | 'moderator' | 'member';
  nickname: string | null;
  isMuted: boolean;
  permissions: ChatMemberPermissions;
  lastReadAt: string | null;
  joinedAt: string;
  displayName?: string;
  /** Public handle — enables @-mention typeahead to match on username, not just display name. */
  username?: string;
  avatarImageId?: string | null;
}

export interface ChatChannelBanResponse {
  banId: string;
  channelId: string;
  bannedUserId: string;
  bannedBy: string;
  reason: string | null;
  bannedAt: string;
}

export interface ChatDmPartnerProfile {
  userId: string;
  displayName: string;
  username: string;
  bio: string;
  bannerColor: string;
  avatarColor: string;
  avatarImageId: string | null;
}

/** Structured reference to a file attached to a chat message. */
export interface ChatAttachmentRef {
  fileId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ChatMessageResponse {
  messageId: string;
  channelId: string;
  senderId: string;
  senderName?: string | null;
  senderAvatarUrl?: string | null;
  senderRole?: 'admin' | 'moderator' | 'ambassador' | 'user';
  content: string;
  messageType: 'text' | 'system' | 'attachment' | 'reply';
  replyToId: string | null;
  replyPreview?: { senderName: string; content: string };
  attachmentRefs: ChatAttachmentRef[] | null;
  mentions: Array<{ userId: string; displayName: string }> | null;
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  createdAt: string;
  reactions?: Array<{ emoji: string; count: number; userIds: string[] }>;
}

/** One entry in the current user's cross-channel Mentions inbox. */
export interface ChatMentionFeedItemResponse {
  messageId: string;
  channelId: string;
  channelName: string | null;
  channelType: 'direct' | 'global' | 'guild' | 'group';
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
}

/** One page of in-channel message search results (cursor-paginated). */
export interface ChatMessageSearchResult {
  messages: ChatMessageResponse[];
  /** Total matches; only computed for the first page, null on later pages. */
  total: number | null;
  hasMore: boolean;
  nextCursor: string | null;
}

/**
 * How chat search text is matched (server `match=` param). Defined once in
 * `@fias/platform-resources`, shared with the server's validation.
 */
export type { ChatSearchMatchMode } from '@fias/platform-resources';

/** One cross-channel search hit — a message plus the conversation it's in. */
export interface ChatMessageSearchHitResponse {
  messageId: string;
  channelId: string;
  channelName: string | null;
  channelType: 'direct' | 'global' | 'guild' | 'group';
  senderId: string;
  senderName: string;
  content: string;
  messageType: string;
  createdAt: string;
}

/** One page of "search all chats" results (cursor-paginated). */
export interface ChatGlobalMessageSearchResult {
  messages: ChatMessageSearchHitResponse[];
  /** Matches counted up to a server cap; null on later pages. */
  total: number | null;
  /** True when more matches exist than `total` reports. */
  totalCapped?: boolean;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ChatChannelListResult {
  channels: ChatChannelResponse[];
  total: number;
}

export interface ChatMessageListResult {
  messages: ChatMessageResponse[];
  hasMore: boolean;
  oldestMessageId: string | null;
  newestMessageId: string | null;
}

export interface ChatUnreadCount {
  channelId: string;
  unreadCount: number;
}

/** Per-arche badge entry from GET /notifications/summary. */
export interface ArcheBadgeSummary {
  archeId: string;
  count: number;
  mentionCount: number;
}

export interface NotificationSummaryResponse {
  badges: ArcheBadgeSummary[];
  total: number;
  totalMentions: number;
}

export interface ChatPinnedMessageResponse {
  messageId: string;
  channelId: string;
  content: string;
  senderId: string;
  senderName: string | null;
  pinnedBy: string;
  pinnedAt: string;
  createdAt: string;
}

export interface ChatBlockResponse {
  blockId: string;
  blockedId: string;
  /**
   * Resolved name for the blocked account (chat display name → account name →
   * handle → 'User'). Never the raw userId — a management list the user cannot
   * read names in is a list they cannot act on.
   */
  displayName: string;
  /** Account handle, or '' when unknown. Render "@handle" only when non-empty. */
  username: string;
  createdAt: string;
}

export interface ChatUserSettingsResponse {
  userId: string;
  displayName: string;
  bio: string;
  bannerColor: string;
  avatarColor: string;
  avatarImageId: string | null;
  notificationNewMessages: 'all' | 'dms' | 'never';
  notificationReactions: boolean;
  notificationChannelAdded: boolean;
  notificationSoundDMs: boolean;
  notificationSoundChannels: boolean;
  appearanceShowEmojis: boolean;
  appearanceShowReactions: boolean;
  appearanceCompactMode: boolean;
  appearanceAccentColor: string;
  appearanceChannelListView: 'sections' | 'unified';
  backgroundImageId: string | null;
  backgroundColor: string;
  backgroundBlur: boolean;
  privacyWhoCanDM: 'everyone' | 'nobody';
  privacyReadReceipts: boolean;
  privacyShowOnlineStatus: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateChatUserSettingsInput {
  displayName?: string;
  bio?: string;
  bannerColor?: string;
  avatarColor?: string;
  avatarImageId?: string | null;
  notificationNewMessages?: 'all' | 'dms' | 'never';
  notificationReactions?: boolean;
  notificationChannelAdded?: boolean;
  notificationSoundDMs?: boolean;
  notificationSoundChannels?: boolean;
  appearanceShowEmojis?: boolean;
  appearanceShowReactions?: boolean;
  appearanceCompactMode?: boolean;
  appearanceAccentColor?: string;
  appearanceChannelListView?: 'sections' | 'unified';
  backgroundImageId?: string | null;
  backgroundColor?: string;
  backgroundBlur?: boolean;
  privacyWhoCanDM?: 'everyone' | 'nobody';
  privacyReadReceipts?: boolean;
  privacyShowOnlineStatus?: boolean;
}

export interface CreateChatChannelInput {
  channelType: 'group' | 'guild';
  name: string;
  description?: string;
  guildId?: string;
}

// ==========================================================================
// Guild Types
// ==========================================================================

export type GuildType = 'regular' | 'earning';
export type GuildLevel = 'bronze' | 'iron' | 'silver' | 'gold' | 'diamond';
export type GuildVisibility = 'public' | 'private';
export type GuildMemberRole = 'owner' | 'admin' | 'moderator' | 'member';

export interface GuildSummary {
  guildId: string;
  name: string;
  description: string | null;
  imageRef: string | null;
  guildType: GuildType;
  level: GuildLevel;
  visibility: GuildVisibility;
  ownerUsername?: string | null;
  memberCount: number;
  lifetimeEarnings: string;
  status: string;
  createdAt: string;
}

export interface GuildDetailResponse extends GuildSummary {
  ownerId: string;
  maxMembers: number | null;
  settings?: Record<string, unknown>;
  myRole?: GuildMemberRole | null;
  /** The guild's shared Vault data space, null until the owner enables it.
   * Optional: absent from older server builds during a deploy window. */
  holderId?: string | null;
  /** Owner opt-in: plugin arches may provision guild-backed shared workspaces
   * (and see the guild's name to offer it as a share target). Separate from
   * `holderId` and, unlike it, reversible. Optional: absent from older server
   * builds during a deploy window. */
  pluginSharingEnabled?: boolean;
  updatedAt: string;
}

export interface GuildListResult {
  guilds: GuildSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildMemberResponse {
  guildId: string;
  userId: string;
  username?: string | null;
  role: GuildMemberRole;
  /** Custom roles held by this member (members-only view). */
  roles?: Array<Pick<GuildRoleResponse, 'roleId' | 'name' | 'color' | 'permissions'>>;
  memberSociat: string;
  joinedAt: string;
}

export type GuildPermission =
  | 'manage_guild'
  | 'manage_roles'
  | 'manage_channels'
  | 'manage_invites'
  | 'kick_members'
  | 'ban_members'
  | 'view_treasury'
  | 'manage_treasury'
  | 'view_data'
  | 'manage_data';

export interface GuildRoleResponse {
  roleId: string;
  guildId: string;
  name: string;
  color: string | null;
  permissions: GuildPermission[];
  position: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
}

/** A member's resolved authorization: base role + custom grants, unioned. */
export interface GuildPermissionState {
  baseRole: GuildMemberRole | null;
  roles: GuildRoleResponse[];
  permissions: GuildPermission[];
}

export interface GuildMemberListResult {
  members: GuildMemberResponse[];
  bans?: GuildBanResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildChannelSummary {
  channelId: string;
  name: string | null;
  description: string | null;
  isPrivate: boolean;
  isReadOnly: boolean;
  lastMessageAt: string | null;
  /** Unread messages for the caller. 0 when they haven't joined the channel. */
  unreadCount: number;
  /** Unread messages @-mentioning the caller. Populated regardless of mute. */
  mentionCount: number;
  /** True when the caller has muted this channel. */
  isMuted: boolean;
}

export interface GuildInviteResponse {
  inviteId: string;
  guildId: string;
  inviteCode: string;
  createdBy?: string;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  status: string;
  createdAt: string;
}

export interface GuildInviteInfo {
  inviteCode: string;
  expiresAt: string | null;
}

export interface GuildBanResponse {
  banId: string;
  guildId: string;
  userId: string;
  username?: string | null;
  bannedBy: string;
  bannedByUsername?: string | null;
  reason: string | null;
  createdAt: string;
}

export interface GuildActivityEntry {
  activityId: string;
  guildId: string;
  userId: string;
  username?: string | null;
  activityType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface GuildActivityResult {
  activities: GuildActivityEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildTreasuryResponse {
  guildId: string;
  balance: string;
  lifetimeEarned: string;
  lifetimeDistributed: string;
  distributionRule: string;
  lastDistributionAt: string | null;
  updatedAt: string;
}

export interface GuildTreasuryTransactionResponse {
  transactionId: string;
  guildId: string;
  type: string;
  amount: string;
  balanceAfter: string;
  recipientId: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface CreateGuildPayload {
  name: string;
  description?: string;
  imageRef?: string;
  guildType: GuildType;
  visibility: GuildVisibility;
}

export interface UpdateGuildPayload {
  name?: string;
  description?: string;
  imageRef?: string;
  visibility?: GuildVisibility;
  maxMembers?: number | null;
  /** Partial — the server merges the provided keys into the stored settings
   * blob, so callers send only the keys they own (never a spread of the
   * cached blob, which would let a stale copy clobber other keys). */
  settings?: Record<string, unknown>;
}

// ==========================================================================
// Builder Types
// ==========================================================================

export interface BuilderSessionSummary {
  sessionId: string;
  archId: string | null;
  currentName: string | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  updatedAt: string;
  totalCostCents: number;
}

export interface BuilderMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  artifacts?: BuilderArtifact[];
}

export interface BuilderArtifact {
  type: 'ui_definition' | 'workflow' | 'entity_selection' | 'settings';
  content: unknown;
  description: string;
}

export interface BuilderSession {
  sessionId: string;
  userId: string;
  archId: string | null;
  templateId: string | null;
  conversationHistory: BuilderMessage[];
  currentName: string | null;
  currentDescription: string | null;
  currentUiDefinition: unknown | null;
  currentWorkflow: unknown | null;
  currentSettings: unknown | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  lastClaudeResponseAt: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface BuilderTemplate {
  templateId: string;
  name: string;
  description: string | null;
  category: string | null;
  thumbnailRef: string | null;
  uiDefinition: unknown;
  workflow: unknown | null;
  defaultSettings: unknown | null;
  displayOrder: number;
  isFeatured: boolean;
}

export interface ArcheReviewSummary {
  reviewId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReviewFindingSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface ReviewFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  message: string;
  location?: string;
  suggestion?: string;
}

export interface ArcheReviewDetail {
  reviewId: string;
  archId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  securityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  policyFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  qualityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  reviewedBy: string | null;
  humanNotes: string | null;
  reviewCostCents: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ClaudeMessageResponse {
  message: string;
  artifacts: BuilderArtifact[];
  tokensUsed: {
    input: number;
    output: number;
  };
  suggestedNextSteps?: string[];
  creditsCost: number;
  validationWarnings?: string[];
}

// ==========================================================================
// Vault Notes Types (Phase 3)
// ==========================================================================
//
// Wire shape (Date columns serialized as ISO 8601 strings).

export interface VaultNoteDto {
  noteId: string;
  holderId: string;
  holderType: 'personal';
  title: string;
  bodyMarkdown: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// =========================================================================
// Fias 360 DTOs (Phase 10)
// =========================================================================

/** Non-primary email/phone entry on a customer. Has no `isPrimary` field —
 * `primaryEmail`/`primaryPhone` are authoritative for "primary." */
export interface Fias360AltContactValueDto {
  value: string;
  label?: string;
}

/** Response from POST /arches/fias360/import/customers|companies.
 * Partial-failure semantics: `created` + `skipped` + `failed.length`
 * == rows submitted. `failed` rows did NOT land; `skipped` rows were
 * duplicates (existing email or duplicate within the upload). */
export interface Fias360ImportResult {
  created: number;
  skipped: number;
  failed: Array<{ rowIndex: number; error: string }>;
}

/** Schema-on-read filter shape stored in a segment.
 * v1 dimensions; lastContactedBefore deferred to Phase 11. */
export interface Fias360SegmentFilterDto {
  lifecycleStage?: string;
  ownerPrincipalId?: string | null;
  tags?: string[];
  status?: 'active' | 'archived';
}

export interface Fias360SegmentDto {
  segmentId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  name: string;
  targetKind: 'customer' | 'company';
  filter: Fias360SegmentFilterDto;
  createdByPrincipalId: string;
  ownerPrincipalId: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface Fias360ApplySegmentResultDto {
  targetKind: 'customer' | 'company';
  matches: string[];
  total: number;
}

// Custom fields (Phase 10e-1 — arche-private, outside Vault substrate)
export interface Fias360CustomFieldDefDto {
  defId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  targetKind: 'customer' | 'company';
  name: string;
  valueType: 'text' | 'number' | 'date' | 'select';
  options: string[] | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface Fias360CustomFieldValueDto {
  valueId: string;
  defId: string;
  targetId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  valueText: string | null;
  valueNumber: number | null;
  valueDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Returned by GET endpoints — joins the def metadata so the UI gets
 * label + value_type alongside each value in one round-trip. */
export interface Fias360CustomFieldValueWithDefDto extends Fias360CustomFieldValueDto {
  defName: string;
  defValueType: 'text' | 'number' | 'date' | 'select';
  defOptions: string[] | null;
}

/** Per-Workspace UI preferences. Currently just the vocabulary
 * preset; expanded later as more workspace-level UI knobs land.
 * The preset_id is opaque from the backend; the frontend resolves
 * it to a term dictionary via VOCABULARY_PRESETS. */
export interface Fias360WorkspacePreferencesDto {
  holderId: string;
  holderType: 'personal' | 'org';
  vocabularyPresetId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Fias360CustomerDto {
  contactId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  companyId: string | null;
  /** Raw company-name text captured at contact import (provenance).
   * Display rule: `companyId` (resolved to the company record) wins;
   * show this text only while `companyId` is null — it marks an
   * imported company that doesn't exist in the company list yet and
   * auto-links when a matching company is created or imported. */
  companyName: string | null;
  firstName: string;
  lastName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  /** Optional label for the primary phone (e.g. "mobile", "work"). */
  primaryPhoneLabel: string | null;
  title: string | null;
  notes: string | null;
  ownerPrincipalId: string | null;
  /** Free-text lifecycle stage, canonical lowercase form. */
  lifecycleStage: string | null;
  /** Canonical lowercase tags. */
  tags: string[];
  altEmails: Fias360AltContactValueDto[];
  altPhones: Fias360AltContactValueDto[];
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface Fias360CompanyDto {
  companyId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  name: string;
  domain: string | null;
  industry: string | null;
  website: string | null;
  description: string | null;
  ownerPrincipalId: string | null;
  /** Canonical lowercase tags. */
  tags: string[];
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface Fias360ActivityDto {
  activityId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  kind: 'note' | 'call' | 'email' | 'meeting';
  regardingType: 'vault_company' | 'vault_contact';
  regardingId: string;
  subject: string;
  bodyMarkdown: string;
  typeData: Record<string, unknown>;
  occurredAt: string;
  createdByPrincipalId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type Fias360TaskStatus = 'open' | 'completed' | 'cancelled';
export type Fias360TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type Fias360TaskRegardingType = 'vault_company' | 'vault_contact';

export interface Fias360TaskDto {
  taskId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  title: string;
  description: string;
  status: Fias360TaskStatus;
  priority: Fias360TaskPriority;
  dueAt: string | null;
  reminderAt: string | null;
  completedAt: string | null;
  assigneePrincipalId: string | null;
  createdByPrincipalId: string;
  regardingType: Fias360TaskRegardingType | null;
  regardingId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Fias360CreateTaskInput {
  title: string;
  description?: string;
  priority?: Fias360TaskPriority;
  dueAt?: string | null;
  reminderAt?: string | null;
  assigneePrincipalId?: string | null;
  regardingType?: Fias360TaskRegardingType;
  regardingId?: string;
}

export interface Fias360UpdateTaskInput {
  title?: string;
  description?: string;
  priority?: Fias360TaskPriority;
  dueAt?: string | null;
  reminderAt?: string | null;
  assigneePrincipalId?: string | null;
  regardingType?: Fias360TaskRegardingType | null;
  regardingId?: string | null;
}

export interface Fias360HealthSnapshotDto {
  snapshotId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  subjectKind: 'vault_company' | 'vault_contact';
  subjectId: string;
  computedAt: string;
  score: number;
  breakdown: Record<string, unknown>;
  createdByPrincipalId: string;
  createdAt: string;
}

export interface Fias360RenewalDto {
  renewalId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  companyId: string;
  renewalDate: string;
  status: 'open' | 'at_risk' | 'renewed' | 'churned';
  valueCents: number | null;
  termMonths: number | null;
  notes: string | null;
  ownerPrincipalId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface VaultHolderDto {
  holderId: string;
  type: 'personal' | 'org';
  displayName: string;
  principalId: string | null;
  createdAt: string;
  archivedAt: string | null;
}

export interface VaultMembershipDto {
  membershipId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  holderDisplayName: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  grantedAt: string;
  expiresAt: string | null;
  status: 'active' | 'expired' | 'revoked';
}

/** A guild's shared data space the caller can reach, surfaced by
 * /vault/me/memberships. No membershipId — access derives from the
 * guild's own member list, not vault_memberships. The role shown is
 * the base-role floor; custom-role grants can only raise it and the
 * server re-derives with grants on every access. */
export interface VaultGuildSpaceDto {
  guildId: string;
  holderId: string;
  holderType: 'guild';
  holderDisplayName: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

/** The caller's Personal Holder, surfaced by /vault/me/memberships
 * alongside org memberships so the shared HolderSwitcher can list
 * both in one place. There's no Membership row for a Personal Holder
 * (you ARE its principal); we synthesize the shape the UI needs. */
export interface VaultPersonalHolderDto {
  holderId: string;
  type: 'personal';
  displayName: string;
}

/** A row from GET /vault/holders/:id/members — includes status for
 * revoked / expired members and exposes the principalId (which the
 * caller-facing /vault/me/memberships endpoint does not). */
export interface VaultHolderMemberDto {
  membershipId: string;
  principalId: string;
  holderId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'expired' | 'revoked';
  grantedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  /**
   * Human-readable display name for the member, looked up from the user
   * profile by the API server. `null` if the user couldn't be resolved
   * (deleted account, lookup failure) — the UI falls back to username,
   * then to a redacted principal-id suffix.
   */
  displayName: string | null;
  /** The member's @-handle. Same nullable fallback chain as `displayName`. */
  username: string | null;
}

/** One row from the platform user directory search (GET /users/search).
 * Deliberately no userId — usernames outward, ids resolved server-side. */
export interface UserSearchResultDto {
  username: string;
  displayName: string;
}

/** Invite a teammate by exactly one of email or username. Username
 * invites are stored user-keyed server-side — the invitee's email is
 * never exposed to the inviting workspace. */
export type VaultInviteInput = { role: 'owner' | 'admin' | 'member' | 'viewer' } & (
  | { inviteeEmail: string }
  | { inviteeUsername: string }
);

/** Owner-side view of a pending invitation. Exactly one of
 * `inviteeEmail` / `inviteeUsername` is set, matching how the invite
 * was issued. */
export interface VaultInvitationDto {
  invitationId: string;
  holderId: string;
  inviteeEmail: string | null;
  inviteeUsername: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  createdAt: string;
  expiresAt: string;
  invitedByPrincipalId: string;
  status?: 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired';
}

// ── Companies + workspace arche approvals (ADR-022) ────────────────────────

export type CompanyMemberRole = 'owner' | 'admin';

export interface CompanyDto {
  companyId: string;
  name: string;
  billingUserId: string;
  trustFiasArches: boolean;
  createdAt: string;
}

export interface CompanyMembershipDto {
  membershipId: string;
  companyId: string;
  principalId: string;
  role: CompanyMemberRole;
  grantedAt: string;
  userId: string | null;
  displayName: string | null;
  username: string | null;
}

export interface CompanyLinkInvitationDto {
  invitationId: string;
  companyId: string;
  holderId: string;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  proposedAt: string;
  expiresAt: string;
}

// ── Company-owned arches + arche audience (ADR-026) ─────────────────────────

export type ArcheAudienceKind = 'anyone' | 'signed_in' | 'restricted';

export interface CompanyArcheTransferDto {
  transferId: string;
  archeId: string;
  direction: 'to_company' | 'release_to_user';
  companyId: string;
  targetUserId: string | null;
  proposedByUserId: string;
  proposedAt: string;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired';
  expiresAt: string;
  decidedAt?: string | null;
  reason: string | null;
}

interface PersonLabelDto {
  username: string | null;
  displayName: string | null;
}

/** A plugin arche collaborator's role (`arche_collaborators.role`). */
export type ArcheCollaboratorRole = 'owner' | 'publisher' | 'viewer';

export interface CompanyArcheTeamMemberDto extends PersonLabelDto {
  userId: string;
  role: ArcheCollaboratorRole;
  /** Still on the Company's roster or in a linked workspace. False = left the Company, row remains. */
  affiliated: boolean;
}

export interface CompanyOwnedArcheDto {
  archeId: string;
  name: string;
  slug: string | null;
  iconKey: string | null;
  status: string;
  lifecycleState: ArcheLifecycleState;
  audience: ArcheAudienceKind;
  siteSnapshotRemovalPending: boolean;
  /** The arche's active team (ADR-026 offboarding view). */
  team: CompanyArcheTeamMemberDto[];
  unaffiliatedCount: number;
  /**
   * Net earnings since the arche joined this Company (the server bounds both
   * figures to the current ownership period); cents (= credits).
   */
  earnings: { totalNetCents: number; recentNetCents: number; recentDays: number };
}

export interface CompanyArchesDto {
  arches: CompanyOwnedArcheDto[];
  transfers: Array<
    CompanyArcheTransferDto & {
      proposedBy: PersonLabelDto | null;
      target: PersonLabelDto | null;
      /** Null when the arche could not be read; absent on an older API build. */
      archeName?: string | null;
    }
  >;
}

/** What an acceptance echoes back: the payer and every collaborator's role it showed. */
export interface CompanyArcheTransferExpectedTerms {
  billingUserId: string;
  collaborators: Array<{ userId: string; role: ArcheCollaboratorRole }>;
}

export interface CompanyArcheTransferDisclosureDto {
  transfer: CompanyArcheTransferDto;
  archeName: string;
  proposedBy: PersonLabelDto | null;
  billingUserId: string;
  billingUser: PersonLabelDto | null;
  collaborators: Array<
    {
      userId: string;
      roleNow: ArcheCollaboratorRole;
      roleAfter: ArcheCollaboratorRole;
    } & PersonLabelDto
  >;
  transferEnabled: boolean;
}

/** `GET /arches/company-transfers/mine` — everything the account menu answers for. */
export interface MyArcheTransfersDto {
  /** My own offers to a Company, and releases offered to me. */
  transfers: Array<
    CompanyArcheTransferDto & {
      companyName: string | null;
      /** Null when the arche could not be read; absent on an older API build. */
      archeName?: string | null;
    }
  >;
  /** As a Company owner: offers waiting for one of my Companies. */
  offersToMyCompanies: Array<
    CompanyArcheTransferDto & {
      companyName: string | null;
      archeName: string | null;
      proposedBy: PersonLabelDto;
    }
  >;
}

export interface ArchesSharedWithMeCursor {
  createdAt: string;
  archId: string;
}

export interface ArcheSharedWithMeDto {
  archId: string;
  name: string;
  slug?: string | null;
  simpleDescription?: string;
  imageRef?: string | null;
  fromCompany: { companyId: string; name: string | null } | null;
}

/** `GET /arches/shared-with-me` — restricted arches shared with me, keyset paginated. */
export interface ArchesSharedWithMeDto {
  arches: ArcheSharedWithMeDto[];
  nextCursor: ArchesSharedWithMeCursor | null;
}

export interface ArcheOwnershipDto {
  archeId: string;
  owningCompany: { companyId: string; name: string | null } | null;
  /** The caller is the financial owner (not company-owned). Absent on an older API build. */
  isOwner?: boolean;
  lifecycleActive?: boolean;
  canOfferToCompany: boolean;
  pendingTransfer: (CompanyArcheTransferDto & { companyName: string | null }) | null;
}

export interface ArcheAudienceGrantDto {
  grantId: string;
  granteeType: 'user' | 'workspace' | 'company' | 'guild';
  granteeId: string;
  createdAt: string;
  username?: string | null;
  displayName?: string | null;
}

export interface ArcheAudienceDto {
  archeId: string;
  audience: ArcheAudienceKind;
  siteSnapshotRemovalPending: boolean;
  restrictionEnabled: boolean;
  /** Set on a company-owned arche: the Restrict dialog offers to grant it in one step. */
  owningCompany: { companyId: string; name: string | null } | null;
  grants: ArcheAudienceGrantDto[];
}

export interface CompanySettingsDto {
  company: CompanyDto;
  myRole: CompanyMemberRole;
  /** The fixed reviewed list "Trust Fias arches" covers (empty until curated). */
  fiasTrustedArches: Array<{ archeId: string; name: string }>;
  members: CompanyMembershipDto[];
  workspaces: Array<{
    holderId: string;
    displayName: string;
    billingPrincipalUserId: string;
    /** Set when the workspace's owner soft-deleted it while linked. */
    archivedAt: string | null;
    /** Company owners who are NOT members of this workspace — a visible gap. */
    missingOwnerPrincipalIds: string[];
  }>;
  pendingLinks: CompanyLinkInvitationDto[];
  /** Seats offered and not yet accepted — nobody holds a seat they did not agree to. */
  pendingMembers: Array<
    CompanyMemberInvitationDto & {
      userId: string | null;
      displayName: string | null;
      username: string | null;
    }
  >;
  /**
   * What Fias has granted this Company (ADR-023). Decides which sections the
   * page offers; the routes behind those sections enforce the grant themselves.
   */
  entitlements: CompanyEntitlementKey[];
}

/** A Company seat waits for the invitee's yes. */
export interface CompanyMemberInvitationDto {
  invitationId: string;
  companyId: string;
  inviteePrincipalId: string;
  role: CompanyMemberRole;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  invitedByPrincipalId: string;
  invitedAt: string;
  expiresAt: string;
  resolvedAt: string | null;
  resolvedByPrincipalId: string | null;
}

/** The invitee's view of a Company seat invitation. */
export interface CompanySeatInvitationForMeDto {
  invitationId: string;
  companyId: string;
  companyName: string;
  role: CompanyMemberRole;
  invitedByPrincipalId: string;
  invitedAt: string;
  expiresAt: string;
  invitedBy: { userId: string | null; displayName: string | null; username: string | null };
}

/** A DNS record a Company publishes to send from its domain (ADR-023). */
export interface CompanyDnsRecordDto {
  purpose: 'dkim' | 'mail_from_mx' | 'mail_from_spf' | 'dmarc';
  type: 'CNAME' | 'MX' | 'TXT';
  /** Fully-qualified name. */
  name: string;
  /** Relative to the domain, as a provider's Host/Name field wants it (`@` = the domain). */
  host: string;
  value: string;
  priority?: number;
  required: boolean;
  /**
   * How the record stands in the Company's Fias-managed zone: the sync state
   * of the row Fias published for it; `present` when a row of the Company's
   * own already says the same; `conflict` when a row of theirs at that name
   * says something else (`error` names it). Null when nothing is there (or
   * there is no zone).
   */
  published?: {
    state: 'pending' | 'applied' | 'failed' | 'deleting' | 'present' | 'conflict';
    error: string | null;
  } | null;
}

/** What the public DNS showed for one record when the Company clicked Verify. */
export interface CompanyDnsRecordCheckDto {
  name: string;
  type: CompanyDnsRecordDto['type'];
  state: 'found' | 'different' | 'doubled' | 'missing';
}

export interface CompanySendingDomainDto {
  domainId: string;
  domain: string;
  mailFromDomain: string;
  status: 'pending_dns' | 'verified' | 'failed' | 'paused' | 'removed';
  statusReason: string | null;
  dkimStatus: string | null;
  mailFromStatus: string | null;
  dmarcPresent: boolean | null;
  dmarcPolicy: string | null;
  lastCheckedAt: string | null;
  verifiedAt: string | null;
  createdAt?: string;
  pausedBy?: string | null;
  orphanedAt?: string | null;
  records: CompanyDnsRecordDto[];
  /**
   * The Fias-managed zone (ADR-024) that holds this domain's records, when the
   * Company has one: Fias publishes the records there instead of asking for them.
   */
  managedZone?: { domainId: string; zoneName: string } | null;
}

/** A domain as the Dev Console lists it: with its Company and seven-day figures. */
export interface AdminCompanySendingDomainDto extends Omit<
  CompanySendingDomainDto,
  'records' | 'managedZone'
> {
  companyId: string;
  companyName: string;
  providerTenant: string | null;
  pausedAt: string | null;
  stats: CompanyEmailDomainStatsDto | null;
}

export interface AdminCompanySearchResultDto {
  companyId: string;
  name: string;
  workspaceCount: number;
  createdAt: string;
}

/** The entitlements only Fias can grant a Company (ADR-023). */
export type CompanyEntitlementKey = 'company_branding' | 'company_email' | 'company_web';

export interface CompanyEntitlementGrantDto {
  companyId: string;
  companyName?: string | null;
  entitlement: CompanyEntitlementKey;
  grantedBy: string;
  reason: string;
  grantedAt: string;
  approvedDomains: string[] | null;
  /** `company_web` only: may the Company buy domains through Fias? */
  domainPurchases?: boolean;
  /** `company_web` only: how many Fias-managed domains it may hold. */
  maxDomains?: number;
}

/** A Company portal as its owners and admins see it (ADR-024). */
export interface CompanyWebHostDto {
  hostId: string;
  hostname: string;
  /** The Fias-managed domain the portal lives in. */
  domainId: string | null;
  displayLabel: string;
  accessPolicy: 'members_only' | 'open';
  landingArcheId: string | null;
  /** Display name of the landing arche, when known. */
  landingArcheName?: string | null;
  status: 'pending_dns' | 'provisioning' | 'active' | 'failed' | 'removing' | 'removed';
  dnsStatus: 'unknown' | 'resolving' | 'not_resolving' | 'mismatch';
  certStatus: 'none' | 'pending_validation' | 'issued' | 'attached' | 'failed';
  certFailureReason: string | null;
  activatedAt: string | null;
  createdAt: string;
  /** Open holds by meaning; a security hold's details are for Fias. */
  holds: Array<'admin' | 'billing' | 'security'>;
}

export interface CompanyWebDto {
  webEnabled: boolean;
  /** Domains a Fias admin approved for transfer in or delegation. */
  approvedDomains: string[];
  /** May this Company buy new domains through Fias? */
  domainPurchases: boolean;
  maxDomains: number;
  maxHosts: number;
  role?: 'owner' | 'admin';
  domains: CompanyWebDomainDto[];
  hosts: CompanyWebHostDto[];
}

/** How a zone became Fias's to host (ADR-024 rev 4). */
export type CompanyWebDomainMode = 'registered' | 'transferred' | 'delegated' | 'nameservers';

/** A Fias-managed domain portals live in, as its owners and admins see it. */
export interface CompanyWebDomainDto {
  domainId: string;
  zoneName: string;
  mode: CompanyWebDomainMode;
  status:
    | 'pending_registration'
    | 'pending_review'
    | 'pending_transfer'
    | 'pending_delegation'
    | 'active'
    | 'failed'
    | 'removing'
    | 'removed';
  /** Delegated: the NS records to add at the parent. Nameservers: the name servers to set at the registrar. */
  nameServers: string[];
  delegationStatus: 'delegated' | 'not_delegated' | 'mismatch' | null;
  registeredAt: string | null;
  expiresAt: string | null;
  autoRenew: boolean;
  contactVerified: boolean | null;
  renewalPriceCredits: number | null;
  /** A renewal price that rose past the allowed increase, waiting for the owner to confirm. */
  renewalQuoteHoldCredits: number | null;
  /** Days in a row a renewal charge has failed; renewal turns off after the retry window. */
  renewalFailures: number;
  /** Why the last renewal charge failed (cleared once one succeeds). */
  renewalLastError: string | null;
  /** Every mode but delegated: the owner manages the zone's other records. */
  recordsEditor: boolean;
  lastError: string | null;
  createdAt: string;
}

/** One NS value the Company was asked to publish, and whether the parent zone shows it. */
export interface CompanyWebNsRecordCheckDto {
  type: 'NS';
  name: string;
  value: string;
  state: 'found' | 'missing' | 'unknown';
}

/** The Verify result for a domain the Company points at Fias (delegated / name servers). */
export interface CompanyWebDelegationCheckDto {
  status: 'delegated' | 'not_delegated' | 'mismatch' | 'unknown';
  records: CompanyWebNsRecordCheckDto[];
  /** Name servers the parent zone shows that are not Fias's (empty unless `mismatch`). */
  unexpected: string[];
  /** Why the DNS could not be read (`unknown` only): a closed set the card words. */
  error: CompanyWebDelegationCheckError | null;
  checkedAt: string;
}

export type CompanyWebDelegationCheckError =
  | 'timeout'
  | 'no_parent_zone'
  | 'no_parent_server'
  | 'bad_answer'
  | 'dns_error';

export type CompanyWebDnsRecordType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT' | 'SRV' | 'CAA';

export interface CompanyWebDnsRecordDto {
  recordId: string;
  /** Relative to the zone; '@' is the domain itself. */
  name: string;
  type: CompanyWebDnsRecordType;
  ttl: number;
  values: string[];
  /** `email`: published by Fias for a Company sending domain (ADR-023). */
  source: 'owner' | 'imported' | 'email';
  syncStatus: 'pending' | 'applied' | 'failed' | 'deleting';
  syncError: string | null;
}

export interface CompanyWebDomainQuoteDto {
  zoneName: string;
  available: boolean;
  /** Credits (1 credit = $0.01), charged to the Company's payer on purchase. */
  priceCredits: number | null;
  renewalPriceCredits: number | null;
}

/** ICANN's registrant contact; the Company is the registrant (privacy protection on). */
export interface CompanyDomainRegistrantContactDto {
  firstName: string;
  lastName: string;
  organizationName: string;
  email: string;
  /** +CC.NUMBER, e.g. +1.4155550100 */
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  countryCode: string;
  zipCode: string;
}

export interface CompanyWebPortalUserDto {
  userId: string;
  username: string | null;
  displayName: string | null;
  /** Only for users who consented under the disclosure that names email (rev 5). */
  email: string | null;
  joinedVia: 'registered_here' | 'existing_account';
  firstSeenAt: string;
  lastSeenAt: string;
  blocked: boolean;
}

export interface CompanyPortalDto {
  hostId: string;
  hostname: string;
  displayLabel: string;
  companyName: string;
  consentedAt: string;
  lastSeenAt: string;
  /** This Company also sees your email (you agreed under the disclosure that names it). */
  sharesEmail: boolean;
}

export interface AdminCompanyWebHoldDto {
  holdId: string;
  hostId: string;
  kind: 'admin' | 'billing' | 'dns_drift' | 'ct_alert';
  note: string | null;
  placedByPrincipalId: string | null;
  placedAt: string;
  releasedByPrincipalId: string | null;
  releasedAt: string | null;
}

export interface AdminCompanyWebHostDto extends Omit<CompanyWebHostDto, 'holds'> {
  companyId: string;
  companyName?: string;
  tier: 'managed' | 'custom';
  desiredState: 'present' | 'absent';
  lastError: string | null;
  providerTenantId: string | null;
  providerEnabled: boolean | null;
  removedAt: string | null;
  openHolds?: AdminCompanyWebHoldDto[];
}

export type HostProfileDto =
  | { kind: 'platform' }
  | {
      kind: 'company';
      hostId: string;
      hostname: string;
      displayLabel: string | null;
      companyName: string | null;
      brand: CompanyPortalBrand | null;
      accessPolicy: 'members_only' | 'open';
      signedIn: boolean;
      /**
       * `slug` is the landing's URL slug (plugin arches are shown at
       * `/a/<slug>`). Optional on the wire so an older API still type-checks.
       */
      landing: { archeId: string; slug?: string | null; name: string; fullScreen: boolean } | null;
      /** The portal server route that starts a sign-in on fias.io; null until ready. */
      signInPath: string | null;
    };

/** What a portal's own sign-in page offers (W5c). */
export interface PortalSignInOptionsDto {
  companyName: string;
  portalLabel: string;
  /** Emailed-code sign-in is available here. */
  emailCode: boolean;
  /** New accounts can be created here (open portals). */
  signUp: boolean;
  /** The S14 disclosure version the page shows. */
  disclosureVersion: number;
}

export interface CompanyHostBrandDto {
  hostId: string;
  hostname: string;
  displayLabel: string | null;
  companyName: string | null;
  accessPolicy: 'members_only' | 'open';
  brand: CompanyPortalBrand | null;
}

export interface CompanyWebKillSwitchDto {
  paused: boolean;
  reason: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CompanyEmailKillSwitchDto {
  paused: boolean;
  reason: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Seven-day sending figures for one domain (ADR-023 PR-F). */
export interface CompanyEmailDomainStatsDto {
  domainId: string;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  /** Final verdicts — the denominator for the rates. */
  settled: number;
}

export interface CompanySenderIdentityDto {
  identityId: string;
  domainId: string;
  localPart: string;
  displayName: string;
  replyTo: string | null;
}

export interface CompanySenderAuthorizationDto {
  authorizationId: string;
  archeId: string;
  archeName?: string;
  identityId: string;
  recipientScope: 'workspace' | 'company' | 'any' | null;
  showMuteLine: boolean;
}

/** An arche the Company may authorize to send as it right now (ADR-023). */
export interface CompanyEmailEligibleArcheDto {
  archeId: string;
  archeName: string | null;
  /**
   * A reviewed first-party adopter (Document Sign). It decides its own
   * recipients, so its authorization carries no recipient scope or mute line.
   */
  firstParty: boolean;
}

export interface CompanyEmailDto {
  enabled: boolean;
  approvedDomains: string[];
  domains: CompanySendingDomainDto[];
  identities: CompanySenderIdentityDto[];
  authorizations: CompanySenderAuthorizationDto[];
  /**
   * Every arche that may be authorized to send as this Company right now: the
   * first-party adopters, plus published plugins the Company approved whose
   * approval covers `email:company:send`. Decided server-side, so the picker
   * never pages through approvals or keeps its own list of first-party ids.
   */
  eligibleArches: CompanyEmailEligibleArcheDto[];
  health?: CompanyEmailDomainStatsDto[];
  /** Fias has paused all Company email (the reason stays internal). */
  pausedByFias?: boolean;
}

export interface ArcheApprovalDto {
  approvalId: string;
  scopeKind: 'company' | 'workspace';
  scopeId: string;
  archeId: string;
  decision: 'approve' | 'deny';
  /** The reach this approval covers, pinned to what its disclosure named. */
  approvedPermissions: string[];
  approvedEntityIds: string[];
  /** The decider's role on the scope when deciding; an owner's deny is sticky. */
  decidedByRole: 'owner' | 'admin' | null;
  decidedAt: string;
  note: string | null;
  archeName: string | null;
  currentPermissions: string[];
  /** Permissions the arche declares NOW that it did not when approved. */
  permissionsAddedSinceApproval: string[];
  /** Data classes it can reach now that this approval did not disclose. */
  newReachEntityIds: string[];
  /** New reach is refused until someone approves again (through the disclosure). */
  needsReapproval: boolean;
  /** Suspended, deprecated, disabled or deleted — it reads nothing while so. */
  archeInactive: boolean;
  /** People who can publish this arche and were added after the decision. */
  publishersAddedSinceApproval: number;
  /** Who decided (display name, else @username); null when unresolvable. */
  decidedByLabel: string | null;
  /** The decider held publish rights on the arche when they decided. */
  deciderPublishesArche: boolean;
  /**
   * The arche was company-owned and the decider held an owner or admin seat
   * on the Company that owns it, so they chose who publishes it.
   */
  deciderGovernsArche: boolean;
}

export interface WorkspaceArcheApprovalDto extends ArcheApprovalDto {
  origin: 'workspace' | 'company';
}

export interface ArcheSearchResultDto {
  archeId: string;
  name: string;
  contributorId: string | null;
  isPlatformOwned: boolean;
  /**
   * On the Company-scoped search: the Company owns the arche (ADR-026) or
   * holds an audience grant on it. Null (or absent) for a store match.
   */
  companyRelation?: 'owned' | 'granted' | null;
}

/** A search over installable (plugin) arches; built-ins that matched by name are named separately. */
export interface ArcheSearchDto {
  results: ArcheSearchResultDto[];
  builtIn: Array<{ archeId: string; name: string }>;
}

export interface ArcheApprovalDisclosureDto {
  archeId: string;
  name: string;
  contributorId: string | null;
  isPlatformOwned: boolean;
  permissions: string[];
  impliedPermissions: string[];
  reach: Array<{ entityId: string; entityName: string; verbs: string[]; sentence: string }>;
}

export interface ArcheReadActivityDto {
  invocationId: string;
  holderId: string;
  userId: string;
  /** Display name or @username of the reader; null when the directory has no row. */
  userLabel: string | null;
  entityId: string;
  verb: string | null;
  outcome: string;
  startedAt: string;
}

/** What a Company-approved arche read in one linked workspace — counts, never who. */
export interface CompanyArcheActivityDto {
  holderId: string;
  reads: number;
  distinctMembers: number;
  lastReadAt: string;
}

export interface HolderAccessDenialDto {
  denialId: string;
  principalId: string;
  holderId: string;
  entityId: string;
  verb: string | null;
  archeId: string | null;
  reason: string;
  createdAt: string;
}

export interface CompanyWorkspaceBillingDto {
  holderId: string;
  displayName: string;
  storage: { totalBytes: number; fileCount: number };
  assetLibrary: {
    chargedCredits: number;
    chargedToOthersCredits: number;
    chargedUnattributedCredits: number;
    pendingSnapshots: number;
    byteDays: number;
  };
  activity: { invocations: number; successes: number; failures: number; members: number };
}

export interface CompanyBillingDto {
  company: CompanyDto;
  month: string;
  periodStart: string;
  periodEnd: string;
  payer: { userId: string; label: string | null };
  workspaces: CompanyWorkspaceBillingDto[];
  totals: { storageBytes: number; assetLibraryChargedCredits: number; invocations: number };
}

export interface CompanyLinkProposalForMeDto {
  invitationId: string;
  companyId: string;
  companyName: string;
  holderId: string;
  holderDisplayName: string;
  proposedByPrincipalId: string;
  /** Display name or @username of whoever proposed it; null when unknown. */
  proposedByLabel: string | null;
  proposedAt: string;
  expiresAt: string;
}

/** A person named on the accept screen: display name or @username, when known. */
export interface CompanyLinkPersonDto {
  principalId: string;
  userId: string | null;
  label: string | null;
}

/**
 * One open proposal on a workspace. Everything past `canAccept` is sent only
 * to the workspace's OWNERS (canAccept true): what they let in by accepting.
 */
export interface WorkspacePendingCompanyLinkDto {
  invitationId: string;
  companyId: string;
  companyName: string;
  proposedAt: string;
  expiresAt: string;
  canAccept: boolean;
  proposedBy?: CompanyLinkPersonDto;
  payer?: { userId: string; label: string | null };
  /** The owners "add as admins" would seat — required back on the accept. */
  companyOwners?: CompanyLinkPersonDto[];
  /** Every approval that applies the moment the workspace joins, in its pinned reach. */
  companyApprovals?: Array<{ archeId: string; archeName: string | null; reach: string[] }>;
  companyTrustsFias?: boolean;
}

export interface WorkspaceCompanyDto {
  company: { companyId: string; name: string } | null;
  /**
   * Apps the Company lets send email as itself (ADR-023). Shown to this
   * workspace's owners and admins only; empty for everyone else.
   */
  emailApps?: Array<{
    archeId: string;
    archeName: string | null;
    fromAddress: string;
    recipientScope: 'workspace' | 'company' | 'any';
  }>;
  /** Several Companies may each hold one open proposal on a workspace. */
  pendingLinks: WorkspacePendingCompanyLinkDto[];
}

/** "<workspace> joined <Company>" — told to every member for a while. */
export interface CompanyLinkNoticeDto {
  invitationId: string;
  companyId: string;
  companyName: string;
  holderId: string;
  holderDisplayName: string;
  linkedAt: string;
}

/** Invitee-side view of a pending invitation — includes the workspace
 * display name so the user can decide whether to accept without
 * knowing the bare holder ID. */
export interface VaultInvitationForMeDto {
  invitationId: string;
  holderId: string;
  holderDisplayName: string;
  /** Null for username-keyed invites (the invitee is identified by userId). */
  inviteeEmail: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  createdAt: string;
  expiresAt: string;
}

// ==========================================================================
// Contact Book Types
// ==========================================================================

export interface LabeledValue {
  value: string;
  label: string;
}

export interface AddressValue {
  label: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  poBox?: string;
  extendedAddress?: string;
}

export interface ContactDetail {
  id: string;
  userId: string;
  categoryId: string | null;
  priorityId: string | null;
  firstName: string;
  lastName: string;
  middleName: string;
  namePrefix: string;
  nameSuffix: string;
  nickname: string;
  emails: LabeledValue[];
  phones: LabeledValue[];
  company: string | null;
  title: string | null;
  department: string;
  notes: string | null;
  source: string | null;
  gatheredAt: string;
  tags: string[];
  metadata: Record<string, unknown>;
  addresses: AddressValue[];
  birthday: string | null;
  anniversary: string | null;
  websites: LabeledValue[];
  photoUrl: string | null;
  isFavorite: boolean;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  categoryName?: string | null;
  categoryColor?: string | null;
  priorityName?: string | null;
  priorityColor?: string | null;
}

export interface ContactInput {
  firstName: string;
  lastName?: string;
  middleName?: string;
  namePrefix?: string;
  nameSuffix?: string;
  nickname?: string;
  emails?: LabeledValue[];
  phones?: LabeledValue[];
  company?: string | null;
  title?: string | null;
  department?: string;
  notes?: string | null;
  source?: string | null;
  gatheredAt?: string | null;
  categoryId?: string | null;
  priorityId?: string | null;
  tags?: string[];
  metadata?: Record<string, unknown>;
  addresses?: AddressValue[];
  birthday?: string | null;
  anniversary?: string | null;
  websites?: LabeledValue[];
  photoUrl?: string | null;
  isFavorite?: boolean;
}

export interface ImportedContact extends ContactInput {
  originalSource?: string;
}

export interface ParsedImportResult {
  format: string;
  contacts: ImportedContact[];
  warnings: string[];
  errors: string[];
  totalParsed: number;
}

export interface ImportConfirmRequest {
  contacts: ImportedContact[];
  duplicateStrategy: 'skip' | 'overwrite' | 'create_new';
  source?: string;
  addTags?: string[];
}

export interface ImportConfirmResult {
  imported: number;
  skipped: number;
  updated: number;
  errors: string[];
}

export type OAuthProvider = 'google' | 'microsoft';

export interface OAuthConnection {
  id: string;
  userId: string;
  provider: OAuthProvider;
  tokenExpiresAt: string | null;
  scopes: string;
  providerAccountId: string | null;
  providerAccountName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactCategory {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  contactCount: number;
}

export interface ContactCategoryInput {
  name: string;
  color: string;
}

export interface ContactPriority {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  contactCount: number;
}

export interface ContactPriorityInput {
  name: string;
  color: string;
  sortOrder?: number;
}

export interface ContactAttachment {
  id: string;
  contactId: string;
  fileId: string;
  label: string | null;
  displayOrder: number;
  createdAt: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

// ==========================================================================
// Contact Book Extended Types
// ==========================================================================

export type DuplicateSensitivity = 'off' | 'email-only' | 'email-phone' | 'all';

export interface DuplicateGroup {
  contacts: ContactDetail[];
  matchReason: string;
  confidence: 'high' | 'medium' | 'low';
}

export type MergeStrategy = 'keep_primary' | 'keep_newest' | 'merge_all';

export interface MergeRequest {
  primaryId: string;
  mergeIds: string[];
  strategy: MergeStrategy;
}

export interface CustomFieldDefinition {
  id: string;
  userId: string;
  fieldName: string;
  fieldType: 'text' | 'number' | 'date' | 'url' | 'select';
  selectOptions: string[] | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomFieldInput {
  fieldName: string;
  fieldType: 'text' | 'number' | 'date' | 'url' | 'select';
  selectOptions?: string[] | null;
}

export interface ContactShare {
  id: string;
  contactId: string;
  token: string;
  expiresAt: string;
  createdAt: string;
}

export interface SharedContactView {
  firstName: string;
  lastName: string;
  emails: LabeledValue[];
  phones: LabeledValue[];
  company: string | null;
  title: string | null;
  addresses: AddressValue[];
  websites: LabeledValue[];
  vcf: string;
}

export type InteractionType = 'note' | 'call' | 'email' | 'meeting' | 'message' | 'other';

export interface ContactInteraction {
  id: string;
  contactId: string;
  userId: string;
  interactionType: InteractionType;
  summary: string;
  details: string | null;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface InteractionInput {
  interactionType: InteractionType;
  summary: string;
  details?: string | null;
  occurredAt?: string | null;
}

export interface ContactRelationship {
  id: string;
  userId: string;
  contactId: string;
  relatedContactId: string;
  relationshipType: string;
  createdAt: string;
  relatedContactName?: string;
}

export interface RelationshipInput {
  relatedContactId: string;
  relationshipType: string;
}

// ==========================================================================
// DFE Inventory Types
// ==========================================================================

export type DfeFungibility = 'fungible' | 'non_fungible';
export type DfeTransferType =
  | 'transfer'
  | 'mint'
  | 'burn'
  | 'admin_grant'
  | 'legacy_import'
  | 'game_reward_mint'
  | 'game_reward_burn'
  | 'mining_mint'
  | 'mining_burn'
  | 'crafting_mint'
  | 'crafting_burn'
  | 'liquidity_mint'
  | 'liquidity_burn'
  | 'guild_mint';

export interface DfeDefinitionSummary {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  metadata: Record<string, unknown>;
  maxSupply: number | null;
  currentSupply: number;
  marketCategory: string;
  status: string;
  createdAt: string;
}

export interface DfeDefinitionDetail extends DfeDefinitionSummary {
  createdBy: string;
  updatedAt: string;
}

export interface DfeDefinitionListResult {
  definitions: DfeDefinitionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeInventoryEntry {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  definitionMetadata: Record<string, unknown>;
  category: string;
  position: string;
  balanceId: string | null;
  quantity: number | null;
  itemId: string | null;
  instanceNumber: number | null;
  itemMetadata: Record<string, unknown> | null;
  acquiredAt: string | null;
}

export interface DfeInventoryListResult {
  items: DfeInventoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeInventoryItemDetail {
  itemId: string;
  itemDefId: string;
  ownerId: string;
  instanceNumber: number;
  metadata: Record<string, unknown>;
  category: string;
  position: string;
  acquiredAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface DfeTransferEntry {
  transferId: string;
  itemDefId: string;
  itemId: string | null;
  fromUserId: string | null;
  toUserId: string | null;
  quantity: number;
  transferType: DfeTransferType;
  notes: string | null;
  createdAt: string;
}

export interface DfeTransferListResult {
  transfers: DfeTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeEnrichedTransferEntry extends DfeTransferEntry {
  fromUsername: string | null;
  toUsername: string | null;
  itemName: string | null;
}

export interface DfeEnrichedTransferListResult {
  transfers: DfeEnrichedTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeMintResult {
  success: boolean;
  items?: Array<{ itemId: string; instanceNumber: number; metadata: Record<string, unknown> }>;
  balance?: { balanceId: string; quantity: number };
}

// DFE Legacy Import types
//
// `itemsImported`/`itemsSkipped`/`skippedItems` are cross-source totals
// (DFE + LitSquad combined); the per-source `dfe*`/`litsquad*` counters
// drive the split view in the ImportResultDisplay UI.
export interface DfeLegacyImportResult {
  importId: string;
  legacyServer: string;
  legacyUsername: string;
  itemsImported: number;
  itemsSkipped: number;
  skippedItems: string[];
  dfeItemsImported: number;
  dfeItemsSkipped: number;
  dfeSkippedItems: string[];
  litsquadItemsImported: number;
  litsquadItemsSkipped: number;
  litsquadSkippedItems: string[];
  importStatus: string;
  errorMessage: string | null;
}

export interface DfeLegacyImportEntry extends DfeLegacyImportResult {
  createdAt: string;
  updatedAt: string | null;
}

export interface AdminDfeLegacyImportEntry extends DfeLegacyImportEntry {
  fiasUserId: string;
  fiasUsername: string | null;
  fiasDisplayName: string | null;
}

export interface AdminDfeLegacyImportListResult {
  imports: AdminDfeLegacyImportEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ===========================================================================
// LitSquad (arche_litsquad) — NFT-only in v1
// ===========================================================================

export interface LitSquadDefinitionSummary {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  metadata: Record<string, unknown>;
  maxSupply: number | null;
  currentSupply: number;
  status: string;
  createdAt: string;
}

export interface LitSquadDefinitionDetail extends LitSquadDefinitionSummary {
  createdBy: string;
  updatedAt: string;
}

export interface LitSquadDefinitionListResult {
  definitions: LitSquadDefinitionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface LitSquadInventoryEntry {
  itemDefId: string;
  /** Species name from the definition (e.g. "LitSquad Elephant"). */
  speciesName: string;
  description: string | null;
  fungibility: DfeFungibility;
  definitionThumbnailUri: string | null;
  definitionImageUri: string | null;
  definitionMetadata: Record<string, unknown>;
  category: string;
  position: string;
  itemId: string | null;
  instanceNumber: number | null;
  /** Per-item display name (falls back to speciesName if null). */
  displayName: string | null;
  /** Per-item thumbnail URL (falls back to definitionThumbnailUri if null). */
  thumbnailUri: string | null;
  itemMetadata: Record<string, unknown> | null;
  acquiredAt: string | null;
}

export interface LitSquadInventoryListResult {
  items: LitSquadInventoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

/** The owned NFTs a saved avatar is composed from (mirrors `@fias/db-types`). */
export interface LitSquadAvatarRecipe {
  baseItemId: string;
  accessoryItemIds: string[];
}

/** `PUT /arches/litsquad/avatar` — the saved recipe and the rendered images' storage keys. */
export interface LitSquadAvatarSaveResult {
  recipe: LitSquadAvatarRecipe;
  imageKey: string;
  thumbnailKey: string;
}

/**
 * `GET /arches/litsquad/avatar` — the saved recipe resolved against the
 * caller's current inventory (items no longer owned are dropped), plus the
 * inventory entries for every surviving item.
 */
export interface LitSquadAvatarResponse {
  recipe: LitSquadAvatarRecipe | null;
  items: LitSquadInventoryEntry[];
}

export interface LitSquadInventoryItemDetail {
  itemId: string;
  itemDefId: string;
  ownerId: string;
  instanceNumber: number;
  displayName: string | null;
  thumbnailUri: string | null;
  metadata: Record<string, unknown>;
  category: string;
  position: string;
  acquiredAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitSquadEnrichedTransferEntry {
  transferId: string;
  itemDefId: string;
  itemName: string | null;
  itemId: string | null;
  fromUserId: string | null;
  fromUsername: string | null;
  toUserId: string | null;
  toUsername: string | null;
  quantity: number;
  transferType: string;
  notes: string | null;
  createdAt: string;
}

export interface LitSquadEnrichedTransferListResult {
  transfers: LitSquadEnrichedTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateLitSquadDefinitionInput {
  name: string;
  description?: string;
  fungibility: string;
  thumbnailUri?: string;
  imageUri?: string;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  status?: string;
}

// ==========================================================================
// LitSquad Accessory Crafting
//
// These types MUST stay in sync with `packages/db-types/src/litsquad-crafting.ts`.
// The webapp-client package deliberately does not depend on `@fias/db-types`
// (that package ships Node-only deps), so the shapes are duplicated here.
// When you change one, change both — the backend tests and frontend tests
// both assert on the wire shape.
// ==========================================================================

export type LitSquadCraftSlotName =
  | 'ficiant'
  | 'classCoin'
  | 'lituCloth'
  | 'mmc'
  | 'expertise'
  | 'themeCoin';

export interface LitSquadCraftInput {
  ficiant: string;
  classCoin: string;
  lituCloth: string;
  mmc?: string;
  expertise?: string;
  themeCoin?: string;
}

export interface LitSquadCraftIngredient {
  /**
   * `null` when the user has no stock of this named ingredient. Callers
   * that render zero-count entries should display the name but must not
   * send the null back as a craft input.
   */
  itemDefId: string | null;
  name: string;
  thumbnailUri: string | null;
  userCount: number;
}

export interface LitSquadCraftSlot {
  slot: LitSquadCraftSlotName;
  required: boolean;
  quantity: number;
  eligibleIngredients: LitSquadCraftIngredient[];
}

export interface LitSquadCraftIngredientsResult {
  slots: LitSquadCraftSlot[];
}

export interface LitSquadCraftPreviewItem {
  identifier: string;
  partialName: string;
  displayName: string;
  thumbnailUri: string;
  group: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  avatarItemGroup: string;
  themeCoin: string;
  probability: number;
}

export interface LitSquadCraftPreviewResult {
  species: string;
  possibleGroups: string[];
  ficiantLevel: number;
  useBetterOdds: boolean;
  rarityLadder: Array<{ rarity: string; probability: number }>;
  possibleOutcomes: LitSquadCraftPreviewItem[];
  themeCoinApplies: boolean;
  /**
   * Probability that a craft attempt will abort pre-burn because the
   * rolled rarity has no matching accessory in the possible groups.
   * `possibleOutcomes[].probability + failureProbability === 1`.
   */
  failureProbability: number;
}

export interface LitSquadCraftResult {
  itemId: string;
  itemDefId: string;
  species: string;
  displayName: string;
  thumbnailUri: string;
  group: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  identifier: string;
  avatarItemGroup: string;
  metadata: Record<string, unknown>;
}

export interface UpdateLitSquadDefinitionInput {
  name?: string;
  description?: string | null;
  thumbnailUri?: string | null;
  imageUri?: string | null;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  status?: string;
}

export interface TransferLitSquadItemInput {
  toUserId?: string;
  toUsername?: string;
  itemId: string;
  notes?: string;
  category?: string;
  position?: string;
}

export interface LitSquadMintResult {
  success: boolean;
  items: Array<{
    itemId: string;
    instanceNumber: number;
    displayName: string | null;
    thumbnailUri: string | null;
    metadata: Record<string, unknown>;
  }>;
}

export interface CreateDfeDefinitionInput {
  name: string;
  description?: string;
  fungibility: string;
  thumbnailUri?: string;
  imageUri?: string;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  marketCategory?: string;
  status?: string;
}

export interface UpdateDfeDefinitionInput {
  name?: string;
  description?: string | null;
  thumbnailUri?: string | null;
  imageUri?: string | null;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  marketCategory?: string;
  status?: string;
}

export interface TransferDfeItemInput {
  toUserId?: string;
  toUsername?: string;
  itemDefId?: string;
  quantity?: number;
  itemId?: string;
  notes?: string;
  category?: string;
  position?: string;
}

export interface DfePositionConflict {
  category: string;
  position: string;
  items: { itemDefId: string; name: string }[];
}

// ==========================================================================
// Game Rewards Types
// ==========================================================================

export type GrSubscriptionTier = 'bronze' | 'silver' | 'gold';

export interface GrSubscriptionResponse {
  subscriptionId: string;
  tier: GrSubscriptionTier;
  startsAt: string;
  expiresAt: string;
  purchasedVia?: string;
  isActive?: boolean;
  userId?: string;
  username?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string;
}

export interface GrTierResponse {
  tierId: string;
  tier: GrSubscriptionTier;
  displayName: string;
  currencyItemDefId: string;
  currencyItemName?: string | null;
  priceAmount: number;
  durationDays: number;
  description: string | null;
}

export interface GrTierListResult {
  tiers: GrTierResponse[];
  total: number;
}

export interface GrSubscriptionHistoryResult {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  limit: number;
  offset: number;
}

// LitCraft Store Types
export interface LitCraftStoreProduct {
  productId: string;
  productIdentifier: string;
  productType: string;
  name: string;
  description: string | null;
  priceCredits: number;
  priceUsd: string;
  metadata: Record<string, unknown>;
  subscriptionPeriod?: string;
}

export interface LitCraftStoreResponse {
  gemBundles: LitCraftStoreProduct[];
  subscriptions: LitCraftStoreProduct[];
}

export interface LitCraftStorePurchaseResponse {
  success: boolean;
  purchaseId: string;
  priceCredits: number;
  newBalance: number;
  gemsAwarded?: number;
  subscription?: {
    subscriptionId: string;
    tier: string;
    expiresAt: string;
    bonusDays: number;
  };
  product: {
    productIdentifier: string;
    productType: string;
    name: string;
  };
}

export interface GrRewardDelivered {
  rewardType: string;
  itemDefId: string;
  rawAmount: number;
  multiplier: number;
  finalAmount: number;
}

export interface GrRewardLogEntry {
  logId: string;
  gameConfigId: string;
  /**
   * Resolved keyword/name of the claim (gr_game_configs.game_name). Surfaced
   * server-side via JOIN so the admin audit log can render the human-readable
   * game name. Null when the underlying config row was deleted.
   */
  gameName?: string | null;
  /** Resolved display name (gr_game_configs.display_name). Null if missing. */
  displayName?: string | null;
  gameEvent: string;
  gameScore: number;
  subscriptionTier: GrSubscriptionTier | null;
  rewardsDelivered: GrRewardDelivered[];
  createdAt: string;
  userId?: string;
  /** Resolved server-side via batchGetUsernames. Null if the user record is missing. */
  username?: string | null;
  requestNonce?: string | null;
  ipAddress?: string | null;
}

export interface GrRewardLogListResult {
  entries: GrRewardLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export type GrLeaderboardType = 'high_score' | 'low_score' | 'none';

export type GrRewardMode = 'standard' | 'item_list' | 'recipe';

export type GrItemListFormat = 'repeated' | 'counted';

export interface GrClaimCostRule {
  pattern: 'exact' | 'prefix';
  names?: string[];
  value?: string;
  currency: string;
  amount: number;
}

export type GrGameConfigReviewStatus = 'pending' | 'approved' | 'rejected';

export interface GrGameConfigDetail {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  description: string | null;
  apiKeyPrefix: string;
  minScore: number;
  maxScore: number;
  leaderboardType: GrLeaderboardType;
  rewardMode: GrRewardMode;
  itemListFormat?: GrItemListFormat;
  allowedItems: string[] | null;
  claimCosts: GrClaimCostRule[] | null;
  claimStateKeyword: string | null;
  allowedItemPrefixes: string[] | null;
  rewardLookupEvent?: string | null;
  isActive: boolean;
  /** Server-side enforced: claims for non-'approved' configs grant nothing. */
  reviewStatus: GrGameConfigReviewStatus;
  /** Plugin arche id this game is linked to (1:1, platform-admin only). */
  linkedArcheId?: string | null;
  /** When set, leaderboard writes redirect to this game (single-hop only). */
  leaderboardTargetGameId?: string | null;
  /**
   * Guild whose chat this game shows its players (LitCraft Player
   * `get_guild_chat` / `join_guild_chat`). Platform admin only; the guild must
   * be active, public and opted into plugin sharing by its owner.
   */
  chatGuildId?: string | null;
  /**
   * The LitCraft tester reset covers this game (staging only). Admin or
   * litcraft_admin; false for games that are not LitCraft (Flappyland, Fiardle).
   */
  includeInLitcraftReset?: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Soft-delete metadata. When `deletedAt` is non-null the config is
   * tombstoned: hidden from default admin lists and the claim auth path,
   * but still surfaced when `includeDeleted` or `onlyDeleted` is set.
   */
  deletedAt?: string | null;
  deletedBy?: string | null;
}

export interface GrGameConfigCreateResult {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  apiKey: string;
  apiKeyPrefix: string;
}

export interface GrGameConfigListResult {
  gameConfigs: GrGameConfigDetail[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrStepDefinition {
  minScore: number;
  maxScore: number;
  reward: number;
}

export interface GrRewardRuleResponse {
  ruleId: string;
  gameConfigId: string;
  gameEvent: string;
  rewardType: string;
  itemDefId: string;
  m: number;
  b: number;
  maxRewardCount: number;
  bronzeMultiplier: number;
  silverMultiplier: number;
  goldMultiplier: number;
  steps: GrStepDefinition[] | null;
  baseMultiplier: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GrRewardRuleListResult {
  rules: GrRewardRuleResponse[];
  total: number;
  limit: number;
  offset: number;
}

export type GrRejectionTrustLevel = 'internal' | 'plugin' | 'oauth';

export interface GrClaimRejectionEntry {
  rejectionId: string;
  occurredAt: string;
  trustLevel: GrRejectionTrustLevel;
  archeId: string | null;
  /** Human-readable arche/plugin name resolved server-side. Null when unresolved/deleted. */
  archeName: string | null;
  oauthAppId: string | null;
  userId: string | null;
  /** Resolved server-side via batchGetUsernames. Null if userId is missing or unknown. */
  username: string | null;
  attemptedGameName: string | null;
  gameConfigId: string | null;
  gameEvent: string | null;
  gameScore: number | null;
  attemptedItemNames: string[] | null;
  rejectionCode: string;
  rejectionMessage: string;
}

export interface GrClaimRejectionListResult {
  rejections: GrClaimRejectionEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrRewardTypeLimitResponse {
  limitId: string;
  rewardType: string;
  minSecondsBetweenRewards: number;
  maxAmountPerDay: number;
  createdAt?: string;
}

export interface GrGameRewardLimitResponse {
  limitId: string;
  gameConfigId: string;
  rewardType: string;
  /** Free-tier cap; paid tiers scale up from it. 0 = no cap on this axis. */
  maxAmountPerDay: number;
  createdAt?: string;
  updatedAt?: string;
  /** Present on update: how many game configs the edit re-pended for review. */
  gamesFlippedToPending?: number;
}

export interface GrGameRewardLimitListResult {
  limits: GrGameRewardLimitResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrRewardTypeLimitListResult {
  limits: GrRewardTypeLimitResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrTierConfigResponse {
  tierId: string;
  tier: GrSubscriptionTier;
  displayName: string;
  priceAmount: number;
  durationDays: number;
  isActive?: boolean;
  updatedAt?: string;
}

export interface GrAdminTierListResult {
  tiers: Array<GrTierResponse & { isActive: boolean; createdAt: string }>;
  total: number;
}

export interface GrAdminSubscriptionListResult {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrTimePlayedAggregates {
  totalSeconds: number;
  totalSessions: number;
  games: Array<{
    gameConfigId: string;
    gameName: string;
    displayName: string;
    totalSeconds: number;
    sessionCount: number;
  }>;
}

export interface GrLeaderboardEntryResponse {
  rank: number;
  /** Null for another user's anonymous row (masked server-side); present for your own row and non-anonymous rows. */
  userId: string | null;
  username: string | null;
  /** True when this user opted out of leaderboard visibility — render "(hidden)". */
  anonymous: boolean;
  bestScore: number;
  playCount: number;
  lastPlayedAt: string;
}

export interface GrLeaderboardResult {
  entries: GrLeaderboardEntryResponse[];
  total: number;
  leaderboardType: GrLeaderboardType;
  limit: number;
  offset: number;
  myRank?: { rank: number; bestScore: number; playCount: number } | null;
}

export interface GrUserBestScoresResult {
  scores: Array<{
    gameConfigId: string;
    gameName: string;
    displayName: string;
    gameEvent: string;
    bestScore: number;
    playCount: number;
    lastPlayedAt: string;
    leaderboardType: GrLeaderboardType;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface GrLeaderboardGameInfo {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  leaderboardType: GrLeaderboardType;
  /**
   * Distinct `game_event` keys this game records scores under (e.g. `win`).
   * The leaderboard UI selects one of these to query; there is no implicit
   * `'default'` event.
   */
  events: string[];
}

/** A game that has tracked time — option for the Time/Rewards metric selectors. */
export interface GrTrackedTimeGame {
  gameConfigId: string;
  gameName: string;
  displayName: string;
}

export interface GrTimePlayedLeaderboardResult {
  entries: Array<{
    rank: number;
    /** Null for another user's anonymous row (masked server-side); present for your own row and non-anonymous rows. */
    userId: string | null;
    username: string | null;
    anonymous: boolean;
    totalSeconds: number;
    sessionCount: number;
    lastPlayedAt: string;
  }>;
  total: number;
  community: { totalSeconds: number; totalSessions: number; uniquePlayers: number };
  limit: number;
  offset: number;
  myRank?: { rank: number; totalSeconds: number; sessionCount: number } | null;
}

export interface GrRewardEarningsLeaderboardResult {
  rewardType: string;
  gameConfigId: string | null;
  entries: Array<{
    rank: number;
    /** Null for another user's anonymous row (masked server-side); present for your own row and non-anonymous rows. */
    userId: string | null;
    username: string | null;
    anonymous: boolean;
    totalAmount: number;
  }>;
  total: number;
  communityTotal: number;
  limit: number;
  offset: number;
  myRank?: { rank: number; totalAmount: number } | null;
}

// ==========================================================================
// OAuth / Connected Apps Types
// ==========================================================================

export interface OAuthAuthorizeInfo {
  app: {
    name: string;
    description: string | null;
    logoUrl: string | null;
  };
  scopes: Array<{
    scope: string;
    description: string;
  }>;
  redirectDomain: string;
  existingConsent: boolean;
}

export interface ConnectedApp {
  appId: string;
  appName: string;
  appLogoUrl: string | null;
  scopes: { scope: string; description: string }[];
  grantedAt: string;
}

// ==========================================================================
// DFE Game State Types
// ==========================================================================

export interface GameStateResponse {
  gameStateId: string;
  keyword: string;
  dataType: string;
  data?: string;
  dataBinary?: string;
  version: number;
  dataSizeBytes: number;
  checksum?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ==========================================================================
// Polls Types
// ==========================================================================

export interface PollQuestionPayload {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  required: boolean;
}

export interface CreatePollPayload {
  title: string;
  description?: string;
  questions: PollQuestionPayload[];
  resultsVisibility?: string;
  closesAt?: string;
}

export interface UpdatePollPayload {
  title?: string;
  description?: string | null;
  questions?: PollQuestionPayload[];
  resultsVisibility?: string;
  closesAt?: string | null;
}

export interface PollAnswerPayload {
  questionId: string;
  selectedOptions?: number[];
  textAnswer?: string;
}

export interface PollDetailResponse {
  pollId: string;
  title: string;
  description: string | null;
  questions: PollQuestionPayload[];
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GameStateListResult {
  states: GameStateResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface PollFullDetailResponse extends PollDetailResponse {
  createdBy: string;
  responseCount: number;
  hasResponded: boolean;
  isCreator: boolean;
}

export interface PollListResult {
  polls: Array<{
    pollId: string;
    title: string;
    status: string;
    resultsVisibility: string;
    closesAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface GameStateAuditEntry {
  auditId: string;
  gameStateId: string;
  userId: string;
  /** Resolved server-side via batchGetUsernames; null when the user has been
   * deleted from the user-pool. UI falls back to userId in that case. */
  username: string | null;
  keyword: string;
  action: string;
  oldVersion: number | null;
  newVersion: number | null;
  dataSizeBytes: number | null;
  actorId: string;
  /** Resolved server-side; null fallback semantics same as `username`. */
  actorUsername: string | null;
  /** Plugin arche that drove the mutation, if any. */
  viaArcheId: string | null;
  createdAt: string;
}

export interface GameStateAuditLogResult {
  entries: GameStateAuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// DFE Game State — Plugin Resource Grant types
// ==========================================================================

export type GsResourceType = 'state' | 'config';
export type GsAccessLevel = 'read' | 'read_write';
export type GsGrantReviewStatus = 'pending' | 'approved' | 'rejected';

/** A global admin-curated config entry (dfe_game_config). */
export interface GameStateConfig {
  configId: string;
  keyword: string;
  /** Arbitrary JSON value (object or array). */
  data: unknown;
  /**
   * `sha256:<hex>` over a canonical serialization of `data` — compare it
   * against the hash of a source file to tell "what I believe is live" from
   * "what IS live". Also served as the response `ETag`, and accepted back as
   * `If-Match` on a write to refuse a stale overwrite.
   */
  contentHash: string;
  description: string | null;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * List rows omit `contentHash`: hashing a whole page of blobs costs real CPU
 * to answer a question that is always about ONE keyword. Fetch the keyword to
 * get its hash.
 */
export type GameStateConfigListItem = Omit<GameStateConfig, 'contentHash'>;

export interface GameStateConfigListResult {
  configs: GameStateConfigListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface GameStateGrant {
  archeId: string;
  /** Display name of the arche; null when the arche has been deleted. */
  archeName: string | null;
  resourceType: GsResourceType;
  resourceName: string;
  access: GsAccessLevel;
  createdBy: string;
  /**
   * Username of the requestor (resolved from `createdBy`). For admin-
   * created grants this is the admin who pre-approved access; for auto-
   * requested grants it is the end user whose plugin invocation triggered
   * the request. Null when the user has been deleted.
   */
  createdByUsername: string | null;
  createdAt: string;
  updatedAt: string;
  reviewStatus: GsGrantReviewStatus;
  reviewedBy: string | null;
  /** Username of the admin who approved/rejected; null for pending grants. */
  reviewedByUsername: string | null;
  reviewedAt: string | null;
  reviewReason: string | null;
}

export interface GameStateGrantListResult {
  grants: GameStateGrant[];
  total: number;
  limit: number;
  offset: number;
}

export interface SetGameStateGrantResult {
  grant: GameStateGrant;
  created: boolean;
  accessChanged: boolean;
  reviewReset: boolean;
}

export interface ApproveRejectGameStateGrantResult {
  grant: GameStateGrant;
  statusChanged: boolean;
}

// ==========================================================================
// Admin User Types
// ==========================================================================

export interface InviteCodeResponse {
  code: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  createdAt: string;
  expiresAt: string | null;
  usedAt?: string;
  usedByEmail?: string;
  usedByUserId?: string;
  grantRoles: string[];
  metadata?: { description?: string; notes?: string; createdBy?: string; batchId?: string };
}

export interface AdminRoleSummary {
  role: string;
  label: string;
  description: string;
  sensitivity: 'critical' | 'elevated' | 'routine';
  /** True when no production code path reads this role yet. */
  reserved: boolean;
  /** Grantable and revocable from the Dev Console. */
  managed: boolean;
  /** Displayed for recertification but never mutated here. */
  readOnly: boolean;
  privileged: boolean;
  /** Null when the membership index is unavailable — unknown, not zero. */
  memberCount: number | null;
}

export interface AdminRolesCatalogue {
  roles: AdminRoleSummary[];
  /**
   * False while the RoleIndex GSI does not exist or is still backfilling. Member
   * counts are then null — explicitly UNKNOWN, never 0, since 0 would read as
   * "nobody holds this role".
   */
  indexAvailable: boolean;
}

export interface AdminRoleMember {
  userId: string;
  username: string | null;
  displayName: string | null;
  status: string | null;
  mfaState: string | null;
  grantedAt: string | null;
  grantedBy: string | null;
  reason: string | null;
}

export interface AdminRoleMembersPage {
  role: string;
  members: AdminRoleMember[];
  /**
   * Index entries whose PROFILE no longer backs the role — drift left by a
   * direct DynamoDB write. Excluded from `members`; surfaced so it is visible
   * rather than silently swallowed.
   */
  staleIndexEntries: number;
  /** False when the membership index is unavailable; `members` is then empty. */
  indexAvailable: boolean;
  nextCursor: string | null;
}

/**
 * Narrow username→player resolution used by the DFE Game State admin UI.
 * Deliberately not `AdminUserLookupResult`: that one carries email,
 * suspension forensics and payment flags from the platform support surface,
 * which a `litcraft_admin` has no business reading. Roles are omitted for the
 * same reason — targeting a player for a game-state edit does not require
 * knowing who holds `admin`.
 */
export interface DfeGameStateUserLookupResult {
  userId: string;
  username: string;
}

export interface AdminUserLookupResult {
  userId: string;
  username: string;
  displayName: string | null;
  email: string | null;
  status: string;
  roles: string[];
  primaryAuthMethod: string;
  contributorStatus: string | null;
  paymentEnabled: boolean | null;
  devvxAccountLinked: boolean | null;
  devvxAccountId: string | null;
  /** Set only while `status === 'suspended'`; cleared on reinstatement. */
  suspendedAt: string | null;
  suspendedBy: string | null;
  suspensionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PollResponseResult {
  responseId: string;
  pollId: string;
  answers: PollAnswerPayload[];
  submittedAt: string;
}

export interface PollQuestionResultData {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  optionCounts?: number[];
  textAnswers?: string[];
  totalResponses: number;
}

export interface PollResultsResponse {
  pollId: string;
  title: string;
  totalResponses: number;
  questions: PollQuestionResultData[];
}

export interface PollPublicViewResponse {
  pollId: string;
  title: string;
  description: string | null;
  questions: PollQuestionPayload[];
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  responseCount: number;
  createdAt: string;
}

// ==========================================================================
// Social Media Types
// ==========================================================================

export type SocialPlatform =
  | 'twitter'
  | 'linkedin'
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'youtube'
  | 'telegram';
export type SocialAccountStatus = 'active' | 'error' | 'revoked' | 'expired';
export type SocialPostStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled';

export interface BasePostSettings {
  taggedPeople?: { id: string; name: string }[];
  location?: { id: string; name: string };
}

export interface TwitterPollSettings {
  options: string[];
  durationMinutes: number;
}

/** A single tweet within a thread, with optional per-tweet media */
export interface ThreadTweet {
  text: string;
  /** fileIds of media to attach to this specific tweet (independent of parent post media) */
  mediaFileIds?: string[];
}

export interface TwitterPostSettings extends BasePostSettings {
  format: 'tweet' | 'poll' | 'reply' | 'thread';
  includeLink: boolean;
  tagUser?: string;
  replyToUrl?: string;
  poll?: TwitterPollSettings;
  /** Individual tweets for thread format — each may have its own media */
  threadTweets?: ThreadTweet[];
}

export interface InstagramPostSettings extends BasePostSettings {
  format: 'post' | 'reel' | 'carousel' | 'story';
  hashtagsInFirstComment?: boolean;
  collaboratorTag?: string;
  locationName?: string;
  coverImageFileId?: string;
}

export interface FacebookPostSettings extends BasePostSettings {
  format: 'post' | 'reel' | 'story' | 'event';
  includeLink: boolean;
  audience?: 'public' | 'friends' | 'custom';
}

export interface LinkedInMention {
  name: string;
  urn: string;
  type: 'organization';
}

export interface LinkedInPostSettings extends BasePostSettings {
  includeLink: boolean;
  postType?: 'post' | 'article' | 'poll' | 'event';
  tagCompanyPage?: string;
  /** LinkedIn post visibility — defaults to PUBLIC per LinkedIn spec */
  visibility?: 'PUBLIC' | 'CONNECTIONS' | 'LOGGED_IN';
  /** Inline organization mentions — each name must appear verbatim in the post text */
  mentions?: LinkedInMention[];
  /** File IDs from the post's media pool to include in this LinkedIn post (up to 9 images or 1 video) */
  selectedMediaFileIds?: string[];
}

export interface TikTokPostSettings extends BasePostSettings {
  postType?: 'video' | 'photo_mode' | 'live';
  whoCanView?: 'everyone' | 'friends' | 'private';
  allowDuets?: boolean;
  allowStitch?: boolean;
  disclosePaidPartnership?: boolean;
  selectedVideoFileId?: string;
}

export type PlatformPostSettings =
  | TwitterPostSettings
  | InstagramPostSettings
  | FacebookPostSettings
  | LinkedInPostSettings;

export interface AdaptedPlatformContent {
  text: string;
  hashtags: string[];
  characterCount: number;
  suggestions: string[];
  adaptedAt: string;
  postType?: string;
  settings?: PlatformPostSettings;
}

export interface PublishAttemptResult {
  success: boolean;
  accountId?: string;
  platformPostId?: string;
  url?: string;
  error?: string;
  errorCode?: string;
}

export interface SocialMediaPost {
  postId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  createdByPrincipalId: string;
  originalContent: string;
  platformContent: Record<string, AdaptedPlatformContent>;
  targetAccountIds: string[];
  /** Vault document IDs attached to this post. Server field on the row
   *  is `media_document_ids`; the client also receives a `mediaFileIds`
   *  alias for the duration of the Phase D transition (the routes
   *  format both keys until the client fully migrates). */
  mediaDocumentIds: string[];
  /** @deprecated Use `mediaDocumentIds`. Server still emits this as an
   *  alias so existing consumers don't break; will be removed once all
   *  call sites read `mediaDocumentIds`. */
  mediaFileIds: string[];
  status: SocialPostStatus;
  publishAt: string;
  publishResults: Partial<Record<SocialPlatform, PublishAttemptResult>>;
  hasAiContent: boolean;
  timezone: string;
  failureCount: number;
  title: string | null;
  notes: string | null;
  tags: string[];
  link: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaAccountPublic {
  accountId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  /** Principal that ran the OAuth flow. Used for audit + revocation
   *  pre-flight in the scheduled publish path. */
  connectedByPrincipalId: string;
  platform: SocialPlatform;
  platformUserId: string;
  platformUsername: string | null;
  platformName: string | null;
  platformAvatarUrl: string | null;
  /** Account holds a paid subscription unlocking higher limits (X Premium →
   *  25,000-char long-form posts). Defaults to false. */
  isPremium: boolean;
  status: SocialAccountStatus;
  lastError: string | null;
  tokenExpiresAt: string | null;
  scopes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialPostSummary {
  postId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  createdByPrincipalId: string;
  status: SocialPostStatus;
  publishAt: string;
  hasAiContent: boolean;
  failureCount: number;
  targetAccountIds: string[];
  title: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaPublishLogEntry {
  logId: string;
  postId: string;
  accountId: string;
  /** Denormalized for fast Holder-filter on the audit query path. */
  holderId: string;
  platform: SocialPlatform;
  attemptAt: string;
  success: boolean;
  platformPostId: string | null;
  platformPostUrl: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface SocialMediaPostCounts {
  total: number;
  draft: number;
  scheduled: number;
  published: number;
  failed: number;
  cancelled: number;
  publishing: number;
}

export interface SocialMediaDailyCount {
  /** YYYY-MM-DD */
  date: string;
  count: number;
}

// ==========================================================================
// Social Media Analytics Types
// ==========================================================================

export type SocialAnalyticsRange = '7d' | '30d' | '90d' | '6m' | '1y' | '1y+';

export interface AudienceTimePoint {
  /** YYYY-MM-DD */
  date: string;
  byPlatform: Partial<Record<SocialPlatform, number>>;
}

export interface SocialEngagementByDay {
  /** YYYY-MM-DD */
  date: string;
  engagements: number;
}

/** Per-platform aggregate stats for the selected range */
export interface PlatformAnalyticsStat {
  platform: SocialPlatform;
  /** @handle for the connected account */
  platformUsername: string | null;
  /** Current audience size (followers/page likes/etc); null if no snapshots yet */
  audienceSize: number | null;
  /** % change over the range; null if insufficient data */
  audienceGrowthPct: number | null;
  totalPosts: number;
  /** null = platform does not expose this metric */
  likes: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
}

/** Per-post engagement snapshot (most recent for the range) */
export interface PostMediaItem {
  fileId: string;
  url: string;
  mimeType: string | null;
}

export interface PostEngagementRow {
  postId: string;
  platform: SocialPlatform;
  platformPostId: string;
  /** The post copy / caption text */
  content: string;
  /** Resolved media attachments (image or video URLs) */
  mediaItems: PostMediaItem[];
  /** null = platform does not expose this metric */
  likes: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
  publishedAt: string; // ISO timestamp
  fetchedAt: string; // ISO timestamp
}

export interface SocialAnalyticsResponse {
  platforms: PlatformAnalyticsStat[];
  audienceTimeSeries: AudienceTimePoint[];
  engagementByDay: SocialEngagementByDay[];
  postEngagement: PostEngagementRow[];
  postsHeatmap: Array<{ date: string; count: number }>;
  generatedAt: string; // ISO timestamp
}

// ==========================================================================
// Content Inspiration Types
// ==========================================================================

export type InspirationPlatform = 'twitter' | 'instagram' | 'tiktok' | 'rss' | 'manual';
export type ContentStatus = 'new' | 'reviewed' | 'saved' | 'used' | 'discarded';
export type EngagementScoreType = 'full' | 'partial' | 'estimated';
export type InspirationSourceType = 'competitor' | 'rss' | 'manual';

export interface UnifiedContentItem {
  contentId: string;
  userId: string;
  sourceType: InspirationSourceType;
  sourceId: string;
  platform: InspirationPlatform;
  author: string;
  contentPreview: string;
  draftPostId: string | null;
  mediaUrl: string | null;
  externalId: string;
  externalUrl: string | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  views: number | null;
  engagementScore: number;
  scoreType: EngagementScoreType;
  status: ContentStatus;
  isFavorited: boolean;
  usedCount: number;
  pillarTag: string | null;
  postedAt: string | null;
  fetchedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaCompetitorItem {
  competitorId: string;
  platform: InspirationPlatform;
  username: string;
  /** Platform-native user ID (e.g. Twitter/X numeric user ID). NULL until resolved on first fetch. */
  platformUserId: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isEnabled: boolean;
  fetchIntervalHours: number;
  lastFetchedAt: string | null;
  fetchStatus: 'idle' | 'fetching' | 'error';
  fetchError: string | null;
  fetchStartedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaRssFeedItem {
  feedId: string;
  title: string;
  url: string;
  isEnabled: boolean;
  fetchIntervalHours: number;
  lastFetchedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MediaAttachment {
  /** Temporary ID during upload, then the real fileId after confirm */
  fileId: string;
  fileName: string;
  mimeType: string;
  /** Blob URL for local preview (or presigned URL once uploaded) */
  previewUrl: string;
  sizeBytes: number;
  uploadState: 'uploading' | 'done' | 'error';
  errorMessage?: string;
}

// ==========================================================================
// Calendar Types
// ==========================================================================

export interface CalendarCollectionDetail {
  id: string;
  userId: string;
  name: string;
  description: string;
  color: string;
  timezone: string;
  isDefault: boolean;
  isVisible: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  eventCount?: number;
}

export interface CalendarCollectionInput {
  name: string;
  description?: string;
  color?: string;
  timezone?: string;
  isDefault?: boolean;
  isVisible?: boolean;
}

export interface CalendarEventDetail {
  id: string;
  calendarId: string;
  userId: string;
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  timezone: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  metadata: Record<string, unknown>;
  icalUid: string | null;
  originalEventId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  calendarName?: string;
  calendarColor?: string;
}

export interface CalendarEventCreateInput {
  calendarId: string;
  title: string;
  description?: string;
  location?: string;
  startAt: string;
  endAt: string;
  isAllDay?: boolean;
  timezone?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string | null;
  url?: string;
  isRecurring?: boolean;
  rrule?: string | null;
  /** The series' recurrence anchor zone. New recurring events persist it
   *  explicitly (the picker zone; 'UTC' for all-day) so the durable anchor
   *  never falls back to the column default. */
  rruleTimezone?: string | null;
  reminders?: CalendarEventReminder[];
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface CalendarEventInstance {
  id: string;
  eventId: string;
  calendarId: string;
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  timezone: string;
  /** The series' recurrence anchor zone — pre-check moves in THIS zone so
   *  the client agrees with the server's in-transaction reconciliation. */
  rruleTimezone?: string | null;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  calendarName?: string;
  calendarColor?: string;
  isException: boolean;
  originalStartAt?: string;
  metadata?: Record<string, unknown>;
}

export interface CalendarEventReminder {
  type: 'notification' | 'email';
  minutesBefore: number;
}

export interface CalendarParsedImportResult {
  events: CalendarImportedEvent[];
  warnings: string[];
  errors: string[];
  calendarName?: string;
  calendarColor?: string;
  calendarTimezone?: string;
  totalParsed: number;
}

export interface CalendarImportedEvent extends CalendarEventCreateInput {
  originalSource?: string;
  exdates?: string[];
  icalUid?: string;
}

export interface CalendarImportConfirmRequest {
  calendarId: string;
  events: CalendarImportedEvent[];
  duplicateStrategy: 'skip' | 'overwrite' | 'create_new';
}

export interface CalendarImportConfirmResult {
  imported: number;
  skipped: number;
  updated: number;
  errors: string[];
}

export interface ParsedEventFields {
  title: string;
  startAt?: string;
  endAt?: string;
  isAllDay?: boolean;
  location?: string;
  description?: string;
  isRecurring?: boolean;
  rrule?: string;
  confidence: number;
}

export interface CalendarEventTemplateDetail {
  id: string;
  userId: string;
  name: string;
  title: string;
  description: string;
  location: string;
  durationMinutes: number;
  isAllDay: boolean;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  calendarId: string | null;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  metadata: Record<string, unknown>;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEventTemplateInput {
  name: string;
  title: string;
  description?: string;
  location?: string;
  durationMinutes?: number;
  isAllDay?: boolean;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string;
  url?: string;
  calendarId?: string;
  isRecurring?: boolean;
  rrule?: string;
  reminders?: CalendarEventReminder[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  displayOrder?: number;
}

// ==========================================================================
// Calendar Sharing Types — Vault consent backed
// ==========================================================================
//
// Shares are stored as `vault_consents` rows with
// dataClass = ent_intg_vault_calendar_00000000 and a per-calendar
// scope. The substrate's CalendarConsentScope shape is:
//   { visibility: 'free-busy' | 'titles' | 'full',
//     calendar_ids: [calendarId],   // required & non-empty for per-calendar shares
//     date_range?: { from, to } }
// `calendar_ids` omitted means "all my calendars" (global grant) —
// those are intentionally hidden from the per-calendar dialog and
// will live in a future "Manage all shares" page.

export type CalendarShareVisibility = 'free-busy' | 'titles' | 'full';

export interface CalendarShareDateRange {
  from: string;
  to: string;
}

export interface CalendarShareDetail {
  /** Underlying `vault_consents.consent_id`. */
  consentId: string;
  /** The calendar this share is being viewed under (the dialog's `calendarId`). */
  calendarId: string;
  /** The grantee's Personal Holder. */
  granteeHolderId: string;
  /** Best-effort username (populated by the create flow or the dialog's batch reverse-resolver). */
  granteeUsername?: string;
  visibility: CalendarShareVisibility;
  dateRange?: CalendarShareDateRange;
  createdAt: string;
  expiresAt: string | null;
  /**
   * True when the underlying consent has no `calendar_ids` scope (grants
   * access to ALL of the granter's calendars). Surfaced in the per-calendar
   * dialog so the UI can warn before revoking.
   */
  isGlobal: boolean;
}

export interface CalendarShareInput {
  /** Resolved by searchCalendarUsers — provides the grantee's Personal Holder ID. */
  granteeHolderId: string;
  /** Echoed back into the local detail row for nicer post-create display. */
  granteeUsername?: string;
  visibility: CalendarShareVisibility;
  dateRange?: CalendarShareDateRange;
  expiresAt?: string;
}

export interface CalendarShareUpdate {
  visibility?: CalendarShareVisibility;
  dateRange?: CalendarShareDateRange | null;
  expiresAt?: string | null;
}

// A PUBLISHED FEED is the inverse of a share: a read-only `webcal://` URL for
// one of the user's OWN calendars, handed to a phone. The visibility vocabulary
// is the same as a share (free-busy | titles | full).

export interface CalendarFeedDetail {
  feedId: string;
  calendarId: string;
  calendarName: string;
  /** User-facing device label, e.g. "iPhone". */
  label: string;
  visibility: CalendarShareVisibility;
  windowPastDays: number;
  windowFutureDays: number;
  refreshHintMinutes: number;
  createdAt: string;
  tokenRotatedAt: string | null;
  lastServedAt: string | null;
  serveCount: number;
  // NOTE: the URL is deliberately absent — it is returned ONCE at mint/rotate
  // (CalendarFeedMintResult) and is never recoverable afterwards.
}

export interface CalendarFeedInput {
  label?: string;
  visibility?: CalendarShareVisibility;
  windowPastDays?: number;
  windowFutureDays?: number;
  refreshHintMinutes?: number;
}

export interface CalendarFeedUpdate {
  label?: string;
  visibility?: CalendarShareVisibility;
  windowPastDays?: number;
  windowFutureDays?: number;
  refreshHintMinutes?: number;
}

/**
 * Returned ONCE, at mint and at rotate. The raw `url` / `webcalUrl` are not
 * stored server-side and cannot be re-displayed — the UI must show them once
 * and never persist them in a list row.
 */
export interface CalendarFeedMintResult {
  feedId: string;
  url: string;
  webcalUrl: string;
  label?: string;
  visibility?: CalendarShareVisibility;
  windowPastDays?: number;
  windowFutureDays?: number;
  refreshHintMinutes?: number;
  createdAt?: string;
  tokenRotatedAt?: string | null;
}

export interface SharedCalendarSummary {
  consentId: string;
  granterHolderId: string;
  visibility: CalendarShareVisibility;
  /** Calendars the grant covers. `null` means "all granter calendars". */
  calendarIds: string[] | null;
  dateRange?: CalendarShareDateRange;
  expiresAt: string | null;
}

export interface CalendarUserSearchResult {
  userId: string;
  username: string;
  personalHolderId: string;
}

export interface FreeSlot {
  startAt: string;
  endAt: string;
}

/**
 * Per-participant outcome from a Find-Time search.
 *
 * `available` — participant resolved AND granted us calendar consent AND
 *   their granted date_range overlaps the requested window.
 *
 * `unknown-no-consent` — participant unknown to the platform OR has
 *   not shared their calendar with us OR shared but their granted
 *   date_range doesn't overlap the requested window. We deliberately
 *   collapse these so the response can't be used to probe for
 *   "this user exists" vs "this user blocked me".
 *
 * `error` — transient failure reading their events.
 *
 * Critical invariant: free slots are only computed within the
 * intersection of every `available` participant's effective range.
 * Time outside any participant's granted range is treated as unknown,
 * NEVER as free — surfacing a slot the participant never consented
 * to expose would breach the consent boundary.
 */
export type FindTimeParticipantStatus = 'available' | 'unknown-no-consent' | 'error';

export interface FindTimeParticipantResult {
  username: string;
  status: FindTimeParticipantStatus;
  /** Populated when status === 'available'. */
  holderId?: string;
  /** Intersection of (requested window, participant's granted date_range). Populated when status === 'available'. */
  effectiveRange?: { from: string; to: string };
  /** Human-readable explanation; mainly used for `error` and the explanatory bucket of `unknown-no-consent`. */
  note?: string;
}

export interface FindTimeResult {
  participants: FindTimeParticipantResult[];
  freeSlots: FreeSlot[];
  /** Intersection-of-granted-ranges the search ran in. Null when no overlap exists. */
  searchWindow: { from: string; to: string } | null;
}

// =============================================================================
// Scheduling Links Types
// =============================================================================

export interface AvailabilityWindow {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface SchedulingLinkDetail {
  id: string;
  userId: string;
  slug: string;
  title: string;
  description: string;
  durationMinutes: number;
  location: string;
  color: string | null;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  dailyCap: number | null;
  availabilityWindows: AvailabilityWindow[];
  calendarId: string | null;
  timezone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulingLinkInput {
  slug: string;
  title: string;
  description?: string;
  durationMinutes?: number;
  location?: string;
  color?: string;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  dailyCap?: number | null;
  availabilityWindows?: AvailabilityWindow[];
  calendarId?: string | null;
  timezone?: string;
  isActive?: boolean;
}

export interface BookingDetail {
  id: string;
  schedulingLinkId: string;
  bookerName: string;
  bookerEmail: string;
  eventId: string | null;
  startAt: string;
  endAt: string;
  status: 'confirmed' | 'cancelled';
  notes: string;
  cancellationToken: string;
  createdAt: string;
}

export interface AvailableSlotDetail {
  startAt: string;
  endAt: string;
}

export interface PublicSchedulingInfo {
  schedulingLink: {
    slug: string;
    title: string;
    description: string;
    durationMinutes: number;
    location: string;
    color: string | null;
    timezone: string;
    availabilityWindows: AvailabilityWindow[];
  };
  availableSlots: AvailableSlotDetail[];
}

export interface BookingConfirmation {
  booking: {
    id: string;
    startAt: string;
    endAt: string;
    status: string;
    cancellationToken: string;
  };
}

// =============================================================================
// Schedulable Tasks
// =============================================================================

export interface SchedulableTaskDetail {
  id: string;
  userId: string;
  title: string;
  description: string;
  deadline: string;
  estimatedMinutes: number;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'scheduled' | 'completed' | 'cancelled';
  eventId: string | null;
  calendarId: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  aiSuggestions: AiScheduleSuggestion[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulableTaskInput {
  title: string;
  description?: string;
  deadline: string;
  estimatedMinutes?: number;
  priority?: 'high' | 'medium' | 'low';
  calendarId?: string;
  metadata?: Record<string, unknown>;
}

export interface AiScheduleSuggestion {
  startAt: string;
  endAt: string;
  reasoning: string;
  score: number;
}

// =============================================================================
// Focus Time
// =============================================================================

export interface FocusTimeSettingsDetail {
  id: string;
  userId: string;
  isEnabled: boolean;
  preferredTimeOfDay: 'morning' | 'afternoon' | 'evening';
  minBlockMinutes: number;
  maxBlockMinutes: number;
  daysPerWeek: number;
  preferredDays: number[];
  preferredStartHour: number;
  preferredEndHour: number;
  lookaheadWeeks: number;
  calendarId: string | null;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface FocusTimeSettingsInput {
  isEnabled?: boolean;
  preferredTimeOfDay?: 'morning' | 'afternoon' | 'evening';
  minBlockMinutes?: number;
  maxBlockMinutes?: number;
  daysPerWeek?: number;
  preferredDays?: number[];
  preferredStartHour?: number;
  preferredEndHour?: number;
  lookaheadWeeks?: number;
  calendarId?: string | null;
  color?: string;
}

export interface CalendarSubscriptionDetail {
  id: string;
  calendarId: string;
  feedUrl: string;
  lastFetchedAt: string | null;
  refreshIntervalMinutes: number;
  status: 'active' | 'error';
  errorMessage: string | null;
  calendarName: string;
  calendarColor: string;
  eventCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarAnalyticsHoursPerCalendar {
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsHoursPerWeek {
  weekStart: string;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsHoursPerDayOfWeek {
  dayOfWeek: number;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsSummary {
  totalEvents: number;
  totalHours: number;
  avgDailyHours: number;
  avgEventDurationMinutes: number;
}

export interface CalendarAnalyticsResult {
  summary: CalendarAnalyticsSummary;
  hoursPerCalendar: CalendarAnalyticsHoursPerCalendar[];
  hoursPerWeek: CalendarAnalyticsHoursPerWeek[];
  hoursPerDayOfWeek: CalendarAnalyticsHoursPerDayOfWeek[];
}

// ==========================================================================
// Lit Legion Types
// ==========================================================================

export interface LitLegionNpc {
  npcId: string;
  name: string;
  image: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionUserLevel {
  levelId: string;
  levelNumber: number;
  name: string;
  xpRequired: number;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionBadge {
  badgeId: string;
  name: string;
  image: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionTask {
  taskId: string;
  parentId: string | null;
  npcId: string | null;
  minLevelId: string | null;
  taskType: string;
  name: string;
  description: string;
  category: string | null;
  /**
   * Free-form string. The platform assigns it no meaning and no reader
   * branches on its value. Unrelated to `tag`.
   */
  icon: string | null;
  /** Free-form string, uninterpreted by the platform. Unrelated to `icon`. */
  tag: string | null;
  xpReward: number;
  maxCompletions: number | null;
  maxRetakesPerUser: number | null;
  startDate: string | null;
  endDate: string | null;
  timeIntervalMs: number | null;
  /** 'player' = completable from this UI; 'external' = completion comes from LitCraft plugins / the standalone game. */
  completionSource: 'player' | 'external';
  definition: Record<string, unknown> | null;
  npcTextIntro: string;
  npcTextObjectives: string;
  npcTextInProgress: string;
  npcTextSuccess: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionAdminTask extends LitLegionTask {
  /**
   * Player visibility. `false` hides the task from every player surface — and,
   * by cascade, its entire descendant subtree. Admin-only: a hidden task never
   * reaches a player response, so the field is absent from `LitLegionTask`.
   */
  isActive: boolean;
  /**
   * True when this task is itself active but sits beneath an inactive
   * ancestor, so players cannot see it either. Computed server-side from the
   * same cascade that gates players — do not re-derive it by walking
   * `parentId` on the client, which is impossible to do correctly on a
   * paginated list anyway.
   *
   * Present on every admin task response — list, single-task, and writes —
   * so consumers never have to know which endpoint computed it.
   */
  ancestorInactive: boolean;
  privateDefinition: Record<string, unknown> | null;
}

export interface LitLegionTaskSubmission {
  checkboxes: number[];
  multipleChoices: { index: number; optionId: string }[];
  textInputs: { responseTag: string; value: string }[];
}

export interface LitLegionTaskStatusResponse {
  taskStatusId: string;
  userId: string;
  taskId: string;
  /** Effective status from the server's availability engine (what the player acts on). */
  status: string;
  /** Raw stored status; differs from `status` when the engine computed waiting/expired/unavailable or a retake offer. */
  storedStatus: string;
  attempt: number;
  adminOverride: boolean;
  availabilityReason: string | null;
  /** Cooldown expiry ISO timestamp when availabilityReason === 'cooldown'. */
  retakeAvailableAt: string | null;
  completedAt: string | null;
  xpAmountReceived: number | null;
  userInput: string | null;
  submission: LitLegionTaskSubmission | null;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionAdminTaskStatusWithTask extends LitLegionTaskStatusResponse {
  task: LitLegionAdminTask | null;
}

export interface LitLegionCoinReward {
  coinRewardId: string;
  taskId: string;
  coinId: string;
  coinName: string;
  quantity: number;
  createdAt: string;
}

export interface LitLegionBadgeReward {
  badgeRewardId: string;
  taskId: string;
  badgeId: string;
  createdAt: string;
}

export interface LitLegionTaskWithStatus {
  task: LitLegionTask;
  taskStatus: LitLegionTaskStatusResponse | null;
  npc: LitLegionNpc | null;
  coinRewards: LitLegionCoinReward[];
  badgeRewards: LitLegionBadgeReward[];
}

export interface LitLegionTaskStatusListResult {
  taskStatuses: LitLegionTaskWithStatus[];
  total: number;
  limit: number;
  offset: number;
}

export interface LitLegionEarnedBadge {
  badgeId: string;
  name: string;
  image: string;
  description: string;
  category: string;
  earliestAwardedAt: string;
}

export interface LitLegionPlayerStats {
  userId: string;
  xpAmount: number;
  level: LitLegionUserLevel;
  nextLevel: LitLegionUserLevel | null;
  tasksCompleted: number;
  tasksInProgress: number;
  earnedBadges: LitLegionEarnedBadge[];
}

export interface LitLegionAdminTaskListResult {
  tasks: LitLegionAdminTask[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateLitLegionTaskPayload {
  name: string;
  description: string;
  taskType: string;
  xpReward: number;
  category?: string;
  /** Free-form string. See {@link LitLegionTask.icon}. Null is not a create value. */
  icon?: string;
  /** Free-form string. See {@link LitLegionTask.icon}. Null is not a create value. */
  tag?: string;
  npcId?: string;
  parentId?: string;
  minLevelId?: string;
  npcTextIntro?: string;
  npcTextObjectives?: string;
  npcTextInProgress?: string;
  npcTextSuccess?: string;
  /** Player visibility; omitted means visible. See {@link LitLegionAdminTask.isActive}. */
  isActive?: boolean;
  definition?: Record<string, unknown> | null;
  privateDefinition?: Record<string, unknown> | null;
  coinRewards?: { coinId: string; coinName: string; quantity: number }[];
  badgeRewards?: { badgeId: string }[];
}

export interface UpdateLitLegionTaskPayload {
  name?: string;
  description?: string;
  taskType?: string;
  xpReward?: number;
  category?: string | null;
  /** Free-form string. See {@link LitLegionTask.icon}. */
  icon?: string | null;
  /** Free-form string. See {@link LitLegionTask.icon}. */
  tag?: string | null;
  npcId?: string | null;
  parentId?: string | null;
  minLevelId?: string | null;
  npcTextIntro?: string;
  npcTextObjectives?: string;
  npcTextInProgress?: string;
  npcTextSuccess?: string;
  /** Player visibility. See {@link LitLegionAdminTask.isActive}. */
  isActive?: boolean;
  definition?: Record<string, unknown> | null;
  privateDefinition?: Record<string, unknown> | null;
  coinRewards?: { coinId: string; coinName: string; quantity: number }[];
  badgeRewards?: { badgeId: string }[];
}

// ==========================================================================
// Business Types
// ==========================================================================

export interface BusinessResponse {
  businessId: string;
  userId?: string;
  businessType: string;
  name: string;
  level: number;
  status: string;
  lastCollectedAt: string | null;
  cooldownRemaining: number;
  canCollect: boolean;
  dailyCollectionsUsed: number;
  createdAt: string;
  updatedAt?: string;
  /** A held business token still inside its 24h maturity window. */
  immatureTokenName?: string | null;
}

export interface CollectRevenueResponse {
  heptalsEarned: number;
  bonusItemName: string | null;
  bonusItemCount: number;
  /** Effician only: what was manufactured. Null / 0 for the other types. */
  producedItemName: string | null;
  producedItemCount: number;
  subscriptionTier: string;
  subscriptionMultiplier: number;
  revenueMultiplier: number;
  logId: string;
}

export interface BusinessParameterResponse {
  paramId: string;
  businessType: string;
  level: number;
  requirements: {
    jion: number;
    freshWater: number;
    cogsValue: number;
    components: Record<string, number>;
    foodValue: number;
  };
  rewards: {
    mode: string;
    heptals: number;
    bonusItem: string | null;
    bonusCount: number;
  };
  creationCost: {
    heptals: number;
    spentCoinName: string;
  };
}

export interface BusinessHistoryResponse {
  logs: Array<{
    logId: string;
    businessId: string;
    businessType: string;
    level: number;
    heptalsEarned: number;
    bonusItemName: string | null;
    bonusItemCount: number;
    subscriptionTier: string;
    subscriptionMultiplier: number;
    revenueMultiplier: number;
    cogsConsumed: Record<string, number>;
    foodsConsumed: Record<string, number>;
    materialsConsumed: Record<string, number>;
    createdAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface BusinessStatsResponse {
  totalBusinesses: number;
  activeBusinesses: number;
  dissolvedBusinesses: number;
  businessesByType: Record<string, number>;
  totalCollections: number;
  totalHeptalsEarned: number;
}

// =============================================================================
// Beta Access Types
// =============================================================================

export interface BetaAccessSubmitInput {
  name: string;
  email: string;
  telegram?: string;
  description: string;
}

export interface BetaAccessSubmitResponse {
  message: string;
}

export interface BetaAccessRequestResponse {
  requestId: string;
  name: string;
  email: string;
  telegram: string | null;
  description: string;
  status: 'pending' | 'accepted' | 'rejected';
  accessCode: string | null;
  adminNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  ipAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BetaAccessListResponse {
  requests: BetaAccessRequestResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface BetaAccessStatsResponse {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
}

// ==========================================================================
// Learn About Fias Types
// ==========================================================================

export interface LearnAboutFiasResourceResponse {
  docId: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceType: string;
  iconType: string | null;
  buttonLabel: string | null;
  fileKey: string | null;
  ragEnabled: boolean;
  totalChunks: number;
  createdAt: string;
}

export interface LearnAboutFiasResourcesResponse {
  resources: LearnAboutFiasResourceResponse[];
}

export interface LearnAboutFiasDocumentResponse {
  docId: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceType: string;
  metadata: Record<string, unknown>;
  status: string;
  totalChunks: number;
  errorMessage: string | null;
  showInResources: boolean;
  ragEnabled: boolean;
  fileKey: string | null;
  sortOrder: number;
  createdAt: string;
  processedAt: string | null;
}

export interface LearnAboutFiasDocumentDetailResponse extends LearnAboutFiasDocumentResponse {
  content: string | null;
}

export interface LearnAboutFiasDocumentListResponse {
  documents: LearnAboutFiasDocumentResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface LearnAboutFiasStatsResponse {
  documentCount: number;
  chunkCount: number;
  pendingDocuments: number;
  failedDocuments: number;
  avgChunksPerDocument: number;
}

// ==========================================================================
// Storage Types (shared by DFE Avatars, DFE Icons, Litcraft Addressables)
// ==========================================================================

export interface StorageFile {
  key: string;
  size?: number;
  lastModified?: string;
}

export interface StorageFileListResponse {
  files: StorageFile[];
  folders: string[];
  cdnBaseUrl: string | null;
  continuationToken: string | null;
  isTruncated: boolean;
}

export interface StorageUploadUrlResponse {
  uploadUrl: string;
  cdnUrl: string | null;
  expiresIn: number;
}

export interface StorageFileMetadataResponse {
  key: string;
  size?: number;
  lastModified?: string;
  contentType?: string;
  etag?: string;
  cdnUrl: string | null;
}

/**
 * API interface for a storage arche.
 * Created by ApiClient.createStorageApi() for each storage arche.
 */
export interface StorageApi {
  listFiles(
    prefix?: string,
    delimiter?: string,
    maxKeys?: number,
    continuationToken?: string,
  ): Promise<StorageFileListResponse>;
  getUploadUrl(
    key: string,
    contentType: string,
    sizeBytes: number,
  ): Promise<StorageUploadUrlResponse>;
  deleteFile(key: string): Promise<{ success: boolean; key: string }>;
  deleteGroup(prefix: string): Promise<{
    success: boolean;
    prefix: string;
    deletedCount: number;
    failedCount?: number;
    failedKeys?: string[];
  }>;
  getMetadata(key: string): Promise<StorageFileMetadataResponse>;
}

// ==========================================================================
// Todo List Types (W6 — Vault-resident; replaces the legacy user-scoped
// shape. Sharing has moved to Vault Membership; the collaborator /
// section-assignment surfaces are gone. Single assignee per item now.)
// ==========================================================================

export interface TodoListResponse {
  listId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  createdByPrincipalId: string;
  name: string;
  description: string | null;
  /** Item-id prefix (e.g. "FR"); the display id of an item is `${itemKey}-${seq}`. */
  itemKey: string;
  /** Monotonic per-list counter for assigning item seq numbers. */
  itemSeqCounter: number;
  isPublic: boolean;
  publicSlug: string | null;
  /** When true, checking an item off routes it to `in_review` for verification
   * before it can be completed. */
  reviewMode: boolean;
  /** Show the project chip on a task in the section view. */
  showProjectOnTasks: boolean;
  /** Show the task's section path in the project view. */
  showSectionPathOnTasks: boolean;
  /** Manual drag-to-reorder position on the Holder's list dashboard (shared
   * across the workspace; lower sorts first, `updatedAt` DESC as tiebreaker). */
  sortOrder: number;
  archivedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TodoSectionResponse {
  sectionId: string;
  listId: string;
  holderId: string;
  parentSectionId: string | null;
  name: string;
  description: string | null;
  sortOrder: number;
  sectionType: 'timeframe' | 'category';
  hiddenFromPublic: boolean;
  assignedToPrincipalId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Todo item workflow status. Mirrors VaultTodoItemStatus in @fias/db-types (the
 * client doesn't depend on that package). `completed` and `cancelled` are the
 * terminal "closed" states that drop out of the active list.
 */
export type TodoItemStatus =
  | 'pending'
  | 'in_progress'
  | 'blocked'
  | 'in_review'
  | 'completed'
  | 'cancelled';

export interface TodoItemResponse {
  itemId: string;
  listId: string;
  sectionId: string | null;
  /** Parent item when this row is a sub-task; null for a top-level task. */
  parentItemId: string | null;
  holderId: string;
  title: string;
  description: string | null;
  status: TodoItemStatus;
  /** Doer's "finished my part" flag, orthogonal to status. On a review-mode list
   *  the top-level checkbox toggles this; a task must be `done` to send for review;
   *  reaching `completed` sets it true. */
  done: boolean;
  /** Immutable per-list number; the display id is `${list.itemKey}-${seq}`. */
  seq: number;
  sortOrder: number;
  completedAt: string | null;
  /** Replaces completed_by_user_id/username in the legacy shape. For a
   * review-mode item this is the verifier who approved it. */
  completedByPrincipalId: string | null;
  /** When the item was submitted for review (status → in_review). */
  reviewSubmittedAt: string | null;
  /** The principal who submitted the item for review (the "doer"). */
  reviewSubmittedByPrincipalId: string | null;
  /** The targeted reviewer for an in-review item (null = broadcast to all list
   * reviewers). Drives the "Review requested from X" badge. */
  reviewAssignedToPrincipalId: string | null;
  /** Single assignee per item in V-1 (the legacy todo_item_assignments
   * many-to-many is gone). */
  assignedToPrincipalId: string | null;
  /** The workspace project this task belongs to; null when unassigned. */
  projectId: string | null;
  /** Per-task public-visibility override. When true, the task is hidden from the
   * public share view even if its section is visible. Owner/admin-controlled. */
  hiddenFromPublic: boolean;
  createdAt: string;
  updatedAt: string;
  /** Support-roadmap +1 demand count (present for items routed from support). */
  roadmapVoteCount?: number;
  /** Display name of the original reporter, for tasks routed from a Customer
   * Support submission (bug report / suggestion); null/absent otherwise.
   * Resolved server-side from the reporter's id. */
  reportedBy?: string | null;
  /** Sub-task progress on a top-level task: total non-cancelled / completed. */
  subtaskTotal?: number;
  subtaskDone?: number;
  /** Checklist progress on a task: total items / done. Drives the row badge. */
  checklistTotal?: number;
  checklistDone?: number;
}

/** A checklist item — lightweight text + checkbox breakdown of a shared task. */
export interface TodoChecklistItemResponse {
  checklistItemId: string;
  itemId: string;
  listId: string;
  title: string;
  isDone: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** A checklist item on a private personal task (the My Tasks mirror). */
export interface TodoPersonalChecklistItemResponse {
  personalChecklistItemId: string;
  personalItemId: string;
  listId: string;
  title: string;
  isDone: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// --- Personal "My Tasks" overlay ---

export interface TodoPersonalSectionResponse {
  personalSectionId: string;
  listId: string;
  principalId: string;
  name: string;
  description: string | null;
  /** Parent personal section when nested; null for a top-level section. */
  parentPersonalSectionId: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TodoPersonalItemResponse {
  personalItemId: string;
  listId: string;
  principalId: string;
  personalSectionId: string | null;
  /** Parent personal item when this row is a sub-task; null for a top-level item. */
  parentPersonalItemId: string | null;
  title: string;
  description: string | null;
  status: TodoItemStatus;
  sortOrder: number;
  completedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The workspace project this private task belongs to; null when unassigned. */
  projectId: string | null;
  /** Checklist progress on a private task: total items / done. Drives the badge. */
  checklistTotal?: number;
  checklistDone?: number;
}

/** A workspace project — a cross-cutting task grouping orthogonal to sections. */
export interface TodoProjectResponse {
  projectId: string;
  holderId: string;
  name: string;
  /** Preset-palette key (e.g. "blue"); null = default. */
  color: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TodoProjectListResponse {
  projects: TodoProjectResponse[];
}

/** A directional cross-list move shortcut: source list → target list in the same
 * workspace. Powers the "Move to list…" picker; may carry a default landing
 * section in the target list. */
export interface TodoListLinkResponse {
  linkId: string;
  listId: string;
  targetListId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  defaultTargetSectionId: string | null;
  createdByPrincipalId: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TodoListLinksResponse {
  links: TodoListLinkResponse[];
}

export interface TodoPersonalPlacement {
  personalSectionId: string | null;
  sortOrder: number;
}

/** A shared task assigned to the caller, plus their private placement of it. */
export type TodoPlacedTaskResponse = TodoItemResponse & {
  placement: TodoPersonalPlacement | null;
  /** For a sub-task, the parent task's display seq + title — drives the board's
   *  "↳ FR-100" parent chip even when the parent isn't on the caller's board. */
  parentSeq?: number;
  parentTitle?: string;
};

export interface TodoPersonalBoardResponse {
  sections: TodoPersonalSectionResponse[];
  personalItems: TodoPersonalItemResponse[];
  assignedTasks: TodoPlacedTaskResponse[];
}

/** Lean sub-task row for the shared List view's search index. */
export interface TodoSubtaskIndexEntry {
  itemId: string;
  parentItemId: string;
  title: string;
  description: string | null;
}

export interface TodoSubtaskIndexResponse {
  subtasks: TodoSubtaskIndexEntry[];
}

export interface TodoPersonalEntryRef {
  kind: 'personal' | 'shared';
  id: string;
}

export interface TodoListDetailResponse {
  list: TodoListResponse;
}

export interface TodoListListResponse {
  lists: TodoListResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface TodoWorkspaceSummary {
  holderId: string;
  holderType: 'personal' | 'org';
  /** Count of visible (not archived, not deleted) lists in this workspace. */
  listCount: number;
}

export interface TodoWorkspaceSummaryResponse {
  summaries: TodoWorkspaceSummary[];
}

/** The caller's unread assignment notifications, each mapped to its parent list
 * so the UI can render per-list badges and per-item unread dots. */
export interface TodoAssignmentNotificationsResponse {
  items: Array<{ itemId: string; listId: string }>;
  sections: Array<{ sectionId: string; listId: string }>;
  /** Items the caller has an unread "review requested" notification for. */
  reviews: Array<{ itemId: string; listId: string }>;
}

export interface TodoListReviewersResponse {
  reviewers: Array<{ principalId: string }>;
}

export interface TodoSectionListResponse {
  sections: TodoSectionResponse[];
}

/** One access grant: `principalId` may work in `sectionId` (and its subsections). */
export interface TodoSectionAccessEntry {
  sectionId: string;
  principalId: string;
}

export interface TodoSectionAccessResponse {
  access: TodoSectionAccessEntry[];
}

export interface TodoItemListResponse {
  items: TodoItemResponse[];
}

/** Public read shape — strips holder + audit columns and skips the
 * completed-by attribution. Lives behind a publicSlug + is_public=true
 * on the underlying list. */
export interface PublicTodoListResponse {
  listId: string;
  name: string;
  description: string | null;
  publicSlug: string | null;
  sections: Array<{
    sectionId: string;
    parentSectionId: string | null;
    name: string;
    description: string | null;
    sortOrder: number;
    sectionType: 'timeframe' | 'category';
  }>;
  items: Array<{
    itemId: string;
    listId: string;
    sectionId: string | null;
    title: string;
    description: string | null;
    status: TodoItemStatus;
    sortOrder: number;
    completedAt: string | null;
  }>;
}

export interface PublicTodoCompletedResponse {
  items: Array<{
    itemId: string;
    listId: string;
    sectionId: string | null;
    title: string;
    description: string | null;
    status: 'pending' | 'in_progress' | 'completed';
    sortOrder: number;
    completedAt: string | null;
  }>;
}

// ==========================================================================
// Customer Support Types
// ==========================================================================

/**
 * Human-readable identity attached to support admin DTOs. The admin UI renders
 * `displayName ?? username ?? id`, keeping the raw id only as a small fallback.
 * Null when the row has no associated user (e.g. system-authored).
 */
export interface SupportUserRef {
  id: string;
  username: string | null;
  displayName: string | null;
}

export interface SupportTicketResponse {
  ticketId: string;
  /** Short human-quotable number, rendered as "#1042". */
  ticketNumber: number;
  userId: string;
  categoryId: string | null;
  subject: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  tags?: SupportTagResponse[];
}

export interface SupportTicketDetailResponse extends SupportTicketResponse {
  assignedTo: string | null;
  resolvedAt: string | null;
}

export interface SupportTicketListResult {
  tickets: Array<{
    ticketId: string;
    ticketNumber: number;
    categoryId: string | null;
    subject: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

/**
 * Admin route segment for each support record class. Used to address the
 * shared waiver endpoint; the values are the URL segments the API exposes.
 */
/** Per-tab counts for one dimension of the support admin nav badges. */
export interface SupportTabCounts {
  tickets: number;
  suggestions: number;
  bugReports: number;
  problemReports: number;
  fiasAiReports: number;
}

/**
 * Support admin nav badge counts. `unread` (top level) means nobody has looked;
 * `awaiting` means nobody has answered. An item can be read and still owed a
 * reply, so both are needed.
 */
export interface SupportAdminBadgeCounts extends SupportTabCounts {
  awaiting: SupportTabCounts;
}

export type SupportAdminCollection =
  | 'tickets'
  | 'suggestions'
  | 'bug-reports'
  | 'problem-reports'
  | 'fias-ai-reports';

export interface SupportAdminTicketResponse extends SupportTicketResponse {
  /** `null` = unset priority. */
  priority: string | null;
  aiPriority: string | null;
  assignedTo: string | null;
  resolvedAt: string | null;
  tags?: SupportTagResponse[];
  user?: SupportUserRef | null;
  archeId?: string | null;
  /** Derived: the customer is waiting on a reply from us. */
  awaitingReply?: boolean;
  /** True when an admin has waived the awaiting-reply flag on this ticket. */
  replyWaived?: boolean;
  /** Which admin waived it, as a display ref — never a bare id to render. */
  replyWaivedBy?: SupportUserRef | null;
  /** When it was waived, ISO-8601. */
  replyWaivedAt?: string | null;
}

export interface SupportAdminTicketListResult {
  tickets: Array<{
    ticketId: string;
    ticketNumber: number;
    userId?: string;
    user?: SupportUserRef | null;
    unread?: boolean;
    awaitingReply?: boolean;
    archeId?: string | null;
    categoryId: string | null;
    subject: string;
    description?: string;
    status: string;
    priority: string | null;
    aiPriority: string | null;
    assignedTo?: string | null;
    createdAt: string;
    updatedAt: string;
    resolvedAt?: string | null;
    tags?: SupportTagResponse[];
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface SupportMessageResponse {
  messageId: string;
  senderId: string | null;
  senderType: string;
  content: string;
  createdAt: string;
}

export interface SupportAdminMessageResponse extends SupportMessageResponse {
  isInternal: boolean;
  deletedAt: string | null;
  /**
   * Resolved identity of the sender. Admin-only — the customer-facing message
   * endpoint deliberately does not expose agent identities. Null for
   * system-authored rows with no sender id.
   */
  sender?: SupportUserRef | null;
}

export interface SupportFaqResponse {
  faqId: string;
  categoryId: string | null;
  question: string;
  answer: string;
  displayOrder: number;
}

export interface SupportFaqAdminResponse extends SupportFaqResponse {
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCategoryResponse {
  categoryId: string;
  name: string;
  description: string | null;
}

export interface SupportCategoryAdminResponse extends SupportCategoryResponse {
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCannedResponseResponse {
  responseId: string;
  title: string;
  content: string;
  categoryId: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportTagResponse {
  tagId: string;
  name: string;
  color: string;
  description: string | null;
  isSystem: boolean;
  createdAt?: string;
}

export interface SupportKbArticleResponse {
  kbId: string;
  categoryId: string | null;
  title: string;
  content: string;
  keywords: string[];
  isPublished?: boolean;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ResolutionTrendPoint {
  periodStart: string;
  avgHours: number | null;
  resolvedCount: number;
}

export interface SupportChatConversationSummary {
  conversationId: string;
  firstMessage: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SupportChatMessageResponse {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface SupportAnalyticsResponse {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  avgResolutionTimeHours: number | null;
  avgResolutionTimeHoursWeek: number | null;
  avgResolutionTimeHoursMonth: number | null;
  resolutionTrendWeekly: ResolutionTrendPoint[];
  resolutionTrendMonthly: ResolutionTrendPoint[];
  categoryBreakdown: Array<{ categoryId: string; categoryName: string; count: number }>;
  priorityBreakdown: Array<{ priority: string; count: number }>;
}

// ==========================================================================
// Document Sign Types
//
// These shapes MUST stay in sync with `packages/db-types/src/document-sign.ts`.
// The webapp-client package can't depend on `@fias/db-types` directly (that
// package ships Node-only deps), so the shapes + constants are duplicated
// here. When you change one, change both — the backend tests and frontend
// tests both assert on the wire shape.
// ==========================================================================

/** MUST match CHAT_ATTACHMENT_MAX_BYTES in @fias/db-types. */
export const CHAT_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;

/**
 * MUST match MAX_MESSAGE_LENGTH in @fias/db-types (sanitized-HTML characters).
 * The server is the authoritative gate (Zod + a DB CHECK constraint); the chat
 * composer mirrors this value for the client-side length guard/counter. Drift is
 * caught by tests/architecture/chat-message-length-mirror.test.ts.
 */
export const CHAT_MESSAGE_MAX_LENGTH = 10000;

/**
 * MUST match CHAT_MESSAGE_MAX_ATTACHMENTS in @fias/db-types, which also bounds
 * the send-message route's `attachmentRefs` array. The composer enforces it so
 * a user is stopped at the cap rather than by a 400 on send. Drift is caught by
 * tests/architecture/chat-client-caps-mirror.test.ts.
 */
export const CHAT_MESSAGE_MAX_ATTACHMENTS = 20;

// Chat attachment MIME inference now lives in `@fias/platform-resources`
// (`resolveFileType` under `CHAT_FILE_TYPE_POLICY`) — shared verbatim with the
// server, no mirror. The former `inferAttachmentMimeType` guessed only `.md` and
// otherwise fell back to `application/octet-stream`; the catalog covers every
// known type and reconciles the several MIMEs browsers report per extension.

/** MUST match DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
/**
 * Client-side upload-UX guard only. Since the move to Vault storage the server
 * enforces per-holder Vault storage quota, not a fixed per-envelope cap — this
 * keeps the builder from letting a user pile up an unreasonable amount before
 * the first upload call. Mirrors the `@fias/db-types` constant of the same name.
 */
export const DOCUMENT_SIGN_MAX_ENVELOPE_SIZE_BYTES = 200 * 1024 * 1024;
/** MUST match DOCUMENT_SIGN_MAX_SOURCE_FILES in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_SOURCE_FILES = 25;
/** MUST match DOCUMENT_SIGN_SOURCE_MIME_TYPE in @fias/db-types. */
export const DOCUMENT_SIGN_SOURCE_MIME_TYPE = 'application/pdf';
/** MUST match DOCUMENT_SIGN_MAX_RECIPIENTS in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_RECIPIENTS = 20;
/** MUST match DOCUMENT_SIGN_MAX_BULK_RECIPIENTS in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_BULK_RECIPIENTS = 500;
/** MUST match DOCUMENT_SIGN_RECIPIENT_NAME_MAX_LENGTH in @fias/db-types. */
export const DOCUMENT_SIGN_RECIPIENT_NAME_MAX_LENGTH = 300;
/** MUST match DOCUMENT_SIGN_RECIPIENT_EMAIL_MAX_LENGTH in @fias/db-types. */
export const DOCUMENT_SIGN_RECIPIENT_EMAIL_MAX_LENGTH = 500;
/** MUST match DOCUMENT_SIGN_MAX_FIELDS_PER_FILE in @fias/db-types. */
export const DOCUMENT_SIGN_MAX_FIELDS_PER_FILE = 200;
/** MUST match DOCUMENT_SIGN_VOID_REASON_MAX_LENGTH in @fias/db-types. */
export const DOCUMENT_SIGN_VOID_REASON_MAX_LENGTH = 500;

export type DocumentSignEnvelopeStatus =
  | 'draft'
  | 'sent'
  | 'in_progress'
  | 'completed'
  | 'declined'
  | 'voided'
  | 'expired';

export type DocumentSignSigningOrder = 'parallel' | 'sequential';

/**
 * Assurance tier (FIAS-ESTE-009). `regulated` covers consumer-facing documents
 * under a legal writing requirement and carries a stricter consent procedure
 * plus a minimum authentication method.
 */
export type DocumentSignAssuranceTier = 'standard' | 'regulated';

/**
 * Which refusal a recipient made. `agreement` — they rejected the document.
 * `electronic_records` — they declined to sign it ELECTRONICALLY and asked
 * for paper, which is not a rejection of the deal.
 */
export type DocumentSignDeclineKind = 'agreement' | 'electronic_records';

/**
 * How a signature image was produced. Both are valid electronic signatures;
 * the distinction is descriptive and goes on the certificate.
 */
export type SignatureMode = 'typed' | 'drawn';

/**
 * Sender branding shown to a recipient. Validated tokens only — the signing
 * page is on the platform origin, so sender-supplied markup or CSS there would
 * be an XSS vector against other customers' sessions. Colours are hex-checked
 * server-side and the logo is rendered through an `<img>`.
 */
/** Mirrors the server cap; the upload control resizes to fit it. */
export const DS_BRAND_LOGO_MAX_BYTES = 64 * 1024;

export interface DocumentSignPublicBranding {
  displayName: string | null;
  logoDataUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  /** Header foreground override; null = computed from `primaryColor`. */
  headerTextColor: string | null;
  completionMessage: string | null;
  completionCtaLabel: string | null;
  completionCtaUrl: string | null;
}

/** Vault holder kind — `personal` for a user's own holder, `org` for a company. */
export type DocumentSignHolderType = 'personal' | 'org';

export interface DocumentSignEnvelopeResponse {
  envelopeId: string;
  ownerUserId: string;
  /** Vault holder (`vh_…`) the envelope is owned by. Fixed once created. */
  holderId: string;
  holderType: DocumentSignHolderType;
  title: string;
  message: string | null;
  status: DocumentSignEnvelopeStatus;
  signingOrder: DocumentSignSigningOrder;
  assuranceTier?: DocumentSignAssuranceTier;
  expiresAt: string | null;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  declinedAt: string | null;
  /** Sealing lifecycle for the audit-PDF artifact. */
  sealStatus: 'not_required' | 'pending' | 'done' | 'failed';
  /** The owner's Vault document for the completed sealed PDF — null until sealed. */
  completedVaultDocumentId: string | null;
  /**
   * Cross-arche handoff provenance — the arche that handed this document over,
   * null for envelopes started inside Document Sign. Drives the "Back to …"
   * return affordance. Host-derived, never attested: do not gate anything on it.
   */
  originArcheId?: string | null;
  originRequestId?: string | null;
  originDocumentId?: string | null;
  originCorrelationId?: string | null;
  /** `bulk` = a draft sent as a separate copy to each person on `bulkRecipients`. */
  sendMode?: DocumentSignSendMode;
  bulkRecipients?: DocumentSignBulkRecipient[] | null;
  /** On a copy made by a bulk send: the id grouping that send's copies. */
  bulkBatchId?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type DocumentSignSendMode = 'single' | 'bulk';

export interface DocumentSignBulkRecipient {
  name: string;
  email: string;
}

/** A bulk send has been recorded; its copies are made and sent in the background. */
export interface DocumentSignBulkSendResponse {
  batchId: string;
  total: number;
  status: 'processing';
}

/** Where one person in a bulk send stands. */
export interface DocumentSignBulkSendItem {
  itemId: string;
  position: number;
  name: string;
  email: string;
  /** pending → processing → sent | failed. */
  status: 'pending' | 'processing' | 'sent' | 'failed';
  /** Why it failed, or why a sent copy's email failed. A user-safe sentence. */
  problem: string | null;
  /** The person's own envelope, once created. */
  envelopeId: string | null;
  envelopeStatus: DocumentSignEnvelopeStatus | null;
  recipientStatus: DocumentSignRecipientResponse['status'] | null;
}

export interface DocumentSignBulkBatchResponse {
  batchId: string;
  title: string;
  status: 'processing' | 'done';
  total: number;
  createdAt: string;
  completedAt: string | null;
  items: DocumentSignBulkSendItem[];
}

/** One of the requester's bulk sends, as their list of sends shows it. */
export interface DocumentSignBulkBatchSummary {
  batchId: string;
  title: string;
  status: 'processing' | 'done';
  total: number;
  sent: number;
  failed: number;
  createdAt: string;
  completedAt: string | null;
}

export interface DocumentSignBulkBatchListResponse {
  bulkSends: DocumentSignBulkBatchSummary[];
  /** Pass back for the next (older) page; null when there are no more. */
  nextCursor: string | null;
}

/** Optional features this deployment has switched on, and the send fee. */
export interface DocumentSignFeaturesResponse {
  bulkSend: boolean;
  /** Credits charged per envelope sent (each bulk copy is one); null if unpriced. */
  sendFeeCredits: number | null;
}

export interface DocumentSignFileResponse {
  fileId: string;
  envelopeId: string;
  kind: 'source' | 'sealed' | 'certificate';
  fileName: string;
  /** The Vault document holding this file's bytes (System C substrate). */
  vaultDocumentId: string;
  /** For `sealed` rows, the holder this owned copy belongs to; null for `source`. */
  deliveryHolderId: string | null;
  pageCount: number | null;
  sortOrder: number;
  createdAt: string;
}

export interface DocumentSignRecipientResponse {
  recipientId: string;
  envelopeId: string;
  role: 'signer' | 'viewer' | 'approver';
  routingOrder: number;
  name: string | null;
  email: string | null;
  userId: string | null;
  status:
    | 'pending'
    | 'notified'
    | 'viewed'
    | 'signed'
    | 'declined'
    | 'declined_electronic'
    | 'expired';
  notifiedAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  /**
   * True when the sender set an access code for this recipient. The code
   * itself is never returned — it is stored hashed. Optional so a cached
   * client type still matches a server that predates the field.
   */
  requiresAccessCode?: boolean;
  /** When the recipient proved knowledge of the code; null until they do. */
  accessCodeVerifiedAt?: string | null;
  /** Failed attempts so far; at the cap the recipient is locked out. */
  accessCodeAttempts?: number;
  /** Vault holder this signer chose to receive their completed copy into. */
  deliveryHolderId: string | null;
  deliveryHolderType: DocumentSignHolderType | null;
  /** The signer's owned Vault document for the completed copy, set at seal. */
  deliveredVaultDocumentId: string | null;
  /**
   * When this signer's signing-request email last failed to go out; null once
   * one is sent. There is no resend, so the owner has to reach them directly.
   * Optional: absent from responses served before the column existed.
   */
  signingEmailFailedAt?: string | null;
  createdAt: string;
}

export interface DocumentSignFieldResponse {
  fieldId: string;
  envelopeId: string;
  fileId: string;
  recipientId: string;
  type:
    | 'signature'
    | 'initial'
    | 'date_signed'
    | 'signer_name'
    | 'email'
    | 'company'
    | 'title'
    | 'text'
    | 'number'
    | 'checkbox';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required: boolean;
  value: string | null;
  signedAt: string | null;
  createdAt: string;
}

export interface DocumentSignEnvelopeDetailResponse extends DocumentSignEnvelopeResponse {
  files: DocumentSignFileResponse[];
  recipients: DocumentSignRecipientResponse[];
  fields: DocumentSignFieldResponse[];
  /**
   * Display name of the arche named by `originArcheId`, resolved SERVER-side
   * from the stored id. Null when there is no origin, or when that arche has
   * since been deleted.
   *
   * Present only on the detail read, because that is where it is needed: the
   * pre-send disclosure has to name the app that will be able to read this
   * envelope afterwards. It is the compensating control for `originArcheId`
   * being host-derived rather than attested — a forged origin becomes visible
   * at the moment before the user commits. Never send it FROM the client;
   * that would let the thing being disclosed pick its own label.
   */
  originArcheName?: string | null;
  /**
   * A bulk draft's review fingerprint (null otherwise): sent back with
   * `bulkSendDocumentSignEnvelope`, so a send refuses an envelope changed
   * since it was reviewed.
   */
  reviewFingerprint?: string | null;
}

export interface DocumentSignEnvelopeListResponse {
  envelopes: DocumentSignEnvelopeResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Fias Transfer
// ============================================================================

export type FiasTransferCapability =
  | 'password'
  | 'nda'
  | 'viewOnly'
  | 'watermark'
  | 'pageAnalytics';

export type FiasTransferCapabilitySet = Partial<Record<FiasTransferCapability, boolean>>;

export interface FiasTransferSpace {
  spaceId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  title: string;
  message: string | null;
  status: 'active' | 'deleted';
  createdAt: string;
  updatedAt: string;
  /**
   * Set when the sender filed this transfer away. An archived transfer keeps
   * `status: 'active'` — the state describes the record, not the sharing, and
   * archiving revokes the links rather than deleting the space.
   */
  archivedAt: string | null;
}

/** A transfer as it appears in a list, with the one derived count the list needs. */
export interface FiasTransferSpaceListItem extends FiasTransferSpace {
  /**
   * Live links on this transfer. Drives the delete/archive confirmations ("3
   * links stop working"), and flags an anomaly in the archive view — archiving
   * revokes, so an archived transfer with a live link should not exist.
   */
  activeLinkCount: number;
}

export interface FiasTransferLink {
  linkId: string;
  spaceId: string;
  label: string | null;
  capabilities: FiasTransferCapabilitySet;
  payloadBytes: number;
  downloadAuthorizations: number;
  authorizationsUsed: number;
  recipientEmail: string | null;
  recipientName: string | null;
  /** Derived server-side. The hash and ciphertext never leave the database. */
  hasPassword: boolean;
  status: 'draft' | 'pending_payment' | 'active' | 'expired' | 'revoked' | 'exhausted';
  expiresAt: string;
  lastAccessedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface FiasTransferSpaceDocument {
  documentId: string;
  name?: string;
  path?: string | null;
  sizeBytes?: number;
  mimeType?: string | null;
  /** False when the document was deleted or is no longer shareable. */
  available: boolean;
}

export interface FiasTransferSpaceDetailResponse {
  space: FiasTransferSpace;
  documents: FiasTransferSpaceDocument[];
  links: FiasTransferLink[];
}

/**
 * One document's malware-scan state.
 *
 * `verdict` is GuardDuty's answer; `servable` and `notScannable` are the
 * two questions a sender actually has, resolved server-side so the UI never
 * re-implements the serve policy. Note `UNSUPPORTED` is servable AND
 * not-scannable at once — GuardDuty could not inspect the format, and the file
 * is served anyway because large files are this product's core use case.
 *
 * Mirrors `TransferScanVerdict` in `@fias/db-types` — keep the two in step;
 * `OBJECT_MISSING` is the one member Fias synthesizes rather than GuardDuty
 * reporting it (the row and its bytes have diverged).
 */
export interface FiasTransferScanFile {
  documentId: string;
  name: string;
  verdict:
    | 'NO_THREATS_FOUND'
    | 'THREATS_FOUND'
    | 'UNSUPPORTED'
    | 'ACCESS_DENIED'
    | 'FAILED'
    | 'PENDING'
    | 'OBJECT_MISSING';
  /** Whether a public link would hand this file over right now. */
  servable: boolean;
  /** Served, but never inspected. The recipient is told so at download time. */
  notScannable: boolean;
}

export interface FiasTransferScanStatusResponse {
  files: FiasTransferScanFile[];
  /** The space holds more documents than one request inspects. */
  truncated: boolean;
  blockedCount: number;
  /**
   * Whether this environment has a scanner behind the Vault bucket at all.
   *
   * Always true in staging and prod. False only on a developer machine, where
   * LocalStack has no GuardDuty — every file then reports `notScannable`, and
   * the per-file reason ("too large or an unusual format") would be nonsense.
   */
  scanningAvailable: boolean;
}

export interface FiasTransferQuoteResponse {
  payloadBytes: number;
  downloadAuthorizations: number;
  linkProjectedGb: number;
  spaceBeforeGb: number;
  sizeFeeCredits: number;
  capabilityFeeCredits: number;
  totalCredits: number;
  /** False while billing (Phase 1b) is unbuilt and the quote is non-zero. */
  availableInCurrentPhase: boolean;
}

/**
 * How a viewer is identified. A discriminated union rather than a string so
 * the two trust levels cannot be collapsed by accident:
 *
 *   recipient — the link was issued to a named recipient, so the token itself
 *               is the credential. Trustworthy.
 *   claimed   — an anonymous visitor typed an email into a gate. NOT verified.
 *               MUST render as "claimed", never as identity.
 *   anonymous — no identity signal at all.
 */
export type FiasTransferViewerIdentity =
  | { kind: 'recipient'; email: string; name: string | null }
  | { kind: 'claimed'; email: string }
  | { kind: 'anonymous' };

export interface FiasTransferViewer {
  sessionId: string;
  identity: FiasTransferViewerIdentity;
  firstSeenAt: string;
  lastSeenAt: string;
  unlockedPasswordAt: string | null;
  acceptedNdaAt: string | null;
  /** Null once the retention sweep has scrubbed it; the row survives. */
  ip: string | null;
  piiPurgedAt: string | null;
}

export interface FiasTransferActivityEvent {
  eventId: string;
  sessionId: string | null;
  documentId: string | null;
  type: string;
  /** Mint-time estimate, NOT measured transfer. Nothing bills from it. */
  estimatedBytes: number | null;
  createdAt: string;
}

export interface FiasTransferActivityResponse {
  link: {
    linkId: string;
    label: string | null;
    status: FiasTransferLink['status'];
    expiresAt: string;
    downloadAuthorizations: number;
    authorizationsUsed: number;
    recipientEmail: string | null;
  };
  viewers: FiasTransferViewer[];
  events: FiasTransferActivityEvent[];
}

/**
 * The pre-gate view. Deliberately carries NO filenames, paths, or per-file
 * sizes — a path like `/Project Cobalt/Term Sheet — Acquirer B.pdf` discloses
 * a deal before a byte moves, so the manifest sits behind every gate.
 */
export interface FiasTransferPublicSummary {
  title: string;
  message: string | null;
  fileCount: number;
  totalBytes: number;
  expiresAt: string;
  requiresPassword: boolean;
  requiresNda: boolean;
  unlocked: { password: boolean; nda: boolean };
  recipientName: string | null;
  downloadAuthorizationsRemaining: number;
  /** False on a view-only link. */
  canDownload: boolean;
  canPreview: boolean;
  questionsEnabled: boolean;
}

export interface FiasTransferManifestFile {
  documentId: string;
  name?: string;
  path?: string | null;
  sizeBytes?: number;
  mimeType?: string | null;
  available: boolean;
  /**
   * Whether the renderer can turn THIS file into page images (PDF only).
   *
   * Distinct from the manifest's link-level `canPreview`: that says the link
   * has a viewer at all, this says the file can go in it. Offering View on a
   * file where this is false produces a button whose only outcome is "this
   * file cannot be previewed".
   */
  previewable?: boolean;
  /**
   * Why the recipient cannot have it.
   *   'over_capacity' — real and readable, but beyond what this link covers.
   *                     Distinguishable on purpose: unlike a deleted file it
   *                     tells the visitor something exists AND the sender can
   *                     fix it by issuing a new link.
   *   'unavailable'   — everything else. Deleted, moved, reclassified: one
   *                     word, because a public surface must not say which.
   */
  unavailableReason?: 'over_capacity' | 'unavailable';
}

export interface FiasTransferManifest {
  title: string;
  message: string | null;
  files: FiasTransferManifestFile[];
  downloadAuthorizationsRemaining: number;
  windowExpiresAt: string | null;
  /** False on a view-only link — there is no download surface at all. */
  canDownload: boolean;
  /**
   * True when the link renders documents in the Fias viewer AT ALL. Per-link.
   * Whether a given file can go in that viewer is `file.previewable`.
   */
  canPreview: boolean;
  questionsEnabled: boolean;
}

/** One server-rendered page. `dataUri` is a PNG — the original file never
 * reaches the client on a view-only link. */
export interface FiasTransferPreviewPage {
  pageNumber: number;
  width: number;
  height: number;
  dataUri: string;
}

export interface FiasTransferPreview {
  documentId: string;
  name: string;
  /** One page WINDOW, not the whole document — see `from`/`to`. */
  pages: FiasTransferPreviewPage[];
  totalPages: number;
  /** True when the document has more pages than the renderer's ceiling. */
  truncated: boolean;
  watermarked: boolean;
  /** First page in this response (1-based, inclusive). */
  from: number;
  /** Last page in this response (1-based, inclusive). */
  to: number;
  /** Pages the server will render in one request. */
  pageSpan: number;
  /**
   * Renders left in this download window.
   *
   * Rendering is the most expensive work on the public surface, so it is
   * budgeted per window rather than per document. Surfaced so the viewer can
   * stop asking before it starts getting 429s.
   */
  rendersRemaining: number;
  /** True when the viewer should report page dwell (`pageAnalytics`). */
  reportsPages: boolean;
}

/**
 * A dataroom question. `body` and `answerBody` are UNTRUSTED text — the
 * question is written by an anonymous visitor. Render as text, never as HTML.
 */
export interface FiasTransferQuestion {
  questionId: string;
  documentId: string | null;
  body: string;
  answerBody: string | null;
  status: 'open' | 'answered' | 'dismissed';
  answeredAt: string | null;
  createdAt: string;
}

/** The sender's view — adds `sessionId` so five questions from ONE investor
 * read as one person rather than five. */
export interface FiasTransferSenderQuestion extends FiasTransferQuestion {
  linkId: string;
  sessionId: string | null;
}

/**
 * Per-page reading stats.
 *
 * `views` and `sessions` are counts of reports the server received — sturdy.
 * `totalDwellMs` is what the recipient's browser SAID, over a public endpoint.
 * The API returns it under an explicit `unverified` flag for that reason; the
 * UI must not present it as a measurement.
 */
export interface FiasTransferPageStat {
  documentId: string;
  pageNumber: number;
  views: number;
  sessions: number;
  totalDwellMs: number;
}

export interface FiasTransferPageStats {
  enabled: boolean;
  pages: FiasTransferPageStat[];
  documents: Array<{ documentId: string; pagesRead: number; views: number; dwellMs: number }>;
  dwell: { unverified: true; measurement: string };
  /** 'complete' on a view-only link; 'viewer_only' when the file is also
   * downloadable and reading can happen entirely outside the viewer. */
  coverage: 'complete' | 'viewer_only';
}

export interface FiasTransferSpaceFolder {
  folderId: string;
  /** Null when the folder was deleted out from under the subscription. */
  name: string | null;
  path: string | null;
  includeSubfolders: boolean;
  createdAt: string;
}

export interface FiasTransferFolderExposure {
  activeLinkCount: number;
  spaceCount: number;
}

export interface DocumentSignUploadUrlResponse {
  fileId: string;
  /** The pending Vault document id — round-tripped back on confirm. */
  documentId: string;
  uploadUrl: string;
  /**
   * Headers the presigned-URL signature pins. EVERY one of these MUST be sent
   * on the S3 PUT or the upload fails with a signature mismatch. Includes
   * `Content-Type`, `Content-Length`, `x-amz-checksum-sha256`, `x-amz-tagging`.
   */
  requiredHeaders: Record<string, string>;
  expiresAt: string;
}

export interface DocumentSignRecipientInput {
  /** Existing recipient to update in place; omitted for new recipients. */
  recipientId?: string;
  name: string;
  email: string;
  role: 'signer' | 'viewer' | 'approver';
  routingOrder?: number;
  /**
   * Second authentication factor. Omit to leave any stored code untouched —
   * the server never returns it, so a blank field means "unchanged".
   */
  accessCode?: string | null;
}

export interface DocumentSignFieldInput {
  recipientId: string;
  type:
    | 'signature'
    | 'initial'
    | 'date_signed'
    | 'signer_name'
    | 'email'
    | 'company'
    | 'title'
    | 'text'
    | 'number'
    | 'checkbox';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required?: boolean;
}

export interface DocumentSignSendEnvelopeResponse {
  envelope: DocumentSignEnvelopeResponse;
  recipients: DocumentSignRecipientResponse[];
  notifiedRecipientIds: string[];
  /**
   * Signing emails whose dispatch failed AFTER the send committed. The
   * envelope is sent either way; a non-empty list means a signer may not
   * have a link, and there is no resend for a signature request yet — so
   * show it, never swallow it into a clean success.
   */
  deliveryFailures?: Array<{ recipientId: string; reason: string }>;
}

export interface DocumentSignSigningEnvelopeView {
  envelopeId: string;
  title: string;
  message: string | null;
  status: DocumentSignEnvelopeStatus;
  signingOrder: DocumentSignSigningOrder;
  assuranceTier?: DocumentSignAssuranceTier;
  expiresAt: string | null;
  ownerName: string;
}

export interface DocumentSignSigningRecipientView {
  recipientId: string;
  name: string | null;
  email: string | null;
  role: 'signer' | 'viewer' | 'approver';
  status: 'pending' | 'notified' | 'viewed' | 'signed' | 'declined' | 'expired';
  /**
   * True when this recipient maps to a Fias user — only then can they choose
   * a Vault holder to receive their completed copy. External email-only
   * signers get the email / token download instead.
   */
  deliveryEligible: boolean;
  /** The holder the signer has already chosen, if any. */
  deliveryHolderId: string | null;
}

export interface DocumentSignSigningFileView {
  fileId: string;
  fileName: string;
  pageCount: number | null;
  sortOrder: number;
  downloadUrl: string;
  expiresAt: string;
}

/**
 * An EARLIER signer's completed field, served as read-only context so a
 * later sequential signer sees what has already been signed — the source
 * PDF is only flattened with signatures at envelope completion.
 */
export interface DocumentSignCompletedFieldView {
  fieldId: string;
  fileId: string;
  recipientId: string;
  type: DocumentSignFieldResponse['type'];
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  value: string | null;
  signedAt: string | null;
  /** Short-lived inline URL for a signature/initial image, when one exists. */
  signatureUrl?: string;
}

export interface DocumentSignSigningSessionResponse {
  envelope: DocumentSignSigningEnvelopeView;
  recipient: DocumentSignSigningRecipientView;
  fields: DocumentSignFieldResponse[];
  /** Earlier signers' completed fields (never the current recipient's own). */
  completedFields: DocumentSignCompletedFieldView[];
  files: DocumentSignSigningFileView[];
  consentDisclosure: string;
  consentDisclosureVersion: number;
  /**
   * Whether this recipient has retrieved the document as a real PDF. Optional
   * so a cached client type still matches a server that predates the field.
   */
  recordAccessDemonstrated?: boolean;
  /**
   * Whether the pre-consent access step applies. False on the Standard tier,
   * where no consumer writing requirement is engaged.
   */
  requiresPreConsentAccess?: boolean;
  /**
   * True when this recipient consented under an older disclosure version and
   * the live one declares prior consent insufficient. The page always runs
   * the consent step on load, so the flag's job is to say WHY it is running
   * again for someone who remembers agreeing.
   */
  requiresReconsent?: boolean;
  assuranceTier?: 'standard' | 'regulated';
  /** Sender's branding for this signing experience; null when unbranded. */
  branding?: DocumentSignPublicBranding | null;
}

export interface DocumentSignSigningFieldSubmission {
  fieldId: string;
  value?: string | null;
  signatureDataUrl?: string | null;
  /** How the image was produced; omitted for non-signature fields. */
  signatureMode?: SignatureMode | null;
}

export type DocumentSignLedgerStatus =
  | 'disabled'
  | 'pending'
  | 'submitted'
  | 'confirmed'
  | 'failed';

export interface DocumentSignEventResponse {
  eventId: string;
  envelopeId: string;
  recipientId: string | null;
  actorUserId: string | null;
  actorKind: 'owner' | 'recipient' | 'system';
  type: string;
  payload: Record<string, unknown>;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  ledgerStatus: DocumentSignLedgerStatus;
  ledgerRef: string | null;
  ledgerAttempts: number;
  ledgerLastError: string | null;
  ledgerSubmittedAt: string | null;
  ledgerConfirmedAt: string | null;
}

// ==========================================================================
// Fias Leads DTOs (Phase 9 — Holder-scoped CRM on Vault substrate)
// ==========================================================================
//
// Holder-scoped DTOs returned by /v1/arches/fias-leads/*. Shared concepts
// (companies, contacts, activities, tasks, email templates, contact
// relationships) live in Vault and lens over the same rows Fias 360
// reads — those DTOs are aliased from the Fias360 versions where the
// shape is identical. Leads-private rows (deals, pipelines, automations,
// custom fields, saved views, contact extension) have their own DTOs.

export type FiasLeadsCompanyDto = Fias360CompanyDto;
export type FiasLeadsContactDto = Fias360CustomerDto;

/** Lightweight (id, name) company ref for complete, alphabetized
 * dropdowns — see listFiasLeadsCompanyRefs. */
export interface FiasLeadsCompanyRefDto {
  companyId: string;
  name: string;
}

/** Per-item result of the bulk permanent-delete endpoints. `code` is
 * the machine reason for a refusal (CONTACT_NOT_ARCHIVED,
 * CONTACT_REFERENCED_BY_DEALS, COMPANY_REFERENCED_BY_DEALS,
 * COMPANY_HAS_RENEWALS, RECORD_REFERENCED). */
export interface FiasLeadsBulkDeleteResult {
  id: string;
  ok: boolean;
  error?: string;
  code?: string;
}

export type FiasLeadsActivityKind = 'note' | 'call' | 'email' | 'meeting';
export type FiasLeadsActivityRegardingType = 'vault_company' | 'vault_contact' | 'fias_leads_deal';

export interface FiasLeadsActivityDto {
  activityId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  kind: FiasLeadsActivityKind;
  regardingType: FiasLeadsActivityRegardingType;
  regardingId: string;
  subject: string;
  bodyMarkdown: string;
  typeData: Record<string, unknown>;
  occurredAt: string;
  createdByPrincipalId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type FiasLeadsActivityParticipantKind = 'vault_contact' | 'vault_principal';
export type FiasLeadsActivityParticipantRole = 'organizer' | 'attendee' | 'cc' | 'bcc';

export interface FiasLeadsActivityParticipantDto {
  activityId: string;
  participantKind: FiasLeadsActivityParticipantKind;
  participantId: string;
  role: FiasLeadsActivityParticipantRole;
}

export type FiasLeadsTaskStatus = 'open' | 'completed' | 'cancelled';
export type FiasLeadsTaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type FiasLeadsTaskRegardingType = 'vault_company' | 'vault_contact' | 'fias_leads_deal';

export interface FiasLeadsTaskDto {
  taskId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  title: string;
  description: string;
  status: FiasLeadsTaskStatus;
  priority: FiasLeadsTaskPriority;
  dueAt: string | null;
  reminderAt: string | null;
  completedAt: string | null;
  assigneePrincipalId: string | null;
  createdByPrincipalId: string;
  regardingType: FiasLeadsTaskRegardingType | null;
  regardingId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type FiasLeadsEmailTemplateCategory =
  | 'sales_outreach'
  | 'customer_success'
  | 'support'
  | 'general';

export interface FiasLeadsEmailTemplateDto {
  templateId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  category: FiasLeadsEmailTemplateCategory;
  name: string;
  subject: string;
  bodyMarkdown: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export type FiasLeadsContactRelationshipType =
  | 'colleague'
  | 'manager'
  | 'reports_to'
  | 'mentor'
  | 'spouse'
  | 'partner'
  | 'friend'
  | 'sibling'
  | 'parent'
  | 'child'
  | 'client'
  | 'vendor'
  | 'referral'
  | 'other';

export interface FiasLeadsContactRelationshipDto {
  relationshipId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  fromContactId: string;
  toContactId: string;
  relationshipType: FiasLeadsContactRelationshipType;
  notes: string;
  createdByPrincipalId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// Leads-private contact extension (lead_score / ai_summary etc.)
export interface FiasLeadsContactExtensionDto {
  leadScore: number | null;
  aiSummary: string | null;
  lastContactedAt: string | null;
}

// Leads-private rows below.

export type FiasLeadsPipelineStageType = 'open' | 'won' | 'lost';

export interface FiasLeadsPipelineStageDto {
  id: string;
  name: string;
  display_order: number;
  stage_type: FiasLeadsPipelineStageType;
  color: string | null;
}

export interface FiasLeadsPipelineDto {
  id: string;
  name: string;
  is_default: boolean;
  /**
   * Days of no logged activity before this pipeline's open deals raise a
   * stale-deal alert. `null` means alerts are off for the pipeline — which is
   * how every pipeline starts.
   */
  stale_after_days: number | null;
  created_at: string;
  updated_at: string;
  stages: FiasLeadsPipelineStageDto[];
}

// --- Fias Leads CSV import ---

export type FiasLeadsImportEntityType = 'contact' | 'company' | 'deal';
export type FiasLeadsImportDedupStrategy = 'skip' | 'create_anyway';
export type FiasLeadsImportNameFallback = 'none' | 'email';
export type FiasLeadsImportBatchStatus =
  | 'uploading'
  | 'active'
  | 'archiving'
  | 'archived'
  | 'restoring';

export interface FiasLeadsImportBatchDto {
  id: string;
  holderId: string;
  entityType: FiasLeadsImportEntityType;
  fileName: string;
  dedupStrategy: FiasLeadsImportDedupStrategy;
  nameFallback: FiasLeadsImportNameFallback;
  pipelineId: string | null;
  defaultStageId: string | null;
  status: FiasLeadsImportBatchStatus;
  totalRows: number | null;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  createdAt: string;
  completedAt: string | null;
  archivedAt: string | null;
}

export interface FiasLeadsImportFieldIssue {
  level: 'error' | 'warning';
  message: string;
}

export interface FiasLeadsImportPreviewCell {
  value: string | number | null;
  issue?: FiasLeadsImportFieldIssue;
}

export interface FiasLeadsImportDuplicateInfo {
  kind: 'existing' | 'in_file';
  id?: string;
  label?: string;
  matchedOn: 'email' | 'domain' | 'name' | 'name+pipeline';
  rowIndex?: number;
}

export type FiasLeadsImportLinkResolution = { id: string; label: string } | { unmatched: string };

export interface FiasLeadsImportDealResolution {
  stage: { id: string; name: string; matchedBy: 'name' | 'default' };
  contact?: FiasLeadsImportLinkResolution;
  company?: FiasLeadsImportLinkResolution;
}

export interface FiasLeadsImportPreviewRow {
  index: number;
  fields: Record<string, FiasLeadsImportPreviewCell>;
  rowStatus: 'ok' | 'warning' | 'error';
  duplicateOf?: FiasLeadsImportDuplicateInfo;
  resolution?: FiasLeadsImportDealResolution;
}

export interface FiasLeadsImportPreviewSummary {
  total: number;
  ok: number;
  warnings: number;
  errors: number;
  duplicatesExisting: number;
  duplicatesInFile: number;
}

export interface FiasLeadsImportPreviewResponse {
  entityType: FiasLeadsImportEntityType;
  dedupStrategy: FiasLeadsImportDedupStrategy;
  rows: FiasLeadsImportPreviewRow[];
  summary: FiasLeadsImportPreviewSummary;
  emittedKeys: string[];
}

export interface FiasLeadsImportBatchLifecycleResponse {
  processed: number;
  skipped: number;
  failed: Array<{ recordId: string; error: string }>;
  remaining: number;
  batch: FiasLeadsImportBatchDto;
}

export type FiasLeadsDealStatus = 'open' | 'won' | 'lost' | 'deleted';
export type FiasLeadsDealRiskLevel = 'low' | 'medium' | 'high';
export type FiasLeadsDealSortBy =
  | 'updated_at'
  | 'created_at'
  | 'value'
  | 'name'
  | 'expected_close_date'
  | 'last_activity_at';

export interface FiasLeadsDealDto {
  id: string;
  holder_id: string;
  holder_type: 'personal' | 'org';
  pipeline_id: string;
  stage_id: string;
  contact_id: string | null;
  company_id: string | null;
  name: string;
  value: string;
  currency: string;
  expected_close_date: string | null;
  actual_close_date: string | null;
  win_likelihood: string | null;
  lost_reason: string | null;
  notes: string;
  tags: string[];
  metadata: Record<string, unknown>;
  status: FiasLeadsDealStatus;
  risk_level: FiasLeadsDealRiskLevel | null;
  risk_flags: string[];
  risk_assessed_at: string | null;
  owner_principal_id: string | null;
  created_by_principal_id: string;
  created_at: string;
  updated_at: string;
  /**
   * Most recent activity timestamp logged against this deal (computed).
   * Present on read endpoints (list / pipeline-summary / single GET);
   * absent on raw mutation responses (create / update / move / restore),
   * hence optional.
   */
  last_activity_at?: string | null;
}

export interface FiasLeadsPipelineSummaryStageDto {
  stage_id: string;
  name: string;
  stage_type: FiasLeadsPipelineStageType;
  color: string | null;
  display_order: number;
  deals: FiasLeadsDealDto[];
}

/** A contact's membership on a deal. `isPrimary` mirrors deals.contact_id. */
export interface FiasLeadsDealContactDto {
  contactId: string;
  role: string;
  isPrimary: boolean;
}

export type FiasLeadsAutomationStatus = 'success' | 'failure' | 'skipped';

export interface FiasLeadsAutomationRuleDto {
  id: string;
  holder_id: string;
  holder_type: 'personal' | 'org';
  name: string;
  is_active: boolean;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  action_type: string;
  action_config: Record<string, unknown>;
  execution_count: number;
  last_executed_at: string | null;
  created_by_principal_id: string;
  created_at: string;
  updated_at: string;
}

export interface FiasLeadsAutomationExecutionDto {
  id: string;
  rule_id: string;
  trigger_entity_type: string;
  trigger_entity_id: string;
  status: FiasLeadsAutomationStatus;
  error_message: string | null;
  executed_at: string;
}

export type FiasLeadsCustomFieldEntityType = 'contact' | 'company' | 'deal';
export type FiasLeadsCustomFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'url'
  | 'email';

export interface FiasLeadsCustomFieldDefinitionDto {
  id: string;
  holder_id: string;
  entity_type: FiasLeadsCustomFieldEntityType;
  field_name: string;
  field_label: string;
  field_type: FiasLeadsCustomFieldType;
  options: unknown[];
  is_required: boolean;
  display_order: number;
  created_at: string;
}

export type FiasLeadsSavedViewEntityType = 'contact' | 'company' | 'deal' | 'task';

export interface FiasLeadsSavedViewDto {
  id: string;
  holder_id: string;
  entity_type: FiasLeadsSavedViewEntityType;
  name: string;
  filters: Record<string, unknown>;
  sort_by: string | null;
  sort_dir: 'asc' | 'desc' | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// Dashboard / Reports / Deal-risk response shapes.

export interface FiasLeadsDashboardMetricsDto {
  dealsByStatus: Array<{ status: FiasLeadsDealStatus; count: string; total_value: string }>;
  openPipelineValue: string;
  dealsByStage: Array<{
    stage_id: string;
    stage_name: string;
    deal_count: string;
    total_value: string;
  }>;
  recentActivityCount: string;
}

export interface FiasLeadsDealRiskAssessmentDto extends FiasLeadsDealDto {
  risk_level: FiasLeadsDealRiskLevel;
  risk_flags: string[];
}

export interface FiasLeadsPipelineVelocityRowDto {
  stage_id: string;
  stage_name: string;
  deal_count: string;
  avg_days_in_stage: string;
}

export interface FiasLeadsForecastRowDto {
  month: string;
  deal_count: string;
  total_value: string;
  weighted_value: string;
}

export interface FiasLeadsActivityTrendsRowDto {
  day: string;
  kind: FiasLeadsActivityKind;
  count: string;
}

export interface FiasLeadsLostDealRowDto {
  lost_reason: string;
  deal_count: string;
  total_value: string;
}

// AI response shapes (lead score / win likelihood / summarize).

export interface FiasLeadsLeadScoreResultDto {
  contactId: string;
  score: number;
  summary: string;
}

export interface FiasLeadsWinLikelihoodResultDto {
  dealId: string;
  likelihood: number;
  reasoning: string;
}

export interface FiasLeadsAiSummaryResultDto {
  entityType: 'contact' | 'company';
  entityId: string;
  summary: string;
}

// Global cross-entity search.

export interface FiasLeadsSearchDealRefDto {
  id: string;
  name: string;
  value: number;
  currency: string;
  status: string;
  stageName: string;
}

export interface FiasLeadsSearchContactResultDto {
  contactId: string;
  firstName: string;
  lastName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  title: string | null;
  companyId: string | null;
  companyName: string | null;
  /** Deals this contact is attached to (primary contact or a participant). */
  deals: FiasLeadsSearchDealRefDto[];
}

export interface FiasLeadsSearchCompanyResultDto {
  companyId: string;
  name: string;
  domain: string | null;
  industry: string | null;
  contactCount: number;
  dealCount: number;
}

export interface FiasLeadsSearchDealResultDto {
  id: string;
  name: string;
  value: number;
  currency: string;
  status: string;
  stageName: string;
  contactId: string | null;
  contactName: string | null;
  companyId: string | null;
  companyName: string | null;
}

export interface FiasLeadsGlobalSearchResultDto {
  query: string;
  contacts: FiasLeadsSearchContactResultDto[];
  companies: FiasLeadsSearchCompanyResultDto[];
  deals: FiasLeadsSearchDealResultDto[];
}

// Dedup.

export interface FiasLeadsDuplicateGroupDto {
  signature: string;
  contactIds: string[];
}

// Cross-arche presence.

export interface FiasLeadsCrossArchePresenceDto {
  contactPresence: Record<string, boolean>;
  companyPresence: Record<string, boolean>;
}

// Create / Update inputs.

export interface FiasLeadsCreateCompanyInput {
  name: string;
  domain?: string;
  industry?: string;
  website?: string;
  description?: string;
  ownerPrincipalId?: string;
  tags?: string[];
}

export interface FiasLeadsUpdateCompanyInput {
  name?: string;
  domain?: string | null;
  industry?: string | null;
  website?: string | null;
  description?: string | null;
  tags?: string[];
}

export interface FiasLeadsCreateContactInput {
  firstName: string;
  lastName?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  title?: string;
  notes?: string;
  companyId?: string;
  /** Raw company-name provenance text (import path). */
  companyName?: string | null;
  ownerPrincipalId?: string;
  lifecycleStage?: string | null;
  tags?: string[];
  altEmails?: Fias360AltContactValueDto[];
  altPhones?: Fias360AltContactValueDto[];
  extension?: {
    leadScore?: number | null;
    aiSummary?: string | null;
    lastContactedAt?: string | null;
  };
}

export interface FiasLeadsUpdateContactInput {
  firstName?: string;
  lastName?: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  title?: string | null;
  notes?: string | null;
  companyId?: string | null;
  /** Raw company-name provenance text. Changing `companyId` without
   * this field auto-clears the stored text server-side. */
  companyName?: string | null;
  lifecycleStage?: string | null;
  tags?: string[];
  altEmails?: Fias360AltContactValueDto[];
  altPhones?: Fias360AltContactValueDto[];
  extension?: {
    leadScore?: number | null;
    aiSummary?: string | null;
    lastContactedAt?: string | null;
  };
}

export interface FiasLeadsCreatePipelineInput {
  name: string;
  isDefault?: boolean;
  /** Stale-deal alert threshold in days; omit or `null` for no alerts. */
  staleAfterDays?: number | null;
  stages?: Array<{ name: string; stageType?: FiasLeadsPipelineStageType; color?: string }>;
}

export interface FiasLeadsSendDealEmailInput {
  /** An Email-arche connection id — the mailbox the send goes out through. */
  accountId: string;
  to: Array<{ email: string; name?: string }>;
  cc?: Array<{ email: string; name?: string }>;
  bcc?: Array<{ email: string; name?: string }>;
  subject: string;
  text?: string;
  html?: string;
}

export interface FiasLeadsUpdatePipelineInput {
  name?: string;
  isDefault?: boolean;
  /**
   * Stale-deal alert threshold in days. Send `null` to turn alerts off;
   * OMIT the key to leave the current threshold untouched. The server
   * distinguishes the two, so don't collapse `undefined` to `null`.
   */
  staleAfterDays?: number | null;
}

export interface FiasLeadsCreateDealInput {
  pipelineId: string;
  stageId: string;
  contactId?: string | null;
  companyId?: string | null;
  name: string;
  value?: number;
  currency?: string;
  expectedCloseDate?: string | null;
  ownerPrincipalId?: string | null;
  notes?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface FiasLeadsUpdateDealInput {
  pipelineId?: string;
  stageId?: string;
  contactId?: string | null;
  companyId?: string | null;
  name?: string;
  value?: number;
  currency?: string;
  expectedCloseDate?: string | null;
  actualCloseDate?: string | null;
  winLikelihood?: number | null;
  lostReason?: string | null;
  notes?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  ownerPrincipalId?: string | null;
}

export interface FiasLeadsCreateTaskInput {
  title: string;
  description?: string;
  priority?: FiasLeadsTaskPriority;
  dueAt?: string | null;
  reminderAt?: string | null;
  assigneePrincipalId?: string | null;
  regardingType?: FiasLeadsTaskRegardingType;
  regardingId?: string;
}

export interface FiasLeadsUpdateTaskInput {
  title?: string;
  description?: string;
  priority?: FiasLeadsTaskPriority;
  dueAt?: string | null;
  reminderAt?: string | null;
  assigneePrincipalId?: string | null;
  regardingType?: FiasLeadsTaskRegardingType | null;
  regardingId?: string | null;
}

export interface FiasLeadsCreateActivityInput {
  kind: FiasLeadsActivityKind;
  regardingType: FiasLeadsActivityRegardingType;
  regardingId: string;
  subject: string;
  bodyMarkdown?: string;
  typeData?: Record<string, unknown>;
  occurredAt?: string;
}

export interface FiasLeadsCreateEmailTemplateInput {
  name: string;
  subject: string;
  bodyMarkdown?: string;
  variables?: string[];
  category?: FiasLeadsEmailTemplateCategory;
}

export interface FiasLeadsCreateAutomationInput {
  name: string;
  triggerType: string;
  triggerConfig?: Record<string, unknown>;
  actionType: string;
  actionConfig?: Record<string, unknown>;
  isActive?: boolean;
}

export interface FiasLeadsCreateCustomFieldInput {
  entityType: FiasLeadsCustomFieldEntityType;
  fieldName: string;
  fieldLabel: string;
  fieldType: FiasLeadsCustomFieldType;
  options?: unknown[];
  isRequired?: boolean;
  displayOrder?: number;
}

export interface FiasLeadsCreateSavedViewInput {
  entityType: FiasLeadsSavedViewEntityType;
  name: string;
  filters?: Record<string, unknown>;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc' | null;
  isDefault?: boolean;
}

export interface FiasLeadsCreateRelationshipInput {
  fromContactId: string;
  toContactId: string;
  relationshipType: FiasLeadsContactRelationshipType;
  notes?: string;
}

// ==========================================================================
// Vault Documents DTOs (Phase 12)
// ==========================================================================

export type VaultDocumentSensitivityDto = 'standard' | 'confidential' | 'proprietary';
export type VaultDocumentVisibilityDto = 'user-vault' | 'arche-private';

export type VaultDocumentStatusDto =
  | 'pending'
  | 'uploaded'
  | 'extracting'
  | 'available'
  | 'extraction_failed'
  | 'archived'
  | 'deleted';

export interface VaultDocumentActivityEntryDto {
  auditId: string;
  action: string;
  occurredAt: string;
  actor:
    | { kind: 'app'; archeId: string; name: string | null }
    /** `isYou`: the person is the viewer. On a shared (org) holder it can be
     *  another member, so "by you" is only ever said when this is true.
     *  Optional: absent from servers that predate it. */
    | { kind: 'person'; isYou?: boolean };
}

/** The access level a stored app grant confers. Absent → read. */
export function archeGrantAccess(
  scopeJson: { access?: string } | null | undefined,
): 'read' | 'write' {
  return scopeJson?.access === 'write' ? 'write' : 'read';
}

/** A file's own version-history choice (file areas §4.12). */
export type VaultHistoryPolicyDto = 'keep' | 'off';

/** A workspace's version-history default. */
export interface WorkspaceFileSettingsDto {
  historyEnabled: boolean;
  /** 1–10. */
  historyMaxVersions: number;
  updatedAt: string | null;
}

/** What one arche keeps for the user outside their files. */
export interface VaultArcheDataEntryDto {
  archeId: string;
  archeName: string | null;
  sandboxStorage: { objectCount: number; bytes: number } | null;
  dataStore: {
    collections: Array<{ name: string; documentCount: number; sizeBytes: number }>;
    workspaceCount: number;
    bytes: number;
  } | null;
  /** Its DataStore couldn't be read just now. */
  dataStoreUnavailable?: boolean;
  gameState: { keywordCount: number; bytes: number } | null;
}

export interface VaultArcheDataDto {
  arches: VaultArcheDataEntryDto[];
  /** DataStore arches past the per-request cap, not read this time. */
  truncatedDataStoreArches: number;
  /** The arches' DataStore could not be reached just now — its data is
   *  missing from `arches`, not absent. */
  dataStoreUnavailable?: true;
  /** All of the user's game state, whichever arche wrote it. */
  gameState: { keywordCount: number; bytes: number };
  vaultData: {
    holderId: string;
    byClass: Array<{ dataClass: string; bytes: number; rowCount: number }>;
    totalBytes: number;
    /** Only for the workspace's billing principal. */
    chargedThisMonthCredits: number | null;
  } | null;
}

/** One area's share of a workspace's file storage. */
export interface VaultStorageAreaDto {
  area: 'documents' | 'arche-saves' | 'my-fias';
  bytes: number;
  fileCount: number;
  /** Earlier versions kept, apart from current files. */
  historyBytes: number;
  historyCount: number;
  /** Within Arche Saves and My-Fias: by the arche the files belong to
   *  (`archeId` null = Fias's own input folders). Empty for Documents. */
  byArche: Array<{
    archeId: string | null;
    archeName: string | null;
    bytes: number;
    fileCount: number;
    historyBytes: number;
  }>;
}

export interface VaultStorageBreakdownDto {
  areas: VaultStorageAreaDto[];
  /** Files above the caller's sensitivity tier — one total, never broken down. */
  restricted: { fileCount: number; bytes: number };
  totalBytes: number;
  /** The storage meter's own figure — equal to `totalBytes`. */
  meteredBytes: number;
}

export interface VaultDocumentHistoryDto {
  /** The file's own choice; null = the workspace default. */
  policy: VaultHistoryPolicyDto | null;
  workspace: Pick<WorkspaceFileSettingsDto, 'historyEnabled' | 'historyMaxVersions'>;
  /** What applies to the next save. */
  effective: { keep: boolean; maxVersions: number };
  /** Whether the caller may turn this file's history off — an owner or admin. */
  canTurnOff?: boolean;
  /** Earlier versions kept, and their total size. */
  versionCount: number;
  historyBytes: number;
}

export interface VaultDocumentDto {
  documentId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  uploadedByPrincipalId: string;
  name: string;
  description: string | null;
  mimeType: string | null;
  sizeBytes: number;
  checksumSha256: string | null;
  s3Key: string;
  sensitivity: VaultDocumentSensitivityDto;
  status: VaultDocumentStatusDto;
  previousStatus: VaultDocumentStatusDto | null;
  tags: string[];
  /** Folder this document lives in. `null` = Holder root. The
   * server's `moveVaultDocument` updates this on relocation. */
  folderId: string | null;
  path: string | null;
  /** Provenance — the arche that wrote this document via
   * `saveGeneratedArtifactToVault`, or `null` for direct user uploads. */
  sourceArcheId: string | null;
  /** Cross-arche-picker visibility flag. `user-vault` rows appear in
   * cross-arche pickers (Email "Attach from Vault", etc.); `arche-
   * private` rows are scoped to their producing arche. Folder
   * placement is presentation only — code MUST NOT infer access from
   * the folder. See docs/vault/arche-files-policy.md. */
  visibility: VaultDocumentVisibilityDto;
  metadata: Record<string, unknown>;
  replacesDocumentId: string | null;
  /** First 200KB of extracted text (with truncation marker if cut). */
  extractedText: string | null;
  extractedAt: string | null;
  extractionError: string | null;
  ragEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  deletedAt: string | null;
  /** The app that last changed this file's content, when one did (display
   *  attribution; never an access gate). Absent/null = a person, or never
   *  edited in place. */
  lastWrittenByArcheId?: string | null;
  /** Display name of that app, resolved by the server from the app itself —
   *  NOT from the user's current grants, so it still reads correctly after
   *  the user removes the app's access (which is exactly when they are
   *  looking at this line). Absent on a server that predates it, and null
   *  when the app no longer exists. */
  lastWrittenByArcheName?: string | null;
  /** When the content was last changed in place. */
  lastWrittenAt?: string | null;
  /** The file's own history choice; null/absent = the workspace default. */
  historyPolicy?: VaultHistoryPolicyDto | null;
  /** The arche that made the file, when one did — the badge on its tile.
   *  Present on the Files list. */
  sourceArcheName?: string | null;
}

export interface VaultDocumentsStorageDto {
  holderId: string;
  holderType: 'personal' | 'org';
  totalStorageBytes: number;
  quotaBytes: number;
  fileCount: number;
  /** ISO 8601, or null when no upload has happened yet on this
   * Holder (the storage trigger hasn't fired). */
  updatedAt: string | null;
}

/** Billing-principal aggregate. Free-tier cap is enforced at this
 * level — the per-Holder `quotaBytes` is a display-only breakdown.
 * The UI's usage bar should bind to `principal.totalStorageBytes /
 * principal.quotaBytes`. */
/** The areas the host file dialogs browse (never My-Fias). */
export type VaultFileDialogArea = 'documents' | 'arche-saves';

/** Coarse file kinds — `VAULT_FILE_KINDS` (client mirror of `@fias/db-types`). */
export type VaultFileKindDto = VaultFileKind;

/** What an internal arche's INPUT upload is for (a chat avatar, a recording to
 *  transcribe). Fias files it into that purpose's own My-Fias folder, not the
 *  user's Documents. Mirrors `VAULT_INPUT_FOLDERS` in `@fias/db-types`. */
export type VaultInputPurposeDto =
  | 'ai-context'
  | 'reference-image'
  | 'chat-image'
  | 'voice-recording'
  | 'social-post-media'
  | 'contact-attachment'
  | 'outgoing-email-attachment';

export interface VaultFileDialogQuery {
  areas?: VaultFileDialogArea[];
  folderId?: string | null;
  search?: string;
  kinds?: VaultFileKindDto[];
  sort?: 'updated' | 'name' | 'size' | 'kind';
  direction?: 'asc' | 'desc';
  forArche?: boolean;
  /** Default 50, max 200. */
  limit?: number;
  offset?: number;
}

/** A row of the file dialogs' listing: the document plus where it sits and
 *  which arche made it (display only — never an access decision). */
export interface VaultFileDialogDocumentDto extends VaultDocumentDto {
  /** Path of its folder; `null` = the Holder root. */
  folderPath: string | null;
  sourceArcheName: string | null;
}

/** Where the user chose to save, in the Save dialog: a folder of their
 *  Documents, a name, and what to do about a clash. */
export interface VaultSaveTargetDto {
  holderId: string;
  folderId: string | null;
  name: string;
  onConflict: 'fail' | 'keep-both' | 'replace';
  replace?: { documentId: string; expectedRevision: string; keepPreviousVersion: boolean };
}

/** What an arche keeps after a save: nothing, "open again", or "keep editing". */
export type VaultContinuedAccess = 'none' | 'read' | 'write';

export interface VaultSaveToDocumentsInitBody {
  holderId: string;
  folderId: string | null;
  name: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  /** The arche whose output this is (plugin or internal) — its provenance. */
  onBehalfOfArcheId?: string;
  onConflict: 'fail' | 'keep-both' | 'replace';
  replace?: { documentId: string; expectedRevision: string; keepPreviousVersion?: boolean };
  access?: VaultContinuedAccess;
}

export type VaultSaveToDocumentsInitDto = {
  document: VaultDocumentDto;
  finalName: string;
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
  expiresAt: string;
  access: VaultContinuedAccess;
} & ({ kind: 'new' } | { kind: 'replace'; saveId: string });

export interface VaultSaveToDocumentsFinalizeBody {
  holderId: string;
  documentId: string;
  checksumSha256: string;
  /** The arche whose output this is (plugin or internal) — its provenance. */
  onBehalfOfArcheId?: string;
  access?: VaultContinuedAccess;
  replace?: {
    saveId: string;
    expectedRevision: string;
    folderId: string | null;
    name: string;
    mimeType: string;
    keepPreviousVersion?: boolean;
  };
}

/** An arche that can open a file, and why (the Replace row lists them). */
export interface VaultEffectiveArcheAccessDto {
  archeId: string;
  archeName: string | null;
  access: 'read' | 'write';
  via: 'grant' | 'approval';
}

/** A file a save would collide with (409 `NAME_EXISTS` / `REPLACE_TARGET_CHANGED`). */
export interface VaultSaveConflictDto {
  documentId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number;
  sensitivity: VaultDocumentSensitivityDto;
  status: VaultDocumentStatusDto;
  updatedAt: string;
  revision: string;
  sourceArcheId: string | null;
  sourceArcheName: string | null;
  arches: VaultEffectiveArcheAccessDto[];
  /** Whether Replace keeps the current version by default — the file's own
   *  history choice, then the workspace's. */
  keepsHistory?: boolean;
  /** Whether the caller may replace it WITHOUT keeping the current version —
   *  an owner or admin only. */
  mayTurnOffHistory?: boolean;
}

/** The colliding files carried by a Save refusal, or `[]`. */
export function vaultSaveConflictsOf(err: unknown): VaultSaveConflictDto[] {
  if (!(err instanceof ApiError)) return [];
  const body = err.body as { conflicts?: unknown } | undefined;
  return Array.isArray(body?.conflicts) ? (body.conflicts as VaultSaveConflictDto[]) : [];
}

/** One folder row from `GET /vault/folders`. The recursive tree is
 * walked client-side via repeated parent_folder_id pointers. */
/** Free metadata filters shared by the workspace list and its By Type /
 *  By Arche summaries (routes/vault/documents.ts `listQuerySchema`). */
export interface VaultDocumentMetadataFilters {
  status?: VaultDocumentStatusDto;
  tags?: string[];
  includeArchived?: boolean;
  includeDeleted?: boolean;
  /** When true, return ONLY soft-deleted rows (Trash view). */
  deletedOnly?: boolean;
  folderId?: string | null;
  /** Case-insensitive substring of the name. */
  search?: string;
  kinds?: readonly VaultFileKindDto[];
  areas?: readonly VaultAreaDto[];
  minSizeBytes?: number;
  maxSizeBytes?: number;
  /** ISO-8601 instants. */
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  /** An arche id, or `none` for files no arche made. */
  sourceArche?: string;
  sort?: 'updated' | 'name' | 'size' | 'kind';
  direction?: 'asc' | 'desc';
}

/** A file area — the client mirror is `@/lib/vault-areas`. */
export type VaultAreaDto = 'documents' | 'arche-saves' | 'my-fias';

/** A few of a group's newest files, for its card. */
export interface VaultDocumentGroupSampleDto {
  documentId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number;
  updatedAt: string;
}

export interface VaultDocumentGroupTotalsDto {
  count: number;
  totalBytes: number;
}

export interface VaultDocumentKindGroupsDto {
  groups: Array<{
    kind: VaultFileKindDto;
    count: number;
    totalBytes: number;
    newestAt: string | null;
    sample: VaultDocumentGroupSampleDto[];
  }>;
  total: VaultDocumentGroupTotalsDto;
}

export interface VaultDocumentArcheGroupsDto {
  groups: Array<{
    /** The arche id, or `uploads` / `fias` for files no arche made. */
    key: string;
    group: 'arche' | 'uploads' | 'fias';
    archeId: string | null;
    archeName: string | null;
    count: number;
    totalBytes: number;
    newestAt: string | null;
    sample: VaultDocumentGroupSampleDto[];
  }>;
  /** Groups past the cap, rolled up. */
  other: { count: number; totalBytes: number; groupCount: number } | null;
  total: VaultDocumentGroupTotalsDto;
}

export interface VaultDesktopFolderViewDto {
  mode:
    | 'icons-xl'
    | 'icons-lg'
    | 'icons-md'
    | 'icons-sm'
    | 'list'
    | 'details'
    | 'columns'
    | 'gallery';
  sort: 'name' | 'modified' | 'type' | 'size';
  dir: 'asc' | 'desc';
}

export interface VaultDesktopLayoutDto {
  v: 1;
  containers: Record<string, Record<string, { c: number; r: number }>>;
  windows: Record<
    string,
    { x: number; y: number; w: number; h: number; z: number; min: boolean; folder: string }
  >;
  views: Record<string, VaultDesktopFolderViewDto>;
}

export interface VaultDesktopLayoutPatchDto {
  holder: string;
  baseRevision: number;
  upserts: Array<{ container: string; item: string; c: number; r: number }>;
  removals: Array<{ container: string; item: string }>;
  windows: Array<
    | {
        id: string;
        x: number;
        y: number;
        w: number;
        h: number;
        z: number;
        min: boolean;
        folder: string;
      }
    | { id: string; remove: true }
  >;
  views: Array<
    ({ container: string } & VaultDesktopFolderViewDto) | { container: string; remove: true }
  >;
}

/** The query string for a workspace listing or summary. */
function vaultDocumentListParams(
  holderId: string,
  options?: VaultDocumentMetadataFilters,
): URLSearchParams {
  const params = new URLSearchParams({ holder: holderId });
  if (!options) return params;
  if (options.status) params.set('status', options.status);
  if (options.tags && options.tags.length > 0) params.set('tags', options.tags.join(','));
  if (options.includeArchived) params.set('includeArchived', 'true');
  if (options.includeDeleted) params.set('includeDeleted', 'true');
  if (options.deletedOnly) params.set('deletedOnly', 'true');
  if (options.folderId === null) params.set('folderId', 'root');
  else if (typeof options.folderId === 'string') params.set('folderId', options.folderId);
  if (options.search?.trim()) params.set('search', options.search.trim());
  if (options.kinds && options.kinds.length > 0) params.set('kinds', options.kinds.join(','));
  if (options.areas && options.areas.length > 0) params.set('areas', options.areas.join(','));
  if (options.minSizeBytes !== undefined) params.set('minSizeBytes', String(options.minSizeBytes));
  if (options.maxSizeBytes !== undefined) params.set('maxSizeBytes', String(options.maxSizeBytes));
  if (options.createdAfter) params.set('createdAfter', options.createdAfter);
  if (options.createdBefore) params.set('createdBefore', options.createdBefore);
  if (options.updatedAfter) params.set('updatedAfter', options.updatedAfter);
  if (options.updatedBefore) params.set('updatedBefore', options.updatedBefore);
  if (options.sourceArche) params.set('sourceArche', options.sourceArche);
  if (options.sort) params.set('sort', options.sort);
  if (options.direction) params.set('direction', options.direction);
  return params;
}

export interface VaultDocumentFolderDto {
  folderId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  parentFolderId: string | null;
  name: string;
  path: string;
  isProtected: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VaultDocumentsPrincipalUsageDto {
  billingPrincipalUserId: string;
  totalStorageBytes: number;
  fileCount: number;
  quotaBytes: number;
}

/** Response from POST /vault/documents/upload-init. The client PUTs
 * bytes to `uploadUrl` with `requiredHeaders` exactly (any divergence
 * fails the S3 signature), then calls finalizeVaultDocumentUpload. */
export interface VaultDocumentUploadInitDto {
  document: VaultDocumentDto;
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
  /** ISO 8601 — the presigned URL expires at this time (5 min TTL). */
  expiresAt: string;
}

export interface VaultDocumentDownloadDto {
  url: string;
  expiresAt: string;
  /** What the URL was signed for — `inline` only when asked AND safe. */
  disposition?: 'attachment' | 'inline';
  /** Slimmed view of the doc for the consuming UI's title bar. */
  document: {
    documentId: string;
    name: string;
    mimeType: string | null;
    sizeBytes: number;
  };
}

export interface VaultDocumentSearchMatchDto {
  documentId: string;
  documentName: string;
  chunkId: string;
  chunkIndex: number;
  similarity: number;
  content: string | null;
}

// ==========================================================================
// Vault Documents — cross-arche attachment DTOs (Phase 12e-1)
// ==========================================================================

/**
 * Client mirror of `VaultDocumentReferenceTargetType` (@fias/db-types). Kept
 * local because the client bundle does not import db-types; pinned against the
 * canonical union and the database CHECK by
 * `tests/architecture/vault-document-reference-targets.test.ts`.
 */
export type VaultDocumentReferenceTargetTypeDto =
  | 'vault_company'
  | 'vault_business_contact'
  | 'vault_fias360_renewal'
  | 'travel_trip'
  | 'travel_event'
  | 'travel_item'
  | 'fias_transfer_space';

export interface VaultDocumentReferenceDto {
  referenceId: string;
  documentId: string;
  holderId: string;
  holderType: 'personal' | 'org';
  regardingType: VaultDocumentReferenceTargetTypeDto;
  regardingId: string;
  attachedByPrincipalId: string;
  attachedAt: string;
  detachedAt: string | null;
}

/** Phase 12e-2 — document_share consent projection returned by
 * GET /vault/documents/:documentId/shares. */
export interface VaultDocumentShareDto {
  consentId: string;
  granterHolderId: string;
  granteeHolderId: string;
  documentId: string;
  grantedAt: string;
  expiresAt: string | null;
  grantedByPrincipalId: string;
}

// Email Types
// ==========================================================================

export interface EmailAccount {
  id: string;
  userId: string;
  emailAddress: string;
  displayName: string | null;
  isPrimary: boolean;
  status: 'active' | 'suspended' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateAccountInput {
  username: string;
  displayName?: string;
}

export interface EmailAccountSettings {
  id: string;
  accountId: string;
  theme: 'light' | 'dark' | 'system';
  readingPane: 'right' | 'bottom' | 'off';
  messagesPerPage: number;
  defaultSignatureId: string | null;
  undoSendDelaySeconds: number;
  conversationView: boolean;
  autoAdvance: 'next' | 'previous' | 'list';
  notificationMode: 'all' | 'important' | 'none';
  digestFrequency: 'realtime' | 'hourly' | 'daily' | 'off';
  timeFormat: '12h' | '24h';
  createdAt: string;
  updatedAt: string;
}

export interface EmailAccountSettingsInput {
  theme?: 'light' | 'dark' | 'system';
  readingPane?: 'right' | 'bottom' | 'off';
  messagesPerPage?: number;
  defaultSignatureId?: string | null;
  undoSendDelaySeconds?: number;
  conversationView?: boolean;
  autoAdvance?: 'next' | 'previous' | 'list';
  notificationMode?: 'all' | 'important' | 'none';
  digestFrequency?: 'realtime' | 'hourly' | 'daily' | 'off';
  timeFormat?: '12h' | '24h';
}

export interface EmailAccountModule {
  id: string;
  accountId: string;
  moduleName: string;
  enabled: boolean;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAddress {
  email: string;
  name?: string;
}

export interface EmailMessage {
  id: string;
  accountId: string;
  threadId: string;
  folderId: string;
  fromAddress: string;
  fromName: string | null;
  toAddresses: EmailAddress[];
  ccAddresses: EmailAddress[];
  bccAddresses: EmailAddress[];
  subject: string;
  bodyText: string | null;
  bodyHtml: string | null;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  isDraft: boolean;
  importance: 'low' | 'normal' | 'high';
  inReplyTo: string | null;
  referencesChain: string[];
  hasAttachments: boolean;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentId: string | null;
  isInline: boolean;
  createdAt: string;
}

export interface EmailLabelRef {
  id: string;
  name: string;
  color: string;
}

export interface EmailMessageDetail extends EmailMessage {
  attachments: EmailAttachment[];
  labels: EmailLabelRef[];
}

export interface EmailMessageListOptions {
  accountId: string;
  folderId?: string;
  labelId?: string;
  threadId?: string;
  isRead?: boolean;
  isStarred?: boolean;
  importance?: 'low' | 'normal' | 'high';
  limit?: number;
  offset?: number;
  sort?: string;
}

export interface EmailMessageListResult {
  messages: EmailMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailComposeInput {
  accountId: string;
  threadId?: string;
  folderId?: string;
  toAddresses: EmailAddress[];
  ccAddresses?: EmailAddress[];
  bccAddresses?: EmailAddress[];
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
  isDraft?: boolean;
  importance?: 'low' | 'normal' | 'high';
  scheduledFor?: string;
  /** IDs of email_attachments rows uploaded during compose. The send
   * pipeline binds them to this message transactionally. */
  attachmentIds?: string[];
}

export interface EmailMessageUpdateInput {
  isRead?: boolean;
  isStarred?: boolean;
  folderId?: string;
  importance?: 'low' | 'normal' | 'high';
}

export interface EmailReplyInput {
  accountId: string;
  bodyText?: string;
  bodyHtml?: string;
  ccAddresses?: EmailAddress[];
  bccAddresses?: EmailAddress[];
  replyAll?: boolean;
  attachmentIds?: string[];
}

export interface EmailForwardInput {
  accountId: string;
  toAddresses: EmailAddress[];
  bodyText?: string;
  bodyHtml?: string;
  attachmentIds?: string[];
}

export interface EmailBulkActionInput {
  accountId: string;
  action:
    | 'read'
    | 'unread'
    | 'star'
    | 'unstar'
    | 'archive'
    | 'trash'
    | 'move'
    | 'label'
    | 'unlabel';
  messageIds: string[];
  folderId?: string;
  labelId?: string;
}

export interface EmailThread {
  id: string;
  accountId: string;
  subject: string;
  lastMessageAt: string | null;
  messageCount: number;
  isRead: boolean;
  isStarred: boolean;
  snippet: string | null;
  smartCategory: 'primary' | 'feed' | 'paper_trail' | 'notifications' | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailThreadDetail extends EmailThread {
  messages: (EmailMessage & { attachments: EmailAttachment[] })[];
}

export interface EmailThreadListOptions {
  accountId: string;
  folderId?: string;
  labelId?: string;
  isRead?: boolean;
  isStarred?: boolean;
  smartCategory?: string;
  limit?: number;
  offset?: number;
  sort?: string;
}

export interface EmailThreadListResult {
  threads: EmailThread[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailThreadUpdateInput {
  isRead?: boolean;
  isStarred?: boolean;
  smartCategory?: 'primary' | 'feed' | 'paper_trail' | 'notifications' | null;
}

export interface EmailBulkThreadActionInput {
  accountId: string;
  action: 'read' | 'unread' | 'star' | 'unstar' | 'archive' | 'trash';
  threadIds: string[];
}

export interface EmailFolderBase {
  id: string;
  accountId: string;
  name: string;
  folderType: 'system' | 'custom';
  systemRole: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive' | null;
  color: string | null;
  sortOrder: number;
  parentFolderId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailFolder extends EmailFolderBase {
  unreadCount: number;
  totalCount: number;
}

export interface EmailCreateFolderInput {
  accountId: string;
  name: string;
  color?: string;
  sortOrder?: number;
  parentFolderId?: string;
}

export interface EmailUpdateFolderInput {
  name?: string;
  color?: string;
  sortOrder?: number;
}

export interface EmailLabelBase {
  id: string;
  accountId: string;
  name: string;
  color: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailLabel extends EmailLabelBase {
  messageCount: number;
}

export interface EmailCreateLabelInput {
  accountId: string;
  name: string;
  color?: string;
  sortOrder?: number;
}

export interface EmailUpdateLabelInput {
  name?: string;
  color?: string;
  sortOrder?: number;
}

// Email Contacts

export interface EmailContact {
  id: string;
  accountId: string;
  emailAddress: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  phone: string | null;
  notes: string | null;
  avatarUrl: string | null;
  isFavorite: boolean;
  lastContactedAt: string | null;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailContactListOptions {
  accountId: string;
  search?: string;
  isFavorite?: boolean;
  limit?: number;
  offset?: number;
  sort?: 'name' | 'lastContacted' | 'contactCount';
}

export interface EmailContactListResult {
  contacts: EmailContact[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailCreateContactInput {
  accountId: string;
  emailAddress: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  notes?: string;
  avatarUrl?: string;
  isFavorite?: boolean;
}

export interface EmailUpdateContactInput {
  emailAddress?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  notes?: string;
  avatarUrl?: string;
  isFavorite?: boolean;
}

export interface EmailContactGroup {
  id: string;
  accountId: string;
  name: string;
  description: string | null;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateContactGroupInput {
  accountId: string;
  name: string;
  description?: string;
}

export interface EmailUpdateContactGroupInput {
  name?: string;
  description?: string;
}

export interface EmailContactGroupMember extends EmailContact {
  addedAt: string;
}

// Email Filters

export interface EmailFilterCondition {
  field: 'from' | 'to' | 'subject' | 'body' | 'has_attachment';
  operator: 'contains' | 'not_contains' | 'equals' | 'starts_with' | 'ends_with' | 'matches_regex';
  value: string;
}

export interface EmailFilterAction {
  type:
    | 'move_to_folder'
    | 'add_label'
    | 'mark_read'
    | 'mark_starred'
    | 'archive'
    | 'trash'
    | 'forward_to'
    | 'set_category';
  value: string;
}

export interface EmailFilter {
  id: string;
  accountId: string;
  name: string;
  conditions: EmailFilterCondition[];
  actions: EmailFilterAction[];
  isEnabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateFilterInput {
  accountId: string;
  name: string;
  conditions: EmailFilterCondition[];
  actions: EmailFilterAction[];
  isEnabled?: boolean;
  sortOrder?: number;
}

export interface EmailUpdateFilterInput {
  name?: string;
  conditions?: EmailFilterCondition[];
  actions?: EmailFilterAction[];
  isEnabled?: boolean;
  sortOrder?: number;
}

// Email Templates

export interface EmailTemplate {
  id: string;
  accountId: string;
  name: string;
  subject: string | null;
  bodyHtml: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateTemplateInput {
  accountId: string;
  name: string;
  subject?: string;
  bodyHtml: string;
  category?: string;
}

export interface EmailUpdateTemplateInput {
  name?: string;
  subject?: string;
  bodyHtml?: string;
  category?: string;
}

// Email Snippets

export interface EmailSnippet {
  id: string;
  accountId: string;
  trigger: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateSnippetInput {
  accountId: string;
  trigger: string;
  body: string;
}

export interface EmailUpdateSnippetInput {
  trigger?: string;
  body?: string;
}

// Email Signatures

export interface EmailSignature {
  id: string;
  accountId: string;
  name: string;
  bodyHtml: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailCreateSignatureInput {
  accountId: string;
  name: string;
  bodyHtml: string;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface EmailUpdateSignatureInput {
  name?: string;
  bodyHtml?: string;
  isDefault?: boolean;
  sortOrder?: number;
}

// Email Search

export interface EmailSearchOptions {
  accountId: string;
  q?: string;
  from?: string;
  to?: string;
  subject?: string;
  hasAttachment?: boolean;
  isRead?: boolean;
  isStarred?: boolean;
  folder?: string;
  label?: string;
  dateFrom?: string;
  dateTo?: string;
  importance?: 'low' | 'normal' | 'high';
  limit?: number;
  offset?: number;
}

export interface EmailSearchResult {
  messages: EmailMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailNaturalSearchResult extends EmailSearchResult {
  parsedFilters: Record<string, string>;
}

// Email Screener

export interface EmailScreenedSender {
  id: string;
  accountId: string;
  senderAddress: string;
  senderName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  screenedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface EmailScreenedSenderTrigger {
  id: string;
  subject: string;
  snippet: string | null;
  fromAddress: string;
  fromName: string | null;
  createdAt: string;
}

export interface EmailScreenedSenderWithMessage extends EmailScreenedSender {
  triggeringMessage: EmailScreenedSenderTrigger | null;
}

export interface EmailScreenerApproveResult {
  sender: EmailScreenedSender;
  movedMessages: number;
}

export interface EmailScreenerRejectResult {
  sender: EmailScreenedSender;
  deletedMessages: number;
}

// Email Scheduling

export interface EmailScheduledMessage {
  id: string;
  messageId: string;
  scheduledFor: string;
  status: 'pending' | 'sent' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface EmailScheduleInput {
  accountId: string;
  messageId: string;
  scheduledFor: string;
}

export interface EmailSnoozedMessage {
  id: string;
  messageId: string;
  snoozeUntil: string;
  originalFolderId: string | null;
  status: 'snoozed' | 'returned' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface EmailSnoozeInput {
  accountId: string;
  messageId: string;
  snoozeUntil: string;
}

// Email AI

export interface EmailAIComposeInput {
  accountId: string;
  prompt: string;
  context?: {
    replyToMessageId?: string;
    tone?: 'formal' | 'casual' | 'friendly' | 'professional';
    length?: 'short' | 'medium' | 'long';
  };
}

export interface EmailAIComposeResult {
  subject: string;
  bodyHtml: string;
}

export interface EmailAIReplySuggestion {
  label: string;
  bodyHtml: string;
}

export interface EmailAISummarizeResult {
  summary: string;
  actionItems: string[];
  keyDecisions: string[];
}

export interface EmailAICategoryResult {
  messageId: string;
  category: 'primary' | 'feed' | 'paper_trail' | 'notifications';
}

export interface EmailAIPhishingResult {
  risk: 'low' | 'medium' | 'high';
  signals: string[];
}

// Email Attachments

export interface EmailAttachmentMeta {
  id: string;
  accountId: string;
  messageId: string | null;
  /** The Vault document this attachment references. NULL only after the
   * vault hard-delete worker has purged the bytes; the snapshot fields
   * (filename / mimeType / sizeBytes) keep the row renderable. */
  documentId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentId: string | null;
  isInline: boolean;
  /** Set on sent-message attachments that the user removed after send;
   * the row stays for historical rendering. */
  deletedAt: string | null;
  createdAt: string;
}

export interface AttachEmailDocumentInput {
  accountId: string;
  messageId?: string;
  /** The vault_documents.document_id returned by the canonical Vault
   * upload flow (init → S3 PUT → finalize). Must belong to the caller's
   * Personal Holder. */
  documentId: string;
  contentId?: string;
  isInline?: boolean;
}

export interface AttachEmailDocumentResult {
  attachment: EmailAttachmentMeta;
}

export interface EmailAttachmentDownloadResult {
  downloadUrl: string;
  expiresAt: string;
}

// =============================================================================
// Sync Types
// =============================================================================

export interface SyncConnection {
  id: string;
  provider: 'google' | 'microsoft' | 'apple';
  providerAccountName: string | null;
  providerAccountEmail: string | null;
  authType: 'oauth2' | 'app_password';
  scopes: string[];
  status: string;
  lastError: string | null;
  createdAt: string;
}

/** A connected external mailbox (Email arche). Credentials are never returned. */
export interface EmailConnectionCapabilities {
  folders: boolean;
  writeback: boolean;
  serverSentFiling: boolean;
  snooze: boolean;
  search: boolean;
}
export interface EmailConnection {
  accountId: string;
  emailAddress: string;
  displayName: string | null;
  isPrimary: boolean;
  /** User-controlled display order within the account list (ascending). */
  sortOrder: number;
  authType: 'oauth2' | 'password' | 'app_password';
  provider: 'gmail' | 'microsoft' | 'generic';
  transportStrategy: string;
  capabilities: EmailConnectionCapabilities;
  incomingProtocol: 'imap' | 'pop3' | null;
  // Transport details (non-secret) — present for generic IMAP/POP3 accounts,
  // null for pure-REST OAuth providers (Gmail/Graph). Credentials are never sent.
  incomingHost: string | null;
  incomingPort: number | null;
  incomingSecurity: 'ssl_tls' | 'starttls' | 'none' | null;
  outgoingHost: string | null;
  outgoingPort: number | null;
  outgoingSecurity: 'ssl_tls' | 'starttls' | 'none' | null;
  credentialExpiresAt: string | null;
  scopes: string[];
  modules: Record<string, unknown>;
  syncStatus: 'pending' | 'syncing' | 'active' | 'error';
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  status: 'active' | 'suspended' | 'error' | 'revoked';
  createdAt: string;
  updatedAt: string;
}

export type EmailSwipeActionDto = 'none' | 'trash' | 'archive' | 'read' | 'star';

/**
 * Holder-wide (cross-account) email UI preferences — message-list preview
 * length, mobile swipe gestures, and the Scheduled/Snooze feature opt-ins.
 * Stored server-side in email_user_settings; see GET/PUT
 * /arches/email/user-settings.
 */
export type EmailReplyQuoteOrderDto = 'above' | 'below' | 'none';

export interface EmailUserSettingsDto {
  previewLines: 1 | 2 | 3;
  swipe: { enabled: boolean; left: EmailSwipeActionDto; right: EmailSwipeActionDto };
  features: { scheduleEnabled: boolean; snoozeEnabled: boolean };
  /** Undo-send window (off by default). */
  undoSend: { enabled: boolean; seconds: number };
  /** Auto-insert the default signature on new/reply (off by default). */
  autoSignature: boolean;
  /** Reply quote placement (inline-above by default). */
  replyQuoteOrder: EmailReplyQuoteOrderDto;
  /** Collapse the message list into conversation threads (off by default). */
  conversationThreadingEnabled: boolean;
  /** Calendar meeting invites are added to; null = the default calendar. */
  inviteCalendarId: string | null;
  /** Contact Book contacts count as trusted senders (on by default). */
  trustContacts: boolean;
  /** Reporting spam blocks the sender in ALL mailboxes (off by default). */
  blockAcrossMailboxes: boolean;
}
export interface EmailImapConnectInput {
  emailAddress: string;
  displayName?: string;
  incomingProtocol: 'imap' | 'pop3';
  incomingHost: string;
  incomingPort: number;
  incomingSecurity: 'ssl_tls' | 'starttls' | 'none';
  outgoingHost: string;
  outgoingPort: number;
  outgoingSecurity: 'ssl_tls' | 'starttls' | 'none';
  username: string;
  password: string;
}
export interface EmailAutoconfigSuggestion {
  source: 'ispdb' | 'mx-ispdb' | 'guess';
  incomingProtocol: 'imap';
  incomingHost: string;
  incomingPort: number;
  incomingSecurity: 'ssl_tls' | 'starttls' | 'none';
  outgoingHost: string;
  outgoingPort: number;
  outgoingSecurity: 'ssl_tls' | 'starttls' | 'none';
}

export interface EmailAddressDto {
  email: string;
  name?: string | null;
}
export interface EmailFolderDto {
  folderId: string;
  accountId: string;
  name: string;
  folderType: 'system' | 'custom';
  systemRole: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive' | null;
  serverPath: string | null;
  color: string | null;
  sortOrder: number;
  parentFolderId: string | null;
  messageCount: number;
  /**
   * Live (non-deleted) unread message count for the folder. Optional so older
   * cached payloads / test fixtures without the field are treated as 0 unread.
   */
  unreadCount?: number;
  /**
   * True once this folder's history has been walked to the bottom (no older mail
   * left on the server to download). Drives whether the mailbox shows the "Load
   * more / Load all" controls — when every in-view folder is complete, there's
   * nothing left to fetch and the controls are hidden. Optional so older cached
   * payloads / test fixtures without the field are treated as "not complete".
   */
  backfillComplete?: boolean;
}
/**
 * Scope for the bulk-by-filter ("select all in folder") actions — the same
 * dimensions the message list uses. Must name a folder: `folderId` for a
 * single-account view, or `folderSystemRole` (omit `accountId`) for the unified
 * inbox.
 */
/** What a spam report acts on — its senders are blocked and ALL their mail moves to Trash. */
export type EmailSpamReportTarget = (
  | { accountId: string; messageIds: string[] }
  | { accountId: string; senderAddresses: string[] }
  | { scope: EmailBulkFilterScope }
) & {
  /** Block in every mailbox; omitted = the blockAcrossMailboxes setting. */
  allMailboxes?: boolean;
  /** Refuse (409 KNOWN_SENDERS) if a sender is someone the user knows. */
  checkKnownSenders?: boolean;
};
/** A sender's mail sitting in Trash (offered for restore after unblocking). */
export interface EmailSenderTrashedMail {
  accountId: string;
  senderAddress: string;
  count: number;
}
export interface EmailSpamMovedMessage {
  messageId: string;
  accountId: string;
  fromFolderId: string;
}
export interface EmailSpamBlockedSender {
  accountId: string;
  senderAddress: string;
  previous: {
    status: 'pending' | 'approved' | 'rejected';
    source: 'screener' | 'ai_spam' | 'failed_auth' | 'user' | null;
    reason: string | null;
  } | null;
}
export interface EmailSpamReportResult {
  filed: number;
  blacklisted: number;
  senderCount: number;
  /** The distinct addresses reported (newer servers). */
  senders?: string[];
  moved: EmailSpamMovedMessage[];
  blocked: EmailSpamBlockedSender[];
  capped: boolean;
  /** When capped: continue with these senders rather than the original target. */
  remaining?: { accountId: string; senderAddresses: string[] }[];
  /** A later step failed after earlier ones committed (`moved`/`blocked` hold them). */
  error?: string;
}
export interface EmailSpamReportPreview {
  senderCount: number;
  messageCount: number;
  sampleSenders: string[];
  /** Senders the user knows, and why — worth confirming before blocking. */
  knownSenders?: EmailKnownSender[];
  /** The report would apply in every mailbox. */
  allMailboxes?: boolean;
}
/** Why reporting this sender deserves a confirmation. */
export interface EmailKnownSender {
  address: string;
  reasons: ('emailed' | 'contact' | 'allowed' | 'filed')[];
  /** 'filed': live messages from them kept in the user's own folders. */
  filedCount?: number;
}
export interface EmailBulkFilterScope {
  accountId?: string;
  folderId?: string;
  folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
  isRead?: boolean;
  isStarred?: boolean;
  hasAttachments?: boolean;
  screener?: boolean;
}
export interface EmailThreadDto {
  threadId: string;
  accountId: string;
  subject: string;
  lastMessageAt: string | null;
  messageCount: number;
  isRead: boolean;
  isStarred: boolean;
  snippet: string | null;
  /** FK → the holder's category set; null = Uncategorized. */
  categoryId: string | null;
}

export interface EmailCategoryDto {
  categoryId: string;
  holderId: string;
  name: string;
  color: string;
  sortOrder: number;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}
/** An account-scoped, Gmail-style label (many per message). */
export interface EmailLabelMailboxDto {
  labelId: string;
  accountId: string;
  name: string;
  color: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}
/** A label as it hangs off a message row/reading-pane (chip fields only). */
export interface EmailMessageLabelRefDto {
  labelId: string;
  name: string;
  color: string;
}
/** Structured filter a Fias-AI dynamic email list can be built from. */
export interface EmailAiListFilterInput {
  accountId?: string;
  folderSystemRole?: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';
  fromAddress?: string;
  unanswered?: boolean;
  isRead?: boolean;
  isStarred?: boolean;
  hasAttachments?: boolean;
  /** Free-text narrowing (same matching as email search). */
  query?: string;
  /** Restrict `query` to these fields; absent/empty = all. */
  searchFields?: Array<'from' | 'recipient' | 'subject' | 'body'>;
  receivedAfter?: string;
  receivedBefore?: string;
}

/**
 * The "More filters" dimensions — the advanced quick filters the mailbox
 * toolbar's popover exposes, shared by the list, search and grouping reads so a
 * filter narrows every view of the folder identically.
 */
export interface EmailAdvancedFilters {
  /** Reply state: `true` = I replied, `false` = never replied. Inbound inbox only. */
  answered?: boolean;
  /** Sender is (`true`) / is not (`false`) in the user's address book. */
  inAddressBook?: boolean;
  /** ISO-8601 instants — "newer than" / "older than" age bounds. */
  receivedAfter?: string;
  receivedBefore?: string;
}

export interface EmailMessageDto {
  messageId: string;
  accountId: string;
  threadId: string;
  folderId: string;
  fromAddress: string;
  fromName: string | null;
  toAddresses: EmailAddressDto[];
  subject: string;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  /** True for a Drafts-folder row. Deleting one takes the hard-discard path
   *  (`discardEmailMailboxDraft`), never a move-to-Trash. */
  isDraft?: boolean;
  receivedAt: string | null;
  // Outbound mail (Sent/Drafts) has no receivedAt — fall back to these for
  // ordering/date display so it isn't buried with a blank date.
  sentAt: string | null;
  // Approx. message size (body + attachments) — present on list/search reads only.
  sizeBytes?: number;
  // AI spam verdict (null unless classified) — drives the "flagged because…" chip.
  spamReason?: string | null;
  spamSource?: 'ai_spam' | 'failed_auth' | 'user_report' | 'blacklist' | null;
  spamScore?: number | null;
  // Provider-reported SPF/DKIM/DMARC results (null unless captured) — drives the
  // verified-sender badge. Server-parsed from the message headers, not content.
  spamAuthSummary?: EmailAuthResultsDto | null;
  // The message's labels (row/reading-pane chips). Present on list/search/detail
  // reads; absent (undefined) on the single-message flag/move responses.
  labels?: EmailMessageLabelRefDto[];
  createdAt: string;
}
/** Per-account out-of-office auto-reply config (Group G1). */
export interface EmailVacationSettingsDto {
  enabled: boolean;
  subject: string;
  body: string;
  /** ISO timestamps; null = unbounded on that side. */
  startAt: string | null;
  endAt: string | null;
}
/** Provider-reported email-authentication verdict values. */
export type EmailAuthVerdict =
  | 'pass'
  | 'fail'
  | 'softfail'
  | 'neutral'
  | 'temperror'
  | 'permerror'
  | 'none'
  | 'unknown';
/** Provider-reported SPF/DKIM/DMARC results parsed from the message headers. */
export interface EmailAuthResultsDto {
  spf: EmailAuthVerdict;
  dkim: EmailAuthVerdict;
  dmarc: EmailAuthVerdict;
  raw: string | null;
}
/** Combinable search-scope fields (Apple-Mail-style toggles). */
export type EmailSearchField = 'from' | 'recipient' | 'subject' | 'body';
/** One sender's grouping for the "By Sender" overview. */
export interface EmailSenderGroupDto {
  senderAddress: string;
  senderName: string | null;
  /** Total in the group within scope + exclusions (may exceed `messages.length`). */
  count: number;
  latestAt: string | null;
  latestSubject: string | null;
  /** Capped per-message rows for bulk actions (id + folder + flags). */
  messages: { messageId: string; folderId: string; isRead: boolean; isStarred: boolean }[];
}
/** One category's grouping for the "By Category" overview. `categoryId` is null
 *  for the Uncategorized group (threads with no category). */
export interface EmailCategoryGroupDto {
  categoryId: string | null;
  name: string;
  color: string | null;
  sortOrder: number | null;
  /** Total in the group within scope + exclusions (may exceed `messages.length`). */
  count: number;
  latestAt: string | null;
  latestSubject: string | null;
  /** Capped per-message rows for bulk actions (id + folder + flags). */
  messages: { messageId: string; folderId: string; isRead: boolean; isStarred: boolean }[];
}
export interface EmailMessageDetailDto extends EmailMessageDto {
  ccAddresses: EmailAddressDto[];
  bccAddresses: EmailAddressDto[];
  bodyText: string | null;
  bodyHtml: string | null;
  /**
   * True when the bodies above are a PREFIX of the stored message, because it
   * exceeded the detail read's per-body bound (`DETAIL_BODY_MAX_CHARS`).
   *
   * The bound exists so one oversized message can't push the response past API
   * Gateway's 10 MB ceiling — without it the reading pane gets an opaque 5xx
   * instead of a clipped body. A quote built from a clipped message is clipped
   * too, so the reading pane says so rather than letting the body just stop.
   */
  bodyTruncated: boolean;
  importance: 'low' | 'normal' | 'high';
  sentAt: string | null;
  /** Local parent message id when this is a reply (drives draft-resume threading). */
  inReplyTo: string | null;
  // Raw List-Unsubscribe / -Post header values (null when absent) — drive the
  // reading-pane unsubscribe action + one-click-vs-mailto method selection.
  listUnsubscribe?: string | null;
  listUnsubscribePost?: string | null;
}
/** A Vault-backed mailbox attachment (bytes live in the user's Vault). */
export interface EmailMailboxAttachmentDto {
  attachmentId: string;
  messageId: string | null;
  documentId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentId: string | null;
  isInline: boolean;
  createdAt: string;
}
/** One event parsed from a `.ics` invite, for the in-app meeting-invite preview. */
export interface CalendarInviteEventDto {
  title: string;
  description?: string;
  location?: string;
  startAt: string;
  endAt: string;
  isAllDay?: boolean;
  timezone?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  url?: string;
  isRecurring?: boolean;
  organizer?: { name?: string; email: string };
  /** Vendor X- properties (e.g. X-MICROSOFT-SKYPETEAMSMEETINGURL). Untrusted. */
  metadata?: Record<string, unknown>;
}
/** Response of adding an invite to a calendar. Dedup spans every calendar, so
 *  `alreadyOn` (where skipped events already are) can differ from `calendarName`. */
export interface EmailCalendarInviteAddResult {
  added: number;
  skipped: number;
  calendarId: string;
  calendarName?: string;
  alreadyOn?: string[];
}
/** Response of the calendar-invite preview endpoint (parse-only, no writes). */
export interface CalendarInvitePreview {
  filename: string;
  events: CalendarInviteEventDto[];
  warnings: string[];
  errors: string[];
  calendarName: string | null;
}
export interface EmailScheduledSendDto {
  id: string;
  sendAt: string | null;
  to: string[];
  subject: string;
  status: string;
}
export interface MailboxSignatureDto {
  signatureId: string;
  accountId: string;
  name: string;
  bodyHtml: string;
  isDefault: boolean;
  sortOrder: number;
}
export interface MailboxTemplateDto {
  templateId: string;
  accountId: string;
  name: string;
  subject: string | null;
  bodyHtml: string;
  category: string | null;
}
export interface MailboxSnippetDto {
  snippetId: string;
  accountId: string;
  trigger: string;
  body: string;
}
export interface MailboxContactDto {
  contactId: string;
  accountId: string;
  emailAddress: string;
  displayName: string | null;
  company: string | null;
  isFavorite: boolean;
}
export interface MailboxFilterCondition {
  field: 'from' | 'to' | 'subject' | 'body' | 'has_attachment';
  operator: 'contains' | 'not_contains' | 'equals' | 'starts_with' | 'ends_with' | 'matches_regex';
  value: string;
}
export interface MailboxFilterAction {
  type:
    | 'move_to_folder'
    | 'add_label'
    | 'mark_read'
    | 'mark_starred'
    | 'archive'
    | 'trash'
    | 'set_category';
  value: string;
}
export interface MailboxFilterDto {
  filterId: string;
  accountId: string;
  name: string;
  conditions: MailboxFilterCondition[];
  actions: MailboxFilterAction[];
  isEnabled: boolean;
  sortOrder: number;
  /** When set, this is a TIMED filter: the daily sweep applies it to inbox mail
   *  older than this many days, rather than on new mail. NULL/absent = on-new-mail. */
  runAfterDays?: number | null;
}

// ── Advanced (nested AND/OR) search query tree ──
export type AdvancedSearchField =
  | 'from'
  | 'recipient'
  | 'subject'
  | 'body'
  | 'has_attachment'
  | 'is_read'
  | 'is_starred'
  | 'date_before'
  | 'date_after';
export type AdvancedSearchOperator =
  | 'contains'
  | 'not_contains'
  | 'equals'
  | 'starts_with'
  | 'ends_with';
export interface AdvancedSearchCondition {
  type: 'condition';
  field: AdvancedSearchField;
  /** Only meaningful for the text fields. */
  operator?: AdvancedSearchOperator;
  value: string;
}
export interface AdvancedSearchGroup {
  type: 'group';
  combinator: 'and' | 'or';
  nodes: AdvancedSearchNode[];
}
export type AdvancedSearchNode = AdvancedSearchCondition | AdvancedSearchGroup;
export interface MailboxScreenedSenderDto {
  screenedId: string;
  accountId: string;
  senderAddress: string;
  senderName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  /** Who set the disposition (spam filter / screener / user). */
  source?: 'screener' | 'ai_spam' | 'failed_auth' | 'user' | null;
  reason?: string | null;
  decidedAt?: string | null;
  createdAt?: string;
}

/** One sender on an allow/block list, with every mailbox it is listed in. */
export interface EmailSenderListGroup {
  senderAddress: string;
  senderName: string | null;
  reason: string | null;
  source: MailboxScreenedSenderDto['source'];
  decidedAt: string;
  entries: { screenedId: string; accountId: string }[];
}

/** Opt-in auto-cleanup: move mail out of Spam into Trash after N days. */
export interface MailboxSpamRetentionDto {
  spamToTrash: { enabled: boolean; days: number };
}

/** AI spam filter per-account settings. */
export interface MailboxSpamSettingsDto {
  enabled: boolean;
  rules: string | null;
  defaultRules: string;
  spamRetention: MailboxSpamRetentionDto;
}

/** AI spam-filter spend (credits) over rolling windows, user-wide. */
export interface EmailSpamSpendDto {
  dayCents: number;
  weekCents: number;
  monthCents: number;
  allCents: number;
}

export interface SyncJob {
  id: string;
  connectionId: string;
  resourceType: 'calendar' | 'contact';
  localResourceId: string | null;
  remoteResourceId: string | null;
  remoteResourceName: string | null;
  /** Provider access role (owner/writer/reader/freeBusyReader). null = unknown,
   *  treated as writable. Read-only calendars cannot take a writing direction. */
  remoteAccessRole: string | null;
  direction: 'pull_only' | 'push_only' | 'bidirectional';
  syncToken: string | null;
  lastSyncedAt: string | null;
  lastSyncStatus: string;
  lastSyncError: string | null;
  lastSyncStats: SyncStats;
  autoSyncEnabled: boolean;
  autoSyncIntervalMinutes: number;
  conflictStrategy: string;
  createdAt: string;
  provider?: string;
  providerAccountName?: string;
}

export interface SyncStats {
  pulled?: number;
  pushed?: number;
  deleted?: number;
  conflicts?: number;
  errors?: string[];
  /**
   * Per-event failures the run recorded to `sync_event_failures` for retry.
   *
   * These do NOT fail the run — one unparseable event among fifty should not
   * mark the whole sync failed — so a job can report `lastSyncStatus:
   * 'success'` while having imported nothing at all. That is exactly how a
   * CalDAV parsing bug stayed invisible: push worked, the badge read
   * "Synced", and 100% of pulled events were silently landing in the retry
   * inbox. Surface this wherever sync health is shown.
   */
  failuresRecorded?: number;
  recurrenceSkipped?: number;
}

export interface SyncJobSettings {
  direction?: string;
  autoSyncEnabled?: boolean;
  autoSyncIntervalMinutes?: number;
  conflictStrategy?: string;
}

export interface RemoteCalendar {
  id: string;
  name: string;
  color?: string;
  primary?: boolean;
  accessRole?: string;
  timezone?: string;
}

export interface RemoteContactGroup {
  id: string;
  name: string;
  memberCount?: number;
  system?: boolean;
}
